import type {
  Permissionschema,
  TopologyArtifactOverlayLayerStatusschema,
  TopologyV2ResolveResponse,
  TopologyV2ViewCatalogItemschema,
  TopologyV2ViewCreateRequest,
  TopologyV2ViewOverlayStateschema,
  TopologyV2ViewSaveResponse,
  TopologyV2ViewSaveWarningschema,
  TopologyV2ViewScopeModeschema,
  TopologyV2ViewUpdateRequest
} from '@app/types/sdk';
import {
  Device,
  Connector,
  ConnectorCluster,
  ConnectorClusterCreate,
  ConnectorClusterUpdate,
  ConnectorCreate,
  ConnectorStatusEnum,
  ConnectorUpdate,
  ClusterSitesAttachRequest,
  ClusterSitesAttachResult,
  ClusterSitesDetachRequest,
  ClusterSitesDetachResult,
  IdpConnection,
  IdpConnectionCreate,
  IdpConnectionOidcCreateConfig,
  IdpConnectionSamlCreateConfig,
  IdpConnectionUpdate,
  IdpOidcConfigschema,
  IdpSamlConfigschema,
  Invitation,
  InvitationCreate,
  Membership,
  MembershipUpdate,
  Role,
  RoleCreate,
  RoleMappingRule,
  RoleMappingRuleCreate,
  RoleMappingRuleUpdate,
  RoleUpdate,
  Secretschema,
  SecretCreate,
  SecretSetValue,
  Credential,
  CredentialCreate,
  CredentialUpdate,
  CredentialAssignment,
  CredentialAssignmentCreate,
  CredentialAssignmentUpdate,
  ApiKey,
  ApiKeyCreate,
  ApiKeyUpdate,
  ApiKeyStatusEnum,
  Vendorschema,
  Site,
  SiteSiteTypeEnum,
  SiteCreate,
  SiteUpdate,
  TopologyGraph,
  DiscoveryJob,
  DiscoveryJobCreate,
  DiscoveryJobUpdate,
  DiscoveryRun,
  DiscoveryRunStatusEnum,
  DiscoveryEvent,
  DiscoveryRunSummaryschema,
  ChatRunEventArtifactUpdateschemaArtifactKindEnum,
  ChatRunEventArtifactUpdateschemaUpdateKindEnum,
  TopologyArtifactOverlayStateschemaSchemaEnum,
  TopologyOverlayBaseschemaSourceEnum,
  TopologyOverlayBaseschemaTimeModeEnum,
  TopologyOverlayLayerschemaKindEnum,
  TopologyOverlayPatchOpschemaOpEnum,
  VisibilityProposal,
  VisibilityProposalOriginschema,
  VisibilityProposalStatusschema,
  VisibilitySession,
  VisibilitySessionModeschema,
  VisibilitySessionStatusschema,
  VisibilityWorkspaceFamilyschema
} from '@app/types/sdk';
import type { FileTextLineschema, FileTextSlice, Fileschema } from '@reap/openapi-public-v1';
import type { FileAnalysisState, FileAvailabilityState, UploadedFileRecord } from '@app/components/chat/chatArtifactUtils';
import { applyTopologyOverlayPatch, hydrateTopologyOverlayState } from '@app/utils/topologyOverlay';
import { normalizeUUIDString } from '@app/utils/uuid';
import { searchMockTopologyPathEndpoints, traceMockTopologyPath } from '@mocks/data/topologyPathTracer';
import { buildMockCloudConnectivity } from '@mocks/data/cloudConnectivity';
import {
  CAMPUS_B_FIREWALL_DEMO_SITE_ID,
  createSiteIntelligenceBfdFindingMock,
  createSiteIntelligenceBfdFindingsMock,
  createSiteIntelligenceBfdMock,
  createSiteIntelligenceBfdReviewQuestionsMock,
  createSiteIntelligenceOspfMock,
  createSiteIntelligenceOspfFindingsMock,
  createSiteIntelligenceOspfReviewQuestionsMock,
  createSiteIntelligenceOspfFindingMock,
  createSiteIntelligenceIsisMock,
  createSiteIntelligenceIsisFindingsMock,
  createSiteIntelligenceIsisReviewQuestionsMock,
  createSiteIntelligenceIsisFindingMock,
  createSiteIntelligenceBgpMock,
  createSiteIntelligenceMock,
  createSiteIntelligenceNamingFindingMock,
  createSiteIntelligenceNamingMock,
  createSiteIntelligenceNamingReviewQuestionsMock,
  createSiteIntelligenceTopologyFindingMock,
  createSiteIntelligenceTopologyFindingsMock,
  createSiteIntelligenceTopologyMock,
  createSiteIntelligenceTopologyReviewQuestionsMock,
  createSiteIntelligenceBgpFindingMock,
  createSiteIntelligenceBgpFindingsMock,
  createSiteIntelligenceRoutingMock,
  createSiteIntelligenceRoutingIgpMock,
  createSiteIntelligenceBgpReviewQuestionsMock,
  createSiteIntelligenceVlanMock,
  createSiteIntelligenceVlanFindingMock,
  createSiteIntelligenceVlansMock,
  listSiteIntelligenceMockDevices
} from '@mocks/data/siteIntelligence';
import type { MockContext } from '@mocks/mockFetch';

type MockHandler = (ctx: MockContext) => Promise<Response | null> | Response | null;

const PRIMARY_ORG_ID = '11111111-1111-4111-8111-111111111111';
const SECONDARY_ORG_ID = '22222222-2222-4222-8222-222222222222';

const ORG_PROFILES: Record<string, Record<string, unknown>> = {
  [PRIMARY_ORG_ID]: {
    id: PRIMARY_ORG_ID,
    name: 'Arcadia Networks',
    notes: 'Primary operations org used across mocks.',
    createdAt: '2024-01-11T14:20:00.000Z',
    updatedAt: '2025-10-13T09:00:00.000Z',
    createdBy: 'system',
    updatedBy: 'system'
  },
  [SECONDARY_ORG_ID]: {
    id: SECONDARY_ORG_ID,
    name: 'Reap Labs Internal',
    notes: 'Secondary org for switch testing.',
    createdAt: '2024-02-05T17:40:00.000Z',
    updatedAt: '2025-09-28T07:30:00.000Z',
    createdBy: 'system',
    updatedBy: 'system'
  }
};

type Mutable<T> = { -readonly [K in keyof T]: T[K] };
type ConnectionWithExtras = Mutable<IdpConnection> & {
  startUrl?: string | null;
  idpInitiated?: boolean | null;
};

interface MappingRuleTemplate {
  id: string;
  enabled: boolean;
  priority: number;
  match: object;
  result: object;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

interface SecretTemplate {
  id: string;
  orgId: string;
  type: Secretschema['type'];
  metadata?: { [key: string]: unknown };
  fingerprint?: string;
  last4?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

type HealthPill = 'healthy' | 'mixed' | 'offline';
type HaState = 'ok' | 'warn' | 'degraded';
type UserdataStatus = 'available' | 'redeemed' | 'expired';

const normalizeConnectorStatus = (value?: string | null): ConnectorStatusEnum => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : undefined;
  switch (normalized) {
    case ConnectorStatusEnum.Registered:
    case ConnectorStatusEnum.Online:
    case ConnectorStatusEnum.Offline:
    case ConnectorStatusEnum.Decommissioned:
    case ConnectorStatusEnum.Provisioning:
      return normalized;
    default:
      return ConnectorStatusEnum.Provisioning;
  }
};

interface SiteTemplate extends Pick<Site, 'siteType' | 'awsRegion' | 'awsAccountId' | 'azureSubscriptionId' | 'azureLocation' | 'pingEndpointIp'> {
  id: string;
  name: string;
  address?: Site['address'];
  notes?: Site['notes'];
  labels?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  clusterIds: string[];
  deviceCounts?: {
    total: number;
    active: number;
    offline: number;
  };
  connectorCount?: number;
  health?: HealthPill;
}

interface ClusterTemplate {
  id: string;
  name: string;
  description?: string;
  status?: string;
  labels?: Record<string, string>;
  settings?: ConnectorCluster['settings'];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  siteIds: string[];
  connectorsOnline: number;
  connectorsTotal: number;
  haState: HaState;
  health?: HealthPill;
}

interface DiscoveryRunTemplate {
  id: string;
  jobId: string;
  status: DiscoveryRunStatusEnum;
  queuedAt: string;
  startedAt: string;
  finishedAt: string;
  summary?: DiscoveryRunSummaryschema;
}

interface StoredDiscoveryJob {
  data: Mutable<DiscoveryJob>;
  etag: string;
}

interface StoredDiscoveryRun {
  data: Mutable<DiscoveryRun>;
  etag: string;
  events: DiscoveryEvent[];
  nextSeq: number;
}

interface DiscoveryOrgState {
  jobs: Map<string, StoredDiscoveryJob>;
  runs: Map<string, StoredDiscoveryRun>;
}

const discoveryStore = new Map<string, DiscoveryOrgState>();

interface ConnectorTemplate {
  id: string;
  name: string;
  status: string;
  version: string;
  registeredAt: string;
  lastSeenAt: string;
  labels?: Record<string, string>;
  clusterId: string;
  createdBy?: string;
  updatedBy?: string;
  siteHints?: string[];
  locationHint?: string;
  userdataStatus?: UserdataStatus;
}

const membershipTemplates = [
  {
    id: 'mem-alice',
    userId: 'user-alice',
    orgSlug: 'arcadia-networks',
    rolesResolved: [{ id: 'role-admin', slug: 'admin', name: 'Administrator' }],
    status: 'active',
    labels: { team: 'netops' },
    createdAt: '2025-09-28T08:00:00.000Z',
    updatedAt: '2025-10-11T07:45:00.000Z',
    user: {
      id: 'user-alice',
      email: 'alice@arcadia.example',
      displayName: 'Alice Chen'
    },
    createdBy: 'user-system',
    updatedBy: 'user-system'
  },
  {
    id: 'mem-raj',
    userId: 'user-raj',
    orgSlug: 'arcadia-networks',
    rolesResolved: [{ id: 'role-editor', slug: 'editor', name: 'Editor' }],
    status: 'active',
    labels: { team: 'automation' },
    createdAt: '2025-08-19T11:20:00.000Z',
    updatedAt: '2025-10-10T12:10:00.000Z',
    user: {
      id: 'user-raj',
      email: 'raj@arcadia.example',
      displayName: 'Raj Kumar'
    },
    createdBy: 'user-alice',
    updatedBy: 'user-alice'
  },
  {
    id: 'mem-jess',
    userId: 'user-jess',
    orgSlug: 'arcadia-networks',
    rolesResolved: [{ id: 'role-viewer', slug: 'viewer', name: 'Viewer' }],
    status: 'suspended',
    labels: { team: 'field' },
    createdAt: '2025-10-13T06:40:00.000Z',
    updatedAt: '2025-10-13T06:40:00.000Z',
    user: {
      id: 'user-jess',
      email: 'jessica@beta.example',
      displayName: 'Jessica Lee'
    },
    createdBy: 'user-raj',
    updatedBy: 'user-raj'
  }
];

const roleTemplates = [
  {
    id: 'role-admin',
    scope: 'org',
    slug: 'admin',
    name: 'Administrator',
    description: 'Full control over the organization.',
    status: 'active',
    permissions: [
      { resource: 'org', verbs: ['read', 'update'] },
      { resource: 'site', verbs: ['create', 'update', 'delete'] },
      { resource: 'device', verbs: ['read', 'update'] },
      { resource: 'apiKey', verbs: ['create', 'delete'] }
    ] as Permissionschema[],
    createdAt: '2024-01-02T10:00:00.000Z',
    updatedAt: '2025-06-21T15:30:00.000Z',
    createdBy: 'system',
    updatedBy: 'system'
  },
  {
    id: 'role-editor',
    scope: 'org',
    slug: 'editor',
    name: 'Editor',
    description: 'Manage day-to-day operations.',
    status: 'active',
    permissions: [
      { resource: 'site', verbs: ['read', 'update'] },
      { resource: 'device', verbs: ['read', 'update'] },
      { resource: 'credential', verbs: ['read', 'update'] },
      { resource: 'discoveryJob', verbs: ['read', 'execute'] }
    ] as Permissionschema[],
    createdAt: '2024-01-02T10:00:00.000Z',
    updatedAt: '2025-02-18T09:15:00.000Z',
    createdBy: 'system',
    updatedBy: 'system'
  },
  {
    id: 'role-viewer',
    scope: 'org',
    slug: 'viewer',
    name: 'Viewer',
    description: 'Read-only access.',
    status: 'active',
    permissions: [{ resource: 'org', verbs: ['read'] }, { resource: 'site', verbs: ['read'] }, { resource: 'device', verbs: ['read'] }] as Permissionschema[],
    createdAt: '2024-01-02T10:00:00.000Z',
    updatedAt: '2025-02-18T09:15:00.000Z',
    createdBy: 'system',
    updatedBy: 'system'
  }
];

const invitationTemplates = [
  {
    id: 'inv-jane',
    email: 'jane@beta.example',
    rolesResolved: [{ id: 'role-editor', slug: 'editor', name: 'Editor' }],
    status: 'pending',
    expiresAt: '2025-11-01T12:00:00.000Z',
    invitedBy: 'user-alice',
    createdAt: '2025-10-10T12:30:00.000Z',
    updatedAt: '2025-10-10T12:30:00.000Z'
  },
  {
    id: 'inv-sam',
    email: 'sam@ops.example',
    rolesResolved: [{ id: 'role-viewer', slug: 'viewer', name: 'Viewer' }],
    status: 'accepted',
    expiresAt: '2025-08-01T09:00:00.000Z',
    invitedBy: 'user-raj',
    createdAt: '2025-06-20T09:45:00.000Z',
    updatedAt: '2025-06-21T10:15:00.000Z',
    acceptedAt: '2025-06-21T10:05:00.000Z',
    acceptedBy: 'user-sam'
  }
];

const idpConnectionTemplates: Array<Mutable<IdpConnection>> = [
  {
    id: 'idp-saml-okta',
    type: 'saml',
    status: 'active',
    name: 'Okta Prod',
    description: 'Primary Okta SAML connection',
    config: {
      idpEntityId: 'urn:okta:arcadia:prod',
      idpSsoUrl: 'https://arcadia.okta.com/app/sso/saml',
      nameIdFormat: 'emailAddress',
      allowIdpInitiated: true,
      groupsAttributeName: 'groups'
    },
    orgId: PRIMARY_ORG_ID,
    createdAt: new Date('2024-03-10T12:00:00.000Z'),
    updatedAt: new Date('2025-09-01T15:20:00.000Z'),
    createdBy: 'user-alice',
    updatedBy: 'user-alice'
  },
  {
    id: 'idp-oidc-entra',
    type: 'oidc',
    status: 'active',
    name: 'Entra ID',
    description: 'OIDC connection for Microsoft Entra',
    config: {
      issuer: 'https://login.microsoftonline.com/arcadia/v2.0',
      clientId: 'entra-client-id',
      clientSecretId: 'sec-entra-client',
      tokenAuthMethod: 'client_secret_post',
      scopes: ['openid', 'profile', 'email']
    },
    orgId: PRIMARY_ORG_ID,
    createdAt: new Date('2024-05-18T09:00:00.000Z'),
    updatedAt: new Date('2025-08-12T10:05:00.000Z'),
    createdBy: 'user-raj',
    updatedBy: 'user-raj'
  }
];

const idpMappingRuleTemplates: Record<string, MappingRuleTemplate[]> = {
  'idp-saml-okta': [
    {
      id: 'map-okta-admins',
      enabled: true,
      priority: 10,
      match: {
        source: 'group',
        operator: 'equals',
        value: 'netops-admins'
      },
      result: {
        roleSlugs: ['admin']
      },
      createdAt: new Date('2024-06-01T12:00:00.000Z'),
      updatedAt: new Date('2025-09-09T08:30:00.000Z'),
      createdBy: 'user-alice',
      updatedBy: 'user-alice'
    },
    {
      id: 'map-okta-ops',
      enabled: true,
      priority: 20,
      match: {
        source: 'group',
        operator: 'equals',
        value: 'ops-team'
      },
      result: {
        roleSlugs: ['editor']
      },
      createdAt: new Date('2024-06-15T12:00:00.000Z'),
      updatedAt: new Date('2025-09-10T08:30:00.000Z'),
      createdBy: 'user-alice',
      updatedBy: 'user-alice'
    }
  ],
  'idp-oidc-entra': [
    {
      id: 'map-entra-secops',
      enabled: true,
      priority: 10,
      match: {
        source: 'group',
        operator: 'equals',
        value: 'secops'
      },
      result: {
        roleSlugs: ['viewer']
      },
      createdAt: new Date('2024-07-02T10:00:00.000Z'),
      updatedAt: new Date('2025-08-28T15:45:00.000Z'),
      createdBy: 'user-raj',
      updatedBy: 'user-raj'
    }
  ]
};

const secretTemplates: SecretTemplate[] = [
  {
    id: 'sec-entra-client',
    orgId: PRIMARY_ORG_ID,
    type: 'password' as Secretschema['type'],
    createdAt: new Date('2024-05-02T09:00:00.000Z'),
    updatedAt: new Date('2025-08-18T11:45:00.000Z'),
    createdBy: 'system',
    updatedBy: 'system',
    metadata: {
      label: 'Entra client secret'
    },
    last4: 'E1D2'
  },
  {
    id: 'sec-okta-signing',
    orgId: PRIMARY_ORG_ID,
    type: 'opaque' as Secretschema['type'],
    createdAt: new Date('2024-04-11T07:30:00.000Z'),
    updatedAt: new Date('2025-09-05T12:10:00.000Z'),
    createdBy: 'system',
    updatedBy: 'system',
    metadata: {
      label: 'Okta signing cert'
    },
    last4: 'O1K4'
  },
  {
    id: 'sec-snmpv2-read',
    orgId: PRIMARY_ORG_ID,
    type: 'snmp_community' as Secretschema['type'],
    createdAt: new Date('2024-06-01T10:00:00.000Z'),
    updatedAt: new Date('2025-08-15T08:30:00.000Z'),
    createdBy: 'system',
    updatedBy: 'system',
    metadata: { label: 'SNMPv2 read community' },
    last4: 'RC12'
  },
  {
    id: 'sec-snmpv2-write',
    orgId: PRIMARY_ORG_ID,
    type: 'snmp_community' as Secretschema['type'],
    createdAt: new Date('2024-06-01T10:05:00.000Z'),
    updatedAt: new Date('2025-08-15T08:35:00.000Z'),
    createdBy: 'system',
    updatedBy: 'system',
    metadata: { label: 'SNMPv2 write community' },
    last4: 'WC34'
  },
  {
    id: 'sec-snmpv3-auth',
    orgId: PRIMARY_ORG_ID,
    type: 'snmp_auth_password' as Secretschema['type'],
    createdAt: new Date('2024-07-12T12:15:00.000Z'),
    updatedAt: new Date('2025-08-21T09:45:00.000Z'),
    createdBy: 'system',
    updatedBy: 'system',
    metadata: { label: 'SNMPv3 auth password' },
    last4: 'AP56'
  },
  {
    id: 'sec-snmpv3-priv',
    orgId: PRIMARY_ORG_ID,
    type: 'snmp_priv_password' as Secretschema['type'],
    createdAt: new Date('2024-07-12T12:20:00.000Z'),
    updatedAt: new Date('2025-08-21T09:50:00.000Z'),
    createdBy: 'system',
    updatedBy: 'system',
    metadata: { label: 'SNMPv3 priv password' },
    last4: 'PP78'
  },
  {
    id: 'sec-gnmi-core-password',
    orgId: PRIMARY_ORG_ID,
    type: 'password' as Secretschema['type'],
    createdAt: new Date('2026-07-27T06:00:00.000Z'),
    updatedAt: new Date('2026-07-27T06:00:00.000Z'),
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    metadata: { label: 'gNMI core switch password' },
    last4: 'dmin'
  },
  {
    id: 'sec-gnmi-customer-device-ca',
    orgId: PRIMARY_ORG_ID,
    type: 'tls_ca_bundle_pem' as Secretschema['type'],
    createdAt: new Date('2026-07-27T06:00:00.000Z'),
    updatedAt: new Date('2026-07-27T06:00:00.000Z'),
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    metadata: { label: 'Device CA for gNMI server verification' },
    last4: 'PEM)'
  }
];

interface StoredConnection {
  data: IdpConnection;
  etag: string;
}

interface StoredMappingRule {
  data: RoleMappingRule;
  etag: string;
}

interface StoredSecret {
  data: Mutable<Secretschema>;
  etag: string;
}

const idpConnectionStore = new Map<string, Map<string, StoredConnection>>();
const idpMappingStore = new Map<string, Map<string, Map<string, StoredMappingRule>>>();
const secretStore = new Map<string, Map<string, StoredSecret>>();
interface StoredMembership {
  data: MembershipRecord;
  etag: string;
}

interface StoredInvitation {
  data: InvitationRecord;
  etag: string;
}

interface StoredRole {
  data: RoleRecord;
  etag: string;
}

interface StoredApiKey {
  data: ApiKeyRecord;
  etag: string;
}

const membershipStore = new Map<string, Map<string, StoredMembership>>();
const invitationStore = new Map<string, Map<string, StoredInvitation>>();
const roleStore = new Map<string, Map<string, StoredRole>>();
const apiKeyStore = new Map<string, Map<string, StoredApiKey>>();

type MembershipRecord = Mutable<Membership>;
type InvitationRecord = Mutable<Invitation>;
type RoleRecord = Mutable<Role>;
type ApiKeyRecord = Mutable<ApiKey>;

const cloneValue = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const randomId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
};

const generateEtag = (): string => `W/"mock-${Math.random().toString(36).slice(2, 10)}"`;

const API_KEY_SECRET_PREFIX = 'rk_';
const generateApiKeySecret = (): string => {
  const randomPart = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return `${API_KEY_SECRET_PREFIX}${randomPart.slice(0, 32)}`;
};

function cloneConnectionTemplate(template: Mutable<IdpConnection>, orgId: string): ConnectionWithExtras {
  const createdAt = template.createdAt ? new Date(template.createdAt) : new Date();
  const updatedAt = template.updatedAt ? new Date(template.updatedAt) : new Date(createdAt);
  const createdBy = template.createdBy ?? 'system';
  const updatedBy = template.updatedBy ?? 'system';
  const id = template.id ?? randomId('idp');

  if (template.type === 'oidc') {
    return {
      ...template,
      id,
      orgId,
      type: 'oidc',
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
      startUrl: buildMockStartUrl(id),
      config: cloneValue(template.config) as IdpOidcConfigschema
    } as ConnectionWithExtras;
  }

  return {
    ...template,
    id,
    orgId,
    type: 'saml',
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    startUrl: buildMockStartUrl(id),
    config: cloneValue(template.config) as IdpSamlConfigschema
  } as ConnectionWithExtras;
}

function cloneConnectionForResponse(connection: IdpConnection): ConnectionWithExtras {
  const createdAt = connection.createdAt ? new Date(connection.createdAt) : new Date();
  const updatedAt = connection.updatedAt ? new Date(connection.updatedAt) : new Date(createdAt);
  const createdBy = connection.createdBy ?? 'mock-user';
  const updatedBy = connection.updatedBy ?? 'mock-user';
  const extras = connection as ConnectionWithExtras;
  const startUrl =
    extras.startUrl && extras.startUrl.trim().length > 0
      ? extras.startUrl
      : connection.id
      ? buildMockStartUrl(connection.id)
      : undefined;

  if (connection.type === 'oidc') {
    return {
      ...connection,
      type: 'oidc',
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
      startUrl,
      config: cloneValue(connection.config) as IdpOidcConfigschema
    } as ConnectionWithExtras;
  }

  return {
    ...connection,
    type: 'saml',
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    startUrl,
    config: cloneValue(connection.config) as IdpSamlConfigschema
  } as ConnectionWithExtras;
}

const MOCK_BASE_URL = 'https://mock.reap.local';
const MOCK_SSO_START_BASE = `${MOCK_BASE_URL}/sso/start`;
const MOCK_OIDC_REDIRECT_BASE = `${MOCK_BASE_URL}/auth/callback`;
const MOCK_SAML_BASE = `${MOCK_BASE_URL}/saml`;
const buildMockStartUrl = (connectionId: string): string => `${MOCK_SSO_START_BASE}/${connectionId}`;

const normalizeTokenAuthMethod = (
  method?: IdpConnectionOidcCreateConfig['tokenAuthMethod']
): NonNullable<IdpOidcConfigschema['tokenAuthMethod']> => {
  if (method === 'client_secret_post') {
    return 'client_secret_post';
  }
  return 'client_secret_basic';
};

const buildMockOidcConfigFromCreate = (
  config: IdpConnectionOidcCreateConfig,
  connectionId: string
): IdpOidcConfigschema => ({
  issuer: config.issuer,
  clientId: config.clientId,
  clientSecretId: config.clientSecretId ?? undefined,
  tokenAuthMethod: normalizeTokenAuthMethod(config.tokenAuthMethod),
  redirectUris: [`${MOCK_OIDC_REDIRECT_BASE}/${connectionId}`],
  scopes: config.scopes ? [...config.scopes] : ['openid', 'email', 'profile'],
  enforcePkce: config.enforcePkce ?? false,
  groupsClaimPath: config.groupsClaimPath ?? undefined,
  rolesClaimPath: config.rolesClaimPath ?? undefined,
  clockSkewSeconds: config.clockSkewSeconds ?? 120
});

const buildMockSamlConfigFromCreate = (
  config: IdpConnectionSamlCreateConfig,
  connectionId: string
): IdpSamlConfigschema => ({
  idpMetadataUrl: config.idpMetadataUrl ?? undefined,
  idpEntityId: config.idpEntityId ?? undefined,
  idpSsoUrl: config.idpSsoUrl ?? undefined,
  idpCertificate: config.idpCertificate ?? undefined,
  nameIdFormat: config.nameIdFormat ?? 'unspecified',
  groupsAttributeName: config.groupsAttributeName ?? undefined,
  rolesAttributeName: config.rolesAttributeName ?? undefined,
  allowIdpInitiated: config.allowIdpInitiated ?? true,
  clockSkewSeconds: config.clockSkewSeconds ?? 120,
  acsUrl: `${MOCK_SAML_BASE}/acs/${connectionId}`,
  spEntityId: `${MOCK_SAML_BASE}/sp/${connectionId}`
});

function cloneSecretTemplate(template: SecretTemplate, orgId: string): Mutable<Secretschema> {
  const createdAt = template.createdAt ? new Date(template.createdAt) : new Date();
  const updatedAt = template.updatedAt ? new Date(template.updatedAt) : new Date(createdAt);
  return {
    id: template.id ?? randomId('secret'),
    orgId,
    type: template.type,
    metadata: cloneValue(template.metadata ?? {}) as Secretschema['metadata'],
    fingerprint: template.fingerprint ?? `mock-${Math.random().toString(36).slice(2, 10)}`,
    last4: template.last4 ?? '****',
    createdAt,
    updatedAt,
    createdBy: template.createdBy ?? 'system',
    updatedBy: template.updatedBy ?? 'system'
  } as Mutable<Secretschema>;
}

const computeSecretLast4 = (value: string): string => {
  if (!value) {
    return '----';
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '----';
  }
  return trimmed.slice(-4).padStart(4, '*');
};

function initializeSecretStore(orgId: string): Map<string, StoredSecret> {
  const records = new Map<string, StoredSecret>();
  secretTemplates.forEach((template) => {
    if (template.orgId && template.orgId !== orgId && template.orgId !== PRIMARY_ORG_ID) {
      return;
    }
    const data = cloneSecretTemplate(template, orgId);
    const identifier = data.id as string;
    records.set(identifier, {
      data,
      etag: generateEtag()
    });
  });
  secretStore.set(orgId, records);
  return records;
}

function getSecretStore(orgId: string): Map<string, StoredSecret> {
  return secretStore.get(orgId) ?? initializeSecretStore(orgId);
}

function cloneSecretRecord(record: StoredSecret): Secretschema {
  const data = record.data;
  return {
    id: data.id,
    orgId: data.orgId,
    type: data.type,
    metadata: cloneValue(data.metadata ?? {}) as Secretschema['metadata'],
    fingerprint: data.fingerprint,
    last4: data.last4,
    createdAt: data.createdAt instanceof Date ? new Date(data.createdAt) : data.createdAt,
    updatedAt: data.updatedAt instanceof Date ? new Date(data.updatedAt) : data.updatedAt,
    createdBy: data.createdBy,
    updatedBy: data.updatedBy
  } as Secretschema;
}

function createSecretRecord(orgId: string, payload: SecretCreate): StoredSecret {
  const store = getSecretStore(orgId);
  const now = new Date();
  const id = randomId('secret');
  const data: Mutable<Secretschema> = {
    id,
    orgId,
    type: payload.type,
    metadata: cloneValue(payload.metadata ?? {}) as Secretschema['metadata'],
    fingerprint: `mock-${Math.random().toString(36).slice(2, 10)}`,
    last4: computeSecretLast4(payload.value),
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user'
  };
  const record: StoredSecret = {
    data,
    etag: generateEtag()
  };
  store.set(id, record);
  return record;
}

function getSecretRecord(orgId: string, secretId: string): StoredSecret | null {
  const store = getSecretStore(orgId);
  return store.get(secretId) ?? null;
}

function updateSecretValueRecord(record: StoredSecret, value: string): void {
  const now = new Date();
  record.data.last4 = computeSecretLast4(value);
  record.data.updatedAt = now;
  record.data.updatedBy = 'mock-user';
  record.data.fingerprint = `mock-${Math.random().toString(36).slice(2, 10)}`;
  record.etag = generateEtag();
}

function deleteSecretRecord(orgId: string, secretId: string): boolean {
  const store = getSecretStore(orgId);
  return store.delete(secretId);
}

const siteTemplates: SiteTemplate[] = [
  {
    id: 'site-bangalore-demo',
    name: 'Bangalore',
    address: {
      city: 'Bengaluru',
      state: 'Karnataka',
      country: 'IN'
    },
    notes: 'UI-only Site Intelligence fixture.',
    labels: { region: 'apac', environment: 'demo', feature: 'site-intelligence' },
    pingEndpointIp: '10.110.130.2',
    createdAt: '2026-08-27T02:40:00.000Z',
    updatedAt: '2026-08-27T02:40:00.000Z',
    createdBy: 'site-intelligence-mock',
    updatedBy: 'site-intelligence-mock',
    clusterIds: [],
    deviceCounts: { total: 13, active: 13, offline: 0 },
    connectorCount: 0,
    health: 'healthy'
  },
  {
    id: 'site-campus-b-firewall-demo',
    name: 'CAMPUSB-HQ-S02',
    notes: 'UI-only three-tier topology classification fixture.',
    labels: { region: 'demo', environment: 'demo', feature: 'site-intelligence' },
    pingEndpointIp: '10.110.120.2',
    createdAt: '2026-09-03T16:50:53.840Z',
    updatedAt: '2026-09-03T16:50:53.840Z',
    createdBy: 'site-intelligence-mock',
    updatedBy: 'site-intelligence-mock',
    clusterIds: [],
    deviceCounts: { total: 14, active: 14, offline: 0 },
    connectorCount: 0,
    health: 'healthy'
  },
  {
    id: 'site-sfo',
    name: 'SFO',
    address: {
      line1: '1 Market Street',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94105',
      country: 'US'
    },
    notes: 'Primary west coast POP with dual clusters for HA.',
    labels: { region: 'us-west', tier: 'core', metro: 'sfo' },
    pingEndpointIp: '10.20.30.8',
    createdAt: '2024-02-01T10:00:00.000Z',
    updatedAt: '2025-10-12T08:30:00.000Z',
    createdBy: 'user-alice',
    updatedBy: 'user-alice',
    clusterIds: ['cluster-edge-west', 'cluster-ha-west'],
    deviceCounts: { total: 86, active: 82, offline: 4 },
    connectorCount: 4,
    health: 'healthy'
  },
  {
    id: 'site-ams',
    name: 'AMS',
    address: {
      line1: 'Science Park 123',
      city: 'Amsterdam',
      country: 'NL'
    },
    notes: 'EU regional hub servicing NL and DE sites.',
    labels: { region: 'eu', tier: 'edge' },
    pingEndpointIp: '10.40.50.8',
    createdAt: '2024-02-15T08:00:00.000Z',
    updatedAt: '2025-09-22T06:15:00.000Z',
    createdBy: 'user-raj',
    updatedBy: 'user-raj',
    clusterIds: ['cluster-eu-core'],
    deviceCounts: { total: 48, active: 41, offline: 7 },
    connectorCount: 2,
    health: 'mixed'
  },
  {
    id: 'site-nyc',
    name: 'NYC-HQ',
    address: {
      line1: '350 5th Ave',
      city: 'New York',
      state: 'NY',
      postalCode: '10118',
      country: 'US'
    },
    notes: 'Headquarters campus with dense LAN footprint.',
    labels: { region: 'us-east', tier: 'core', metro: 'nyc' },
    pingEndpointIp: '10.60.70.8',
    createdAt: '2024-04-11T09:20:00.000Z',
    updatedAt: '2025-10-10T12:10:00.000Z',
    createdBy: 'user-alice',
    updatedBy: 'user-alice',
    clusterIds: ['cluster-ha-east', 'cluster-ha-west'],
    deviceCounts: { total: 120, active: 115, offline: 5 },
    connectorCount: 3,
    health: 'healthy'
  },
  {
    id: 'site-sin',
    name: 'Singapore Regional',
    address: {
      line1: '1 Fusionopolis Way',
      city: 'Singapore',
      country: 'SG'
    },
    notes: 'APAC aggregation site preparing for cutover.',
    labels: { region: 'apac', tier: 'aggregation' },
    createdAt: '2024-07-19T05:40:00.000Z',
    updatedAt: '2025-08-02T04:55:00.000Z',
    createdBy: 'user-raj',
    updatedBy: 'user-raj',
    clusterIds: ['cluster-apac'],
    deviceCounts: { total: 32, active: 27, offline: 5 },
    connectorCount: 1,
    health: 'offline'
  }
];

const clusterTemplates: ClusterTemplate[] = [
  {
    id: 'cluster-edge-west',
    name: 'Edge West',
    description: 'Primary west coast ingress cluster.',
    status: 'active',
    labels: { env: 'prod', role: 'ingress' },
    settings: {
      internalInspection: {
        enabled: true
      }
    },
    createdAt: '2024-04-01T09:00:00.000Z',
    updatedAt: '2025-09-28T14:00:00.000Z',
    createdBy: 'user-alice',
    updatedBy: 'user-alice',
    siteIds: ['site-sfo'],
    connectorsOnline: 3,
    connectorsTotal: 3,
    haState: 'ok',
    health: 'healthy'
  },
  {
    id: 'cluster-ha-west',
    name: 'HA West',
    description: 'High-availability cluster spanning SF and NYC.',
    status: 'active',
    labels: { env: 'prod', role: 'ha' },
    createdAt: '2024-05-21T11:30:00.000Z',
    updatedAt: '2025-09-15T18:20:00.000Z',
    createdBy: 'user-raj',
    updatedBy: 'user-raj',
    siteIds: ['site-sfo', 'site-nyc'],
    connectorsOnline: 2,
    connectorsTotal: 2,
    haState: 'ok',
    health: 'healthy'
  },
  {
    id: 'cluster-eu-core',
    name: 'EU Core',
    description: 'Handles EU hub traffic and discovery jobs.',
    status: 'active',
    labels: { env: 'prod', role: 'core' },
    createdAt: '2024-04-15T07:30:00.000Z',
    updatedAt: '2025-08-30T13:45:00.000Z',
    createdBy: 'user-raj',
    updatedBy: 'user-raj',
    siteIds: ['site-ams'],
    connectorsOnline: 1,
    connectorsTotal: 2,
    haState: 'warn',
    health: 'mixed'
  },
  {
    id: 'cluster-ha-east',
    name: 'HA East',
    description: 'East coast HA cluster anchored at NYC.',
    status: 'degraded',
    labels: { env: 'prod', role: 'ha' },
    createdAt: '2024-06-12T10:15:00.000Z',
    updatedAt: '2025-10-11T04:05:00.000Z',
    createdBy: 'user-jess',
    updatedBy: 'user-jess',
    siteIds: ['site-nyc'],
    connectorsOnline: 1,
    connectorsTotal: 1,
    haState: 'warn',
    health: 'mixed'
  },
  {
    id: 'cluster-apac',
    name: 'APAC Bootstrap',
    description: 'Bootstrapping APAC rollout; currently single connector.',
    status: 'planned',
    labels: { env: 'staging', role: 'bootstrap' },
    settings: {
      internalInspection: {
        enabled: false
      }
    },
    createdAt: '2024-08-01T03:00:00.000Z',
    updatedAt: '2025-07-25T02:45:00.000Z',
    createdBy: 'user-alice',
    updatedBy: 'user-raj',
    siteIds: ['site-sin'],
    connectorsOnline: 0,
    connectorsTotal: 1,
    haState: 'degraded',
    health: 'offline'
  }
];

const connectorTemplates: ConnectorTemplate[] = [
  {
    id: 'connector-sfo-1',
    name: 'arc-sfo-conn-1',
    status: 'online',
    version: '1.14.3',
    registeredAt: '2024-05-01T11:00:00.000Z',
    lastSeenAt: '2025-10-15T06:40:00.000Z',
    labels: { region: 'us-west', role: 'ingress', ha: 'primary' },
    clusterId: 'cluster-edge-west',
    siteHints: ['site-sfo'],
    userdataStatus: 'redeemed'
  },
  {
    id: 'connector-sfo-2',
    name: 'arc-sfo-conn-2',
    status: 'online',
    version: '1.14.3',
    registeredAt: '2024-11-12T09:10:00.000Z',
    lastSeenAt: '2025-10-15T06:41:00.000Z',
    labels: { region: 'us-west', role: 'ingress', ha: 'secondary' },
    clusterId: 'cluster-edge-west',
    siteHints: ['site-sfo'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-west-ha-1',
    name: 'ha-west-1',
    status: 'online',
    version: '1.14.2',
    registeredAt: '2024-08-02T08:00:00.000Z',
    lastSeenAt: '2025-10-15T06:35:00.000Z',
    labels: { role: 'ha', metro: 'sfo' },
    clusterId: 'cluster-ha-west',
    siteHints: ['site-sfo', 'site-nyc'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-west-ha-2',
    name: 'ha-west-2',
    status: 'online',
    version: '1.14.2',
    registeredAt: '2024-08-05T08:00:00.000Z',
    lastSeenAt: '2025-10-15T06:29:00.000Z',
    labels: { role: 'ha', metro: 'nyc' },
    clusterId: 'cluster-ha-west',
    siteHints: ['site-nyc'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-ams-1',
    name: 'arc-ams-conn-1',
    status: 'online',
    version: '1.14.3',
    registeredAt: '2024-05-10T16:30:00.000Z',
    lastSeenAt: '2025-10-15T06:38:00.000Z',
    labels: { region: 'eu', role: 'core' },
    clusterId: 'cluster-eu-core',
    siteHints: ['site-ams'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-ams-2',
    name: 'arc-ams-conn-2',
    status: 'offline',
    version: '1.13.9',
    registeredAt: '2024-12-10T11:30:00.000Z',
    lastSeenAt: '2025-10-12T21:02:00.000Z',
    labels: { region: 'eu', role: 'core', state: 'maintenance' },
    clusterId: 'cluster-eu-core',
    siteHints: ['site-ams'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-nyc-1',
    name: 'nyc-ha-1',
    status: 'online',
    version: '1.14.1',
    registeredAt: '2025-01-15T07:55:00.000Z',
    lastSeenAt: '2025-10-15T06:33:00.000Z',
    labels: { region: 'us-east', role: 'ha' },
    clusterId: 'cluster-ha-east',
    siteHints: ['site-nyc'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-nyc-2',
    name: 'nyc-ha-2',
    status: 'provisioning',
    version: '1.14.4',
    registeredAt: '2025-10-14T02:15:00.000Z',
    lastSeenAt: '2025-10-15T05:10:00.000Z',
    labels: { region: 'us-east', role: 'ha', state: 'provisioning' },
    clusterId: 'cluster-ha-east',
    siteHints: ['site-nyc'],
    userdataStatus: 'available'
  },
  {
    id: 'connector-sin-1',
    name: 'apac-bootstrap-1',
    status: 'offline',
    version: '1.13.5',
    registeredAt: '2025-03-01T03:00:00.000Z',
    lastSeenAt: '2025-10-12T10:00:00.000Z',
    labels: { region: 'apac', role: 'bootstrap' },
    clusterId: 'cluster-apac',
    siteHints: ['site-sin'],
    userdataStatus: 'expired'
  }
];

type SiteRecord = Mutable<Site> & {
  labels?: Record<string, string>;
  deviceCounts?: SiteTemplate['deviceCounts'];
  connectorCount?: number;
  health?: HealthPill;
};

type ClusterRecord = Mutable<ConnectorCluster> & {
  labels?: Record<string, string>;
  connectorsOnline?: number;
  connectorsTotal?: number;
  haState?: HaState;
  health?: HealthPill;
};

type ConnectorRecord = Mutable<Connector> & {
  labels?: Record<string, string>;
  siteHints?: string[];
  locationHint?: string;
};

interface StoredSite {
  data: SiteRecord;
  etag: string;
  clusterIds: Set<string>;
}

interface StoredCluster {
  data: ClusterRecord;
  etag: string;
  siteIds: Set<string>;
}

interface StoredConnector {
  data: ConnectorRecord;
  etag: string;
  userdata: {
    status: UserdataStatus;
    redeemedAt?: Date | null;
  };
}

interface StoredDevice {
  data: Mutable<Device>;
  etag: string;
}

const siteStore = new Map<string, Map<string, StoredSite>>();
const clusterStore = new Map<string, Map<string, StoredCluster>>();
const connectorStore = new Map<string, Map<string, StoredConnector>>();
const deviceStore = new Map<string, Map<string, StoredDevice>>();
const runningConfigStore = new Map<string, Map<string, RunningConfigVersion[]>>();

interface StoredTask {
  id: string;
  orgId: string;
  deviceId: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  attributes: string;
}
const mockTaskStore = new Map<string, StoredTask>();

const deviceTemplates = [
  {
    id: 'device-edge-01',
    siteId: 'site-sfo',
    mgmtAddress: '10.42.0.5',
    displayName: 'edge-sw-01',
    vendor: Vendorschema.Cisco,
    model: 'C9300',
    osVersion: '17.9.3',
    state: 'validated',
    role: 'edge',
    firstSeenAt: '2024-07-01T08:00:00.000Z',
    lastSeenAt: '2025-10-15T10:55:00.000Z',
    notes: 'Edge switch aggregating access layer uplinks.',
    labels: { env: 'prod', zone: 'sfo', tier: 'edge' }
  },
  {
    id: 'device-core-01',
    siteId: 'site-sfo',
    mgmtAddress: '10.42.0.1',
    displayName: 'core-sw-01',
    vendor: Vendorschema.Cisco,
    model: 'C9500',
    osVersion: '17.9.3',
    state: 'validated',
    role: 'core',
    firstSeenAt: '2024-07-01T08:10:00.000Z',
    lastSeenAt: '2025-10-15T10:52:00.000Z',
    notes: 'Terminal distribution core for SFO campus.',
    labels: { env: 'prod', zone: 'sfo', tier: 'core' }
  },
  {
    id: 'device-firewall-ams',
    siteId: 'site-ams',
    mgmtAddress: '10.44.0.10',
    displayName: 'fw-ams-01',
    vendor: Vendorschema.PaloAlto,
    model: 'PA-3410',
    osVersion: '11.0.2',
    state: 'validated',
    role: 'firewall',
    firstSeenAt: '2024-07-15T11:30:00.000Z',
    lastSeenAt: '2025-10-15T09:40:00.000Z',
    notes: 'Edge security for AMS colocation.',
    labels: { env: 'prod', zone: 'ams', tier: 'security' }
  }
];

type DeviceTemplateRecord = (typeof deviceTemplates)[number];

/** Adds inventory records for devices that Site Intelligence evidence links to. */
function seedSiteIntelligenceDeviceRecords(orgId: string, records: Map<string, StoredDevice>): void {
  if (orgId !== PRIMARY_ORG_ID) {
    return;
  }

  const observedAt = new Date('2026-09-07T05:33:14.561Z');
  for (const [index, device] of listSiteIntelligenceMockDevices().entries()) {
    if (records.has(device.id)) {
      continue;
    }
    const data: Mutable<Device> = {
      id: device.id,
      orgId,
      siteId: CAMPUS_B_FIREWALL_DEMO_SITE_ID,
      mgmtAddress: `172.20.22.${index + 11}`,
      displayName: device.hostname ?? device.id,
      vendor: device.vendor as Device['vendor'],
      model: device.model ?? undefined,
      state: 'validated',
      firstSeenAt: observedAt,
      lastSeenAt: observedAt,
      createdAt: observedAt,
      updatedAt: observedAt,
      createdBy: 'site-intelligence-mock',
      updatedBy: 'site-intelligence-mock'
    };
    records.set(device.id, { data, etag: generateEtag() });
  }
}

function buildMockDeviceLifecycle(template: DeviceTemplateRecord): Device['lifecycle'] {
  const vendor = String(template.vendor ?? '').toLowerCase();
  const model = String(template.model ?? '').toLowerCase();
  if (vendor === 'cisco' && (model.includes('c9300') || model.includes('c9500'))) {
    return {
      lookupStatus: 'found',
      lifecycleStatus: 'end_of_support',
      endOfSaleDate: new Date('2025-03-31T00:00:00.000Z'),
      endOfSupportDate: new Date('2026-09-30T00:00:00.000Z'),
      endOfLifeDate: new Date('2027-09-30T00:00:00.000Z'),
      sourceUrl: 'https://www.cisco.com/c/en/us/products/eos-eol-listing.html',
      lastCheckedAt: new Date('2026-07-27T22:00:00.000Z')
    };
  }
  if (vendor === 'palo_alto' || vendor === 'paloalto') {
    return {
      lookupStatus: 'pending',
      lifecycleStatus: 'unknown'
    };
  }
  return undefined;
}

interface RunningConfigVersion {
  id: string;
  deviceId: string;
  collectedAt: string;
  sizeBytes: number;
  hash: string;
  text: string;
}

function ensureDeviceStore(orgId: string): Map<string, StoredDevice> {
  if (!deviceStore.has(orgId)) {
    const records = new Map<string, StoredDevice>();
    deviceTemplates.forEach((template, index) => {
      const id = template.id ?? `device-${index + 1}`;
      const createdAt = template.firstSeenAt ? new Date(template.firstSeenAt) : new Date();
      const updatedAt = template.lastSeenAt ? new Date(template.lastSeenAt) : createdAt;
      const firstSeen = template.firstSeenAt ? new Date(template.firstSeenAt) : createdAt;
      const lastSeen = template.lastSeenAt ? new Date(template.lastSeenAt) : updatedAt;
      const data: Mutable<Device> = {
        ...cloneValue(template),
        id,
        orgId,
        state: (template.state ?? 'validated') as Device['state'],
        lifecycle: buildMockDeviceLifecycle(template),
        firstSeenAt: firstSeen,
        lastSeenAt: lastSeen,
        createdAt,
        updatedAt,
        createdBy: 'mock-user',
        updatedBy: 'mock-user'
      };
      records.set(id, { data, etag: generateEtag() });
    });
    seedSiteIntelligenceDeviceRecords(orgId, records);
    deviceStore.set(orgId, records);
  }
  return deviceStore.get(orgId)!;
}

function buildRunningConfigVersions(device: Device): RunningConfigVersion[] {
  const hostname = device.displayName ?? device.id ?? 'device';
  const baseLines = [
    'version 17.9',
    `hostname ${hostname}`,
    'service timestamps debug datetime msec',
    'service timestamps log datetime msec',
    'no ip domain-lookup',
    '!',
    'interface Gi1/0/1',
    ' description Uplink to core',
    ' switchport mode trunk',
    ' spanning-tree portfast trunk',
    '!',
    'interface Vlan10',
    ' description Users',
    ' ip address 10.10.10.1 255.255.255.0',
    '!',
    'end'
  ];

  const v1 = baseLines.filter((line) => line !== ' spanning-tree portfast trunk').join('\n');
  const v2 = baseLines
    .map((line) => (line === ' description Uplink to core' ? ' description Uplink to core (primary)' : line))
    .join('\n');
  const v3 = baseLines.join('\n');

  const now = Date.now();
  const versions = [
    { text: v1, collectedAt: new Date(now - 1000 * 60 * 60 * 24 * 3).toISOString() },
    { text: v2, collectedAt: new Date(now - 1000 * 60 * 60 * 24 * 2).toISOString() },
    { text: v3, collectedAt: new Date(now - 1000 * 60 * 60 * 24 * 1).toISOString() }
  ];

  return versions.map((version, index) => {
    const sizeBytes = new TextEncoder().encode(version.text).length;
    return {
      id: `${device.id ?? hostname}-rc-${index + 1}`,
      deviceId: device.id ?? hostname,
      collectedAt: version.collectedAt,
      sizeBytes,
      hash: `mock-${(index + 1).toString(16)}${sizeBytes.toString(16)}`,
      text: version.text
    };
  });
}

function ensureRunningConfigVersions(orgId: string, device: Device): RunningConfigVersion[] {
  if (!runningConfigStore.has(orgId)) {
    runningConfigStore.set(orgId, new Map());
  }
  const orgStore = runningConfigStore.get(orgId)!;
  if (!orgStore.has(device.id ?? 'unknown')) {
    orgStore.set(device.id ?? 'unknown', buildRunningConfigVersions(device));
  }
  return orgStore.get(device.id ?? 'unknown') ?? [];
}

function findRunningConfigVersion(versions: RunningConfigVersion[], id: string): RunningConfigVersion | undefined {
  return versions.find((version) => version.id === id);
}

function buildSimpleDiff(fromText: string, toText: string): string {
  const fromLines = fromText.split('\n');
  const toLines = toText.split('\n');
  const maxLines = Math.max(fromLines.length, toLines.length);
  const output: string[] = ['--- from', '+++ to'];
  for (let i = 0; i < maxLines; i += 1) {
    const fromLine = fromLines[i];
    const toLine = toLines[i];
    if (fromLine === toLine) {
      continue;
    }
    if (fromLine !== undefined) {
      output.push(`- ${fromLine}`);
    }
    if (toLine !== undefined) {
      output.push(`+ ${toLine}`);
    }
  }
  return output.join('\n');
}

function listDeviceRecords(orgId: string): StoredDevice[] {
  const store = ensureDeviceStore(orgId);
  return Array.from(store.values());
}

function getDeviceRecord(orgId: string, deviceId: string): StoredDevice | undefined {
  const store = ensureDeviceStore(orgId);
  return store.get(deviceId);
}

function deleteDeviceRecord(orgId: string, deviceId: string): boolean {
  const store = ensureDeviceStore(orgId);
  return store.delete(deviceId);
}

const credentialTemplates = [
  {
    id: 'cred-cli-netops',
    name: 'cli-netops',
    description: 'CLI credential for netops team',
    kind: 'cli_ssh',
    details: {
      username: 'netops',
      port: 22,
      execPasswordSecretId: 'sec-cli-netops',
      enablePasswordSecretId: 'sec-cli-enable'
    },
    createdAt: '2024-05-01T12:00:00.000Z',
    updatedAt: '2025-09-12T14:10:00.000Z'
  },
  {
    id: 'cred-snmp-core',
    name: 'snmp-core',
    description: 'SNMPv3 credential for core devices',
    kind: 'snmp_v3',
    details: {
      username: 'snmp-core',
      port: 161,
      securityLevel: 'authPriv',
      engineId: '0x0102030405',
      contextName: 'default',
      authProtocol: 'sha',
      authPasswordSecretId: 'sec-snmpv3-auth',
      privProtocol: 'aes128',
      privPasswordSecretId: 'sec-snmpv3-priv'
    },
    createdAt: '2024-05-03T09:20:00.000Z',
    updatedAt: '2025-08-20T11:05:00.000Z'
  },
  {
    id: 'cred-gnmi-core',
    name: 'gnmi-core-switch',
    description: 'Strict one-way TLS gNMI credential for the core switch',
    kind: 'gnmi',
    details: {
      username: 'admin',
      passwordSecretId: 'sec-gnmi-core-password',
      deviceServerTrustCaBundleSecretId: 'sec-gnmi-customer-device-ca',
      port: 6030
    },
    createdAt: '2026-07-27T06:00:00.000Z',
    updatedAt: '2026-07-27T06:00:00.000Z'
  }
];

const credentialAssignmentTemplates = [
  {
    id: 'assign-cli-sfo',
    name: 'CLI SFO Edge',
    description: 'Applies CLI creds to SFO edge devices',
    enabled: true,
    priority: 50,
    credentialId: 'cred-cli-netops',
    scope: {
      type: 'sites',
      siteIds: ['site-sfo']
    },
    match: {
      hostnamePrefixes: ['edge-', 'acc-']
    },
    updatedAt: '2025-09-12T14:15:00.000Z'
  },
  {
    id: 'assign-org-default',
    name: 'Org Default SNMP',
    description: 'Fallback SNMP credential',
    enabled: true,
    priority: 200,
    credentialId: 'cred-snmp-core',
    scope: {
      type: 'org'
    },
    updatedAt: '2025-08-20T11:10:00.000Z'
  },
  {
    id: 'assign-gnmi-core',
    name: 'gNMI Core Switch',
    description: 'Applies the gNMI hostname probe credential to the mock core switch',
    enabled: true,
    priority: 25,
    credentialId: 'cred-gnmi-core',
    scope: {
      type: 'devices',
      deviceIds: ['device-core-01']
    },
    updatedAt: '2026-07-27T06:00:00.000Z'
  }
];

const apiKeyTemplates = [
  {
    id: 'api-key-ops',
    name: 'NetOps Automation',
    status: ApiKeyStatusEnum.Active,
    prefix: 'rk_',
    last4: '1A2B',
    roleIds: ['role-editor', 'role-viewer'],
    expiresAt: '2026-01-01T00:00:00.000Z',
    createdAt: '2025-05-01T09:00:00.000Z',
    updatedAt: '2025-09-01T11:00:00.000Z',
    lastUsedAt: '2025-10-10T08:30:00.000Z',
    createdBy: 'user-alice',
    updatedBy: 'user-alice',
    createdByUserId: 'user-alice'
  },
  {
    id: 'api-key-ci',
    name: 'CI Integration',
    status: ApiKeyStatusEnum.Active,
    prefix: 'rk_',
    last4: '9F4C',
    roleIds: ['role-admin'],
    expiresAt: '2025-12-31T00:00:00.000Z',
    createdAt: '2025-06-10T10:30:00.000Z',
    updatedAt: '2025-09-20T12:45:00.000Z',
    lastUsedAt: '2025-10-15T06:00:00.000Z',
    createdBy: 'user-raj',
    updatedBy: 'user-raj',
    createdByUserId: 'user-raj'
  }
];

interface StoredCredential {
  data: Mutable<Credential>;
  etag: string;
}

interface StoredAssignment {
  data: Mutable<CredentialAssignment>;
  etag: string;
}

const credentialStore = new Map<string, Map<string, StoredCredential>>();
const assignmentStore = new Map<string, Map<string, StoredAssignment>>();

function ensureCredentialStore(orgId: string): Map<string, StoredCredential> {
  ensureOrgState(orgId);
  let store = credentialStore.get(orgId);
  if (!store) {
    store = new Map();
    credentialStore.set(orgId, store);
    seedCredentialStore(orgId, store);
  }
  return store;
}

function ensureAssignmentStore(orgId: string): Map<string, StoredAssignment> {
  ensureOrgState(orgId);
  let store = assignmentStore.get(orgId);
  if (!store) {
    store = new Map();
    assignmentStore.set(orgId, store);
    seedAssignmentStore(orgId, store);
  }
  return store;
}

function seedCredentialStore(orgId: string, store: Map<string, StoredCredential>): void {
  credentialTemplates.forEach((template) => {
    const data: Mutable<Credential> = {
      id: template.id ?? randomId('cred'),
      orgId,
      name: template.name,
      description: template.description,
      labels: undefined,
      kind: template.kind as Credential['kind'],
      details: cloneValue(template.details ?? {}) as Credential['details'],
      createdAt: template.createdAt ? new Date(template.createdAt) : new Date(),
      updatedAt: template.updatedAt ? new Date(template.updatedAt) : new Date(),
      createdBy: 'mock-user',
      updatedBy: 'mock-user'
    } as Mutable<Credential>;
    store.set(data.id as string, {
      data,
      etag: generateEtag()
    });
  });
}

function seedAssignmentStore(orgId: string, store: Map<string, StoredAssignment>): void {
  credentialAssignmentTemplates.forEach((template) => {
    const data: Mutable<CredentialAssignment> = {
      id: template.id ?? randomId('assign'),
      orgId,
      name: template.name,
      description: template.description,
      labels: undefined,
      enabled: template.enabled ?? true,
      priority: template.priority ?? 100,
      credentialId: template.credentialId,
      scope: cloneValue(template.scope ?? { type: 'org' }) as CredentialAssignment['scope'],
      match: template.match ? cloneValue(template.match) as CredentialAssignment['match'] : undefined,
      createdAt: template.updatedAt ? new Date(template.updatedAt) : new Date(),
      updatedAt: template.updatedAt ? new Date(template.updatedAt) : new Date(),
      createdBy: 'mock-user',
      updatedBy: 'mock-user'
    } as Mutable<CredentialAssignment>;
    store.set(data.id as string, {
      data,
      etag: generateEtag()
    });
  });
}

function listCredentials(orgId: string): Credential[] {
  return Array.from(ensureCredentialStore(orgId).values()).map(cloneCredentialRecord);
}

function listAssignments(orgId: string): CredentialAssignment[] {
  return Array.from(ensureAssignmentStore(orgId).values()).map(cloneAssignmentRecord);
}

function getCredentialRecord(orgId: string, credentialId: string): StoredCredential | null {
  return ensureCredentialStore(orgId).get(credentialId) ?? null;
}

function getAssignmentRecord(orgId: string, assignmentId: string): StoredAssignment | null {
  return ensureAssignmentStore(orgId).get(assignmentId) ?? null;
}

function createCredentialRecord(orgId: string, payload: CredentialCreate): StoredCredential {
  const store = ensureCredentialStore(orgId);
  const now = new Date();
  const id = randomId('cred');
  const data: Mutable<Credential> = {
    id,
    orgId,
    name: payload.name ?? undefined,
    description: payload.description ?? undefined,
    labels: payload.labels ? cloneValue(payload.labels) : undefined,
    kind: payload.kind,
    details: cloneValue(payload.details) as Credential['details'],
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user'
  } as Mutable<Credential>;
  const record: StoredCredential = {
    data,
    etag: generateEtag()
  };
  store.set(id, record);
  return record;
}

function updateCredentialRecord(orgId: string, credentialId: string, payload: CredentialUpdate): StoredCredential | null {
  const record = getCredentialRecord(orgId, credentialId);
  if (!record) {
    return null;
  }
  if ('name' in payload) {
    record.data.name = payload.name ?? undefined;
  }
  if ('description' in payload) {
    record.data.description = payload.description ?? undefined;
  }
  if ('labels' in payload) {
    record.data.labels = payload.labels ? cloneValue(payload.labels) : undefined;
  }
  if (payload.details) {
    record.data.details = cloneValue(payload.details) as Credential['details'];
  }
  record.data.updatedAt = new Date();
  record.data.updatedBy = 'mock-user';
  record.etag = generateEtag();
  return record;
}

function deleteCredentialRecord(orgId: string, credentialId: string): boolean {
  return ensureCredentialStore(orgId).delete(credentialId);
}

function createAssignmentRecord(orgId: string, payload: CredentialAssignmentCreate): StoredAssignment {
  const store = ensureAssignmentStore(orgId);
  const now = new Date();
  const id = randomId('assign');
  const data: Mutable<CredentialAssignment> = {
    id,
    orgId,
    name: payload.name ?? undefined,
    description: payload.description ?? undefined,
    labels: payload.labels ? cloneValue(payload.labels) : undefined,
    enabled: payload.enabled ?? true,
    priority: payload.priority ?? 100,
    credentialId: payload.credentialId,
    scope: cloneValue(payload.scope) as CredentialAssignment['scope'],
    match: payload.match ? cloneValue(payload.match) as CredentialAssignment['match'] : undefined,
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user'
  } as Mutable<CredentialAssignment>;
  const record: StoredAssignment = {
    data,
    etag: generateEtag()
  };
  store.set(id, record);
  return record;
}

function updateAssignmentRecord(orgId: string, assignmentId: string, payload: CredentialAssignmentUpdate): StoredAssignment | null {
  const record = getAssignmentRecord(orgId, assignmentId);
  if (!record) {
    return null;
  }
  if ('name' in payload) {
    record.data.name = payload.name ?? undefined;
  }
  if ('description' in payload) {
    record.data.description = payload.description ?? undefined;
  }
  if ('labels' in payload) {
    record.data.labels = payload.labels ? cloneValue(payload.labels) : undefined;
  }
  if ('enabled' in payload) {
    record.data.enabled = payload.enabled ?? false;
  }
  if ('priority' in payload && typeof payload.priority === 'number') {
    record.data.priority = payload.priority;
  }
  if (payload.credentialId) {
    record.data.credentialId = payload.credentialId;
  }
  if (payload.scope) {
    record.data.scope = cloneValue(payload.scope) as CredentialAssignment['scope'];
  }
  if (payload.match) {
    record.data.match = cloneValue(payload.match) as CredentialAssignment['match'];
  }
  record.data.updatedAt = new Date();
  record.data.updatedBy = 'mock-user';
  record.etag = generateEtag();
  return record;
}

function deleteAssignmentRecord(orgId: string, assignmentId: string): boolean {
  return ensureAssignmentStore(orgId).delete(assignmentId);
}

function cloneCredentialRecord(record: StoredCredential): Credential {
  return cloneValue(record.data) as Credential;
}

function cloneAssignmentRecord(record: StoredAssignment): CredentialAssignment {
  return cloneValue(record.data) as CredentialAssignment;
}

const discoveryJobTemplates = [
  {
    id: 'discover-sfo-nightly',
    siteId: 'site-sfo',
    clusterId: 'cluster-edge-west',
    status: 'active',
    seeds: ['10.42.0.1'],
    targetCidrs: ['10.42.0.0/24'],
    schedule: {
      cron: 'CRON_TZ=UTC 0 2 * * *'
    },
    createdAt: '2024-09-01T08:00:00.000Z',
    updatedAt: '2025-10-10T05:45:00.000Z'
  },
  {
    id: 'discover-ams-weekly',
    siteId: 'site-ams',
    clusterId: 'cluster-eu-core',
    status: 'paused',
    seeds: ['10.44.0.10'],
    targetCidrs: ['10.44.0.0/24'],
    schedule: {
      cron: 'CRON_TZ=UTC 0 4 * * 1'
    },
    createdAt: '2024-09-10T08:00:00.000Z',
    updatedAt: '2025-09-15T12:10:00.000Z'
  }
];

const discoveryRunTemplates = [
  {
    id: 'run-sfo-20251015',
    jobId: 'discover-sfo-nightly',
    status: DiscoveryRunStatusEnum.Succeeded,
    queuedAt: '2025-10-15T01:58:00.000Z',
    startedAt: '2025-10-15T02:00:20.000Z',
    finishedAt: '2025-10-15T02:08:12.000Z',
    summary: {
      devicesDiscovered: 8,
      devicesUpdated: 11,
      devicesUnreachable: 2,
      linksDiscovered: 24
    }
  },
  {
    id: 'run-sfo-20251014',
    jobId: 'discover-sfo-nightly',
    status: DiscoveryRunStatusEnum.Partial,
    queuedAt: '2025-10-14T01:58:00.000Z',
    startedAt: '2025-10-14T02:00:02.000Z',
    finishedAt: '2025-10-14T02:07:10.000Z',
    summary: {
      devicesDiscovered: 4,
      devicesUpdated: 6,
      devicesUnreachable: 5,
      linksDiscovered: 12
    }
  },
  {
    id: 'run-ams-20251010',
    jobId: 'discover-ams-weekly',
    status: DiscoveryRunStatusEnum.Failed,
    queuedAt: '2025-10-10T03:55:00.000Z',
    startedAt: '2025-10-10T04:00:00.000Z',
    finishedAt: '2025-10-10T04:04:30.000Z',
    summary: {
      devicesDiscovered: 1,
      devicesUpdated: 2,
      devicesUnreachable: 9,
      linksDiscovered: 3
    }
  }
];

const chatThreadTemplates = [
  {
    id: 'chat-ops-assistant',
    title: 'Ops Assistant',
    titleSource: 'system',
    visibility: 'org',
    pinned: true,
    tags: ['ops'],
    lastMessageAt: '2025-10-15T10:50:02.000Z',
    createdAt: '2025-07-01T10:15:00.000Z',
    updatedAt: '2025-10-15T10:50:02.000Z',
    latestRunId: 'run-ops-live',
    latestRunStatus: 'running'
  },
  {
    id: 'chat-compliance',
    title: 'Compliance Bot',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['audit'],
    lastMessageAt: '2025-10-15T09:10:00.000Z',
    createdAt: '2025-05-20T09:45:00.000Z',
    updatedAt: '2025-10-15T09:10:00.000Z',
    latestRunId: 'run-compliance-20251015',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-table-preview',
    title: 'Table Preview Demo',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['ux', 'table'],
    lastMessageAt: '2025-10-16T10:12:00.000Z',
    createdAt: '2025-10-16T10:00:00.000Z',
    updatedAt: '2025-10-16T10:12:00.000Z',
    latestRunId: 'run-table-preview-20251016',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-command-preview',
    title: 'Command Output Preview Demo',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['ux', 'cli'],
    lastMessageAt: '2025-10-16T10:25:00.000Z',
    createdAt: '2025-10-16T10:15:00.000Z',
    updatedAt: '2025-10-16T10:25:00.000Z',
    latestRunId: 'run-command-preview-20251016',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-topology-preview',
    title: 'Topology Overlay Preview Demo',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'topology', 'artifact', 'overlay'],
    lastMessageAt: '2026-05-06T12:07:20.000Z',
    createdAt: '2026-05-06T12:00:00.000Z',
    updatedAt: '2026-05-06T12:07:20.000Z',
    latestRunId: 'run-topology-clear-highlight-20260506',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-live-chart-preview',
    title: 'Live Chart Preview Demo',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'telemetry', 'chart'],
    lastMessageAt: '2026-05-03T13:10:00.000Z',
    createdAt: '2026-05-03T13:00:00.000Z',
    updatedAt: '2026-05-03T13:10:00.000Z',
    latestRunId: 'run-live-chart-preview-20260503',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-interactive-routing',
    title: 'Interactive Routing Prompt',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'interactive'],
    lastMessageAt: '2026-04-26T16:05:00.000Z',
    createdAt: '2026-04-26T15:55:00.000Z',
    updatedAt: '2026-04-26T16:05:00.000Z',
    latestRunId: 'run-interactive-routing-20260426',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-interactive-input',
    title: 'Interactive Input Prompt',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'interactive'],
    lastMessageAt: '2026-04-26T16:12:00.000Z',
    createdAt: '2026-04-26T16:00:00.000Z',
    updatedAt: '2026-04-26T16:12:00.000Z',
    latestRunId: 'run-interactive-input-20260426',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-interactive-multi',
    title: 'Interactive Multi-Select Prompt',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'interactive'],
    lastMessageAt: '2026-04-26T16:18:00.000Z',
    createdAt: '2026-04-26T16:10:00.000Z',
    updatedAt: '2026-04-26T16:18:00.000Z',
    latestRunId: 'run-interactive-multi-20260426',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-interactive-freeform',
    title: 'Interactive Freeform Choice Prompt',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'interactive'],
    lastMessageAt: '2026-04-26T16:24:00.000Z',
    createdAt: '2026-04-26T16:15:00.000Z',
    updatedAt: '2026-04-26T16:24:00.000Z',
    latestRunId: 'run-interactive-freeform-20260426',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-interactive-confirmation',
    title: 'Interactive Confirmation Prompt',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'interactive'],
    lastMessageAt: '2026-04-26T16:31:00.000Z',
    createdAt: '2026-04-26T16:25:00.000Z',
    updatedAt: '2026-04-26T16:31:00.000Z',
    latestRunId: 'run-interactive-confirmation-20260426',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-structured-blocks-preview',
    title: 'Structured Blocks Preview',
    titleSource: 'system',
    visibility: 'private',
    pinned: true,
    tags: ['ux', 'interactive', 'contracts'],
    lastMessageAt: '2026-04-27T09:06:00.000Z',
    createdAt: '2026-04-27T09:00:00.000Z',
    updatedAt: '2026-04-27T09:06:00.000Z',
    latestRunId: 'run-structured-blocks-preview-20260427',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-user-input-eval',
    title: 'User Input Evaluation',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['ux'],
    lastMessageAt: '2026-02-04T14:55:16.000Z',
    createdAt: '2026-01-18T09:10:00.000Z',
    updatedAt: '2026-02-04T14:55:16.000Z',
    latestRunId: 'run-user-input-eval-20260204',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-user-input-clarification',
    title: 'User Input Clarification',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['ux'],
    lastMessageAt: '2026-02-04T14:55:13.000Z',
    createdAt: '2026-01-18T09:12:00.000Z',
    updatedAt: '2026-02-04T14:55:13.000Z',
    latestRunId: 'run-user-input-clarification-20260204',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-random-text-input',
    title: 'Random Text Input',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['sandbox'],
    lastMessageAt: '2026-02-04T14:55:08.000Z',
    createdAt: '2026-01-20T10:05:00.000Z',
    updatedAt: '2026-02-04T14:55:08.000Z',
    latestRunId: 'run-random-text-20260204',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-user-input-asdf',
    title: "User Input: 'asdf'",
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['sandbox'],
    lastMessageAt: '2026-02-04T14:55:05.000Z',
    createdAt: '2026-01-20T10:06:00.000Z',
    updatedAt: '2026-02-04T14:55:05.000Z',
    latestRunId: 'run-asdf-20260204',
    latestRunStatus: 'failed'
  },
  {
    id: 'chat-undefined-user-input',
    title: 'Undefined user input',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['triage'],
    lastMessageAt: '2026-02-02T09:52:40.000Z',
    createdAt: '2026-01-22T08:00:00.000Z',
    updatedAt: '2026-02-02T09:52:40.000Z',
    latestRunId: 'run-undefined-input-20260202',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-create-credentials',
    title: 'how to create credentials',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['credentials'],
    lastMessageAt: '2026-01-16T16:21:52.000Z',
    createdAt: '2026-01-16T16:00:00.000Z',
    updatedAt: '2026-01-16T16:21:52.000Z',
    latestRunId: 'run-create-credentials-20260116',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-random-user-input',
    title: 'Random User Input',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['sandbox'],
    lastMessageAt: '2026-01-16T16:21:19.000Z',
    createdAt: '2026-01-16T16:05:00.000Z',
    updatedAt: '2026-01-16T16:21:19.000Z',
    latestRunId: 'run-random-user-20260116',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-first-test-org',
    title: 'first test org',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['org'],
    lastMessageAt: '2026-01-16T16:21:12.000Z',
    createdAt: '2026-01-16T15:45:00.000Z',
    updatedAt: '2026-01-16T16:21:12.000Z',
    latestRunId: 'run-first-test-org-20260116',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-ops-assist',
    title: 'ops assist',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['ops'],
    lastMessageAt: '2026-01-16T16:20:42.000Z',
    createdAt: '2026-01-16T15:30:00.000Z',
    updatedAt: '2026-01-16T16:20:42.000Z',
    latestRunId: 'run-ops-assist-20260116',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-access-review',
    title: 'Access review',
    titleSource: 'system',
    visibility: 'org',
    pinned: true,
    tags: ['identity'],
    lastMessageAt: '2026-01-14T11:20:42.000Z',
    createdAt: '2026-01-05T12:30:00.000Z',
    updatedAt: '2026-01-14T11:20:42.000Z',
    latestRunId: 'run-access-review-20260114',
    latestRunStatus: 'running'
  },
  {
    id: 'chat-incident-1427',
    title: 'Incident 1427',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['incident'],
    lastMessageAt: '2026-01-12T04:10:10.000Z',
    createdAt: '2026-01-11T20:15:00.000Z',
    updatedAt: '2026-01-12T04:10:10.000Z',
    latestRunId: 'run-incident-1427',
    latestRunStatus: 'failed'
  },
  {
    id: 'chat-onboarding-flow',
    title: 'Onboarding flow',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['onboarding'],
    lastMessageAt: '2026-01-10T13:05:00.000Z',
    createdAt: '2026-01-07T08:45:00.000Z',
    updatedAt: '2026-01-10T13:05:00.000Z',
    latestRunId: 'run-onboarding-20260110',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-msp-q3',
    title: 'MSP Q3 sync',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['msp'],
    lastMessageAt: '2025-12-18T09:30:00.000Z',
    createdAt: '2025-12-15T10:15:00.000Z',
    updatedAt: '2025-12-18T09:30:00.000Z',
    latestRunId: 'run-msp-q3-20251218',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-site-rollout',
    title: 'Site rollout',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['deployment'],
    lastMessageAt: '2025-12-11T18:25:00.000Z',
    createdAt: '2025-12-05T14:00:00.000Z',
    updatedAt: '2025-12-11T18:25:00.000Z',
    latestRunId: 'run-site-rollout-20251211',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-audit-delta',
    title: 'Audit delta',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['audit'],
    lastMessageAt: '2025-12-03T12:10:00.000Z',
    createdAt: '2025-11-25T11:20:00.000Z',
    updatedAt: '2025-12-03T12:10:00.000Z',
    latestRunId: 'run-audit-delta-20251203',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-ssh-session',
    title: 'SSH session',
    titleSource: 'system',
    visibility: 'private',
    pinned: false,
    tags: ['ssh'],
    lastMessageAt: '2025-11-28T19:45:00.000Z',
    createdAt: '2025-11-28T18:30:00.000Z',
    updatedAt: '2025-11-28T19:45:00.000Z',
    latestRunId: 'run-ssh-session-20251128',
    latestRunStatus: 'succeeded'
  },
  {
    id: 'chat-discovery-job',
    title: 'Discovery job',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['discovery'],
    lastMessageAt: '2025-11-20T08:30:00.000Z',
    createdAt: '2025-11-19T07:45:00.000Z',
    updatedAt: '2025-11-20T08:30:00.000Z',
    latestRunId: 'run-discovery-job-20251120',
    latestRunStatus: 'queued'
  },
  {
    id: 'chat-knowledge-ingest',
    title: 'Knowledge ingest',
    titleSource: 'system',
    visibility: 'org',
    pinned: false,
    tags: ['knowledge'],
    lastMessageAt: '2025-11-02T15:15:00.000Z',
    createdAt: '2025-11-01T09:30:00.000Z',
    updatedAt: '2025-11-02T15:15:00.000Z',
    latestRunId: 'run-knowledge-ingest-20251102',
    latestRunStatus: 'succeeded'
  }
];

const MOCK_CHAT_USER_ID = '99999999-9999-4999-8999-999999999999';
const MOCK_CHAT_USER_NAME = 'Mock Operator';
const MOCK_CHAT_SHARED_OWNERS: Array<{ id: string; name: string }> = [
  { id: 'user-raj', name: 'Raj Kumar' },
  { id: 'user-alex', name: 'Alex Johnson' },
  { id: 'user-maya', name: 'Maya Patel' }
];

type ChatMessageRole = 'user' | 'assistant';

interface ChatMessageTemplate {
  id: string;
  role: ChatMessageRole;
  text?: string;
  content?: ChatMessageBlock[];
  attachments?: Array<{
    resourceType?: string;
    resourceId?: string;
    artifactId?: string;
    name?: string;
    mimeType?: string;
    sizeBytes?: number;
    inlinePreview?: boolean;
    evidence?: Array<Record<string, unknown>>;
  }>;
  createdAt: string;
  runId?: string;
}

interface ChatArtifactTemplate {
  id: string;
  summary: string;
  mimeType: string;
  kind: 'commandOutput' | 'table' | 'chart' | 'topology' | 'file' | 'image' | 'pcap' | 'config';
  size?: number;
  createdAt: string;
}

interface ChatRunEventTemplate {
  type: 'status' | 'progress' | 'artifact' | 'artifact_update' | 'message' | 'error';
  data: Record<string, unknown>;
  delayMs?: number;
}

interface ChatRunSeed {
  id: string;
  threadId: string;
  status: string;
  startedAt?: string;
  finishedAt?: string;
  events: ChatRunEventTemplate[];
  artifacts?: ChatArtifactTemplate[];
}

const textContent = (text: string): ChatMessageBlock[] => [{ type: 'text', text }];
const textEncoder = new TextEncoder();
const encodeUtf8 = (value: string): Uint8Array => textEncoder.encode(value);
const utf8ByteLength = (value: string): number => encodeUtf8(value).byteLength;
const buildMockFileChecksum = (value: string | Uint8Array): string => {
  const bytes = typeof value === 'string' ? encodeUtf8(value) : value;
  let hash = 2166136261;
  for (const byte of bytes) {
    hash ^= byte;
    hash = Math.imul(hash, 16777619);
  }
  return `mock-fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

const MOCK_PREVIEW_PNG_BYTES = new Uint8Array([
  137, 80, 78, 71, 13, 10, 26, 10, 0, 0, 0, 13, 73, 72, 68, 82, 0, 0, 0, 1, 0, 0, 0, 1, 8, 6, 0, 0, 0, 31, 21,
  196, 137, 0, 0, 0, 13, 73, 68, 65, 84, 120, 156, 99, 248, 15, 4, 0, 9, 251, 3, 253, 160, 36, 181, 202, 0, 0,
  0, 0, 73, 69, 78, 68, 174, 66, 96, 130
]);
const MOCK_DOWNLOAD_ONLY_XLSX_BYTES = new Uint8Array([80, 75, 3, 4, 109, 111, 99, 107, 45, 120, 108, 115, 120]);
const MOCK_BLOCKED_PCAP_BYTES = new Uint8Array([212, 195, 178, 161, 0, 0, 0, 0, 0, 0, 0, 0]);

interface SeededChatFileTemplate {
  id: string;
  threadId?: string;
  sourceKind?: string;
  filename: string;
  mimeType: string;
  textContent?: string;
  bytes?: Uint8Array;
  availabilityState?: FileAvailabilityState;
  analysisState?: FileAnalysisState;
  analysisFamily?: string;
  preferredRep?: string;
  reps?: string[];
  blockedReason?: string;
  status?: Fileschema['status'];
  description?: string;
  createdAt: string;
  updatedAt: string;
}

const buildSeededFileBytes = (template: SeededChatFileTemplate): Uint8Array =>
  template.bytes ? new Uint8Array(template.bytes) : encodeUtf8(template.textContent ?? '');

const resolveSeededFileAvailability = (template: SeededChatFileTemplate): FileAvailabilityState | undefined => {
  if (template.availabilityState) {
    return template.availabilityState;
  }
  const mime = template.mimeType.trim().toLowerCase();
  if (
    mime.startsWith('text/') ||
    mime === 'application/json' ||
    mime === 'application/yaml' ||
    mime === 'text/csv' ||
    mime === 'text/tab-separated-values'
  ) {
    return 'text_ready';
  }
  if (mime === 'application/pdf' || mime.startsWith('image/')) {
    return 'preview_ready';
  }
  if (mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || mime === 'application/vnd.ms-excel') {
    return 'download_only';
  }
  return 'none';
};

const TOPOLOGY_ARTIFACT_SCOPE_ID = 'mock-site-sjc';
const TOPOLOGY_ARTIFACT_AS_OF = '2026-05-06T12:00:00.000Z';
const TOPOLOGY_ARTIFACT_DIST1_NODE_ID = 'ref:inventory.device:740029df-3057-440b-9cba-4e3bfe96ce56';
const TOPOLOGY_ARTIFACT_ID = 'artifact-hq-physical-topology';
const TOPOLOGY_ARTIFACT_RUN_ID = 'run-topology-preview-20260506';
const TOPOLOGY_ARTIFACT_HIGHLIGHT_RUN_ID = 'run-topology-highlight-20260506';
const TOPOLOGY_ARTIFACT_CLEAR_RUN_ID = 'run-topology-clear-highlight-20260506';
const TOPOLOGY_ARTIFACT_OVERLAY_BASE = {
  source: TopologyOverlayBaseschemaSourceEnum.TopologyService,
  viewId: 'physical_overview',
  scope: {
    scopeType: 'site',
    scopeId: TOPOLOGY_ARTIFACT_SCOPE_ID
  },
  limits: {
    limitNodes: 100,
    limitEdges: 300
  },
  timeMode: TopologyOverlayBaseschemaTimeModeEnum.Fixed,
  asOf: TOPOLOGY_ARTIFACT_AS_OF
};
const TOPOLOGY_ARTIFACT_DIST1_HIGHLIGHT_LAYER = {
  id: 'chat:highlight:dist1',
  kind: TopologyOverlayLayerschemaKindEnum.Highlight,
  label: 'Highlighted Dist1',
  priority: 50,
  visible: true,
  targets: [{ type: 'node', id: TOPOLOGY_ARTIFACT_DIST1_NODE_ID }],
  effects: [
    {
      target: { type: 'node', id: TOPOLOGY_ARTIFACT_DIST1_NODE_ID },
      op: 'merge_properties',
      payload: {
        ui: {
          highlighted: true,
          kind: 'highlight'
        }
      }
    }
  ],
  meta: {
    source: 'chat',
    reason: 'Mock artifact follow-up highlighted the distribution switch.'
  }
};
const TOPOLOGY_ARTIFACT_DIST1_SELECTION = {
  nodeIds: [TOPOLOGY_ARTIFACT_DIST1_NODE_ID],
  edgeIds: []
};
const TOPOLOGY_ARTIFACT_OVERLAY_PATCH = {
  schema: 'topology.overlay.patch.v1',
  base: TOPOLOGY_ARTIFACT_OVERLAY_BASE,
  ops: [
    {
      op: TopologyOverlayPatchOpschemaOpEnum.UpsertLayer,
      layer: TOPOLOGY_ARTIFACT_DIST1_HIGHLIGHT_LAYER
    },
    {
      op: TopologyOverlayPatchOpschemaOpEnum.SetSelection,
      selection: TOPOLOGY_ARTIFACT_DIST1_SELECTION
    }
  ]
};
const TOPOLOGY_ARTIFACT_OVERLAY_UPDATE = {
  eventId: 'evt-topology-overlay-dist1-highlight',
  artifactId: TOPOLOGY_ARTIFACT_ID,
  artifactKind: ChatRunEventArtifactUpdateschemaArtifactKindEnum.Topology,
  updateKind: ChatRunEventArtifactUpdateschemaUpdateKindEnum.TopologyOverlayPatch,
  summary: 'Highlighted Dist1 on the HQ physical topology.',
  revision: 1,
  patch: TOPOLOGY_ARTIFACT_OVERLAY_PATCH
};
const TOPOLOGY_ARTIFACT_CLEAR_PATCH = {
  schema: 'topology.overlay.patch.v1',
  base: TOPOLOGY_ARTIFACT_OVERLAY_BASE,
  ops: [
    {
      op: TopologyOverlayPatchOpschemaOpEnum.ClearLayers,
      filter: {
        kinds: [TopologyOverlayLayerschemaKindEnum.Highlight],
        source: 'chat'
      }
    }
  ]
};
const TOPOLOGY_ARTIFACT_CLEAR_UPDATE = {
  eventId: 'evt-topology-overlay-clear-highlight',
  artifactId: TOPOLOGY_ARTIFACT_ID,
  artifactKind: ChatRunEventArtifactUpdateschemaArtifactKindEnum.Topology,
  updateKind: ChatRunEventArtifactUpdateschemaUpdateKindEnum.TopologyOverlayPatch,
  summary: 'Cleared highlight layers on the HQ physical topology.',
  revision: 2,
  patch: TOPOLOGY_ARTIFACT_CLEAR_PATCH
};
const TOPOLOGY_ARTIFACT_PERSISTED_OVERLAY_STATE = {
  schema: TopologyArtifactOverlayStateschemaSchemaEnum.TopologyArtifactOverlayStateV1,
  revision: 2,
  updatedAt: '2026-05-06T12:07:20.000Z',
  updatedByRunId: TOPOLOGY_ARTIFACT_CLEAR_RUN_ID,
  base: TOPOLOGY_ARTIFACT_OVERLAY_BASE,
  selection: TOPOLOGY_ARTIFACT_DIST1_SELECTION,
  layers: [],
  graphOverlays: []
};

const TOPOLOGY_CHAT_NOTES_FILE: SeededChatFileTemplate = {
  id: 'file-topology-hq-notes',
  threadId: 'chat-topology-preview',
  filename: 'hq-topology-notes.yaml',
  mimeType: 'application/yaml',
  textContent: `site: HQ
scope: physical_overview
asOf: 2026-05-06T12:00:00.000Z
observations:
  - Dist1 uplink to Core1 is healthy.
  - Dist1 uplink to Core2 is healthy.
  - Access switch A17 remains on the legacy aggregation path.
nextChecks:
  - Validate VLAN pruning on the access stack.
  - Confirm no stale highlight overlays remain.`,
  description: 'Operator notes captured during the HQ topology walkthrough.',
  createdAt: '2026-05-06T12:04:30.000Z',
  updatedAt: '2026-05-06T12:04:30.000Z'
};

const INCIDENT_ESCALATION_FILE: SeededChatFileTemplate = {
  id: 'file-incident-1427-escalation',
  threadId: 'chat-incident-1427',
  filename: 'incident-1427-escalation.txt',
  mimeType: 'text/plain',
  textContent: `Incident 1427 escalation summary
Time: 2026-01-12 04:08 UTC
Service: Remote branch reachability
Current impact: Intermittent packet loss between HQ and Branch-17
Latest observation: Loss spikes align with the WAN handoff at edge-rtr-2
Next action: Collect a fresh interface error snapshot after the carrier test window`,
  description: 'Escalation note mirrored from the incident thread.',
  createdAt: '2026-01-12T04:09:45.000Z',
  updatedAt: '2026-01-12T04:09:45.000Z'
};

const OPS_ALERT_SUMMARY_CSV_FILE: SeededChatFileTemplate = {
  id: 'file-ops-alert-summary-csv',
  threadId: 'chat-ops-assistant',
  filename: 'sfo-alert-summary.csv',
  mimeType: 'text/csv',
  textContent: `device,severity,status,metric,updatedAt
core-rtr-1,critical,open,bgp_neighbor_down,2025-10-15T10:49:10Z
dist-sw-2,major,cleared,high_cpu,2025-10-15T09:58:02Z
fw-sfo-1,minor,cleared,session_table_warning,2025-10-15T09:22:54Z`,
  description: 'Structured overnight alert export for the SFO region.',
  createdAt: '2025-10-15T10:49:58.000Z',
  updatedAt: '2025-10-15T10:49:58.000Z'
};

const OPS_ALERT_CONTEXT_JSON_FILE: SeededChatFileTemplate = {
  id: 'file-ops-alert-context-json',
  threadId: 'chat-ops-assistant',
  filename: 'sfo-alert-context.json',
  mimeType: 'application/json',
  textContent: `{
  "site": "SFO",
  "window": "overnight",
  "openCriticalCount": 1,
  "focusDevice": {
    "hostname": "core-rtr-1",
    "managementIp": "10.42.0.1",
    "issue": "bgp_neighbor_down"
  },
  "recommendedNextStep": "Validate WAN peer reachability before closing the incident summary."
}`,
  description: 'Structured alert context for the SFO overnight summary.',
  createdAt: '2025-10-15T10:49:59.000Z',
  updatedAt: '2025-10-15T10:49:59.000Z'
};

const OPS_ALERT_RACK_IMAGE_FILE: SeededChatFileTemplate = {
  id: 'file-ops-rack-snapshot-png',
  threadId: 'chat-ops-assistant',
  filename: 'sfo-rack-snapshot.png',
  mimeType: 'image/png',
  bytes: MOCK_PREVIEW_PNG_BYTES,
  availabilityState: 'preview_ready',
  analysisState: 'ready',
  analysisFamily: 'visual',
  preferredRep: 'visual:v1',
  reps: ['visual:v1', 'object:o_regions'],
  description: 'Rack elevation snapshot captured during overnight triage.',
  createdAt: '2025-10-15T10:50:01.000Z',
  updatedAt: '2025-10-15T10:50:01.000Z'
};

const OPS_CAPACITY_WORKBOOK_FILE: SeededChatFileTemplate = {
  id: 'file-ops-capacity-workbook-xlsx',
  threadId: 'chat-ops-assistant',
  filename: 'sfo-capacity-plan.xlsx',
  mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  bytes: MOCK_DOWNLOAD_ONLY_XLSX_BYTES,
  availabilityState: 'download_only',
  analysisState: 'ready',
  analysisFamily: 'table',
  preferredRep: 'object:o_workbook_manifest',
  reps: ['object:o_workbook_manifest', 'table:t_sheet_capacity'],
  description: 'Workbook placeholder for the next-day capacity plan export.',
  createdAt: '2025-10-15T10:50:03.000Z',
  updatedAt: '2025-10-15T10:50:03.000Z'
};

const OPS_CAPTURE_PCAP_FILE: SeededChatFileTemplate = {
  id: 'file-ops-branch17-capture-pcap',
  threadId: 'chat-ops-assistant',
  filename: 'branch17-handoff-capture.pcap',
  mimeType: 'application/vnd.tcpdump.pcap',
  bytes: MOCK_BLOCKED_PCAP_BYTES,
  availabilityState: 'none',
  analysisFamily: 'packet_capture',
  blockedReason: 'PCAP inspection is not enabled in this phase.',
  status: 'blocked',
  description: 'Carrier handoff packet capture retained only as a blocked mock example.',
  createdAt: '2025-10-15T10:50:04.000Z',
  updatedAt: '2025-10-15T10:50:04.000Z'
};

const TOPOLOGY_RACK_SNAPSHOT_FILE: SeededChatFileTemplate = {
  id: 'file-topology-rack-snapshot-png',
  threadId: 'chat-topology-preview',
  filename: 'hq-rack-snapshot.png',
  mimeType: 'image/png',
  bytes: MOCK_PREVIEW_PNG_BYTES,
  availabilityState: 'preview_ready',
  analysisState: 'ready',
  analysisFamily: 'visual',
  preferredRep: 'visual:v1',
  reps: ['visual:v1', 'object:o_regions'],
  description: 'Rack snapshot attached alongside the topology walkthrough.',
  createdAt: '2026-05-06T12:04:40.000Z',
  updatedAt: '2026-05-06T12:04:40.000Z'
};

const PROMOTED_VLAN_SUMMARY_FILE: SeededChatFileTemplate = {
  id: 'file-promoted-vlan-summary-csv',
  sourceKind: 'artifact_promotion',
  filename: 'branch17-vlan-summary.csv',
  mimeType: 'text/csv',
  textContent: `vlan_id,name,status,ports
1,default,active,Cpu Et2
62,DEFAULT-C-BEHIND-EDGE2,active,Cpu Et22
502,ISP2-TRANSIT,active,Cpu Et2`,
  description: 'Promoted CSV summary from a prior VLAN verification thread.',
  createdAt: '2026-07-02T18:30:00.000Z',
  updatedAt: '2026-07-02T18:30:00.000Z'
};

const PROMOTED_RCA_NOTES_FILE: SeededChatFileTemplate = {
  id: 'file-promoted-branch17-rca-md',
  sourceKind: 'artifact_promotion',
  filename: 'branch17-rca-notes.md',
  mimeType: 'text/markdown',
  textContent: `# Branch 17 RCA

- Symptom: BGP flap toward the carrier handoff every 12-15 minutes.
- Evidence: Packet loss aligns with the WAN edge handoff and not the LAN aggregation path.
- Next step: Compare ACL counters before and after the next flap window.`,
  description: 'Promoted markdown notes from the Branch 17 incident review.',
  createdAt: '2026-07-03T07:10:00.000Z',
  updatedAt: '2026-07-03T07:10:00.000Z'
};

const seededChatFileTemplates: SeededChatFileTemplate[] = [
  TOPOLOGY_CHAT_NOTES_FILE,
  INCIDENT_ESCALATION_FILE,
  OPS_ALERT_SUMMARY_CSV_FILE,
  OPS_ALERT_CONTEXT_JSON_FILE,
  OPS_ALERT_RACK_IMAGE_FILE,
  OPS_CAPACITY_WORKBOOK_FILE,
  OPS_CAPTURE_PCAP_FILE,
  TOPOLOGY_RACK_SNAPSHOT_FILE,
  PROMOTED_VLAN_SUMMARY_FILE,
  PROMOTED_RCA_NOTES_FILE
];

const chatMessageTemplates: Record<string, ChatMessageTemplate[]> = {
  'chat-incident-1427': [
    {
      id: 'msg-incident-user-1',
      role: 'user',
      text: 'Attach the latest escalation note for incident 1427 so I can review it here.',
      createdAt: '2026-01-12T04:07:10.000Z'
    },
    {
      id: 'msg-incident-assist-1',
      role: 'assistant',
      text: 'I attached the latest escalation note from the incident thread. Open it to inspect the exact wording inline.',
      attachments: [
        {
          resourceType: 'file',
          resourceId: INCIDENT_ESCALATION_FILE.id,
          name: INCIDENT_ESCALATION_FILE.filename,
          mimeType: INCIDENT_ESCALATION_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(INCIDENT_ESCALATION_FILE).byteLength
        }
      ],
      createdAt: '2026-01-12T04:10:10.000Z'
    }
  ],
  'chat-ops-assistant': [
    {
      id: 'msg-ops-user-1',
      role: 'user',
      text: 'Give me the overnight alert summary for SFO region.',
      createdAt: '2025-10-15T10:49:55.000Z',
      runId: 'run-ops-live'
    },
    {
      id: 'msg-ops-assist-1',
      role: 'assistant',
      text: 'Nine CRITICAL alerts were raised; only one remains unresolved on core-rtr-1. I captured the relevant log excerpt and attached structured summary files for quick inspection.',
      attachments: [
        { artifactId: 'artifact-ops-log', name: 'Core router syslog excerpt' },
        {
          resourceType: 'file',
          resourceId: OPS_ALERT_SUMMARY_CSV_FILE.id,
          name: OPS_ALERT_SUMMARY_CSV_FILE.filename,
          mimeType: OPS_ALERT_SUMMARY_CSV_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(OPS_ALERT_SUMMARY_CSV_FILE).byteLength
        },
        {
          resourceType: 'file',
          resourceId: OPS_ALERT_CONTEXT_JSON_FILE.id,
          name: OPS_ALERT_CONTEXT_JSON_FILE.filename,
          mimeType: OPS_ALERT_CONTEXT_JSON_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(OPS_ALERT_CONTEXT_JSON_FILE).byteLength
        },
        {
          resourceType: 'file',
          resourceId: OPS_ALERT_RACK_IMAGE_FILE.id,
          name: OPS_ALERT_RACK_IMAGE_FILE.filename,
          mimeType: OPS_ALERT_RACK_IMAGE_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(OPS_ALERT_RACK_IMAGE_FILE).byteLength
        },
        {
          resourceType: 'file',
          resourceId: OPS_CAPACITY_WORKBOOK_FILE.id,
          name: OPS_CAPACITY_WORKBOOK_FILE.filename,
          mimeType: OPS_CAPACITY_WORKBOOK_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(OPS_CAPACITY_WORKBOOK_FILE).byteLength
        },
        {
          resourceType: 'file',
          resourceId: OPS_CAPTURE_PCAP_FILE.id,
          name: OPS_CAPTURE_PCAP_FILE.filename,
          mimeType: OPS_CAPTURE_PCAP_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(OPS_CAPTURE_PCAP_FILE).byteLength
        }
      ],
      createdAt: '2025-10-15T10:50:02.000Z',
      runId: 'run-ops-live'
    }
  ],
  'chat-compliance': [
    {
      id: 'msg-comp-user-1',
      role: 'user',
      text: 'Summarize PCI violations detected this week.',
      createdAt: '2025-10-15T08:55:00.000Z',
      runId: 'run-compliance-20251015'
    },
    {
      id: 'msg-comp-assist-1',
      role: 'assistant',
      text: 'No blocking PCI gaps. Two informational findings were logged on the audit cluster—details attached.',
      attachments: [
        {
          artifactId: 'artifact-compliance-cli',
          name: 'show vlan output',
          evidence: [{ kind: 'raw', rep: 'result', startLine: 2, endLine: 14, note: 'VLAN list used for comparison.' }]
        },
        {
          artifactId: 'artifact-uptime-table',
          name: 'VLAN Presence Comparison Table',
          evidence: [{ kind: 'table', rep: 'result', rowKeys: ['1', '2', '10', '11', '12'] }]
        },
        {
          artifactId: 'artifact-uptime-chart',
          name: 'VLAN Presence Comparison Chart'
        }
      ],
      createdAt: '2025-10-15T09:00:00.000Z',
      runId: 'run-compliance-20251015'
    }
  ],
  'chat-table-preview': [
    {
      id: 'msg-table-user-1',
      role: 'user',
      text: 'Show me the summarized VLAN category table.',
      createdAt: '2025-10-16T10:10:00.000Z',
      runId: 'run-table-preview-20251016'
    },
    {
      id: 'msg-table-assist-1',
      role: 'assistant',
      text: 'Here is the compact category table from the audit comparison.',
      attachments: [
        {
          artifactId: 'artifact-table-preview',
          name: 'VLAN category summary',
          evidence: [{ kind: 'table', rep: 'result', rowKeys: ['present-both', 'missing-audit', 'extra-audit'] }]
        }
      ],
      createdAt: '2025-10-16T10:12:00.000Z',
      runId: 'run-table-preview-20251016'
    }
  ],
  'chat-command-preview': [
    {
      id: 'msg-command-user-1',
      role: 'user',
      text: 'Show me the CLI snippet for the denied edge traffic.',
      createdAt: '2025-10-16T10:22:00.000Z',
      runId: 'run-command-preview-20251016'
    },
    {
      id: 'msg-command-assist-1',
      role: 'assistant',
      text: 'I attached the exact command output lines around the deny event.',
      attachments: [
        {
          artifactId: 'artifact-command-preview',
          name: 'Edge ACL deny excerpt',
          evidence: [{ kind: 'raw', rep: 'result', startLine: 3, endLine: 6, note: 'Denied SSH attempts from the edge ACL.' }]
        }
      ],
      createdAt: '2025-10-16T10:25:00.000Z',
      runId: 'run-command-preview-20251016'
    }
  ],
  'chat-live-chart-preview': [
    {
      id: 'msg-live-chart-user-1',
      role: 'user',
      text: 'Show live interface traffic for Dist1 Te1/0/1 for the last hour.',
      createdAt: '2026-05-03T13:05:00.000Z',
      runId: 'run-live-chart-preview-20260503'
    },
    {
      id: 'msg-live-chart-assist-1',
      role: 'assistant',
      text: 'Here is the live traffic trend for Dist1 Te1/0/1. The chart starts with a bounded snapshot and refreshes from the metrics mock endpoint.',
      attachments: [
        {
          artifactId: 'artifact-live-traffic-chart',
          name: 'Dist1 Te1/0/1 traffic trend',
          inlinePreview: true
        }
      ],
      createdAt: '2026-05-03T13:10:00.000Z',
      runId: 'run-live-chart-preview-20260503'
    }
  ],
  'chat-topology-preview': [
    {
      id: 'msg-topology-user-1',
      role: 'user',
      text: 'Show the physical topology for HQ as it looked at noon.',
      createdAt: '2026-05-06T12:00:00.000Z',
      runId: TOPOLOGY_ARTIFACT_RUN_ID
    },
    {
      id: 'msg-topology-assist-1',
      role: 'assistant',
      text: 'Here is the HQ physical topology artifact from the topology-service view binding, along with the supporting operator notes.',
      attachments: [
        {
          artifactId: TOPOLOGY_ARTIFACT_ID,
          name: 'HQ physical topology',
          inlinePreview: true
        },
        {
          resourceType: 'file',
          resourceId: TOPOLOGY_CHAT_NOTES_FILE.id,
          name: TOPOLOGY_CHAT_NOTES_FILE.filename,
          mimeType: TOPOLOGY_CHAT_NOTES_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(TOPOLOGY_CHAT_NOTES_FILE).byteLength
        },
        {
          resourceType: 'file',
          resourceId: TOPOLOGY_RACK_SNAPSHOT_FILE.id,
          name: TOPOLOGY_RACK_SNAPSHOT_FILE.filename,
          mimeType: TOPOLOGY_RACK_SNAPSHOT_FILE.mimeType,
          sizeBytes: buildSeededFileBytes(TOPOLOGY_RACK_SNAPSHOT_FILE).byteLength
        }
      ],
      createdAt: '2026-05-06T12:05:00.000Z',
      runId: TOPOLOGY_ARTIFACT_RUN_ID
    },
    {
      id: 'msg-topology-user-2',
      role: 'user',
      text: 'highlight Dist1',
      createdAt: '2026-05-06T12:06:00.000Z',
      runId: TOPOLOGY_ARTIFACT_HIGHLIGHT_RUN_ID
    },
    {
      id: 'msg-topology-assist-2',
      role: 'assistant',
      text: 'Done — I highlighted Dist1 in the HQ physical topology artifact.',
      createdAt: '2026-05-06T12:06:20.000Z',
      runId: TOPOLOGY_ARTIFACT_HIGHLIGHT_RUN_ID
    },
    {
      id: 'msg-topology-user-3',
      role: 'user',
      text: 'clear highlight',
      createdAt: '2026-05-06T12:07:00.000Z',
      runId: TOPOLOGY_ARTIFACT_CLEAR_RUN_ID
    },
    {
      id: 'msg-topology-assist-3',
      role: 'assistant',
      text: 'Done — I cleared the chat-created highlight layer from the HQ physical topology artifact.',
      createdAt: '2026-05-06T12:07:20.000Z',
      runId: TOPOLOGY_ARTIFACT_CLEAR_RUN_ID
    }
  ],
  'chat-interactive-routing': [
    {
      id: 'msg-routing-user-1',
      role: 'user',
      content: textContent('I need help but I am not sure whether this is a fix, a change, or a runbook.'),
      createdAt: '2026-04-26T16:01:00.000Z',
      runId: 'run-interactive-routing-20260426'
    },
    {
      id: 'msg-routing-assistant-1',
      role: 'assistant',
      content: [
        {
          type: 'choicePrompt',
          questionId: 'q-routing-demo',
          prompt: 'Which workflow should I route this request to?',
          selectionMode: 'single',
          choices: [
            { id: 'fix_issue', label: 'Fix an active issue', description: 'Use this for outages, incidents, or live break/fix work.' },
            { id: 'make_change', label: 'Make a change', description: 'Use this for planned config or policy changes.' },
            { id: 'run_runbook', label: 'Run a runbook', description: 'Use this for an existing guided operational procedure.' },
            { id: 'general_question', label: 'Ask a general question', description: 'Use this for advisory or informational queries.' }
          ]
        }
      ],
      createdAt: '2026-04-26T16:05:00.000Z',
      runId: 'run-interactive-routing-20260426'
    }
  ],
  'chat-interactive-input': [
    {
      id: 'msg-input-user-1',
      role: 'user',
      content: textContent('Trend traffic for Dist1, but I forgot the interface.'),
      createdAt: '2026-04-26T16:07:00.000Z',
      runId: 'run-interactive-input-20260426'
    },
    {
      id: 'msg-input-assistant-1',
      role: 'assistant',
      content: [
        {
          type: 'inputPrompt',
          questionId: 'q-interface-demo',
          prompt: 'Which interface should I inspect on Dist1?',
          label: 'Interface name',
          placeholder: 'Example: Te1/0/1',
          inputMode: 'text',
          allowEmpty: false
        }
      ],
      createdAt: '2026-04-26T16:12:00.000Z',
      runId: 'run-interactive-input-20260426'
    }
  ],
  'chat-interactive-multi': [
    {
      id: 'msg-multi-user-1',
      role: 'user',
      content: textContent('Compare a couple of traffic metrics for Dist1.'),
      createdAt: '2026-04-26T16:13:00.000Z',
      runId: 'run-interactive-multi-20260426'
    },
    {
      id: 'msg-multi-assistant-1',
      role: 'assistant',
      content: [
        {
          type: 'choicePrompt',
          questionId: 'q-metrics-demo',
          prompt: 'Which metrics do you want included?',
          selectionMode: 'multi',
          minSelections: 1,
          choices: [
            { id: 'in_bytes', label: 'Inbound bytes/sec', description: 'Useful for ingress throughput trend comparisons.' },
            { id: 'out_bytes', label: 'Outbound bytes/sec', description: 'Useful for egress throughput trend comparisons.' },
            { id: 'in_packets', label: 'Inbound packets/sec', description: 'Good for packet-rate shifts and burstiness.' },
            { id: 'out_packets', label: 'Outbound packets/sec', description: 'Good for packet-rate shifts and burstiness.' }
          ]
        }
      ],
      createdAt: '2026-04-26T16:18:00.000Z',
      runId: 'run-interactive-multi-20260426'
    }
  ],
  'chat-interactive-freeform': [
    {
      id: 'msg-freeform-user-1',
      role: 'user',
      content: textContent('Trend the interface, but I need a custom time window.'),
      createdAt: '2026-04-26T16:19:00.000Z',
      runId: 'run-interactive-freeform-20260426'
    },
    {
      id: 'msg-freeform-assistant-1',
      role: 'assistant',
      content: [
        {
          type: 'choicePrompt',
          questionId: 'q-window-demo',
          prompt: 'Pick a time window or provide your own.',
          selectionMode: 'single',
          allowFreeform: true,
          freeformLabel: 'Use a custom time range',
          choices: [
            { id: '15m', label: 'Last 15 minutes' },
            { id: '30m', label: 'Last 30 minutes' },
            { id: '1h', label: 'Last 1 hour' }
          ]
        }
      ],
      createdAt: '2026-04-26T16:24:00.000Z',
      runId: 'run-interactive-freeform-20260426'
    }
  ],
  'chat-interactive-confirmation': [
    {
      id: 'msg-confirm-user-1',
      role: 'user',
      content: textContent('Before you pull the larger telemetry range, ask me before proceeding.'),
      createdAt: '2026-04-26T16:27:00.000Z',
      runId: 'run-interactive-confirmation-20260426'
    },
    {
      id: 'msg-confirm-assistant-1',
      role: 'assistant',
      content: [
        {
          type: 'confirmationPrompt',
          questionId: 'q-confirm-demo',
          prompt: 'Proceed with the larger telemetry retrieval? It may take longer to complete.',
          confirmLabel: 'Continue',
          cancelLabel: 'Cancel'
        }
      ],
      createdAt: '2026-04-26T16:31:00.000Z',
      runId: 'run-interactive-confirmation-20260426'
    }
  ],
  'chat-structured-blocks-preview': [
    {
      id: 'msg-structured-user-1',
      role: 'user',
      content: textContent('Show me how structured code and info cards render.'),
      createdAt: '2026-04-27T09:00:00.000Z',
      runId: 'run-structured-blocks-preview-20260427'
    },
    {
      id: 'msg-structured-assistant-1',
      role: 'assistant',
      content: [
        {
          type: 'infoCard',
          title: 'Execution note',
          body: 'The command preview below now uses the hardened `code` field, and this card uses the `body` field.',
          severity: 'info'
        },
        {
          type: 'code',
          code: 'show mac address-table dynamic | include Dist1',
          language: 'bash'
        }
      ],
      createdAt: '2026-04-27T09:06:00.000Z',
      runId: 'run-structured-blocks-preview-20260427'
    }
  ]
};

const chatRunSeeds: ChatRunSeed[] = [
  {
    id: 'run-ops-live',
    threadId: 'chat-ops-assistant',
    status: 'running',
    startedAt: '2025-10-15T10:50:00.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2025-10-15T10:50:00.000Z', detail: 'Fetching diagnostics' },
        delayMs: 200
      },
      {
        type: 'progress',
        data: { stage: 'Collecting logs', percent: 40 },
        delayMs: 900
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-ops-log',
          kind: 'commandOutput',
          summary: 'Core router syslog excerpt',
          mimeType: 'text/plain',
          size: 4096
        },
        delayMs: 800
      },
      {
        type: 'message',
        data: { messageId: 'msg-ops-assist-1' },
        delayMs: 800
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2025-10-15T10:51:00.000Z', detail: 'Assistant summary ready' },
        delayMs: 900
      }
    ],
    artifacts: [
      {
        id: 'artifact-ops-log',
        summary: 'Core router syslog excerpt',
        mimeType: 'text/plain',
        kind: 'commandOutput',
        size: 4096,
        createdAt: '2025-10-15T10:50:30.000Z'
      }
    ]
  },
  {
    id: 'run-compliance-20251015',
    threadId: 'chat-compliance',
    status: 'succeeded',
    startedAt: '2025-10-15T08:58:00.000Z',
    finishedAt: '2025-10-15T09:05:00.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2025-10-15T08:58:00.000Z' },
        delayMs: 200
      },
      {
        type: 'progress',
        data: { stage: 'Scanning audit logs', percent: 65 },
        delayMs: 900
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-compliance-cli',
          kind: 'commandOutput',
          summary: 'show vlan output',
          mimeType: 'text/plain',
          size: 2048
        },
        delayMs: 650
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-uptime-table',
          kind: 'table',
          summary: 'VLAN Presence Comparison Table',
          mimeType: 'application/vnd.reap.artifact+json',
          size: 1024
        },
        delayMs: 650
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-uptime-chart',
          kind: 'chart',
          summary: 'VLAN Presence Comparison Chart',
          mimeType: 'application/vnd.reap.artifact+json',
          size: 2048
        },
        delayMs: 650
      },
      {
        type: 'message',
        data: { messageId: 'msg-comp-assist-1' },
        delayMs: 700
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2025-10-15T09:05:00.000Z' },
        delayMs: 700
      }
    ],
    artifacts: [
      {
        id: 'artifact-uptime-table',
        summary: 'VLAN Presence Comparison Table',
        mimeType: 'application/vnd.reap.artifact+json',
        kind: 'table',
        size: 1024,
        createdAt: '2025-10-15T09:01:10.000Z'
      },
      {
        id: 'artifact-uptime-chart',
        summary: 'VLAN Presence Comparison Chart',
        mimeType: 'application/vnd.reap.artifact+json',
        kind: 'chart',
        size: 2048,
        createdAt: '2025-10-15T09:01:20.000Z'
      },
      {
        id: 'artifact-compliance-cli',
        summary: 'show vlan output',
        mimeType: 'text/plain',
        kind: 'commandOutput',
        size: 2048,
        createdAt: '2025-10-15T09:00:45.000Z'
      },
      {
        id: 'artifact-compliance-report',
        summary: 'Weekly PCI status',
        mimeType: 'application/json',
        kind: 'file',
        size: 2048,
        createdAt: '2025-10-15T09:02:30.000Z'
      }
    ]
  },
  {
    id: 'run-table-preview-20251016',
    threadId: 'chat-table-preview',
    status: 'succeeded',
    startedAt: '2025-10-16T10:11:00.000Z',
    finishedAt: '2025-10-16T10:12:00.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2025-10-16T10:11:00.000Z' },
        delayMs: 200
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-table-preview',
          kind: 'table',
          summary: 'VLAN category summary',
          mimeType: 'application/vnd.reap.artifact+json',
          size: 768
        },
        delayMs: 450
      },
      {
        type: 'message',
        data: { messageId: 'msg-table-assist-1' },
        delayMs: 400
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2025-10-16T10:12:00.000Z' },
        delayMs: 400
      }
    ],
    artifacts: [
      {
        id: 'artifact-table-preview',
        summary: 'VLAN category summary',
        mimeType: 'application/vnd.reap.artifact+json',
        kind: 'table',
        size: 768,
        createdAt: '2025-10-16T10:11:20.000Z'
      }
    ]
  },
  {
    id: 'run-command-preview-20251016',
    threadId: 'chat-command-preview',
    status: 'succeeded',
    startedAt: '2025-10-16T10:23:00.000Z',
    finishedAt: '2025-10-16T10:25:00.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2025-10-16T10:23:00.000Z' },
        delayMs: 200
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-command-preview',
          kind: 'commandOutput',
          summary: 'Edge ACL deny excerpt',
          mimeType: 'text/plain',
          size: 1536
        },
        delayMs: 450
      },
      {
        type: 'message',
        data: { messageId: 'msg-command-assist-1' },
        delayMs: 400
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2025-10-16T10:25:00.000Z' },
        delayMs: 400
      }
    ],
    artifacts: [
      {
        id: 'artifact-command-preview',
        summary: 'Edge ACL deny excerpt',
        mimeType: 'text/plain',
        kind: 'commandOutput',
        size: 1536,
        createdAt: '2025-10-16T10:23:20.000Z'
      }
    ]
  },
  {
    id: 'run-live-chart-preview-20260503',
    threadId: 'chat-live-chart-preview',
    status: 'succeeded',
    startedAt: '2026-05-03T13:05:05.000Z',
    finishedAt: '2026-05-03T13:10:00.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2026-05-03T13:05:05.000Z', detail: 'Resolving telemetry query plan' },
        delayMs: 200
      },
      {
        type: 'progress',
        data: { stage: 'Preparing chart artifact', percent: 70 },
        delayMs: 650
      },
      {
        type: 'artifact',
        data: {
          artifactId: 'artifact-live-traffic-chart',
          kind: 'chart',
          summary: 'Dist1 Te1/0/1 traffic trend',
          mimeType: 'application/vnd.reap.artifact+json',
          size: 3584
        },
        delayMs: 550
      },
      {
        type: 'message',
        data: { messageId: 'msg-live-chart-assist-1' },
        delayMs: 500
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2026-05-03T13:10:00.000Z', detail: 'Live chart ready' },
        delayMs: 450
      }
    ],
    artifacts: [
      {
        id: 'artifact-live-traffic-chart',
        summary: 'Dist1 Te1/0/1 traffic trend',
        mimeType: 'application/vnd.reap.artifact+json',
        kind: 'chart',
        size: 3584,
        createdAt: '2026-05-03T13:09:45.000Z'
      }
    ]
  },
  {
    id: TOPOLOGY_ARTIFACT_RUN_ID,
    threadId: 'chat-topology-preview',
    status: 'succeeded',
    startedAt: '2026-05-06T12:00:05.000Z',
    finishedAt: '2026-05-06T12:05:00.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2026-05-06T12:00:05.000Z', detail: 'Resolving topology artifact binding' },
        delayMs: 200
      },
      {
        type: 'progress',
        data: { stage: 'Preparing topology artifact', percent: 80 },
        delayMs: 650
      },
      {
        type: 'artifact',
        data: {
          artifactId: TOPOLOGY_ARTIFACT_ID,
          kind: 'topology',
          summary: 'HQ physical topology',
          mimeType: 'application/vnd.reap.artifact+json',
          size: 1536
        },
        delayMs: 550
      },
      {
        type: 'message',
        data: { messageId: 'msg-topology-assist-1' },
        delayMs: 500
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2026-05-06T12:05:00.000Z', detail: 'Topology artifact ready' },
        delayMs: 450
      }
    ],
    artifacts: [
      {
        id: TOPOLOGY_ARTIFACT_ID,
        summary: 'HQ physical topology',
        mimeType: 'application/vnd.reap.artifact+json',
        kind: 'topology',
        size: 1536,
        createdAt: '2026-05-06T12:04:45.000Z'
      }
    ]
  },
  {
    id: TOPOLOGY_ARTIFACT_HIGHLIGHT_RUN_ID,
    threadId: 'chat-topology-preview',
    status: 'succeeded',
    startedAt: '2026-05-06T12:06:05.000Z',
    finishedAt: '2026-05-06T12:06:20.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2026-05-06T12:06:05.000Z', detail: 'Applying topology overlay follow-up' },
        delayMs: 200
      },
      {
        type: 'artifact_update',
        data: TOPOLOGY_ARTIFACT_OVERLAY_UPDATE,
        delayMs: 350
      },
      {
        type: 'message',
        data: { messageId: 'msg-topology-assist-2' },
        delayMs: 300
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2026-05-06T12:06:20.000Z', detail: 'Topology overlay updated' },
        delayMs: 250
      }
    ],
    artifacts: []
  },
  {
    id: TOPOLOGY_ARTIFACT_CLEAR_RUN_ID,
    threadId: 'chat-topology-preview',
    status: 'succeeded',
    startedAt: '2026-05-06T12:07:05.000Z',
    finishedAt: '2026-05-06T12:07:20.000Z',
    events: [
      {
        type: 'status',
        data: { status: 'running', startedAt: '2026-05-06T12:07:05.000Z', detail: 'Clearing topology overlay follow-up' },
        delayMs: 200
      },
      {
        type: 'artifact_update',
        data: TOPOLOGY_ARTIFACT_CLEAR_UPDATE,
        delayMs: 350
      },
      {
        type: 'message',
        data: { messageId: 'msg-topology-assist-3' },
        delayMs: 300
      },
      {
        type: 'status',
        data: { status: 'succeeded', finishedAt: '2026-05-06T12:07:20.000Z', detail: 'Topology overlay cleared' },
        delayMs: 250
      }
    ],
    artifacts: []
  }
];

interface ChatThreadRecord {
  id: string;
  orgId: string;
  title: string;
  titleSource: string;
  visibility: string;
  createdBy?: string;
  createdByUserId?: string;
  tags?: string[];
  pinned?: boolean;
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string;
  latestRunId?: string;
  latestRunStatus?: string;
}

interface ChatChoiceOptionRecord {
  id?: string;
  label?: string;
  description?: string;
}

interface ChatFormFieldRecord {
  id?: string;
  label?: string;
  kind?: string;
  inputMode?: string;
  placeholder?: string;
  required?: boolean;
}

interface ChatFormResponseFieldRecord {
  id?: string;
  value?: string;
  values?: string[];
}

interface ChatMessageBlock {
  type?: string;
  text?: string;
  code?: string;
  title?: string;
  body?: string;
  language?: string;
  prompt?: string;
  questionId?: string;
  label?: string;
  placeholder?: string;
  inputMode?: string;
  allowEmpty?: boolean;
  selectionMode?: string;
  allowFreeform?: boolean;
  freeformLabel?: string;
  minSelections?: number;
  maxSelections?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  severity?: string;
  choiceId?: string;
  choiceIds?: string[];
  labels?: string[];
  responseKind?: string;
  choices?: ChatChoiceOptionRecord[];
  fields?: ChatFormFieldRecord[] | ChatFormResponseFieldRecord[];
}

interface ChatMessageRecord {
  id: string;
  orgId: string;
  threadId: string;
  role: ChatMessageRole;
  content: ChatMessageBlock[];
  attachments?: Array<{
    resourceType?: string;
    resourceId?: string;
    artifactId?: string;
    name?: string;
    mimeType?: string;
    sizeBytes?: number;
    inlinePreview?: boolean;
    evidence?: Array<Record<string, unknown>>;
  }>;
  createdAt: string;
  updatedAt: string;
  runId?: string;
}

interface StoredUploadedFileRecord {
  data: UploadedFileRecord;
  bytes: Uint8Array;
  textContent?: string;
}

interface MockOutputExportMemberRecord {
  resourceRef: string;
  fileName: string;
  contentType: string;
}

interface MockOutputExportRecord {
  id: string;
  status: 'queued' | 'running' | 'ready' | 'failed' | 'expired';
  purpose: 'ui_download' | 'ticket_delivery_explicit' | 'ticket_delivery_automatic' | 'promotion';
  bundleMode: 'single' | 'zip' | 'separate';
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sourceResourceRefs: string[];
  members: MockOutputExportMemberRecord[];
  downloadPath: string;
  promotedFileRef?: string;
}

interface StoredOutputExportRecord {
  data: MockOutputExportRecord;
  bytes: Uint8Array;
}

const isMockUploadedFile = (
  value: unknown
): value is Blob & { name?: string } => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<Blob> & Partial<{
    name?: string;
    type: string;
    size: number;
    arrayBuffer: () => Promise<ArrayBuffer>;
    text: () => Promise<string>;
    stream: () => ReadableStream<Uint8Array>;
    slice: Blob['slice'];
  }>;
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.size === 'number' &&
    (typeof candidate.arrayBuffer === 'function' ||
      typeof candidate.stream === 'function' ||
      typeof candidate.slice === 'function')
  );
};

async function readMockUploadedBytes(upload: Blob): Promise<Uint8Array> {
  const arrayBuffer =
    typeof upload.arrayBuffer === 'function'
      ? await upload.arrayBuffer()
      : await new Response(upload).arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

async function readMockUploadedText(upload: Blob): Promise<string> {
  if (typeof upload.text === 'function') {
    return upload.text();
  }
  return new Response(upload).text();
}

type FileTextLineRecord = FileTextLineschema;
type FileTextSliceRecord = FileTextSlice;

interface ChatRunRecord {
  id: string;
  orgId: string;
  threadId: string;
  status: string;
  traceLevel: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  finishedAt?: string;
}

interface ChatArtifactRecord {
  id: string;
  threadId: string;
  runId: string;
  kind: string;
  mimeType: string;
  summary?: string;
  size?: number;
  createdAt: string;
  labels?: Record<string, string>;
  evidence?: Array<Record<string, unknown>>;
}

interface ChatRunEventEnvelope {
  type: 'status' | 'progress' | 'artifact' | 'artifact_update' | 'message' | 'error';
  payload: Record<string, unknown>;
  delayMs: number;
  beforeSend?: (payload: Record<string, unknown>) => void;
}

interface StoredChatThread {
  data: ChatThreadRecord;
  etag: string;
}

interface StoredChatMessage {
  data: ChatMessageRecord;
  etag: string;
}

interface StoredChatRun {
  data: ChatRunRecord;
  etag: string;
  events: ChatRunEventEnvelope[];
  artifacts: ChatArtifactRecord[];
}

interface ChatOrgState {
  threads: Map<string, StoredChatThread>;
  messages: Map<string, StoredChatMessage>;
  messagesByThread: Map<string, string[]>;
  runs: Map<string, StoredChatRun>;
  files: Map<string, StoredUploadedFileRecord>;
  exports: Map<string, StoredOutputExportRecord>;
}

const chatStore = new Map<string, ChatOrgState>();

const mockTextReadyMimeTypes = new Set([
  'text/plain',
  'text/markdown',
  'application/json',
  'application/yaml',
  'text/csv',
  'text/tab-separated-values'
]);
const mockPreviewReadyMimeTypes = new Set(['application/pdf', 'image/png', 'image/jpeg']);
const mockDownloadOnlyMimeTypes = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]);
const mockBlockedMimeTypes = new Set(['application/zip', 'application/vnd.tcpdump.pcap', 'application/x-pcapng']);
const supportedMockChatFileMimeTypes = new Set([
  ...mockTextReadyMimeTypes,
  ...mockPreviewReadyMimeTypes,
  ...mockDownloadOnlyMimeTypes
]);

const sanitizeMockExportStem = (value: string | undefined): string => {
  const normalized = (value ?? '').trim().replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return normalized || 'download';
};

const buildMockOutputFileName = (stem: string, extension: string): string => {
  const normalizedStem = sanitizeMockExportStem(stem);
  return normalizedStem.toLowerCase().endsWith(extension.toLowerCase()) ? normalizedStem : `${normalizedStem}${extension}`;
};

const inferMockChatFileMime = (filename: string, mimeType: string): string => {
  const direct = mimeType.trim().toLowerCase();
  if (direct && direct !== 'application/octet-stream') {
    return direct;
  }
  const name = filename.trim().toLowerCase();
  if (name.endsWith('.pdf')) {
    return 'application/pdf';
  }
  if (name.endsWith('.png')) {
    return 'image/png';
  }
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) {
    return 'image/jpeg';
  }
  if (name.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (name.endsWith('.xls')) {
    return 'application/vnd.ms-excel';
  }
  if (name.endsWith('.zip')) {
    return 'application/zip';
  }
  if (name.endsWith('.pcap')) {
    return 'application/vnd.tcpdump.pcap';
  }
  if (name.endsWith('.pcapng')) {
    return 'application/x-pcapng';
  }
  if (name.endsWith('.md') || name.endsWith('.markdown')) {
    return 'text/markdown';
  }
  if (name.endsWith('.json')) {
    return 'application/json';
  }
  if (name.endsWith('.yaml') || name.endsWith('.yml')) {
    return 'application/yaml';
  }
  if (name.endsWith('.csv')) {
    return 'text/csv';
  }
  if (name.endsWith('.tsv')) {
    return 'text/tab-separated-values';
  }
  if (name.endsWith('.txt') || name.endsWith('.log') || name.endsWith('.cfg')) {
    return 'text/plain';
  }
  return '';
};

const validateMockStructuredUpload = (mimeType: string, text: string): boolean => {
  if (mimeType === 'application/json') {
    try {
      const parsed = JSON.parse(text);
      return Boolean(parsed && typeof parsed === 'object' && !Array.isArray(parsed));
    } catch {
      return false;
    }
  }

  if (mimeType === 'application/yaml') {
    const trimmed = text.trim();
    return trimmed.length > 0 && trimmed.includes(':');
  }

  const delimiter = mimeType === 'text/tab-separated-values' ? '\t' : mimeType === 'text/csv' ? ',' : '';
  if (!delimiter) {
    return true;
  }

  const rows = text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .map((line) => line.split(delimiter));
  if (rows.length === 0) {
    return false;
  }
  const width = rows[0]?.length ?? 0;
  return width > 0 && rows.every((row) => row.length === width);
};

interface MockUploadAnalysis {
  accepted: boolean;
  normalizedMimeType: string;
  availabilityState?: FileAvailabilityState;
  analysisFamily?: string;
  reason?: string;
  textContent?: string;
}

const analyzeMockUploadedFile = (
  filename: string,
  mimeType: string,
  bytes: Uint8Array,
  text: string | null
): MockUploadAnalysis => {
  const normalizedMimeType = inferMockChatFileMime(filename, mimeType);
  if (mockBlockedMimeTypes.has(normalizedMimeType)) {
    return {
      accepted: false,
      normalizedMimeType,
      reason: 'unsupported file content type'
    };
  }
  if (!supportedMockChatFileMimeTypes.has(normalizedMimeType)) {
    return {
      accepted: false,
      normalizedMimeType,
      reason: 'unsupported file content type'
    };
  }
  if (mockPreviewReadyMimeTypes.has(normalizedMimeType)) {
    return {
      accepted: true,
      normalizedMimeType,
      availabilityState: 'preview_ready',
      analysisFamily: normalizedMimeType === 'application/pdf' ? 'document' : 'visual'
    };
  }
  if (mockDownloadOnlyMimeTypes.has(normalizedMimeType)) {
    return {
      accepted: true,
      normalizedMimeType,
      availabilityState: 'download_only',
      analysisFamily: 'table'
    };
  }
  const decodedText = text ?? new TextDecoder().decode(bytes);
  if (!validateMockStructuredUpload(normalizedMimeType, decodedText)) {
    return {
      accepted: false,
      normalizedMimeType,
      reason: 'unsupported file content type'
    };
  }
  return {
    accepted: true,
    normalizedMimeType,
    availabilityState: 'text_ready',
    textContent: decodedText
  };
};

const buildMockFileTextSlice = (
  fileId: string,
  mimeType: string,
  text: string,
  startLine: number,
  maxLines: number
): FileTextSliceRecord => {
  const sourceLines = text.length === 0 ? [] : text.split('\n');
  if (sourceLines[sourceLines.length - 1] === '') {
    sourceLines.pop();
  }
  const totalLines = sourceLines.length;
  const firstIndex = Math.max(0, startLine - 1);
  const lastIndex = Math.min(sourceLines.length, firstIndex + maxLines);
  const lines = sourceLines.slice(firstIndex, lastIndex).map((line, index) => ({
    lineNo: firstIndex + index + 1,
    text: line
  }));
  return {
    fileId,
    mimeType,
    startLine,
    endLine: lines.length > 0 ? lines[lines.length - 1]!.lineNo : startLine - 1,
    totalLines,
    lines
  };
};

const inferMockArtifactExportFormat = (artifact: ChatArtifactRecord): 'txt' | 'json' | 'csv' | 'xlsx' | 'pdf' | 'png' => {
  const kind = String(artifact.kind ?? '').trim().toLowerCase();
  const mime = String(artifact.mimeType ?? '').trim().toLowerCase();
  if (kind === 'table') {
    return 'csv';
  }
  if (kind === 'chart' || kind === 'topology' || mime.startsWith('image/')) {
    return 'png';
  }
  if (kind === 'report' || kind === 'document' || mime === 'application/pdf') {
    return 'pdf';
  }
  if (mime.includes('json') || kind === 'object') {
    return 'json';
  }
  return 'txt';
};

const buildMockArtifactExportPayload = (
  artifact: ChatArtifactRecord,
  requestedFormat?: string,
): { fileName: string; contentType: string; bytes: Uint8Array } => {
  const summaryStem = artifact.summary ?? artifact.id ?? 'artifact-export';
  const format = (requestedFormat?.trim().toLowerCase() || inferMockArtifactExportFormat(artifact)) as
    | 'txt'
    | 'json'
    | 'csv'
    | 'xlsx'
    | 'pdf'
    | 'png'
    | 'zip';
  switch (format) {
    case 'csv':
      return {
        fileName: buildMockOutputFileName(summaryStem, '.csv'),
        contentType: 'text/csv',
        bytes: encodeUtf8(`name,value\nsummary,"${artifact.summary ?? artifact.id}"\nkind,"${artifact.kind}"`)
      };
    case 'xlsx':
      return {
        fileName: buildMockOutputFileName(summaryStem, '.xlsx'),
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        bytes: new Uint8Array(MOCK_DOWNLOAD_ONLY_XLSX_BYTES)
      };
    case 'png':
      return {
        fileName: buildMockOutputFileName(summaryStem, '.png'),
        contentType: 'image/png',
        bytes: new Uint8Array(MOCK_PREVIEW_PNG_BYTES)
      };
    case 'pdf':
      return {
        fileName: buildMockOutputFileName(summaryStem, '.pdf'),
        contentType: 'application/pdf',
        bytes: encodeUtf8(`%PDF-1.7\nMock export for ${artifact.summary ?? artifact.id}\n%%EOF`)
      };
    case 'json':
      return {
        fileName: buildMockOutputFileName(summaryStem, '.json'),
        contentType: 'application/json',
        bytes: encodeUtf8(
          JSON.stringify(
            {
              artifactId: artifact.id,
              kind: artifact.kind,
              summary: artifact.summary,
              labels: artifact.labels ?? {}
            },
            null,
            2
          )
        )
      };
    case 'zip':
      return {
        fileName: buildMockOutputFileName(summaryStem, '.zip'),
        contentType: 'application/zip',
        bytes: encodeUtf8(`mock zip export for ${artifact.summary ?? artifact.id}`)
      };
    case 'txt':
    default:
      return {
        fileName: buildMockOutputFileName(summaryStem, '.txt'),
        contentType: 'text/plain',
        bytes: encodeUtf8(`${artifact.summary ?? artifact.id}\nartifact kind: ${artifact.kind}`)
      };
  }
};

const materializeMockOutputExport = (
  orgId: string,
  state: ChatOrgState,
  requestBody: Record<string, unknown>,
): StoredOutputExportRecord => {
  const sourceResourceRefs = Array.isArray(requestBody.resourceRefs)
    ? requestBody.resourceRefs.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];
  if (sourceResourceRefs.length === 0) {
    throw new Error('resourceRefs is required for mock output export creation.');
  }
  const purpose = typeof requestBody.purpose === 'string' ? requestBody.purpose.trim().toLowerCase() : 'ui_download';
  const requestedFormat = typeof requestBody.format === 'string' ? requestBody.format.trim().toLowerCase() : undefined;
  const bundleMode =
    typeof requestBody.bundleMode === 'string' && requestBody.bundleMode.trim()
      ? requestBody.bundleMode.trim().toLowerCase()
      : sourceResourceRefs.length > 1
        ? 'zip'
        : 'single';
  const explicitFileName = typeof requestBody.fileName === 'string' ? requestBody.fileName.trim() : '';
  const exportId = randomId('export');

  const members = sourceResourceRefs.map((resourceRef) => {
    if (resourceRef.startsWith('file:')) {
      const fileId = resourceRef.slice('file:'.length);
      const stored = state.files.get(fileId);
      if (!stored) {
        throw new Error(`Mock file ${fileId} not found.`);
      }
      return {
        resourceRef,
        fileName: stored.data.filename ?? stored.data.name ?? buildMockOutputFileName(fileId, '.bin'),
        contentType: stored.data.mimeType,
        bytes: new Uint8Array(stored.bytes)
      };
    }
    if (resourceRef.startsWith('artifact:')) {
      const artifactId = resourceRef.slice('artifact:'.length);
      const found = findMockChatArtifactRecord(orgId, artifactId);
      if (!found) {
        throw new Error(`Mock artifact ${artifactId} not found.`);
      }
      const payload = buildMockArtifactExportPayload(found.artifactRecord, requestedFormat);
      return {
        resourceRef,
        fileName: payload.fileName,
        contentType: payload.contentType,
        bytes: payload.bytes
      };
    }
    throw new Error(`Unsupported mock export resourceRef ${resourceRef}.`);
  });

  const combined =
    bundleMode === 'single' && members.length === 1
      ? members[0]
      : {
          fileName: explicitFileName || buildMockOutputFileName('reap-export-bundle', '.zip'),
          contentType: 'application/zip',
          bytes: encodeUtf8(
            JSON.stringify(
              {
                bundleMode,
                resources: members.map((entry) => ({
                  resourceRef: entry.resourceRef,
                  fileName: entry.fileName,
                  contentType: entry.contentType
                }))
              },
              null,
              2
            )
          )
        };

  const record: MockOutputExportRecord = {
    id: exportId,
    status: 'ready',
    purpose:
      purpose === 'ticket_delivery_explicit' ||
      purpose === 'ticket_delivery_automatic' ||
      purpose === 'promotion'
        ? (purpose as MockOutputExportRecord['purpose'])
        : 'ui_download',
    bundleMode:
      bundleMode === 'zip' || bundleMode === 'separate'
        ? (bundleMode as MockOutputExportRecord['bundleMode'])
        : 'single',
    fileName: explicitFileName || combined.fileName,
    contentType: combined.contentType,
    sizeBytes: combined.bytes.byteLength,
    sourceResourceRefs,
    members: members.map((entry) => ({
      resourceRef: entry.resourceRef,
      fileName: entry.fileName,
      contentType: entry.contentType
    })),
    downloadPath: `chat/exports/${exportId}/download`
  };

  const storedRecord: StoredOutputExportRecord = {
    data: record,
    bytes: combined.bytes
  };
  state.exports.set(exportId, storedRecord);
  return storedRecord;
};

interface VisibilityHistoryEntry {
  proposalId?: string;
  beforeState: Mutable<VisibilitySession['state']>;
}

interface StoredVisibilitySession {
  data: Mutable<VisibilitySession>;
  etag: string;
  history: VisibilityHistoryEntry[];
}

interface StoredVisibilityProposal {
  data: Mutable<VisibilityProposal>;
  etag: string;
}

interface VisibilityOrgState {
  sessions: Map<string, StoredVisibilitySession>;
  sessionIdsByResumeKey: Map<string, string>;
  proposals: Map<string, StoredVisibilityProposal>;
  proposalIdsBySession: Map<string, string[]>;
}

const visibilityStore = new Map<string, VisibilityOrgState>();

const delegationTemplates = [
  {
    id: 'delegation-opsco',
    mspOrgSlug: 'msp-opsco',
    status: 'active',
    scopes: [{ type: 'devices', permissions: ['read', 'update'] }, { type: 'discovery', permissions: ['execute'] }],
    activatedAt: '2025-03-18T12:00:00.000Z',
    notes: 'OpsCo manages day-2 operations.',
    createdAt: '2025-03-10T11:00:00.000Z',
    updatedAt: '2025-08-01T07:15:00.000Z'
  },
  {
    id: 'delegation-securehelp',
    mspOrgSlug: 'msp-securehelp',
    status: 'pending',
    scopes: [{ type: 'audit', permissions: ['read'] }],
    createdAt: '2025-10-12T09:00:00.000Z',
    updatedAt: '2025-10-12T09:00:00.000Z'
  }
];

const auditEventTemplates = [
  {
    id: 'audit-connector-move',
    type: 'connector.updated',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-raj',
      displayName: 'Raj Kumar',
      email: 'raj@arcadia.example'
    },
    resource: {
      type: 'connector',
      id: 'connector-sfo-1',
      name: 'arc-sfo-conn-1'
    },
    metadata: {
      action: 'attach',
      siteId: 'site-sfo'
    },
    ip: '203.0.113.15',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-15T08:32:00.000Z'
  },
  {
    id: 'audit-discovery-success',
    type: 'discovery.run.succeeded',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'system'
    },
    resource: {
      type: 'discovery_run',
      id: 'run-sfo-20251015'
    },
    metadata: {
      jobId: 'discover-sfo-nightly',
      devicesAdded: 12,
      devicesUpdated: 34
    },
    createdAt: '2025-10-15T07:10:00.000Z'
  },
  {
    id: 'audit-site-created',
    type: 'site.created',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-alex',
      displayName: 'Alex Johnson',
      email: 'alex@arcadia.example'
    },
    resource: {
      type: 'site',
      id: 'site-nyc',
      name: 'Arcadia NYC'
    },
    metadata: {
      address: '350 5th Ave, New York, NY'
    },
    ip: '198.51.100.23',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-14T16:21:00.000Z'
  },
  {
    id: 'audit-connector-registered',
    type: 'connector.registered',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'system'
    },
    resource: {
      type: 'connector',
      id: 'connector-nyc-2',
      name: 'arc-nyc-conn-2'
    },
    metadata: {
      version: '2.4.1'
    },
    createdAt: '2025-10-14T11:03:00.000Z'
  },
  {
    id: 'audit-member-invited',
    type: 'member.invited',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-maya',
      displayName: 'Maya Patel',
      email: 'maya@arcadia.example'
    },
    resource: {
      type: 'membership',
      id: 'member-ops-1',
      name: 'Ops Team'
    },
    metadata: {
      email: 'ops-oncall@arcadia.example'
    },
    ip: '203.0.113.84',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-13T19:45:00.000Z'
  },
  {
    id: 'audit-api-key-rotated',
    type: 'api_key.rotated',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-jamie',
      displayName: 'Jamie Lee',
      email: 'jamie@arcadia.example'
    },
    resource: {
      type: 'api_key',
      id: 'key-inventory',
      name: 'Inventory Service'
    },
    metadata: {
      scope: 'inventory:write'
    },
    ip: '192.0.2.44',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-13T09:12:00.000Z'
  },
  {
    id: 'audit-device-claimed',
    type: 'device.claimed',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-raj',
      displayName: 'Raj Kumar',
      email: 'raj@arcadia.example'
    },
    resource: {
      type: 'device',
      id: 'device-sfo-core-01',
      name: 'SFO Core 01'
    },
    metadata: {
      siteId: 'site-sfo'
    },
    ip: '203.0.113.15',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-12T14:36:00.000Z'
  },
  {
    id: 'audit-policy-updated',
    type: 'policy.updated',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-sam',
      displayName: 'Sam Rivera',
      email: 'sam@arcadia.example'
    },
    resource: {
      type: 'policy',
      id: 'policy-segmentation',
      name: 'Segmentation Defaults'
    },
    metadata: {
      changes: 3
    },
    ip: '198.51.100.88',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-11T10:05:00.000Z'
  },
  {
    id: 'audit-connector-detached',
    type: 'connector.detached',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'system'
    },
    resource: {
      type: 'connector',
      id: 'connector-sfo-1',
      name: 'arc-sfo-conn-1'
    },
    metadata: {
      reason: 'maintenance'
    },
    createdAt: '2025-10-10T22:18:00.000Z'
  },
  {
    id: 'audit-site-updated',
    type: 'site.updated',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-alex',
      displayName: 'Alex Johnson',
      email: 'alex@arcadia.example'
    },
    resource: {
      type: 'site',
      id: 'site-sfo',
      name: 'Arcadia SFO'
    },
    metadata: {
      notes: 'Updated maintenance window'
    },
    ip: '198.51.100.23',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-10T09:55:00.000Z'
  },
  {
    id: 'audit-invite-accepted',
    type: 'member.invite.accepted',
    orgId: PRIMARY_ORG_ID,
    actor: {
      type: 'user',
      id: 'user-kim',
      displayName: 'Kim Nguyen',
      email: 'kim@arcadia.example'
    },
    resource: {
      type: 'membership',
      id: 'member-ops-1',
      name: 'Ops Team'
    },
    metadata: {
      role: 'operator'
    },
    ip: '192.0.2.101',
    userAgent: 'Mozilla/5.0',
    createdAt: '2025-10-09T18:41:00.000Z'
  }
];

function buildListResponse<T>(items: T[]) {
  return {
    items,
    meta: {
      limit: items.length,
      next: null,
      prev: null
    }
  };
}

function ensureDiscoveryState(orgId: string): DiscoveryOrgState {
  if (!discoveryStore.has(orgId)) {
    const state: DiscoveryOrgState = {
      jobs: new Map(),
      runs: new Map()
    };

    discoveryJobTemplates.forEach((template) => {
      const record = cloneDiscoveryJobTemplate(template, orgId);
      if (record.data.id) {
        state.jobs.set(record.data.id, record);
      }
    });

    discoveryRunTemplates.forEach((template) => {
      const jobRecord = state.jobs.get(template.jobId);
      if (!jobRecord) {
        return;
      }
      const runRecord = createDiscoveryRunRecord(orgId, jobRecord.data, template, state);
      if (runRecord.data.id) {
        state.runs.set(runRecord.data.id, runRecord);
      }
    });

    discoveryStore.set(orgId, state);
  }
  return discoveryStore.get(orgId)!;
}

function cloneDiscoveryJobTemplate(template: (typeof discoveryJobTemplates)[number], orgId: string): StoredDiscoveryJob {
  const record: StoredDiscoveryJob = {
    data: {
      id: template.id ?? randomId('discover-job'),
      orgId,
      siteId: template.siteId,
      clusterId: template.clusterId,
      seeds: template.seeds ?? [],
      targetCidrs: template.targetCidrs ?? [],
      schedule: template.schedule ? { cron: template.schedule.cron } : undefined,
      status: template.status === 'paused' ? 'paused' : 'active',
      createdAt: new Date(template.createdAt),
      updatedAt: new Date(template.updatedAt)
    },
    etag: generateEtag()
  };
  return record;
}

function listDiscoveryJobs(orgId: string): DiscoveryJob[] {
  const state = ensureDiscoveryState(orgId);
  return Array.from(state.jobs.values()).map((record) => cloneValue(record.data) as DiscoveryJob);
}

function getDiscoveryJobRecord(orgId: string, jobId: string): StoredDiscoveryJob | null {
  return ensureDiscoveryState(orgId).jobs.get(jobId) ?? null;
}

function primaryClusterIdForSite(orgId: string, siteId: string | undefined): string | undefined {
  if (!siteId) {
    return undefined;
  }
  const siteRecord = getSiteRecord(orgId, siteId);
  if (!siteRecord || siteRecord.clusterIds.size === 0) {
    return undefined;
  }
  return Array.from(siteRecord.clusterIds).sort()[0];
}

function createDiscoveryJobRecord(orgId: string, payload: DiscoveryJobCreate): StoredDiscoveryJob {
  const state = ensureDiscoveryState(orgId);
  const now = new Date();
  const job: StoredDiscoveryJob = {
    data: {
      id: randomId('discover-job'),
      orgId,
      siteId: payload.siteId,
      clusterId: primaryClusterIdForSite(orgId, payload.siteId),
      seeds: payload.seeds ?? [],
      targetCidrs: payload.targetCidrs ?? [],
      schedule: payload.schedule ? { cron: payload.schedule.cron } : undefined,
      status: payload.status ?? 'active',
      createdAt: now,
      updatedAt: now
    },
    etag: generateEtag()
  };
  state.jobs.set(job.data.id as string, job);
  return job;
}

function deleteDiscoveryJobRecord(orgId: string, jobId: string): boolean {
  const state = ensureDiscoveryState(orgId);
  const deleted = state.jobs.delete(jobId);
  if (deleted) {
    for (const [runId, record] of state.runs.entries()) {
      if (record.data.jobId === jobId) {
        state.runs.delete(runId);
      }
    }
  }
  return deleted;
}

function updateDiscoveryJobRecord(orgId: string, jobId: string, patch: DiscoveryJobUpdate): StoredDiscoveryJob {
  const state = ensureDiscoveryState(orgId);
  const record = state.jobs.get(jobId);
  if (!record) {
    throw new Error(`Discovery job ${jobId} not found`);
  }
  const job = record.data;
  job.clusterId = primaryClusterIdForSite(orgId, job.siteId);
  if (Object.prototype.hasOwnProperty.call(patch, 'seeds')) {
    job.seeds = patch.seeds ? [...patch.seeds] : [];
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'targetCidrs')) {
    job.targetCidrs = patch.targetCidrs ? [...patch.targetCidrs] : [];
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'schedule')) {
    job.schedule = patch.schedule ? { cron: patch.schedule.cron } : undefined;
  }
  if (patch.status) {
    job.status = patch.status;
  }
  job.updatedAt = new Date();
  record.etag = generateEtag();
  return record;
}

function listDiscoveryRuns(orgId: string, jobId: string): DiscoveryRun[] {
  const state = ensureDiscoveryState(orgId);
  return Array.from(state.runs.values())
    .filter((record) => record.data.jobId === jobId)
    .sort((a, b) => {
      const left = a.data.queuedAt ? new Date(a.data.queuedAt).getTime() : 0;
      const right = b.data.queuedAt ? new Date(b.data.queuedAt).getTime() : 0;
      return right - left;
    })
    .map((record) => cloneValue(record.data) as DiscoveryRun);
}

function getDiscoveryRunRecord(orgId: string, runId: string): StoredDiscoveryRun | null {
  return ensureDiscoveryState(orgId).runs.get(runId) ?? null;
}

function createDiscoveryRunRecord(
  orgId: string,
  job: DiscoveryJob,
  template?: DiscoveryRunTemplate,
  existingState?: DiscoveryOrgState
): StoredDiscoveryRun {
  const state = existingState ?? ensureDiscoveryState(orgId);
  const id = template?.id ?? randomId('discover-run');
  const queuedAt = template ? new Date(template.queuedAt) : new Date();
  const startedAt = template ? new Date(template.startedAt) : new Date(queuedAt.getTime() + 1000);
  const finishedAt = template?.finishedAt ? new Date(template.finishedAt) : undefined;
  const status = template?.status ?? DiscoveryRunStatusEnum.Running;
  const summary = template?.summary ? cloneValue(template.summary) as DiscoveryRunSummaryschema : undefined;

  const run: StoredDiscoveryRun = {
    data: {
      id,
      orgId,
      jobId: job.id as string,
      siteId: job.siteId,
      clusterId: job.clusterId,
      status,
      queuedAt,
      startedAt,
      finishedAt,
      summary,
      lastSeq: 0,
      links: {
        eventsHref: `/public/v1/orgs/${orgId}/discovery/jobs/${job.id}/runs/${id}/events`
      },
      configSnapshot: {
        seeds: job.seeds ?? [],
        targetCidrs: job.targetCidrs ?? [],
        schedule: job.schedule ? { cron: job.schedule.cron } : undefined
      },
      createdAt: queuedAt,
      updatedAt: finishedAt ?? startedAt ?? queuedAt,
      createdBy: 'mock-user',
      updatedBy: 'mock-user'
    },
    etag: generateEtag(),
    events: [],
    nextSeq: 1
  };

  run.events = buildRunEvents(run.data, status);
  if (run.events.length > 0) {
    run.data.lastSeq = run.events[run.events.length - 1].seq;
    run.nextSeq = run.data.lastSeq + 1;
  }

  state.runs.set(id, run);
  return run;
}

function scheduleMockRunCompletion(orgId: string, runId: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  setTimeout(() => {
    const state = discoveryStore.get(orgId);
    const record = state?.runs.get(runId);
    if (!record) {
      return;
    }
    const run = record.data;
    if (run.status === DiscoveryRunStatusEnum.Succeeded || run.status === DiscoveryRunStatusEnum.Failed) {
      return;
    }
    run.status = DiscoveryRunStatusEnum.Succeeded;
    run.finishedAt = new Date();
    run.updatedAt = run.finishedAt;
    run.summary = run.summary ?? {
      devicesDiscovered: 5,
      devicesUpdated: 7,
      devicesUnreachable: 1,
      linksDiscovered: 12
    };
    const durationMs =
      run.startedAt && run.finishedAt ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime() : undefined;
    const finishedEvent = createRunEvent(run, record.nextSeq++, {
      type: 'finished',
      status: 'succeeded',
      durationMs,
      summary: run.summary
    });
    record.events.push(finishedEvent);
    run.lastSeq = finishedEvent.seq;
    record.etag = generateEtag();
  }, 9000);
}

function buildRunEvents(run: DiscoveryRun, finalStatus: DiscoveryRunStatusEnum): DiscoveryEvent[] {
  const events: DiscoveryEvent[] = [];
  let seq = 1;
  const base = run.startedAt ? new Date(run.startedAt) : new Date();
  const summary =
    run.summary ??
    ({
      devicesDiscovered: 0,
      devicesUpdated: 0,
      devicesUnreachable: 0,
      linksDiscovered: 0
    } as DiscoveryRunSummaryschema);
  if (!run.summary) {
    run.summary = summary;
  }

  const push = (data: DiscoveryEvent['data'], offset: number) => {
    const time = new Date(base.getTime() + offset);
    events.push(createRunEvent(run, seq++, data, time));
  };
  const pushDevice = (deviceId: string, mgmtAddress: string, vendor: Vendorschema, model: string, offset: number) => {
    push({ type: 'deviceDiscovered', deviceId, mgmtAddress, vendor, model, siteId: run.siteId }, offset);
  };

  push({ type: 'status', old: 'queued', _new: 'running' }, 200);
  pushDevice(`${run.id}-sw-1`, '10.20.0.11', Vendorschema.Cisco, 'C9300', 900);
  push({ type: 'progress', percent: 25, devicesDiscovered: 1, devicesUpdated: 1 }, 1500);
  pushDevice(`${run.id}-sw-2`, '10.20.0.12', Vendorschema.Cisco, 'C9200', 2400);
  pushDevice(`${run.id}-router-1`, '10.20.0.1', Vendorschema.Cisco, 'ISR4451', 3000);
  push({ type: 'progress', percent: 65, devicesDiscovered: 3, devicesUpdated: 4, linksDiscovered: 10 }, 3600);
  pushDevice(`${run.id}-fw-1`, '10.20.0.254', Vendorschema.PaloAlto, 'PA-3220', 4200);
  pushDevice(`${run.id}-ap-1`, '10.20.0.61', Vendorschema.Aruba, 'aruba-ap-a1', 4600);

  if (finalStatus === DiscoveryRunStatusEnum.Failed) {
    push({ type: 'warning', code: 'connector_timeout', message: 'Connector timed out reaching edge nodes' }, 4800);
    push({ type: 'status', old: 'running', _new: 'failed' }, 6200);
    push({ type: 'finished', status: 'failed', summary }, 6500);
  } else if (finalStatus === DiscoveryRunStatusEnum.Partial) {
    push({ type: 'warning', code: 'credential_rejected', message: 'SNMP credential rejected on 3 devices' }, 5200);
    push({ type: 'status', old: 'running', _new: 'partial' }, 6400);
    push({ type: 'finished', status: 'partial', summary }, 6600);
  } else {
    push({ type: 'status', old: 'running', _new: 'succeeded' }, 6000);
    push({ type: 'finished', status: 'succeeded', summary }, 6200);
  }

  return events;
}

function createRunEvent(run: DiscoveryRun, seq: number, data: DiscoveryEvent['data'], time: Date = new Date()): DiscoveryEvent {
  return {
    type: data.type,
    seq,
    time,
    runId: run.id as string,
    jobId: run.jobId,
    data
  };
}

function streamDiscoveryEvents(request: Request, record: StoredDiscoveryRun): Response {
  const encoder = new TextEncoder();
  const stream = new ReadableStream({
    start(controller) {
      let index = 0;
      const timers: Array<ReturnType<typeof setTimeout>> = [];
      const intervals: Array<ReturnType<typeof setInterval>> = [];

      const send = (event: DiscoveryEvent) => {
        controller.enqueue(encoder.encode(`event: ${event.data.type}\ndata: ${JSON.stringify(event)}\n\n`));
      };

      const pump = () => {
        if (index >= record.events.length) {
          const interval = setInterval(() => {
            const heartbeat = createRunEvent(record.data, record.nextSeq++, { type: 'heartbeat', intervalMs: 5000 });
            send(heartbeat);
          }, 5000);
          intervals.push(interval);
          return;
        }
        const handle = setTimeout(() => {
          send(record.events[index++]);
          pump();
        }, index === 0 ? 200 : 900);
        timers.push(handle);
      };

      pump();

      const abort = () => {
        timers.forEach((handle) => clearTimeout(handle));
        intervals.forEach((handle) => clearInterval(handle));
        controller.close();
      };

      request.signal?.addEventListener('abort', abort);
    }
  });

  return new Response(stream, {
    status: 200,
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      Connection: 'keep-alive',
      'X-Mock-Route': 'org-console'
    }
  });
}

function ensureChatState(orgId: string): ChatOrgState {
  if (!chatStore.has(orgId)) {
    const state: ChatOrgState = {
      threads: new Map(),
      messages: new Map(),
      messagesByThread: new Map(),
      runs: new Map(),
      files: new Map(),
      exports: new Map()
    };

    chatThreadTemplates.forEach((template, index) => {
      const owner =
        template.visibility === 'private' || index % 3 === 0
          ? { id: MOCK_CHAT_USER_ID, name: MOCK_CHAT_USER_NAME }
          : MOCK_CHAT_SHARED_OWNERS[index % MOCK_CHAT_SHARED_OWNERS.length];
      const thread: ChatThreadRecord = {
        id: template.id,
        orgId,
        title: template.title,
        titleSource: template.titleSource,
        visibility: template.visibility,
        createdBy: owner.name,
        createdByUserId: owner.id,
        tags: template.tags ? [...template.tags] : undefined,
        pinned: template.pinned,
        createdAt: template.createdAt,
        updatedAt: template.updatedAt,
        lastMessageAt: template.lastMessageAt,
        latestRunId: template.latestRunId,
        latestRunStatus: template.latestRunStatus
      };
      state.threads.set(thread.id, { data: thread, etag: generateEtag() });
      const messages = chatMessageTemplates[thread.id] ?? [];
      const order: string[] = [];
      messages.forEach((templateMessage) => {
        const message: ChatMessageRecord = {
          id: templateMessage.id,
          orgId,
          threadId: thread.id,
          role: templateMessage.role,
          content: cloneValue(templateMessage.content ?? textContent(templateMessage.text ?? '')),
          createdAt: templateMessage.createdAt,
          updatedAt: templateMessage.createdAt
        };
        if (templateMessage.attachments?.length) {
          message.attachments = templateMessage.attachments.map((att) => ({
            resourceType: att.resourceType,
            resourceId: att.resourceId,
            artifactId: att.artifactId,
            name: att.name,
            mimeType: att.mimeType,
            sizeBytes: att.sizeBytes,
            inlinePreview: Boolean(att.inlinePreview),
            evidence: Array.isArray(att.evidence) ? att.evidence.map((ev) => ({ ...ev })) : undefined
          }));
        }
        if (templateMessage.runId) {
          message.runId = templateMessage.runId;
        }
        state.messages.set(message.id, { data: message, etag: generateEtag() });
        order.push(message.id);
      });
      if (!thread.lastMessageAt && order.length > 0) {
        const lastMessage = state.messages.get(order[order.length - 1]);
        if (lastMessage) {
          thread.lastMessageAt = lastMessage.data.createdAt;
        }
      }
      state.messagesByThread.set(thread.id, order);
    });

    seededChatFileTemplates.forEach((template) => {
      if (template.threadId && !state.threads.has(template.threadId)) {
        return;
      }
      const bytes = buildSeededFileBytes(template);
      const fileRecord: UploadedFileRecord = {
        id: template.id,
        orgId,
        name: template.filename,
        threadId: template.threadId,
        filename: template.filename,
        mimeType: template.mimeType,
        sizeBytes: bytes.byteLength,
        checksum: buildMockFileChecksum(bytes),
        sourceKind: (template.sourceKind ??
          (template.threadId === 'chat-incident-1427' ? 'ticket_attachment' : 'system_import')) as UploadedFileRecord['sourceKind'],
        status: template.status ?? 'ready',
        availabilityState: resolveSeededFileAvailability(template),
        analysisState: template.analysisState,
        analysisFamily: template.analysisFamily,
        preferredRep: template.preferredRep,
        reps: template.reps,
        blockedReason: template.blockedReason,
        description: template.description,
        createdAt: new Date(template.createdAt),
        updatedAt: new Date(template.updatedAt)
      };
      state.files.set(template.id, { data: fileRecord, bytes, textContent: template.textContent });
    });

    chatRunSeeds.forEach((seed) => {
      const threadRecord = state.threads.get(seed.threadId);
      if (!threadRecord) {
        return;
      }
      const run: ChatRunRecord = {
        id: seed.id,
        orgId,
        threadId: seed.threadId,
        status: seed.status,
        traceLevel: 'summary',
        createdAt: seed.startedAt ?? seed.finishedAt ?? new Date().toISOString(),
        updatedAt: seed.finishedAt ?? seed.startedAt ?? new Date().toISOString(),
        startedAt: seed.startedAt,
        finishedAt: seed.finishedAt
      };
      const stored: StoredChatRun = {
        data: run,
        etag: generateEtag(),
        events: annotateEventTimestamps(
          seed.events.map((evt) => ({
            type: evt.type,
            payload: cloneValue(evt.data),
            delayMs: evt.delayMs ?? 800
          })),
          run.startedAt ? new Date(run.startedAt) : new Date(run.createdAt)
        ),
        artifacts: (seed.artifacts ?? []).map((artifact) => {
          const kindLower = String(artifact.kind ?? '').toLowerCase();
          const record: ChatArtifactRecord = {
            id: artifact.id,
            threadId: seed.threadId,
            runId: seed.id,
            kind: artifact.kind,
            mimeType: artifact.mimeType,
            summary: artifact.summary,
            size: artifact.size,
            createdAt: artifact.createdAt
          };
          if (artifact.id === 'artifact-ops-log') {
            record.labels = { deviceName: 'core-rtr-1', deviceAddress: '10.42.0.1', command: 'show logging', protocol: 'cli' };
          } else if (artifact.id === 'artifact-compliance-cli') {
            record.labels = { deviceName: 'Dist1', deviceAddress: '172.20.20.107', command: 'show vlan', protocol: 'cli' };
          } else if (kindLower === 'topology') {
            record.labels = {
              'topology.viewId': 'physical_overview',
              'topology.scopeType': 'site',
              'topology.scopeId': TOPOLOGY_ARTIFACT_SCOPE_ID,
              'topology.timeMode': 'fixed'
            };
          } else if (kindLower === 'table') {
            record.labels = { 'tag.scope': 'comparison' };
          }
          return record;
        })
      };
      state.runs.set(run.id, stored);
    });

    chatStore.set(orgId, state);
  }

  return chatStore.get(orgId)!;
}

function listChatThreads(orgId: string): ChatThreadRecord[] {
  const state = ensureChatState(orgId);
  return Array.from(state.threads.values()).map((record) => cloneValue(record.data));
}

function listChatThreadsForUser(orgId: string, createdByUserId?: string | null): ChatThreadRecord[] {
  const allThreads = listChatThreads(orgId);
  const normalizedUserId = createdByUserId?.trim();
  if (!normalizedUserId) {
    return allThreads;
  }
  return allThreads.filter((thread) => thread.createdByUserId === normalizedUserId);
}

function createChatThreadRecord(orgId: string, title?: string): StoredChatThread {
  const state = ensureChatState(orgId);
  const now = new Date().toISOString();
  const record: StoredChatThread = {
    data: {
      id: randomId('chat-thread'),
      orgId,
      title: title?.trim() || 'New thread',
      titleSource: 'user',
      visibility: 'private',
      createdBy: MOCK_CHAT_USER_NAME,
      createdByUserId: MOCK_CHAT_USER_ID,
      pinned: false,
      tags: [],
      createdAt: now,
      updatedAt: now,
      lastMessageAt: now
    },
    etag: generateEtag()
  };
  state.threads.set(record.data.id, record);
  state.messagesByThread.set(record.data.id, []);
  return record;
}

function getChatThreadRecord(orgId: string, threadId: string): StoredChatThread | null {
  return ensureChatState(orgId).threads.get(threadId) ?? null;
}

function deleteChatThreadRecord(orgId: string, threadId: string): boolean {
  const state = ensureChatState(orgId);
  const removed = state.threads.delete(threadId);
  if (!removed) {
    return false;
  }
  const messageIds = state.messagesByThread.get(threadId) ?? [];
  messageIds.forEach((messageId) => {
    state.messages.delete(messageId);
  });
  state.messagesByThread.delete(threadId);

  const runIdsToRemove: string[] = [];
  for (const [runId, run] of state.runs.entries()) {
    if (run.data.threadId === threadId) {
      runIdsToRemove.push(runId);
    }
  }
  runIdsToRemove.forEach((runId) => {
    state.runs.delete(runId);
  });

  return true;
}

function listThreadMessages(orgId: string, threadId: string): ChatMessageRecord[] {
  const state = ensureChatState(orgId);
  const messageIds = state.messagesByThread.get(threadId) ?? [];
  const messages = messageIds
    .map((id) => state.messages.get(id))
    .filter((record): record is StoredChatMessage => Boolean(record))
    .map((record) => cloneValue(record.data));
  return messages;
}

function getChatMessageRecord(orgId: string, messageId: string): StoredChatMessage | null {
  return ensureChatState(orgId).messages.get(messageId) ?? null;
}

function getChatRunRecord(orgId: string, runId: string): StoredChatRun | null {
  return ensureChatState(orgId).runs.get(runId) ?? null;
}

function ensureVisibilityState(orgId: string): VisibilityOrgState {
  if (!visibilityStore.has(orgId)) {
    visibilityStore.set(orgId, {
      sessions: new Map(),
      sessionIdsByResumeKey: new Map(),
      proposals: new Map(),
      proposalIdsBySession: new Map()
    });
  }
  return visibilityStore.get(orgId)!;
}

function getVisibilitySessionRecord(orgId: string, sessionId: string): StoredVisibilitySession | null {
  return ensureVisibilityState(orgId).sessions.get(sessionId) ?? null;
}

function getVisibilityProposalRecord(orgId: string, proposalId: string): StoredVisibilityProposal | null {
  return ensureVisibilityState(orgId).proposals.get(proposalId) ?? null;
}

function pushVisibilityProposalId(orgId: string, sessionId: string, proposalId: string): void {
  const state = ensureVisibilityState(orgId);
  const existing = state.proposalIdsBySession.get(sessionId) ?? [];
  existing.push(proposalId);
  state.proposalIdsBySession.set(sessionId, existing);
}

function deleteVisibilitySessionRecord(orgId: string, sessionId: string): boolean {
  const state = ensureVisibilityState(orgId);
  const sessionRecord = state.sessions.get(sessionId);
  if (!sessionRecord) {
    return false;
  }
  state.sessions.delete(sessionId);
  if (sessionRecord.data.resumeKey) {
    const mappedSessionId = state.sessionIdsByResumeKey.get(sessionRecord.data.resumeKey);
    if (mappedSessionId === sessionId) {
      state.sessionIdsByResumeKey.delete(sessionRecord.data.resumeKey);
    }
  }
  const proposalIds = state.proposalIdsBySession.get(sessionId) ?? [];
  proposalIds.forEach((proposalId) => state.proposals.delete(proposalId));
  state.proposalIdsBySession.delete(sessionId);
  return true;
}

function visibilityNowIso(): string {
  return new Date().toISOString();
}

function normalizeVisibilitySelection(
  value: unknown
): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const nodeIds = Array.isArray(raw.nodeIds)
    ? raw.nodeIds.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
  const edgeIds = Array.isArray(raw.edgeIds)
    ? raw.edgeIds.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
  if (nodeIds.length === 0 && edgeIds.length === 0) {
    return undefined;
  }
  return {
    nodeIds: nodeIds.length > 0 ? nodeIds : undefined,
    edgeIds: edgeIds.length > 0 ? edgeIds : undefined
  };
}

function normalizeVisibilitySelectedElements(value: unknown): Array<{ kind: string; id: string }> {
  const raw = Array.isArray(value) ? value : [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return null;
      }
      const record = item as Record<string, unknown>;
      const kind = typeof record.kind === 'string' ? record.kind.trim() : '';
      const id = typeof record.id === 'string' ? record.id.trim() : '';
      if (!kind || !id) {
        return null;
      }
      return { kind, id };
    })
    .filter((item): item is { kind: string; id: string } => Boolean(item));
}

function buildSelectionFromVisibilityElements(
  selectedElements: Array<{ kind: string; id: string }>
): unknown {
  const nodeIds = selectedElements
    .filter((item) => item.kind === 'topology.node')
    .map((item) => item.id);
  const edgeIds = selectedElements
    .filter((item) => item.kind === 'topology.edge')
    .map((item) => item.id);
  if (nodeIds.length === 0 && edgeIds.length === 0) {
    return undefined;
  }
  return {
    nodeIds: nodeIds.length > 0 ? nodeIds : undefined,
    edgeIds: edgeIds.length > 0 ? edgeIds : undefined
  };
}

function serializeOverlaySelection(selection: { nodeIds?: Iterable<string>; edgeIds?: Iterable<string> } | undefined): unknown {
  if (!selection) {
    return undefined;
  }
  const nodeIds = selection.nodeIds ? Array.from(selection.nodeIds).filter(Boolean) : [];
  const edgeIds = selection.edgeIds ? Array.from(selection.edgeIds).filter(Boolean) : [];
  if (nodeIds.length === 0 && edgeIds.length === 0) {
    return undefined;
  }
  return {
    nodeIds: nodeIds.length > 0 ? nodeIds : undefined,
    edgeIds: edgeIds.length > 0 ? edgeIds : undefined
  };
}

function createVisibilitySessionState(
  base: Mutable<VisibilitySession['state']>['base'],
  selection?: unknown
): Mutable<VisibilitySession['state']> {
  return {
    schema: 'visibility.session.state.topology.v1',
    revision: 0,
    updatedAt: new Date(visibilityNowIso()),
    base: cloneValue(base),
    selection: selection ? cloneValue(selection) : undefined,
    layers: [],
    graphOverlays: []
  } as unknown as Mutable<VisibilitySession['state']>;
}

function buildMockVisibilitySummary(requestText: string, selectedCount: number): string {
  const trimmed = requestText.trim();
  if (trimmed) {
    return trimmed.length > 96 ? `${trimmed.slice(0, 93)}...` : trimmed;
  }
  if (selectedCount > 0) {
    return `Preview change for ${selectedCount} selected topology element${selectedCount === 1 ? '' : 's'}.`;
  }
  return 'Preview a topology workbench change.';
}

function buildMockVisibilityPatch(
  session: Mutable<VisibilitySession>,
  proposalId: string,
  selectedElements: Array<{ kind: string; id: string }>
): Mutable<VisibilityProposal>['patch'] {
  const selection = buildSelectionFromVisibilityElements(selectedElements);
  const targets = selectedElements
    .filter((item) => item.kind === 'topology.node' || item.kind === 'topology.edge')
    .map((item) => ({
      type: item.kind === 'topology.node' ? 'node' : 'edge',
      id: item.id
    }));

  const ops: any[] = [];
  if (targets.length > 0) {
    ops.push({
      op: TopologyOverlayPatchOpschemaOpEnum.UpsertLayer,
      layer: {
        id: `chat:workbench:${proposalId}`,
        kind: TopologyOverlayLayerschemaKindEnum.Highlight,
        label: 'Chat preview',
        priority: 50,
        visible: true,
        targets,
        effects: targets.map((target) => ({
          target,
          op: 'merge_properties',
          payload: {
            ui: {
              highlighted: true,
              kind: 'highlight'
            }
          }
        })),
        meta: {
          source: 'chat',
          reason: 'Mock topology workbench preview change.'
        }
      }
    });
  }
  if (selection) {
    ops.push({
      op: TopologyOverlayPatchOpschemaOpEnum.SetSelection,
      selection
    });
  }

  return {
    schema: 'topology.overlay.patch.v1',
    base: cloneValue(session.state.base),
    ops
  } as unknown as Mutable<VisibilityProposal>['patch'];
}

function deriveVisibilitySessionMode(
  session: Mutable<VisibilitySession>,
  pendingProposalId?: string
): VisibilitySessionModeschema {
  if (pendingProposalId) {
    return VisibilitySessionModeschema.Preview;
  }
  if ((session.historyDepth ?? 0) > 0 || (session.state.layers?.length ?? 0) > 0 || (session.state.graphOverlays?.length ?? 0) > 0) {
    return VisibilitySessionModeschema.Applied;
  }
  return VisibilitySessionModeschema.Draft;
}

function materializePreviewVisibilityState(
  session: Mutable<VisibilitySession>,
  proposal?: Mutable<VisibilityProposal> | null
): Mutable<VisibilitySession['state']> {
  if (!proposal || (proposal.patch?.ops?.length ?? 0) === 0) {
    return cloneValue(session.state) as Mutable<VisibilitySession['state']>;
  }
  const runtime = hydrateTopologyOverlayState(session.state as any, session.state.base);
  const preview = applyTopologyOverlayPatch(runtime, proposal.patch as any, {
    revision: session.state.revision + 1
  });
  const visibleState = cloneValue(session.state) as Mutable<VisibilitySession['state']>;
  visibleState.base = cloneValue(preview.base ?? session.state.base);
  visibleState.selection = serializeOverlaySelection(preview.selection) as any;
  visibleState.layers = cloneValue(preview.layers);
  visibleState.graphOverlays = cloneValue(preview.graphOverlays);
  visibleState.revision = session.state.revision;
  return visibleState;
}

function buildVisibilitySessionEventPayload(
  orgId: string,
  session: Mutable<VisibilitySession>
): Record<string, unknown> {
  const proposal =
    session.pendingProposalId && session.mode === VisibilitySessionModeschema.Preview
      ? getVisibilityProposalRecord(orgId, session.pendingProposalId)?.data ?? null
      : null;
  return {
    sessionId: session.id,
    workspaceFamily: session.workspaceFamily,
    status: session.status,
    mode: session.mode,
    bindingHash: session.bindingHash,
    revision: session.state.revision,
    historyDepth: session.historyDepth,
    chatThreadId: session.chatThreadId,
    pendingProposalId: session.pendingProposalId,
    visibleState: materializePreviewVisibilityState(session, proposal)
  };
}

function createVisibilitySessionRecord(
  orgId: string,
  payload: Record<string, unknown>
): StoredVisibilitySession {
  const state = ensureVisibilityState(orgId);
  const resumeKey = typeof payload.resumeKey === 'string' ? payload.resumeKey.trim() : '';
  const existingId = state.sessionIdsByResumeKey.get(resumeKey);
  if (existingId) {
    const existing = state.sessions.get(existingId);
    if (existing?.data.status === VisibilitySessionStatusschema.Active) {
      return existing;
    }
  }

  const nowIso = visibilityNowIso();
  const selection = normalizeVisibilitySelection(payload.selection);
  const base =
    ((payload.base as unknown as Mutable<VisibilitySession['state']>['base']) ??
      ({
        source: TopologyOverlayBaseschemaSourceEnum.TopologyService,
        viewId: 'physical_overview',
        scope: {
          scopeType: 'site',
          scopeId: 'site-hq'
        },
        timeMode: TopologyOverlayBaseschemaTimeModeEnum.Current
      } as Mutable<VisibilitySession['state']>['base'])) as Mutable<VisibilitySession['state']>['base'];
  const session: Mutable<VisibilitySession> = {
    id: randomId('visibility-session'),
    orgId,
    workspaceFamily:
      payload.workspaceFamily === VisibilityWorkspaceFamilyschema.TopologyWorkbench
        ? VisibilityWorkspaceFamilyschema.TopologyWorkbench
        : VisibilityWorkspaceFamilyschema.TopologyWorkbench,
    status: VisibilitySessionStatusschema.Active,
    mode: VisibilitySessionModeschema.Draft,
    resumeKey,
    bindingHash: `binding:${resumeKey || randomId('binding')}`,
    chatThreadId: typeof payload.chatThreadId === 'string' && payload.chatThreadId.trim() ? payload.chatThreadId.trim() : undefined,
    pendingProposalId: undefined,
    historyDepth: 0,
    createdAt: new Date(nowIso),
    updatedAt: new Date(nowIso),
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    state: createVisibilitySessionState(base, selection)
  };

  const record: StoredVisibilitySession = {
    data: session,
    etag: generateEtag(),
    history: []
  };
  state.sessions.set(session.id!, record);
  state.sessionIdsByResumeKey.set(session.resumeKey, session.id!);
  return record;
}

function createVisibilityProposalRecord(
  orgId: string,
  session: Mutable<VisibilitySession>,
  payload: Record<string, unknown>,
  status: VisibilityProposalStatusschema = VisibilityProposalStatusschema.Pending
): StoredVisibilityProposal {
  const state = ensureVisibilityState(orgId);
  const nowIso = visibilityNowIso();
  const proposalId = randomId('visibility-proposal');
  const patch =
    payload.patch && typeof payload.patch === 'object'
      ? (cloneValue(payload.patch) as Mutable<VisibilityProposal>['patch'])
      : buildMockVisibilityPatch(
          session,
          proposalId,
          normalizeVisibilitySelectedElements((payload as Record<string, unknown>).selectedElements)
        );

  const proposal: Mutable<VisibilityProposal> = {
    id: proposalId,
    orgId,
    sessionId: session.id!,
    origin: (
      payload.origin === VisibilityProposalOriginschema.Ui
        ? VisibilityProposalOriginschema.Ui
        : VisibilityProposalOriginschema.Chat
    ) as Mutable<VisibilityProposal>['origin'],
    status,
    requestText: typeof payload.requestText === 'string' ? payload.requestText : undefined,
    summary:
      typeof payload.summary === 'string' && payload.summary.trim().length > 0
        ? payload.summary.trim()
        : buildMockVisibilitySummary(
            typeof payload.requestText === 'string' ? payload.requestText : '',
            normalizeVisibilitySelectedElements((payload as Record<string, unknown>).selectedElements).length
          ),
    expectedBindingHash:
      typeof payload.expectedBindingHash === 'string' && payload.expectedBindingHash.trim().length > 0
        ? payload.expectedBindingHash
        : session.bindingHash,
    basedOnRevision:
      typeof payload.basedOnRevision === 'number' && Number.isFinite(payload.basedOnRevision)
        ? Math.max(0, Math.floor(payload.basedOnRevision))
        : session.state.revision,
    patch,
    affectedLayerIds: ((patch.ops?.length ?? 0) > 0 ? [`chat:workbench:${proposalId}`] : undefined) as any,
    createdAt: new Date(nowIso),
    updatedAt: new Date(nowIso),
    createdBy: 'mock-user',
    updatedBy: 'mock-user'
  };

  const record: StoredVisibilityProposal = {
    data: proposal,
    etag: generateEtag()
  };
  state.proposals.set(proposal.id!, record);
  pushVisibilityProposalId(orgId, session.id!, proposal.id!);
  return record;
}

function scheduleMockWorkbenchProposal(
  orgId: string,
  threadId: string,
  requestText: string,
  visibilityContext: Record<string, unknown>
): void {
  const sessionId = typeof visibilityContext.sessionId === 'string' ? visibilityContext.sessionId.trim() : '';
  if (!sessionId) {
    return;
  }
  const selectedElements = normalizeVisibilitySelectedElements(visibilityContext.selectedElements);
  setTimeout(() => {
    const sessionRecord = getVisibilitySessionRecord(orgId, sessionId);
    if (!sessionRecord || sessionRecord.data.status !== VisibilitySessionStatusschema.Active) {
      return;
    }
    if (!sessionRecord.data.chatThreadId) {
      sessionRecord.data.chatThreadId = threadId;
    }
    const existingProposalId = sessionRecord.data.pendingProposalId;
    if (existingProposalId) {
      const existingProposal = getVisibilityProposalRecord(orgId, existingProposalId);
      if (existingProposal) {
        existingProposal.data.status = VisibilityProposalStatusschema.Superseded;
        existingProposal.data.updatedAt = new Date(visibilityNowIso());
        existingProposal.data.updatedBy = 'mock-user';
        existingProposal.etag = generateEtag();
      }
    }
    const proposalRecord = createVisibilityProposalRecord(
      orgId,
      sessionRecord.data,
      {
        origin: VisibilityProposalOriginschema.Chat,
        requestText,
        summary: buildMockVisibilitySummary(requestText, selectedElements.length),
        expectedBindingHash: sessionRecord.data.bindingHash,
        basedOnRevision: sessionRecord.data.state.revision,
        selectedElements
      },
      VisibilityProposalStatusschema.Previewed
    );
    const nowIso = visibilityNowIso();
    sessionRecord.data.pendingProposalId = proposalRecord.data.id;
    sessionRecord.data.mode = VisibilitySessionModeschema.Preview;
    sessionRecord.data.updatedAt = new Date(nowIso);
    sessionRecord.data.updatedBy = 'mock-user';
    sessionRecord.etag = generateEtag();
  }, 1800);
}

function streamVisibilitySession(request: Request, orgId: string, record: StoredVisibilitySession): Response {
  const encoder = new TextEncoder();
  const stream = new ReadableStream({
    start(controller) {
      let closed = false;
      let lastSignature = '';

      const send = (event: string, payload: Record<string, unknown>) => {
        if (closed) {
          return;
        }
        controller.enqueue(encoder.encode(`event: ${event}\ndata: ${JSON.stringify(payload)}\n\n`));
      };

      const sendSessionState = () => {
        const currentRecord = getVisibilitySessionRecord(orgId, record.data.id!);
        if (!currentRecord) {
          return;
        }
        const payload = buildVisibilitySessionEventPayload(orgId, currentRecord.data);
        const signature = JSON.stringify(payload);
        if (signature === lastSignature) {
          return;
        }
        lastSignature = signature;
        send('session_state', payload);
      };

      sendSessionState();

      const refreshHandle = setInterval(sendSessionState, 1000);
      const heartbeatHandle = setInterval(() => {
        send('heartbeat', {
          sessionId: record.data.id,
          at: visibilityNowIso()
        });
      }, 5000);

      request.signal?.addEventListener('abort', () => {
        if (closed) {
          return;
        }
        closed = true;
        clearInterval(refreshHandle);
        clearInterval(heartbeatHandle);
        controller.close();
      });
    }
  });

  return new Response(stream, {
    status: 200,
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      Connection: 'keep-alive',
      'X-Mock-Route': 'org-console'
    }
  });
}

const LIVE_TRAFFIC_DATASET_NAME = 'interface_traffic';
const LIVE_TRAFFIC_DEVICE_ID = 'device-dist1';
const LIVE_TRAFFIC_DEVICE_NAME = 'Dist1';
const LIVE_TRAFFIC_INTERFACE = 'Te1/0/1';
const LIVE_TRAFFIC_SITE_ID = 'site-hq';
const LIVE_TRAFFIC_SNAPSHOT_AT = '2026-05-03T13:10:00.000Z';

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseMockDate = (value: unknown): Date | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const mockTrafficMbpsValue = (index: number, direction: 'in' | 'out'): number => {
  const base = direction === 'in' ? 430 : 275;
  const wave = Math.sin(index / 3 + (direction === 'in' ? 0.4 : 1.2)) * (direction === 'in' ? 58 : 42);
  const drift = (index % 14) * (direction === 'in' ? 2.6 : 1.9);
  return Number(Math.max(0, base + wave + drift).toFixed(2));
};

const buildMockTrafficSeries = (orgId: string, anchor: Date, pointCount: number, stepSec: number) => {
  const directions: Array<'in' | 'out'> = ['in', 'out'];
  const startMs = anchor.getTime() - Math.max(0, pointCount - 1) * stepSec * 1000;
  return directions.map((direction) => ({
    labels: {
      orgId,
      siteId: LIVE_TRAFFIC_SITE_ID,
      deviceId: LIVE_TRAFFIC_DEVICE_ID,
      device: LIVE_TRAFFIC_DEVICE_NAME,
      interface: LIVE_TRAFFIC_INTERFACE,
      direction
    },
    values: Array.from({ length: pointCount }, (_, index) => ({
      t: new Date(startMs + index * stepSec * 1000).toISOString(),
      value: mockTrafficMbpsValue(index, direction)
    }))
  }));
};

const buildMockTrafficRows = (orgId: string, anchor: Date, pointCount: number, stepSec: number): Array<Record<string, unknown>> =>
  buildMockTrafficSeries(orgId, anchor, pointCount, stepSec).flatMap((series) =>
    series.values.map((sample) => ({
      ...series.labels,
      t: sample.t,
      value: sample.value,
      series: series.labels.direction
    }))
  );

function buildMockMetricsRangeResponse(orgId: string, body: Record<string, unknown>): Record<string, unknown> {
  const range = isRecordValue(body.range) ? body.range : {};
  const queryPlan = isRecordValue(body.queryPlan) ? body.queryPlan : {};
  const rawStepSec = Number(range.stepSec);
  const stepSec = Number.isFinite(rawStepSec) && rawStepSec > 0 ? Math.max(15, Math.floor(rawStepSec)) : 60;
  const now = new Date();
  const to = parseMockDate(range.to) ?? now;
  const from = parseMockDate(range.from) ?? new Date(to.getTime() - 60 * 60 * 1000);
  const requestedPointCount = Math.floor(Math.max(stepSec * 1000, to.getTime() - from.getTime()) / (stepSec * 1000)) + 1;
  const pointCount = Math.min(120, Math.max(2, requestedPointCount));
  const metric = typeof queryPlan.metric === 'string' && queryPlan.metric.trim() ? queryPlan.metric.trim() : 'interface_octets_total';
  const intent = typeof body.intent === 'string' && body.intent.trim() ? body.intent.trim() : metric;
  return {
    intent,
    series: buildMockTrafficSeries(orgId, to, pointCount, stepSec)
  };
}

function buildMockChatArtifactDocument(
  orgId: string,
  artifactRecord: ChatArtifactRecord,
  runArtifacts: ChatArtifactRecord[],
  full: boolean
): Record<string, unknown> {
  const kind = String(artifactRecord.kind ?? 'unknown');
  const kindLower = kind.toLowerCase();
  const baseDoc = {
    id: artifactRecord.id,
    kind,
    title: artifactRecord.summary ?? `${kind} ${artifactRecord.id}`,
    summary: artifactRecord.summary ?? undefined,
    createdAt: artifactRecord.createdAt ?? new Date().toISOString(),
    mimeType: artifactRecord.mimeType ?? 'application/vnd.reap.artifact+json',
    sizeBytes: artifactRecord.size ?? undefined,
    labels: artifactRecord.labels ?? {},
    content: { type: kind }
  } as Record<string, unknown>;

  if (kindLower === 'commandoutput') {
    const defaultLines = Array.from({ length: 240 }, (_, idx) => `Line ${idx + 1}: mock command output`);
    let lines = defaultLines;
    let command = 'show version';
    if (artifactRecord.id === 'artifact-compliance-cli') {
      command = 'show vlan';
      lines = [
        'VLAN Name                             Status    Ports',
        '---- -------------------------------- --------- -------------------------------',
        '1    default                          active    Et1/0/1, Et1/0/2',
        '10   voice                            active    Et1/0/3, Et1/0/4',
        '11   corp                             active    Et1/0/5, Et1/0/6',
        '12   guest                            active    Et1/0/7, Et1/0/8',
        '20   iot                              active    Et1/0/9',
        '30   quarantine                       suspended ',
        '1002 fddi-default                     act/unsup ',
        '1003 token-ring-default               act/unsup '
      ];
    } else if (artifactRecord.id === 'artifact-ops-log') {
      command = 'show logging';
      lines = [
        'Apr 21 01:03:12.103 UTC: %SYS-5-CONFIG_I: Configured from console by admin',
        'Apr 21 01:08:41.994 UTC: %LINEPROTO-5-UPDOWN: Line protocol on Interface Te1/0/1, changed state to up',
        'Apr 21 01:11:09.120 UTC: %LINK-3-UPDOWN: Interface Te1/0/1, changed state to down',
        'Apr 21 01:11:11.884 UTC: %LINK-3-UPDOWN: Interface Te1/0/1, changed state to up',
        'Apr 21 01:14:54.003 UTC: %SPANTREE-2-RECV_PVID_ERR: Received BPDU with inconsistent peer vlan id 12',
        'Apr 21 01:17:19.223 UTC: %SYS-4-CPUHOG: Task ran for 2128 msec (22/17), process = Net Background',
        'Apr 21 01:23:02.918 UTC: %SEC-6-IPACCESSLOGP: list EDGE-IN denied tcp 10.42.0.8(52544) -> 10.42.1.10(22)',
        'Apr 21 01:31:44.441 UTC: %SYS-5-RELOAD: Reload requested by admin on vty0'
      ];
    } else if (artifactRecord.id === 'artifact-command-preview') {
      command = 'show logging | include EDGE-IN';
      lines = [
        'Apr 21 02:01:42.100 UTC: %SYS-5-CONFIG_I: Configured from console by admin',
        'Apr 21 02:04:11.551 UTC: %SEC-6-IPACCESSLOGP: list EDGE-IN permitted tcp 10.1.1.5(44321) -> 10.42.1.10(443)',
        'Apr 21 02:04:44.881 UTC: %SEC-6-IPACCESSLOGP: list EDGE-IN denied tcp 10.42.0.8(52544) -> 10.42.1.10(22)',
        'Apr 21 02:04:45.108 UTC: %SEC-6-IPACCESSLOGP: list EDGE-IN denied tcp 10.42.0.8(52545) -> 10.42.1.10(22)',
        'Apr 21 02:04:46.002 UTC: %SEC-6-IPACCESSLOGP: list EDGE-IN denied tcp 10.42.0.8(52546) -> 10.42.1.10(22)',
        'Apr 21 02:05:10.441 UTC: %LINEPROTO-5-UPDOWN: Line protocol on Interface Et1/0/1, changed state to up',
        'Apr 21 02:05:14.910 UTC: %SEC-6-IPACCESSLOGP: list EDGE-IN denied tcp 10.42.0.9(53110) -> 10.42.1.12(22)'
      ];
    }
    const output = lines.join('\n');
    const previewText = lines.slice(0, 12).join('\n');
    baseDoc.content = {
      type: 'commandOutput',
      protocol: 'cli',
      command,
      previewText: full ? output : previewText,
      hasMore: previewText !== output,
      ...(full ? { fullText: output } : null)
    };
  }

  if (kindLower === 'table') {
    if (artifactRecord.id === 'artifact-uptime-table' || artifactRecord.id === 'artifact-table-preview') {
      baseDoc.content = {
        type: 'table',
        columns: ['Category', 'Count'],
        rowKeys: ['present-both', 'missing-audit', 'extra-audit'],
        rows: [
          { Category: 'Present on both', Count: 24 },
          { Category: 'Missing on audit cluster', Count: 2 },
          { Category: 'Extra on audit cluster', Count: 1 }
        ],
        rowCount: 3
      };
    } else {
      baseDoc.content = {
        type: 'table',
        columns: ['Device', 'Uptime'],
        rows: [
          { Device: 'Access1', Uptime: '12 weeks, 52 minutes' },
          { Device: 'Access2', Uptime: '17 hours, 3 minutes' },
          { Device: 'Dist1', Uptime: '0 years, 0 weeks, 0 days, 17 hours, 0 minutes' }
        ],
        rowCount: 3
      };
    }
  }

  if (kindLower === 'topology' && artifactRecord.id === TOPOLOGY_ARTIFACT_ID) {
    const content = {
      type: 'topology',
      version: 'v1',
      title: 'HQ physical topology',
      binding: {
        source: 'topology-service',
        viewId: TOPOLOGY_ARTIFACT_OVERLAY_BASE.viewId,
        scope: TOPOLOGY_ARTIFACT_OVERLAY_BASE.scope,
        limits: TOPOLOGY_ARTIFACT_OVERLAY_BASE.limits,
        timeMode: TOPOLOGY_ARTIFACT_OVERLAY_BASE.timeMode,
        asOf: TOPOLOGY_ARTIFACT_OVERLAY_BASE.asOf
      },
      presentation: {
        renderer: 'topology-v2',
        overlayState: TOPOLOGY_ARTIFACT_PERSISTED_OVERLAY_STATE
      }
    };
    baseDoc.content = content;
    baseDoc.views = [
      {
        rep: 'topology:default',
        title: 'Topology',
        content
      }
    ];
    baseDoc.presentationHints = {
      defaultView: 'topology:default',
      views: ['topology:default']
    };
  }

  if (kindLower === 'chart' && artifactRecord.id === 'artifact-live-traffic-chart') {
    baseDoc.content = {
      type: 'chart',
      version: 'v1',
      title: 'Dist1 Te1/0/1 traffic trend',
      renderer: {
        kind: 'vega-lite',
        schemaVersion: 'v5'
      },
      chartType: 'line',
      dataBinding: {
        source: 'metrics',
        datasetName: LIVE_TRAFFIC_DATASET_NAME,
        metrics: {
          queryPlan: {
            version: 'v1',
            backend: 'prometheus',
            metric: 'interface_octets_total',
            metricKind: 'counter',
            matchers: [
              { label: 'site_id', op: 'eq', value: LIVE_TRAFFIC_SITE_ID },
              { label: 'device_id', op: 'eq', value: LIVE_TRAFFIC_DEVICE_ID },
              { label: 'if_name', op: 'eq', value: LIVE_TRAFFIC_INTERFACE }
            ],
            aggregation: 'rate',
            rangeVectorSec: 300,
            groupBy: ['direction'],
            transform: 'octets_to_mbps',
            unit: 'Mbps',
            seriesLabelFields: ['direction']
          },
          scope: {
            scopeType: 'site',
            scopeId: LIVE_TRAFFIC_SITE_ID
          },
          selectors: {
            siteId: LIVE_TRAFFIC_SITE_ID,
            deviceId: LIVE_TRAFFIC_DEVICE_ID,
            device: LIVE_TRAFFIC_DEVICE_NAME,
            interface: LIVE_TRAFFIC_INTERFACE
          },
          time: {
            mode: 'rolling',
            lookbackSeconds: 3600,
            stepSec: 60
          },
          seriesLabelFields: ['direction'],
          unit: 'Mbps'
        }
      },
      refreshPlan: {
        mode: 'poll',
        intervalSeconds: 10,
        visibleOnly: true,
        staleAfterSeconds: 45,
        maxConsecutiveFailures: 3
      },
      lastSnapshot: {
        datasetName: LIVE_TRAFFIC_DATASET_NAME,
        sampledAt: LIVE_TRAFFIC_SNAPSHOT_AT,
        rows: buildMockTrafficRows(orgId, new Date(LIVE_TRAFFIC_SNAPSHOT_AT), 40, 60)
      },
      encodingHints: {
        unit: 'Mbps',
        seriesLabelFields: ['direction'],
        xField: 't',
        yField: 'value'
      },
      spec: {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data: { name: LIVE_TRAFFIC_DATASET_NAME },
        mark: { type: 'line', point: false, interpolate: 'monotone' },
        encoding: {
          x: { field: 't', type: 'temporal', title: 'Time' },
          y: { field: 'value', type: 'quantitative', title: 'Traffic (Mbps)' },
          color: { field: 'series', type: 'nominal', title: 'Direction' },
          tooltip: [
            { field: 'device', type: 'nominal', title: 'Device' },
            { field: 'interface', type: 'nominal', title: 'Interface' },
            { field: 'series', type: 'nominal', title: 'Direction' },
            { field: 't', type: 'temporal', title: 'Time' },
            { field: 'value', type: 'quantitative', title: 'Mbps', format: '.2f' }
          ]
        }
      }
    };
  }

  if (kindLower === 'chart' && artifactRecord.id !== 'artifact-live-traffic-chart') {
    const table = runArtifacts.find((artifact) => String(artifact.kind ?? '').toLowerCase() === 'table');
    const tableId = table?.id ?? '00000000-0000-0000-0000-000000000000';
    baseDoc.content = {
      type: 'chart',
      version: 'v1',
      renderer: {
        kind: 'vega-lite',
        schemaVersion: 'v5'
      },
      dataRefs: [{ artifactId: tableId, rep: 'table:result' }],
      spec: {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data: { name: 'main' },
        mark: { type: 'arc', innerRadius: 24 },
        encoding: {
          theta: { field: 'Count', type: 'quantitative' },
          color: { field: 'Category', type: 'nominal', legend: { title: 'Category' } },
          tooltip: [
            { field: 'Category', type: 'nominal' },
            { field: 'Count', type: 'quantitative' }
          ]
        }
      }
    };
  }

  return baseDoc;
}

function createChatMessageRecord(
  state: ChatOrgState,
  orgId: string,
  threadId: string,
  role: ChatMessageRole,
  content: ChatMessageBlock[],
  runId?: string,
  overrides?: {
    id?: string;
    createdAt?: string;
    attachments?: ChatMessageRecord['attachments'];
  }
): StoredChatMessage {
  const id = overrides?.id ?? randomId(`msg-${role}`);
  const timestamp = overrides?.createdAt ?? new Date().toISOString();
  const message: ChatMessageRecord = {
    id,
    orgId,
    threadId,
    role,
    content: cloneValue(content),
    createdAt: timestamp,
    updatedAt: timestamp
  };
  if (runId) {
    message.runId = runId;
  }
  if (overrides?.attachments?.length) {
    message.attachments = cloneValue(overrides.attachments);
  }
  const record: StoredChatMessage = { data: message, etag: generateEtag() };
  state.messages.set(id, record);
  const order = state.messagesByThread.get(threadId) ?? [];
  order.push(id);
  state.messagesByThread.set(threadId, order);
  return record;
}

function buildMockAssistantContent(content: ChatMessageBlock[]): ChatMessageBlock[] {
  const firstText = content.find((block) => block?.type === 'text' && typeof block.text === 'string')?.text?.trim() ?? '';
  const choiceResponse = content.find((block) => block?.type === 'choiceResponse');
  if (choiceResponse) {
    const label =
      choiceResponse.label ??
      (choiceResponse.labels ?? []).join(', ') ??
      choiceResponse.text ??
      choiceResponse.choiceId ??
      (choiceResponse.choiceIds ?? []).join(', ') ??
      'that option';
    return textContent(`Understood. I’ll continue with **${label}** and start the next step.`);
  }
  const confirmationResponse = content.find((block) => block?.type === 'confirmationResponse');
  if (confirmationResponse) {
    const label = confirmationResponse.label ?? confirmationResponse.text ?? 'your confirmation';
    return textContent(`Thanks. I recorded ${label.toLowerCase()} and I’m proceeding.`);
  }
  const inputResponse = content.find((block) => block?.type === 'inputResponse');
  if (inputResponse?.text?.trim()) {
    return textContent(`Got it. I’ll continue using \`${inputResponse.text.trim()}\` as the requested input.`);
  }
  const formResponse = content.find((block) => block?.type === 'formResponse');
  if (formResponse) {
    return textContent('Thanks. I captured the requested form values and will continue.');
  }
  if (firstText) {
    const normalized = firstText.toLowerCase();
    if (normalized.includes('metric') || normalized.includes('compare')) {
      return [
        {
          type: 'choicePrompt',
          questionId: 'q-metrics-live',
          prompt: 'Which metrics do you want included?',
          selectionMode: 'multi',
          minSelections: 1,
          choices: [
            { id: 'in_bytes', label: 'Inbound bytes/sec', description: 'Ingress throughput trend.' },
            { id: 'out_bytes', label: 'Outbound bytes/sec', description: 'Egress throughput trend.' },
            { id: 'in_packets', label: 'Inbound packets/sec', description: 'Ingress packet rate.' },
            { id: 'out_packets', label: 'Outbound packets/sec', description: 'Egress packet rate.' }
          ]
        }
      ];
    }
    if (normalized.includes('proceed') || normalized.includes('continue') || normalized.includes('confirm')) {
      return [
        {
          type: 'confirmationPrompt',
          questionId: 'q-confirm-live',
          prompt: 'Proceed with the larger operation?',
          confirmLabel: 'Continue',
          cancelLabel: 'Cancel'
        }
      ];
    }
    if (normalized.includes('custom time') || normalized.includes('time window')) {
      return [
        {
          type: 'choicePrompt',
          questionId: 'q-window-live',
          prompt: 'Pick a time window or provide your own.',
          selectionMode: 'single',
          allowFreeform: true,
          freeformLabel: 'Use a custom time range',
          choices: [
            { id: '15m', label: 'Last 15 minutes' },
            { id: '30m', label: 'Last 30 minutes' },
            { id: '1h', label: 'Last 1 hour' }
          ]
        }
      ];
    }
    if (normalized.includes('route') || normalized.includes('workflow') || normalized.includes('fix') || normalized.includes('change')) {
      return [
        {
          type: 'choicePrompt',
          questionId: 'q-routing-live',
          prompt: 'Which workflow should I use for this request?',
          selectionMode: 'single',
          choices: [
            { id: 'fix_issue', label: 'Fix an active issue', description: 'Incident, outage, or break/fix work.' },
            { id: 'make_change', label: 'Make a change', description: 'Planned config or policy change.' },
            { id: 'run_runbook', label: 'Run a runbook', description: 'Guided operational procedure.' },
            { id: 'general_question', label: 'Ask a general question', description: 'Advice or informational guidance.' }
          ]
        }
      ];
    }
    if (normalized.includes('interface') || normalized.includes('trend') || normalized.includes('traffic')) {
      return [
        {
          type: 'inputPrompt',
          questionId: 'q-interface-live',
          prompt: 'Which interface should I inspect?',
          label: 'Interface name',
          placeholder: 'Example: Te1/0/1',
          inputMode: 'text',
          allowEmpty: false
        }
      ];
    }
  }
  return textContent(`Mock assistant response for "${firstText || 'your request'}"`);
}

function createChatRunRecord(
  state: ChatOrgState,
  orgId: string,
  threadRecord: StoredChatThread,
  runId: string,
  assistantContent: ChatMessageBlock[]
): StoredChatRun {
  const now = new Date();
  const run: ChatRunRecord = {
    id: runId,
    orgId,
    threadId: threadRecord.data.id,
    status: 'running',
    traceLevel: 'summary',
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    startedAt: now.toISOString()
  };
  const artifactId = randomId('artifact');
  const includeArtifact = assistantContent.length === 1 && assistantContent[0]?.type === 'text';
  const artifact: ChatArtifactRecord | null = includeArtifact
    ? {
        id: artifactId,
        threadId: threadRecord.data.id,
        runId,
        kind: 'commandOutput',
        mimeType: 'text/plain',
        summary: 'Mock diagnostic output',
        size: 2048,
        createdAt: now.toISOString()
      }
    : null;
  const assistantMessageId = randomId('msg-assistant');
  let assistantCreated = false;
  const ensureAssistantMessage = (timestamp: string) => {
    if (assistantCreated) {
      return;
    }
    const assistantRecord = createChatMessageRecord(state, orgId, threadRecord.data.id, 'assistant', assistantContent, runId, {
      id: assistantMessageId,
      createdAt: timestamp
    });
    assistantCreated = true;
    threadRecord.data.lastMessageAt = assistantRecord.data.createdAt;
    threadRecord.data.updatedAt = assistantRecord.data.createdAt;
    threadRecord.etag = generateEtag();
  };

  const events: ChatRunEventEnvelope[] = [
    { type: 'status', payload: { status: 'running', startedAt: run.startedAt }, delayMs: 200 },
    { type: 'progress', payload: { stage: 'Reasoning', detail: 'Consulting playbooks', percent: 35 }, delayMs: 900 },
    ...(artifact
      ? [
          {
            type: 'artifact' as const,
            payload: { artifactId, kind: artifact.kind, summary: artifact.summary, mimeType: artifact.mimeType, size: artifact.size },
            delayMs: 800
          }
        ]
      : []),
    {
      type: 'message',
      payload: { messageId: assistantMessageId },
      delayMs: 800,
      beforeSend: (payload) => {
        const timestamp = typeof payload.timestamp === 'string' ? payload.timestamp : new Date().toISOString();
        ensureAssistantMessage(timestamp);
      }
    },
    {
      type: 'status',
      payload: { status: 'succeeded', finishedAt: new Date(now.getTime() + 2500).toISOString(), detail: 'Assistant response delivered' },
      delayMs: 900
    }
  ];
  const record: StoredChatRun = {
    data: run,
    etag: generateEtag(),
    events: annotateEventTimestamps(events, now),
    artifacts: artifact ? [artifact] : []
  };
  state.runs.set(runId, record);
  threadRecord.data.latestRunId = runId;
  threadRecord.data.latestRunStatus = 'running';
  threadRecord.data.updatedAt = now.toISOString();
  threadRecord.etag = generateEtag();
  setTimeout(() => {
    record.data.status = 'succeeded';
    record.data.finishedAt = new Date().toISOString();
    record.data.updatedAt = record.data.finishedAt;
    threadRecord.data.latestRunStatus = 'succeeded';
    threadRecord.data.updatedAt = record.data.updatedAt!;
    threadRecord.etag = generateEtag();
  }, 4000);
  return record;
}

function annotateEventTimestamps(events: ChatRunEventEnvelope[], baseTime: Date): ChatRunEventEnvelope[] {
  let elapsed = 0;
  return events.map((event) => {
    const timestamp =
      typeof event.payload.timestamp === 'string'
        ? event.payload.timestamp
        : new Date(baseTime.getTime() + elapsed).toISOString();
    const payload = { ...event.payload, timestamp };
    const next: ChatRunEventEnvelope = {
      type: event.type,
      payload,
      delayMs: event.delayMs,
      beforeSend: event.beforeSend
    };
    elapsed += event.delayMs;
    return next;
  });
}

function streamChatRunEvents(request: Request, record: StoredChatRun): Response {
  const encoder = new TextEncoder();
  const stream = new ReadableStream({
    start(controller) {
      let index = 0;
      const timers: Array<ReturnType<typeof setTimeout>> = [];
      let startTimer: ReturnType<typeof setTimeout> | null = null;

      const send = (event: ChatRunEventEnvelope) => {
        const payload = {
          ...event.payload,
          eventId:
            typeof event.payload.eventId === 'string'
              ? event.payload.eventId
              : `${event.type}-${record.data.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          timestamp:
            typeof event.payload.timestamp === 'string' ? event.payload.timestamp : new Date().toISOString(),
          runId: record.data.id
        };
        if (event.beforeSend) {
          try {
            event.beforeSend(payload);
          } catch (error) {
            console.warn('mock chat event beforeSend failed', error);
          }
        }
        controller.enqueue(encoder.encode(`event: ${event.type}\ndata: ${JSON.stringify(payload)}\n\n`));
      };

      const pump = () => {
        if (index >= record.events.length) {
          controller.close();
          return;
        }
        const current = record.events[index++];
        const handle = setTimeout(() => {
          send(current);
          pump();
        }, current.delayMs);
        timers.push(handle);
      };

      startTimer = setTimeout(pump, 1200);

      request.signal?.addEventListener('abort', () => {
        timers.forEach((handle) => clearTimeout(handle));
        if (startTimer) {
          clearTimeout(startTimer);
        }
        controller.close();
      });
    }
  });

  return new Response(stream, {
    status: 200,
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      Connection: 'keep-alive',
      'X-Mock-Route': 'org-console'
    }
  });
}

async function handleChatRoutes(
  orgId: string,
  remainder: string,
  request: Request,
  providedFormData?: FormData | null
): Promise<Response | null> {
  const segments = remainder.split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }
  const routeRoot = segments[0];
  if (routeRoot !== 'chat' && routeRoot !== 'files') {
    return null;
  }
  const resource = routeRoot === 'chat' ? segments.slice(1) : segments;
  const method = request.method.toUpperCase();
  const url = new URL(request.url);
  const state = ensureChatState(orgId);
  const ragLookupCollections = [
    'ChunkApi',
    'ChunkCLI',
    'ChunkCLISummary',
    'ChunkCLISummaryOnly',
    'ChunkGeneral',
    'ChunkMibs',
    'ChunkSchool',
    'ChunkTest',
    'ChunkTest2',
    'ChunkValidateDesign',
    'Doc'
  ];

  if (resource.length === 1 && resource[0] === 'rag-collections' && method === 'GET') {
    return respond({ collections: ragLookupCollections });
  }

  if (resource.length === 1 && resource[0] === 'rag-lookup' && method === 'POST') {
    const allowedCollections = new Set(ragLookupCollections);
    const allowedVendors = new Set(['cisco', 'arista', 'paloalto', 'palo alto', 'palo alto networks', 'none']);
    let body: Record<string, unknown> = {};
    try {
      body = (await request.clone().json()) as Record<string, unknown>;
    } catch {
      return respond({ title: 'invalid JSON body', status: 400 }, 400);
    }

    const query = typeof body.query === 'string' ? body.query.trim() : '';
    if (!query) {
      return respond({ title: 'query is required', status: 400 }, 400);
    }
    const collections = Array.isArray(body.collections)
      ? body.collections.filter((value): value is string => typeof value === 'string').map((value) => value.trim()).filter(Boolean)
      : [];
    if (collections.length === 0) {
      return respond({ title: 'collections is required', status: 400 }, 400);
    }
    const invalidCollection = collections.find((value) => !allowedCollections.has(value));
    if (invalidCollection) {
      return respond({ title: `invalid collection: ${invalidCollection}`, status: 400 }, 400);
    }
    const vendor = typeof body.vendor === 'string' ? body.vendor.trim().toLowerCase() : '';
    if (!vendor) {
      return respond({ title: 'vendor is required', status: 400 }, 400);
    }
    if (!allowedVendors.has(vendor)) {
      return respond({ title: `invalid vendor: ${vendor}`, status: 400 }, 400);
    }

    const topKRaw = typeof body.topK === 'number' ? body.topK : undefined;
    const topK = Number.isFinite(topKRaw) && topKRaw && topKRaw > 0 ? Math.floor(topKRaw) : 8;
    const effectiveTopK = Math.min(Math.max(topK, 1), 20);

    const chunks = Array.from({ length: Math.min(effectiveTopK, 6) }, (_, idx) => ({
      id: `mock-chunk-${idx + 1}`,
      score: Number((1 - idx * 0.083).toFixed(4)),
      source: collections[idx % collections.length] ?? collections[0] ?? '',
      content: `Mock retrieval chunk ${idx + 1} for query "${query}"`,
      metadata: {
        rank: idx + 1,
        collection: collections[idx % collections.length] ?? collections[0] ?? '',
        vendor: vendor === 'none' ? (idx % 2 === 0 ? 'cisco' : 'arista') : vendor,
        documentId: `doc-${idx + 11}`,
        section: idx % 2 === 0 ? 'Troubleshooting > Routing' : 'Operations > Validation'
      }
    }));

    return respond({
      query,
      topK: effectiveTopK,
      latencyMs: 37,
      chunks
    });
  }

  if (resource.length === 1 && resource[0] === 'threads' && method === 'GET') {
    return respond(buildListResponse(listChatThreadsForUser(orgId, url.searchParams.get('createdByUserId'))));
  }

  if (resource[0] === 'exports') {
    if (resource.length === 1 && method === 'POST') {
      const payload = (await request.clone().json().catch(() => ({}))) as Record<string, unknown>;
      try {
        const stored = materializeMockOutputExport(orgId, state, payload);
        return respond(cloneValue(stored.data), 201);
      } catch (error) {
        return respond(
          {
            detail: error instanceof Error && error.message ? error.message : 'Failed to create mock output export.'
          },
          400
        );
      }
    }
    if (resource.length === 2 && method === 'GET') {
      const exportId = decodeURIComponent(resource[1] ?? '');
      const stored = state.exports.get(exportId);
      if (!stored) {
        return respond({ detail: `Mock output export ${exportId} not found.` }, 404);
      }
      return respond(cloneValue(stored.data));
    }
    if (resource.length === 3 && resource[2] === 'download' && method === 'GET') {
      const exportId = decodeURIComponent(resource[1] ?? '');
      const stored = state.exports.get(exportId);
      if (!stored) {
        return respond({ detail: `Mock output export ${exportId} not found.` }, 404);
      }
      return new Response(stored.bytes.slice().buffer as ArrayBuffer, {
        status: 200,
        headers: {
          'Content-Type': stored.data.contentType,
          'Content-Disposition': `attachment; filename="${stored.data.fileName}"`,
          'Cache-Control': 'no-store',
          'X-Mock-Route': 'org-console'
        }
      });
    }
  }

  if (resource.length === 1 && resource[0] === 'threads' && method === 'POST') {
    const payload = await request.clone().json().catch(() => ({}));
    const title = payload && typeof payload.title === 'string' ? payload.title : undefined;
    const record = createChatThreadRecord(orgId, title);
    return respond(cloneValue(record.data), 201, { ETag: record.etag });
  }

  if (resource[0] === 'threads') {
    const threadId = resource[1] ? decodeURIComponent(resource[1]) : '';
    if (!threadId) {
      return respond({ detail: 'Thread id is required.' }, 400);
    }
    const record = state.threads.get(threadId);
    if (!record) {
      return respond({ detail: `Mock chat thread ${threadId} not found.` }, 404);
    }

    if (resource.length === 2) {
      if (method === 'GET') {
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }
      if (method === 'PATCH') {
        const payload = await request.clone().json();
        if (Object.prototype.hasOwnProperty.call(payload, 'pinned')) {
          record.data.pinned = Boolean(payload.pinned);
        }
        record.data.updatedAt = new Date().toISOString();
        record.etag = generateEtag();
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }
      if (method === 'DELETE') {
        deleteChatThreadRecord(orgId, threadId);
        return respond(undefined, 204);
      }
    }

    if (resource.length === 3 && resource[2] === 'interact' && method === 'POST') {
      const body = await request.clone().json();
      const content = Array.isArray(body.content) ? (body.content as ChatMessageBlock[]) : [];
      const visibilityContext =
        body?.context && typeof body.context === 'object' && !Array.isArray(body.context)
          ? ((body.context as Record<string, unknown>).visibilitySession as Record<string, unknown> | undefined)
          : undefined;
      const attachments = Array.isArray(body.attachments)
        ? (body.attachments as Array<Record<string, unknown>>)
            .map((item) => ({
              resourceType: typeof item.resourceType === 'string' ? item.resourceType : undefined,
              resourceId:
                typeof item.resourceId === 'string'
                  ? item.resourceId
                  : typeof item.artifactId === 'string'
                    ? item.artifactId
                    : undefined,
              name: typeof item.name === 'string' ? item.name : undefined,
              mimeType: typeof item.mimeType === 'string' ? item.mimeType : undefined,
              sizeBytes: typeof item.sizeBytes === 'number' ? item.sizeBytes : undefined
            }))
            .filter((item) => item.resourceType && item.resourceId)
        : [];
      if (content.length === 0 && attachments.length === 0) {
        return respond({ detail: 'At least one content block or attachment is required.' }, 400);
      }
      const runId = randomId('run');
      const userMessage = createChatMessageRecord(state, orgId, threadId, 'user', content, runId, { attachments });
      record.data.lastMessageAt = userMessage.data.createdAt;
      record.data.updatedAt = userMessage.data.createdAt;
      record.etag = generateEtag();
      const runRecord = createChatRunRecord(state, orgId, record, runId, buildMockAssistantContent(content));
      if (visibilityContext?.sessionId) {
        const requestText = content
          .map((block) => (block?.type === 'text' && typeof block.text === 'string' ? block.text.trim() : ''))
          .filter(Boolean)
          .join('\n')
          .trim();
        scheduleMockWorkbenchProposal(orgId, threadId, requestText, visibilityContext);
      }
      return respond(cloneValue(userMessage.data), 202, {
        Location: `/public/v1/orgs/${orgId}/chat/runs/${runRecord.data.id}`,
        Link: `</public/v1/orgs/${orgId}/chat/runs/${runRecord.data.id}/events>; rel="events"`
      });
    }

    if (resource.length === 3 && resource[2] === 'messages' && method === 'GET') {
      const items = listThreadMessages(orgId, threadId);
      return respond(buildListResponse(items));
    }
  }

  if (resource[0] === 'files') {
    if (resource.length === 1 && method === 'GET') {
      const sourceKind = url.searchParams.get('sourceKind')?.trim().toLowerCase();
      const query = url.searchParams.get('q')?.trim().toLowerCase() ?? '';
      const limitRaw = Number.parseInt(url.searchParams.get('limit') ?? '50', 10);
      const limit = Number.isFinite(limitRaw) ? Math.max(1, Math.min(200, limitRaw)) : 50;
      const items = Array.from(state.files.values())
        .map((entry) => cloneValue(entry.data))
        .filter((entry) => (sourceKind ? String(entry.sourceKind ?? '').trim().toLowerCase() === sourceKind : true))
        .filter((entry) => {
          if (!query) {
            return true;
          }
          const haystack = [entry.name, entry.filename, entry.description, entry.mimeType]
            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
            .join('\n')
            .toLowerCase();
          return haystack.includes(query);
        })
        .sort((left, right) => {
          const leftTime = Date.parse(String(left.updatedAt ?? left.createdAt ?? '')) || 0;
          const rightTime = Date.parse(String(right.updatedAt ?? right.createdAt ?? '')) || 0;
          return rightTime - leftTime;
        })
        .slice(0, limit);
      return respond(buildListResponse(items));
    }
    if (resource.length === 1 && method === 'POST') {
      const form = providedFormData ?? (await request.clone().formData().catch(() => null));
      if (!form) {
        return respond({ detail: 'Invalid multipart body.' }, 400);
      }
      const upload = form.get('file');
      if (!isMockUploadedFile(upload)) {
        return respond({ detail: 'file is required.' }, 400);
      }
      const uploadedBytes = await readMockUploadedBytes(upload);
      const uploadName = typeof upload.name === 'string' && upload.name.length > 0 ? upload.name : 'upload.bin';
      const filename = typeof form.get('name') === 'string' ? String(form.get('name')) || uploadName : uploadName;
      const normalizedMimeType = inferMockChatFileMime(filename, upload.type || 'application/octet-stream');
      const uploadedText = mockTextReadyMimeTypes.has(normalizedMimeType) ? await readMockUploadedText(upload) : null;
      const analysis = analyzeMockUploadedFile(filename, upload.type || 'application/octet-stream', uploadedBytes, uploadedText);
      if (!analysis.accepted) {
        return respond({ detail: analysis.reason ?? 'unsupported file content type' }, 400);
      }
      const fileId = randomId('file');
      const now = new Date().toISOString();
      const record: UploadedFileRecord = {
        id: fileId,
        orgId,
        name: filename,
        threadId: typeof form.get('threadId') === 'string' ? String(form.get('threadId')) : undefined,
        filename,
        mimeType: analysis.normalizedMimeType,
        sizeBytes: upload.size,
        checksum: buildMockFileChecksum(uploadedBytes),
        sourceKind: 'user_upload',
        status: 'ready',
        availabilityState: analysis.availabilityState,
        analysisFamily: analysis.analysisFamily,
        description: typeof form.get('description') === 'string' ? String(form.get('description')) : undefined,
        createdAt: new Date(now),
        updatedAt: new Date(now)
      };
      state.files.set(fileId, { data: record, bytes: uploadedBytes, textContent: analysis.textContent });
      return respond(cloneValue(record), 201, { ETag: generateEtag() });
    }
    if (resource.length === 2 && method === 'GET') {
      const fileId = decodeURIComponent(resource[1] ?? '');
      const stored = state.files.get(fileId);
      if (!stored) {
        return respond({ detail: `Mock file ${fileId} not found.` }, 404);
      }
      return respond(cloneValue(stored.data), 200, { ETag: generateEtag() });
    }
    if (resource.length === 2 && method === 'PATCH') {
      const fileId = decodeURIComponent(resource[1] ?? '');
      const stored = state.files.get(fileId);
      if (!stored) {
        return respond({ detail: `Mock file ${fileId} not found.` }, 404);
      }
      const payload = (await request.clone().json().catch(() => ({}))) as Record<string, unknown>;
      stored.data = {
        ...stored.data,
        ...(typeof payload.name === 'string' ? { name: payload.name.trim() || stored.data.name } : {}),
        ...(typeof payload.filename === 'string' ? { filename: payload.filename.trim() || stored.data.filename } : {}),
        ...(typeof payload.description === 'string' ? { description: payload.description.trim() || undefined } : {}),
        updatedAt: new Date(),
      };
      return respond(cloneValue(stored.data), 200, { ETag: generateEtag() });
    }
    if (resource.length === 2 && method === 'DELETE') {
      const fileId = decodeURIComponent(resource[1] ?? '');
      if (!state.files.has(fileId)) {
        return respond({ detail: `Mock file ${fileId} not found.` }, 404);
      }
      state.files.delete(fileId);
      return respond(undefined, 204);
    }
    if (resource.length === 3 && resource[2] === 'content' && method === 'GET') {
      const fileId = decodeURIComponent(resource[1] ?? '');
      const stored = state.files.get(fileId);
      if (!stored) {
        return respond({ detail: `Mock file ${fileId} not found.` }, 404);
      }
      if (typeof stored.textContent !== 'string') {
        return respond({ detail: `Readable preview unavailable for mock file ${fileId}.` }, 409);
      }
      const url = new URL(request.url);
      const startLine = Math.max(1, Number.parseInt(url.searchParams.get('startLine') ?? '1', 10) || 1);
      const maxLinesRaw = Number.parseInt(url.searchParams.get('maxLines') ?? '80', 10) || 80;
      const maxLines = Math.min(200, Math.max(1, maxLinesRaw));
      return respond(buildMockFileTextSlice(fileId, stored.data.mimeType, stored.textContent, startLine, maxLines), 200, {
        ETag: generateEtag()
      });
    }
    if (resource.length === 3 && resource[2] === 'download' && method === 'GET') {
      const fileId = decodeURIComponent(resource[1] ?? '');
      const stored = state.files.get(fileId);
      if (!stored) {
        return respond({ detail: `Mock file ${fileId} not found.` }, 404);
      }
      if (stored.data.status === 'blocked' || stored.data.availabilityState === 'none') {
        return respond({ detail: stored.data.blockedReason || `Download unavailable for mock file ${fileId}.` }, 409);
      }
      const dispositionType = stored.data.availabilityState === 'preview_ready' ? 'inline' : 'attachment';
      const filename = stored.data.filename ?? stored.data.name ?? `${fileId}.bin`;
      return new Response(stored.bytes.slice().buffer as ArrayBuffer, {
        status: 200,
        headers: {
          'Content-Type': stored.data.mimeType,
          'Content-Disposition': `${dispositionType}; filename="${filename}"`,
          'Cache-Control': 'no-store',
          'X-Mock-Route': 'org-console'
        }
      });
    }
  }

  if (resource[0] === 'messages') {
    const messageId = resource[1] ? decodeURIComponent(resource[1]) : '';
    const messageRecord = messageId ? getChatMessageRecord(orgId, messageId) : null;
    if (!messageRecord) {
      return respond({ detail: `Mock chat message ${messageId} not found.` }, 404);
    }
    if (resource.length === 2 && method === 'GET') {
      return respond(cloneValue(messageRecord.data), 200, { ETag: messageRecord.etag });
    }
    if (resource.length === 3 && resource[2] === 'reactions' && method === 'POST') {
      return respond(undefined, 204);
    }
  }

  if (resource[0] === 'artifacts' && resource.length === 2 && method === 'GET') {
    const artifactId = decodeURIComponent(resource[1] ?? '');
    let artifactRecord: ChatArtifactRecord | null = null;
    let artifactRunRecord: StoredChatRun | null = null;
    for (const candidate of state.runs.values()) {
      const found = candidate.artifacts.find((artifact) => artifact.id === artifactId);
      if (found) {
        artifactRecord = found;
        artifactRunRecord = candidate;
        break;
      }
    }
    if (!artifactRecord || !artifactRunRecord) {
      return respond({ detail: `Mock artifact ${artifactId} not found.` }, 404);
    }
    const url = new URL(request.url);
    const full = url.searchParams.get('content')?.toLowerCase() === 'full';
    const baseDoc = buildMockChatArtifactDocument(orgId, artifactRecord, artifactRunRecord.artifacts, full);
    return respond(baseDoc, 200, { 'Content-Type': 'application/vnd.reap.artifact+json', ETag: generateEtag() });
  }

  if (resource[0] === 'runs') {
    const runId = resource[1] ? decodeURIComponent(resource[1]) : '';
    const runRecord = runId ? getChatRunRecord(orgId, runId) : null;
    if (!runRecord) {
      return respond({ detail: `Mock chat run ${runId} not found.` }, 404);
    }
    if (resource.length === 3 && resource[2] === 'events' && method === 'GET') {
      return streamChatRunEvents(request, runRecord);
    }
    if (resource.length === 3 && resource[2] === 'artifacts' && method === 'GET') {
      return respond(buildListResponse(runRecord.artifacts.map((artifact) => cloneValue(artifact))));
    }
    if (resource.length === 4 && resource[2] === 'artifacts' && method === 'GET') {
      const artifactId = decodeURIComponent(resource[3] ?? '');
      const record = runRecord.artifacts.find((a) => a.id === artifactId);
      if (!record) {
        return respond({ detail: `Mock artifact ${artifactId} not found.` }, 404);
      }
      const url = new URL(request.url);
      const full = url.searchParams.get('content')?.toLowerCase() === 'full';
      const baseDoc = buildMockChatArtifactDocument(orgId, record, runRecord.artifacts, full);
      return respond(baseDoc, 200, { 'Content-Type': 'application/vnd.reap.artifact+json', ETag: generateEtag() });
    }
    if (resource.length === 3 && resource[2] === 'ratings' && method === 'POST') {
      return respond(undefined, 204);
    }
  }

  return respond({ detail: `Mock handler missing for /orgs/${orgId}/${remainder}.` }, 501);
}

async function handleMetricsRoutes(orgId: string, remainder: string, request: Request): Promise<Response | null> {
  if (remainder !== 'metrics/query_range' && remainder !== 'metrics/query') {
    return null;
  }
  if (request.method.toUpperCase() !== 'POST') {
    return respond({ detail: 'Method not allowed.' }, 405);
  }
  let body: Record<string, unknown> = {};
  try {
    const parsed = await request.clone().json();
    body = isRecordValue(parsed) ? parsed : {};
  } catch {
    return respond({ title: 'invalid JSON body', status: 400 }, 400);
  }
  return respond(buildMockMetricsRangeResponse(orgId, body));
}

function withOrgId<T extends Record<string, unknown>>(orgId: string, templates: T[]): T[] {
  const profile = ORG_PROFILES[orgId];
  const inferredName = typeof profile?.name === 'string' ? profile.name : undefined;
  return templates.map((template) => {
    const next = { ...template, orgId } as Record<string, unknown>;
    if (inferredName && Object.prototype.hasOwnProperty.call(template, 'orgName') && next.orgName === undefined) {
      next.orgName = inferredName;
    }
    return next as T;
  });
}

function ensureConnectivityState(orgId: string): void {
  if (!siteStore.has(orgId)) {
    const siteEntries = new Map<string, StoredSite>();
    siteTemplates.forEach((template) => {
      const data: SiteRecord = {
        id: template.id,
        orgId,
        name: template.name,
        siteType: template.siteType ?? SiteSiteTypeEnum.OnPrem,
        awsRegion: template.awsRegion,
        awsAccountId: template.awsAccountId,
        azureSubscriptionId: template.azureSubscriptionId,
        azureLocation: template.azureLocation,
        pingEndpointIp: template.pingEndpointIp,
        address: template.address ? cloneValue(template.address) : undefined,
        notes: template.notes,
        createdAt: new Date(template.createdAt),
        updatedAt: new Date(template.updatedAt),
        createdBy: template.createdBy ?? 'mock-user',
        updatedBy: template.updatedBy ?? 'mock-user',
        labels: template.labels ? cloneValue(template.labels) : undefined,
        deviceCounts: template.deviceCounts ? cloneValue(template.deviceCounts) : undefined,
        connectorCount: template.connectorCount,
        health: template.health ?? 'healthy'
      } as SiteRecord;
      siteEntries.set(data.id!, {
        data,
        etag: generateEtag(),
        clusterIds: new Set(template.clusterIds)
      });
    });
    siteStore.set(orgId, siteEntries);
  }

  if (!clusterStore.has(orgId)) {
    const clusterEntries = new Map<string, StoredCluster>();
    clusterTemplates.forEach((template) => {
      const data: ClusterRecord = {
        id: template.id,
        orgId,
        name: template.name,
        description: template.description,
        status: template.status ?? 'active',
        createdAt: new Date(template.createdAt),
        updatedAt: new Date(template.updatedAt),
        createdBy: template.createdBy ?? 'mock-user',
        updatedBy: template.updatedBy ?? 'mock-user',
        labels: template.labels ? cloneValue(template.labels) : undefined,
        settings: template.settings ? cloneValue(template.settings) : undefined,
        connectorsOnline: template.connectorsOnline,
        connectorsTotal: template.connectorsTotal,
        haState: template.haState,
        health: template.health ?? 'healthy'
      } as ClusterRecord;
      clusterEntries.set(data.id!, {
        data,
        etag: generateEtag(),
        siteIds: new Set(template.siteIds)
      });
    });
    clusterStore.set(orgId, clusterEntries);
  }

  if (!connectorStore.has(orgId)) {
    const connectorEntries = new Map<string, StoredConnector>();
    connectorTemplates.forEach((template) => {
      const data: ConnectorRecord = {
        id: template.id,
        orgId,
        name: template.name,
        status: normalizeConnectorStatus(template.status),
        version: template.version,
        clusterId: template.clusterId,
        registeredAt: new Date(template.registeredAt),
        lastSeenAt: new Date(template.lastSeenAt),
        createdAt: new Date(template.registeredAt),
        updatedAt: new Date(template.lastSeenAt),
        createdBy: template.createdBy ?? 'mock-user',
        updatedBy: template.updatedBy ?? 'mock-user',
        labels: template.labels ? cloneValue(template.labels) : undefined,
        siteHints: template.siteHints ? [...template.siteHints] : undefined,
        locationHint: template.locationHint
      } as ConnectorRecord;
      connectorEntries.set(data.id!, {
        data,
        etag: generateEtag(),
        userdata: {
          status: template.userdataStatus ?? 'available',
          redeemedAt: template.userdataStatus === 'redeemed' ? new Date(template.lastSeenAt) : null
        }
      });
    });
    connectorStore.set(orgId, connectorEntries);
  }

  syncConnectivityState(orgId);
}

function syncConnectivityState(orgId: string): void {
  const sites = siteStore.get(orgId);
  const clusters = clusterStore.get(orgId);
  const connectors = connectorStore.get(orgId);
  if (!sites || !clusters || !connectors) {
    return;
  }

  clusters.forEach((clusterRecord) => {
    const clusterId = clusterRecord.data.id!;
    clusterRecord.siteIds.forEach((siteId) => {
      const siteRecord = sites.get(siteId);
      siteRecord?.clusterIds.add(clusterId);
    });
  });

  sites.forEach((siteRecord) => {
    const siteId = siteRecord.data.id!;
    siteRecord.clusterIds.forEach((clusterId) => {
      const clusterRecord = clusters.get(clusterId);
      clusterRecord?.siteIds.add(siteId);
    });
  });

  clusters.forEach((clusterRecord) => {
    const clusterId = clusterRecord.data.id!;
    const clusterConnectorList = Array.from(connectors.values()).filter((entry) => entry.data.clusterId === clusterId);
    clusterRecord.data.connectorsTotal = clusterConnectorList.length;
    clusterRecord.data.connectorsOnline = clusterConnectorList.filter((entry) => entry.data.status === 'online').length;
    clusterRecord.data.updatedAt = clusterRecord.data.updatedAt ? new Date(clusterRecord.data.updatedAt) : new Date();
  });

  sites.forEach((siteRecord) => {
    const connectorSet = new Set<string>();
    siteRecord.clusterIds.forEach((clusterId) => {
      Array.from(connectors.values())
        .filter((entry) => entry.data.clusterId === clusterId)
        .forEach((entry) => connectorSet.add(entry.data.id!));
    });
    siteRecord.data.connectorCount = connectorSet.size;
    if (!siteRecord.data.deviceCounts) {
      const total = connectorSet.size * 18;
      siteRecord.data.deviceCounts = {
        total,
        active: Math.max(0, Math.round(total * 0.9)),
        offline: Math.max(0, total - Math.round(total * 0.9))
      };
    }
    siteRecord.data.updatedAt = siteRecord.data.updatedAt ? new Date(siteRecord.data.updatedAt) : new Date();
  });
}

function ensurePeopleState(orgId: string): void {
  if (!roleStore.has(orgId)) {
    const roleEntries = new Map<string, StoredRole>();
    roleTemplates.forEach((template) => {
      const id = template.id ?? randomId('role');
      const createdAt = template.createdAt ? new Date(template.createdAt) : new Date();
      const updatedAt = template.updatedAt ? new Date(template.updatedAt) : new Date(createdAt);
      const status = (template.status ?? 'active') as Role['status'];
      const data: RoleRecord = {
        id,
        scope: template.scope as Role['scope'],
        slug: template.slug,
        name: template.name,
        description: template.description,
        status,
        permissions: cloneValue(template.permissions) as Permissionschema[],
        createdAt,
        updatedAt,
        createdBy: template.createdBy ?? 'system',
        updatedBy: template.updatedBy ?? 'system'
      };
      roleEntries.set(id, {
        data,
        etag: generateEtag()
      });
    });
    roleStore.set(orgId, roleEntries);
  }

  const roleEntries = roleStore.get(orgId)!;
  const summarizeRoles = (roleIds: string[]): NonNullable<Membership['rolesResolved']> => {
    return roleIds.map((roleId) => {
      const roleRecord = roleEntries.get(roleId);
      return {
        id: roleId,
        slug: roleRecord?.data.slug ?? roleId,
        name: roleRecord?.data.name ?? roleRecord?.data.slug ?? 'Role'
      };
    });
  };

  if (!membershipStore.has(orgId)) {
    const membershipEntries = new Map<string, StoredMembership>();
    membershipTemplates.forEach((template) => {
      const id = template.id ?? randomId('membership');
      const createdAt = template.createdAt ? new Date(template.createdAt) : new Date();
      const updatedAt = template.updatedAt ? new Date(template.updatedAt) : new Date(createdAt);
      const roleIds = (template.rolesResolved ?? []).map((role) => role.id).filter((value): value is string => Boolean(value));
      const data: MembershipRecord = {
        id,
        orgId,
        orgSlug: template.orgSlug ?? 'org',
        userId: template.userId,
        status: (template.status ?? 'active') as Membership['status'],
        labels: template.labels ? cloneValue(template.labels) : undefined,
        rolesResolved: summarizeRoles(roleIds),
        user: template.user ? cloneValue(template.user) : undefined,
        createdAt,
        updatedAt,
        createdBy: template.createdBy ?? 'system',
        updatedBy: template.updatedBy ?? 'system'
      };
      membershipEntries.set(id, {
        data,
        etag: generateEtag()
      });
    });
    membershipStore.set(orgId, membershipEntries);
  }

  if (!invitationStore.has(orgId)) {
    const invitationEntries = new Map<string, StoredInvitation>();
    invitationTemplates.forEach((template) => {
      const id = template.id ?? randomId('invitation');
      const createdAt = template.createdAt ? new Date(template.createdAt) : new Date();
      const updatedAt = template.updatedAt ? new Date(template.updatedAt) : new Date(createdAt);
      const roleIds = (template.rolesResolved ?? []).map((role) => role.id).filter((value): value is string => Boolean(value));
      const expiresAt = template.expiresAt ? new Date(template.expiresAt) : new Date(Date.now() + 14 * 86400000);
      const data: InvitationRecord = {
        id,
        orgId,
        email: template.email,
        rolesResolved: summarizeRoles(roleIds),
        status: (template.status ?? 'pending') as Invitation['status'],
        expiresAt,
        invitedBy: template.invitedBy ?? 'user-system',
        createdAt,
        updatedAt,
        createdBy: 'user-system',
        updatedBy: 'user-system',
        acceptedAt: template.acceptedAt ? new Date(template.acceptedAt) : undefined,
        acceptedBy: template.acceptedBy
      };
      invitationEntries.set(id, {
        data,
        etag: generateEtag()
      });
    });
    invitationStore.set(orgId, invitationEntries);
  }
}

function getRoleStore(orgId: string): Map<string, StoredRole> {
  ensurePeopleState(orgId);
  const store = roleStore.get(orgId);
  if (!store) {
    throw new Error(`Role store missing for org ${orgId}`);
  }
  return store;
}

function getMembershipStore(orgId: string): Map<string, StoredMembership> {
  ensurePeopleState(orgId);
  const store = membershipStore.get(orgId);
  if (!store) {
    throw new Error(`Membership store missing for org ${orgId}`);
  }
  return store;
}

function getInvitationStore(orgId: string): Map<string, StoredInvitation> {
  ensurePeopleState(orgId);
  const store = invitationStore.get(orgId);
  if (!store) {
    throw new Error(`Invitation store missing for org ${orgId}`);
  }
  return store;
}

function resolveRoleSummaries(orgId: string, roleIds: string[]): NonNullable<Membership['rolesResolved']> {
  const store = getRoleStore(orgId);
  return roleIds.map((roleId) => {
    const roleRecord = store.get(roleId);
    return {
      id: roleId,
      slug: roleRecord?.data.slug ?? roleId,
      name: roleRecord?.data.name ?? roleRecord?.data.slug ?? 'Role'
    };
  });
}

function ensureApiKeyStore(orgId: string): Map<string, StoredApiKey> {
  if (!apiKeyStore.has(orgId)) {
    const entries = new Map<string, StoredApiKey>();
    apiKeyTemplates.forEach((template) => {
      const id = template.id ?? randomId('api-key');
      const createdAt = template.createdAt ? new Date(template.createdAt) : new Date();
      const updatedAt = template.updatedAt ? new Date(template.updatedAt) : new Date(createdAt);
      const expiresAt =
        template.expiresAt !== undefined ? new Date(template.expiresAt) : new Date(createdAt.getTime() + 90 * 86_400_000);
      const rawRoleIds = Array.isArray(template.roleIds) ? template.roleIds.filter((value): value is string => Boolean(value)) : [];
      const roleIds = rawRoleIds.length > 0 ? Array.from(new Set(rawRoleIds)) : ['role-viewer'];
      const rolesResolved = resolveRoleSummaries(orgId, roleIds);
      const status = (template.status ?? ApiKeyStatusEnum.Active) as ApiKey['status'];
      const record: ApiKeyRecord = {
        id,
        orgId,
        name: template.name,
        status,
        prefix: template.prefix ?? API_KEY_SECRET_PREFIX,
        last4: (template.last4 ?? '0000').toUpperCase(),
        roleIds,
        rolesResolved,
        createdAt,
        updatedAt,
        createdBy: template.createdBy ?? 'mock-user',
        updatedBy: template.updatedBy ?? template.createdBy ?? 'mock-user',
        createdByUserId: template.createdByUserId ?? template.createdBy ?? 'mock-user',
        expiresAt
      };
      if (template.lastUsedAt) {
        record.lastUsedAt = new Date(template.lastUsedAt);
      }
      entries.set(id, {
        data: record,
        etag: generateEtag()
      });
    });
    apiKeyStore.set(orgId, entries);
  }

  const store = apiKeyStore.get(orgId);
  if (!store) {
    throw new Error(`API key store missing for org ${orgId}`);
  }
  return store;
}

function getApiKeyStore(orgId: string): Map<string, StoredApiKey> {
  return ensureApiKeyStore(orgId);
}

function listApiKeys(orgId: string): ApiKey[] {
  return Array.from(getApiKeyStore(orgId).values()).map((record) => cloneValue(record.data) as ApiKey);
}

function getApiKeyRecord(orgId: string, apiKeyId: string): StoredApiKey | undefined {
  return getApiKeyStore(orgId).get(apiKeyId);
}

function listMemberships(orgId: string): Membership[] {
  return Array.from(getMembershipStore(orgId).values()).map((record) => cloneValue(record.data));
}

function listInvitations(orgId: string): Invitation[] {
  return Array.from(getInvitationStore(orgId).values()).map((record) => cloneValue(record.data));
}

function listRoles(orgId: string): Role[] {
  return Array.from(getRoleStore(orgId).values()).map((record) => cloneValue(record.data));
}

function refreshResolvedRolesForRole(orgId: string, roleId: string): void {
  const summary = resolveRoleSummaries(orgId, [roleId])[0];
  const membershipEntries = getMembershipStore(orgId);
  membershipEntries.forEach((record) => {
    if (!record.data.rolesResolved) {
      return;
    }
    let changed = false;
    record.data.rolesResolved = record.data.rolesResolved.map((role) => {
      if (role.id === roleId) {
        changed = true;
        return summary;
      }
      return role;
    });
    if (changed) {
      record.data.updatedAt = new Date();
      record.data.updatedBy = 'mock-user';
      record.etag = generateEtag();
    }
  });

  const invitationEntries = getInvitationStore(orgId);
  invitationEntries.forEach((record) => {
    let changed = false;
    record.data.rolesResolved = record.data.rolesResolved.map((role) => {
      if (role.id === roleId) {
        changed = true;
        return summary;
      }
      return role;
    });
    if (changed) {
      record.data.updatedAt = new Date();
      record.data.updatedBy = 'mock-user';
      record.etag = generateEtag();
    }
  });

  const apiKeyEntries = getApiKeyStore(orgId);
  apiKeyEntries.forEach((record) => {
    const roleIds = record.data.roleIds ?? [];
    if (!roleIds.includes(roleId)) {
      return;
    }
    record.data.rolesResolved = resolveRoleSummaries(orgId, roleIds);
    record.data.updatedAt = new Date();
    record.data.updatedBy = 'mock-user';
    record.etag = generateEtag();
  });
}

function removeRoleReferences(orgId: string, roleId: string): void {
  const membershipEntries = getMembershipStore(orgId);
  membershipEntries.forEach((record) => {
    const current = record.data.rolesResolved ?? [];
    const next = current.filter((role) => role.id !== roleId);
    if (next.length !== current.length) {
      record.data.rolesResolved = next;
      record.data.updatedAt = new Date();
      record.data.updatedBy = 'mock-user';
      record.etag = generateEtag();
    }
  });

  const invitationEntries = getInvitationStore(orgId);
  invitationEntries.forEach((record) => {
    const current = record.data.rolesResolved;
    const next = current.filter((role) => role.id !== roleId);
    if (next.length !== current.length) {
      record.data.rolesResolved = next;
      record.data.updatedAt = new Date();
      record.data.updatedBy = 'mock-user';
      record.etag = generateEtag();
    }
  });

  const apiKeyEntries = getApiKeyStore(orgId);
  apiKeyEntries.forEach((record) => {
    const current = record.data.roleIds ?? [];
    if (!current.includes(roleId)) {
      return;
    }
    const next = current.filter((id) => id !== roleId);
    record.data.roleIds = next;
    record.data.rolesResolved = next.length > 0 ? resolveRoleSummaries(orgId, next) : [];
    record.data.updatedAt = new Date();
    record.data.updatedBy = 'mock-user';
    record.etag = generateEtag();
  });
}

function ensureOrgState(orgId: string): void {
  ensurePeopleState(orgId);
  ensureApiKeyStore(orgId);
  if (!idpConnectionStore.has(orgId)) {
    const connectionEntries = new Map<string, StoredConnection>();
    idpConnectionTemplates.forEach((template) => {
      const connection = cloneConnectionTemplate(template, orgId);
      const identifier = connection.id as string;
      const stored: StoredConnection = {
        data: connection,
        etag: generateEtag()
      };
      connectionEntries.set(identifier, stored);
      seedConnectionMappings(orgId, identifier, connection);
    });
    idpConnectionStore.set(orgId, connectionEntries);
  }

  if (!secretStore.has(orgId)) {
    initializeSecretStore(orgId);
  }

  ensureConnectivityState(orgId);
  ensureDiscoveryState(orgId);
}

function getSiteStore(orgId: string): Map<string, StoredSite> {
  ensureOrgState(orgId);
  const store = siteStore.get(orgId);
  if (!store) {
    throw new Error(`Connectivity mock store for sites missing in org ${orgId}`);
  }
  return store;
}

function getClusterStore(orgId: string): Map<string, StoredCluster> {
  ensureOrgState(orgId);
  const store = clusterStore.get(orgId);
  if (!store) {
    throw new Error(`Connectivity mock store for clusters missing in org ${orgId}`);
  }
  return store;
}

function getConnectorStore(orgId: string): Map<string, StoredConnector> {
  ensureOrgState(orgId);
  const store = connectorStore.get(orgId);
  if (!store) {
    throw new Error(`Connectivity mock store for connectors missing in org ${orgId}`);
  }
  return store;
}

function cloneSiteRecord(record: StoredSite): SiteRecord {
  return cloneValue(record.data) as SiteRecord;
}

function cloneClusterRecord(record: StoredCluster): ClusterRecord {
  return cloneValue(record.data) as ClusterRecord;
}

function cloneConnectorRecord(record: StoredConnector): ConnectorRecord {
  return cloneValue(record.data) as ConnectorRecord;
}

function listSites(orgId: string): SiteRecord[] {
  return Array.from(getSiteStore(orgId).values()).map(cloneSiteRecord);
}

function getSiteRecord(orgId: string, siteId: string): StoredSite | null {
  return getSiteStore(orgId).get(siteId) ?? null;
}

class SiteMockValidationError extends Error {}

const canonicalAzureLocation = (raw?: string | null): string => raw?.trim().toLowerCase() ?? '';

const azureLocationPattern = /^(?:[a-z0-9]|[a-z0-9][a-z0-9-]{0,62}[a-z0-9])$/;

const isDisallowedAzureLocation = (location: string): boolean =>
  location === 'all-locations' ||
  location === 'global' ||
  location.includes('euap') ||
  location.includes('stg') ||
  location.includes('stage');

function normalizeSiteRecordByType(record: SiteRecord): void {
  const siteType = record.siteType ?? SiteSiteTypeEnum.OnPrem;
  record.siteType = siteType;
  if (siteType === SiteSiteTypeEnum.AwsCloud) {
    const awsRegion = record.awsRegion?.trim();
    const awsAccountId = record.awsAccountId?.trim();
    if (!awsRegion || !awsAccountId) {
      throw new SiteMockValidationError('AWS Region and AWS account ID are required for AWS Cloud Sites.');
    }
    record.awsRegion = awsRegion;
    record.awsAccountId = awsAccountId;
    record.address = undefined;
    record.azureSubscriptionId = undefined;
    record.azureLocation = undefined;
    return;
  }
  if (siteType === SiteSiteTypeEnum.AzureCloud) {
    const subscriptionId = normalizeUUIDString(record.azureSubscriptionId);
    if (!subscriptionId) {
      throw new SiteMockValidationError('A valid non-nil Azure subscription ID is required for Azure Cloud Sites.');
    }
    record.azureSubscriptionId = subscriptionId;
    const azureLocation = canonicalAzureLocation(record.azureLocation);
    if (!azureLocation) {
      throw new SiteMockValidationError('azureLocation is required when siteType=azureCloud');
    }
    if (!azureLocationPattern.test(azureLocation) || isDisallowedAzureLocation(azureLocation)) {
      throw new SiteMockValidationError('azureLocation must be a canonical Azure location code when siteType=azureCloud');
    }
    record.azureLocation = azureLocation;
    record.address = undefined;
    record.awsRegion = undefined;
    record.awsAccountId = undefined;
    return;
  }
  if (siteType === SiteSiteTypeEnum.OnPrem) {
    record.awsRegion = undefined;
    record.awsAccountId = undefined;
    record.azureSubscriptionId = undefined;
    record.azureLocation = undefined;
    return;
  }
  throw new SiteMockValidationError(`Unsupported Site type: ${String(siteType)}.`);
}

function createSiteRecord(orgId: string, payload: SiteCreate): StoredSite {
  const store = getSiteStore(orgId);
  const id = randomId('site');
  const now = new Date();
  const labels = (payload as Partial<{ labels: Record<string, string> }>).labels;
  const deviceCounts = (payload as Partial<{ deviceCounts: SiteTemplate['deviceCounts'] }>).deviceCounts;
  const health = (payload as Partial<{ health: HealthPill }>).health ?? 'healthy';
  const data: SiteRecord = {
    id,
    orgId,
    name: payload.name,
    siteType: payload.siteType ?? SiteSiteTypeEnum.OnPrem,
    awsRegion: payload.awsRegion,
    awsAccountId: payload.awsAccountId,
    azureSubscriptionId: payload.azureSubscriptionId,
    azureLocation: payload.azureLocation,
    address: payload.address ? cloneValue(payload.address) : undefined,
    notes: payload.notes ?? undefined,
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    labels: labels ? cloneValue(labels) : undefined,
    deviceCounts: deviceCounts ? cloneValue(deviceCounts) : undefined,
    connectorCount: 0,
    health
  } as SiteRecord;
  normalizeSiteRecordByType(data);
  const record: StoredSite = {
    data,
    etag: generateEtag(),
    clusterIds: new Set()
  };
  store.set(id, record);
  syncConnectivityState(orgId);
  return record;
}

function updateSiteRecord(orgId: string, siteId: string, payload: SiteUpdate): StoredSite | null {
  const record = getSiteRecord(orgId, siteId);
  if (!record) {
    return null;
  }

  const nextData: SiteRecord = { ...record.data };

  if (payload.name !== undefined) {
    nextData.name = payload.name;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'notes')) {
    nextData.notes = payload.notes ?? undefined;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'pingEndpointIp')) {
    nextData.pingEndpointIp = payload.pingEndpointIp ?? undefined;
  }
  if (payload.address !== undefined) {
    nextData.address = payload.address ? cloneValue(payload.address) : undefined;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'siteType')) {
    nextData.siteType = payload.siteType;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'awsRegion')) {
    nextData.awsRegion = payload.awsRegion ?? undefined;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'awsAccountId')) {
    nextData.awsAccountId = payload.awsAccountId ?? undefined;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'azureSubscriptionId')) {
    nextData.azureSubscriptionId = payload.azureSubscriptionId ?? undefined;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'azureLocation')) {
    nextData.azureLocation = payload.azureLocation ?? undefined;
  }
  normalizeSiteRecordByType(nextData);

  const labels = (payload as Partial<{ labels?: Record<string, string> }>).labels;
  if (labels !== undefined) {
    nextData.labels = labels ? cloneValue(labels) : undefined;
  }

  const health = (payload as Partial<{ health?: HealthPill }>).health;
  if (health) {
    nextData.health = health;
  }

  const deviceCounts = (payload as Partial<{ deviceCounts?: SiteTemplate['deviceCounts'] }>).deviceCounts;
  if (deviceCounts) {
    nextData.deviceCounts = cloneValue(deviceCounts);
  }

  nextData.updatedAt = new Date();
  nextData.updatedBy = 'mock-user';
  record.data = nextData;
  record.etag = generateEtag();
  syncConnectivityState(orgId);
  return record;
}

function deleteSiteRecord(orgId: string, siteId: string): boolean {
  const store = getSiteStore(orgId);
  const existing = store.get(siteId);
  if (!existing) {
    return false;
  }
  store.delete(siteId);
  const clusters = getClusterStore(orgId);
  clusters.forEach((cluster) => {
    if (cluster.siteIds.delete(siteId)) {
      cluster.data.updatedAt = new Date();
      cluster.etag = generateEtag();
    }
  });
  syncConnectivityState(orgId);
  return true;
}

function listSiteClusters(orgId: string, siteId: string): ClusterRecord[] {
  const record = getSiteRecord(orgId, siteId);
  if (!record) {
    return [];
  }
  const clusters = getClusterStore(orgId);
  const results: ClusterRecord[] = [];
  record.clusterIds.forEach((clusterId) => {
    const clusterRecord = clusters.get(clusterId);
    if (clusterRecord) {
      results.push(cloneClusterRecord(clusterRecord));
    }
  });
  return results;
}

interface BuildSiteTopologyGraphOptions {
  expandInterfaces?: boolean;
}

const DEVICE_INTERFACE_PRESETS: Array<{ label: string; short: string; speed: string; vlan: string; status: string }> = [
  { label: 'Gi1/0/1', short: 'Gi1', speed: '25G', vlan: 'trunk', status: 'up' },
  { label: 'Gi1/0/24', short: 'Gi24', speed: '10G', vlan: 'access', status: 'up' },
  { label: 'Te1/1/1', short: 'Te1', speed: '40G', vlan: 'fabric', status: 'up' },
  { label: 'Eth1/49', short: 'Eth49', speed: '100G', vlan: 'fabric', status: 'up' }
];

function createInterfaceNodesForDevice(
  device: DeviceTemplateRecord,
  deviceNodeId: string,
  nodes: TopologyGraph['nodes'],
  edges: TopologyGraph['edges']
): string[] {
  const interfaceIds: string[] = [];
  if (DEVICE_INTERFACE_PRESETS.length === 0) {
    return interfaceIds;
  }

  const baselineCount = device.role === 'core' ? 4 : 3;
  const count = Math.min(DEVICE_INTERFACE_PRESETS.length, baselineCount);
  const seed = device.id.length;

  for (let index = 0; index < count; index++) {
    const preset = DEVICE_INTERFACE_PRESETS[(index + seed) % DEVICE_INTERFACE_PRESETS.length];
    const interfaceId = `${device.id}#if-${index + 1}`;
    const nodeId = `interface-${interfaceId}`;
    nodes.push({
      id: nodeId,
      kind: 'interface',
      label: preset.label ?? `Gi1/0/${index + 1}`,
      parentId: deviceNodeId,
      ref: { type: 'device_interface', id: interfaceId },
      properties: {
        speed: preset.speed,
        vlan: preset.vlan,
        status: preset.status,
        deviceRole: device.role ?? 'edge'
      }
    });
    edges.push({
      fromId: deviceNodeId,
      toId: nodeId,
      layer: 'physical',
      relation: 'ports',
      properties: {
        adminState: preset.status
      }
    });
    interfaceIds.push(nodeId);
  }

  return interfaceIds;
}

function shouldExpandInterfaces(searchParams: URLSearchParams): boolean {
  return Array.from(searchParams.getAll('expand'))
    .flatMap((value) => value.split(','))
    .map((token) => token.trim().toLowerCase())
    .includes('interfaces');
}


function buildSiteTopologyGraph(orgId: string, siteId: string, options: BuildSiteTopologyGraphOptions = {}): TopologyGraph {
  const { expandInterfaces = false } = options;
  const siteRecord = getSiteRecord(orgId, siteId);
  if (!siteRecord) {
    return { nodes: [], edges: [] };
  }

  const clusters = getClusterStore(orgId);
  const connectors = getConnectorStore(orgId);
  const nodes: TopologyGraph['nodes'] = [];
  const edges: TopologyGraph['edges'] = [];
  const siteNodeId = `site-${siteId}`;

  nodes.push({
    id: siteNodeId,
    kind: 'site',
    label: siteRecord.data.name ?? siteId,
    ref: { type: 'site', id: siteId }
  });

  siteRecord.clusterIds.forEach((clusterId) => {
    const cluster = clusters.get(clusterId);
    if (!cluster) {
      return;
    }

    const clusterNodeId = `cluster-${clusterId}`;
    nodes.push({
      id: clusterNodeId,
      kind: 'cluster',
      label: cluster.data.name ?? clusterId,
      parentId: siteNodeId,
      ref: { type: 'connector_cluster', id: clusterId }
    });

    edges.push({
      fromId: siteNodeId,
      toId: clusterNodeId,
      layer: 'physical',
      relation: 'attached'
    });

    Array.from(connectors.values())
      .filter((connector) => connector.data.clusterId === clusterId)
      .forEach((connector) => {
        const connectorId = connector.data.id ?? randomId('connector-node');
        const connectorNodeId = `connector-${connectorId}`;
        nodes.push({
          id: connectorNodeId,
          kind: 'connector',
          label: connector.data.name ?? connectorId ?? 'Connector',
          parentId: clusterNodeId,
          ref: { type: 'connector', id: connectorId }
        });
        edges.push({
          fromId: clusterNodeId,
          toId: connectorNodeId,
          layer: 'physical',
          relation: 'hosts'
        });
      });
  });

  const siteDevices = deviceTemplates.filter((device) => device.siteId === siteId);
  const deviceInterfaces = new Map<string, Array<{ id: string; label: string; shortLabel: string }>>();
  const deviceNodeIds: string[] = [];

  siteDevices.forEach((device) => {
    const deviceNodeId = `device-${device.id}`;
    deviceNodeIds.push(deviceNodeId);
    nodes.push({
      id: deviceNodeId,
      kind: 'device',
      label: device.displayName ?? device.id ?? 'Device',
      parentId: siteNodeId,
      ref: { type: 'device', id: device.id },
      properties: {
        role: device.role ?? 'edge',
        vendor: device.vendor,
        model: device.model
      }
    });

    edges.push({
      fromId: siteNodeId,
      toId: deviceNodeId,
      layer: 'physical',
      relation: 'hosts'
    });

    const interfaces = DEVICE_INTERFACE_PRESETS.map((preset, index) => ({
      id: `${deviceNodeId}-if-${index}`,
      label: preset.label,
      shortLabel: preset.short
    }));
    deviceInterfaces.set(deviceNodeId, interfaces);

    if (expandInterfaces) {
      interfaces.forEach((iface) => {
        nodes.push({
          id: iface.id,
          kind: 'interface',
          label: iface.label,
          parentId: deviceNodeId,
          ref: { type: 'interface', id: iface.id }
        });
      });
    }
  });

  const linkPairs: Array<{
    fromDeviceId: string;
    toDeviceId: string;
    fromInterface?: string;
    toInterface?: string;
    fromLabel: string;
    toLabel: string;
  }> = [];

  for (let index = 0; index < deviceNodeIds.length; index++) {
    const fromDeviceId = deviceNodeIds[index];
    const toDeviceId = deviceNodeIds[(index + 1) % deviceNodeIds.length];
    if (fromDeviceId === toDeviceId) {
      continue;
    }
    const fromInterfaces = deviceInterfaces.get(fromDeviceId) ?? [];
    const toInterfaces = deviceInterfaces.get(toDeviceId) ?? [];
    const pairCount = Math.min(fromInterfaces.length, toInterfaces.length, 2);
    for (let pair = 0; pair < pairCount; pair++) {
      const fromInterface = fromInterfaces[pair];
      const toInterface = toInterfaces[pair];
      linkPairs.push({
        fromDeviceId,
        toDeviceId,
        fromInterface: fromInterface.id,
        toInterface: toInterface.id,
        fromLabel: fromInterface.label,
        toLabel: toInterface.label
      });
    }
  }

  if (expandInterfaces) {
    linkPairs.forEach((pair, index) => {
      if (!pair.fromInterface || !pair.toInterface) {
        return;
      }
      edges.push({
        fromId: pair.fromInterface,
        toId: pair.toInterface,
        layer: 'physical',
        relation: 'connects',
        properties: {
          mockId: `interface-link-${index}`,
          fromLabel: pair.fromLabel,
          toLabel: pair.toLabel
        }
      });
    });
  } else {
    const aggregate = new Map<string, Array<{ fromLabel: string; toLabel: string }>>();
    linkPairs.forEach((pair) => {
      const deviceAId = pair.fromDeviceId < pair.toDeviceId ? pair.fromDeviceId : pair.toDeviceId;
      const deviceBId = deviceAId === pair.fromDeviceId ? pair.toDeviceId : pair.fromDeviceId;
      const key = `${deviceAId}::${deviceBId}`;
      const bucket = aggregate.get(key) ?? [];
      bucket.push({ fromLabel: pair.fromLabel, toLabel: pair.toLabel });
      aggregate.set(key, bucket);
    });

    aggregate.forEach((connections, key) => {
      const [deviceAId, deviceBId] = key.split('::');
      edges.push({
        fromId: deviceAId,
        toId: deviceBId,
        layer: 'physical',
        relation: 'connects',
        properties: { connections, mockId: `device-link-${key}` }
      });
    });
  }

  return { nodes, edges };
}


function listClusters(orgId: string): ClusterRecord[] {
  return Array.from(getClusterStore(orgId).values()).map(cloneClusterRecord);
}

function getClusterRecord(orgId: string, clusterId: string): StoredCluster | null {
  return getClusterStore(orgId).get(clusterId) ?? null;
}

function createClusterRecord(orgId: string, payload: ConnectorClusterCreate): StoredCluster {
  const store = getClusterStore(orgId);
  const id = randomId('cluster');
  const now = new Date();
  const labels = (payload as Partial<{ labels: Record<string, string> }>).labels;
  const settings = (payload as Partial<{ settings?: ConnectorCluster['settings'] }>).settings;
  const description = (payload as Partial<{ description: string }>).description;
  const normalizedSettings = settings ? cloneValue(settings) : undefined;
  const data: ClusterRecord = {
    id,
    orgId,
    name: payload.name ?? 'Connector Cluster',
    description,
    status: 'active',
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    labels: labels ? cloneValue(labels) : undefined,
    settings: normalizedSettings,
    connectorsOnline: 0,
    connectorsTotal: 0,
    haState: 'warn',
    health: 'mixed'
  } as ClusterRecord;
  const record: StoredCluster = {
    data,
    etag: generateEtag(),
    siteIds: new Set()
  };
  store.set(id, record);
  syncConnectivityState(orgId);
  return record;
}

function updateClusterRecord(orgId: string, clusterId: string, payload: ConnectorClusterUpdate): StoredCluster | null {
  const record = getClusterRecord(orgId, clusterId);
  if (!record) {
    return null;
  }

  if (payload.name !== undefined) {
    record.data.name = payload.name;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'description')) {
    record.data.description = (payload as Partial<{ description: string }>).description ?? undefined;
  }
  const labels = (payload as Partial<{ labels?: Record<string, string> }>).labels;
  if (labels !== undefined) {
    record.data.labels = labels ? cloneValue(labels) : undefined;
  }
  const settings = (payload as Partial<{ settings?: ConnectorCluster['settings'] }>).settings;
  if (settings !== undefined) {
    record.data.settings = settings ? cloneValue(settings) : undefined;
  }
  const haState = (payload as Partial<{ haState?: HaState }>).haState;
  if (haState) {
    record.data.haState = haState;
  }
  const health = (payload as Partial<{ health?: HealthPill }>).health;
  if (health) {
    record.data.health = health;
  }

  record.data.updatedAt = new Date();
  record.data.updatedBy = 'mock-user';
  record.etag = generateEtag();
  syncConnectivityState(orgId);
  return record;
}

function deleteClusterRecord(orgId: string, clusterId: string): { deleted: boolean; reason?: string } {
  const store = getClusterStore(orgId);
  const record = store.get(clusterId);
  if (!record) {
    return { deleted: false, reason: 'not_found' };
  }

  const connectors = getConnectorStore(orgId);
  const hasConnectors = Array.from(connectors.values()).some((entry) => entry.data.clusterId === clusterId);
  if (hasConnectors) {
    return { deleted: false, reason: 'connectors_attached' };
  }

  store.delete(clusterId);
  const sites = getSiteStore(orgId);
  sites.forEach((site) => {
    if (site.clusterIds.delete(clusterId)) {
      site.data.updatedAt = new Date();
      site.data.updatedBy = 'mock-user';
      site.etag = generateEtag();
    }
  });
  syncConnectivityState(orgId);
  return { deleted: true };
}

function listClusterSites(orgId: string, clusterId: string): SiteRecord[] {
  const cluster = getClusterRecord(orgId, clusterId);
  if (!cluster) {
    return [];
  }
  const sites = getSiteStore(orgId);
  const items: SiteRecord[] = [];
  cluster.siteIds.forEach((siteId) => {
    const siteRecord = sites.get(siteId);
    if (siteRecord) {
      items.push(cloneSiteRecord(siteRecord));
    }
  });
  return items;
}

function attachSitesToCluster(orgId: string, clusterId: string, request: ClusterSitesAttachRequest): { record: StoredCluster; result: ClusterSitesAttachResult } | null {
  const cluster = getClusterRecord(orgId, clusterId);
  if (!cluster) {
    return null;
  }

  const sites = getSiteStore(orgId);
  const attached: string[] = [];
  const alreadyAttached: string[] = [];
  const notFound: string[] = [];
  const multiAttached: Array<{ siteId: string; alsoAttachedTo: string[] }> = [];

  const siteIds = Array.isArray(request.siteIds) ? request.siteIds : [];

  siteIds.forEach((siteId) => {
    const site = sites.get(siteId);
    if (!site) {
      notFound.push(siteId);
      return;
    }

    if (cluster.siteIds.has(siteId)) {
      alreadyAttached.push(siteId);
    } else {
      cluster.siteIds.add(siteId);
      site.clusterIds.add(clusterId);
      site.data.updatedAt = new Date();
      site.data.updatedBy = 'mock-user';
      site.etag = generateEtag();
      attached.push(siteId);
    }

    const attachedClusters = Array.from(site.clusterIds).filter((id) => id !== clusterId);
    if (site.clusterIds.size > 1 && attachedClusters.length > 0) {
      multiAttached.push({ siteId, alsoAttachedTo: attachedClusters });
    }
  });

  cluster.data.updatedAt = new Date();
  cluster.data.updatedBy = 'mock-user';
  cluster.etag = generateEtag();
  syncConnectivityState(orgId);

  const result: ClusterSitesAttachResult = {
    attached,
    alreadyAttached,
    multiAttached,
    notFound,
    forbidden: []
  };

  return { record: cluster, result };
}

function detachSitesFromCluster(orgId: string, clusterId: string, request: ClusterSitesDetachRequest): { record: StoredCluster; result: ClusterSitesDetachResult } | null {
  const cluster = getClusterRecord(orgId, clusterId);
  if (!cluster) {
    return null;
  }

  const sites = getSiteStore(orgId);
  const detached: string[] = [];
  const notAttached: string[] = [];
  const notFound: string[] = [];

  const siteIds = Array.isArray(request.siteIds) ? request.siteIds : [];

  siteIds.forEach((siteId) => {
    const site = sites.get(siteId);
    if (!site) {
      notFound.push(siteId);
      return;
    }

    if (!cluster.siteIds.has(siteId)) {
      notAttached.push(siteId);
      return;
    }

    cluster.siteIds.delete(siteId);
    site.clusterIds.delete(clusterId);
    site.data.updatedAt = new Date();
    site.data.updatedBy = 'mock-user';
    site.etag = generateEtag();
    detached.push(siteId);
  });

  cluster.data.updatedAt = new Date();
  cluster.data.updatedBy = 'mock-user';
  cluster.etag = generateEtag();
  syncConnectivityState(orgId);

  const result: ClusterSitesDetachResult = {
    detached,
    notAttached,
    notFound,
    forbidden: []
  };

  return { record: cluster, result };
}

interface ConnectorListFilters {
  clusterId?: string;
  siteId?: string;
  status?: string;
}

function listConnectors(orgId: string, filters: ConnectorListFilters = {}): ConnectorRecord[] {
  const connectors = getConnectorStore(orgId);
  const sites = getSiteStore(orgId);
  return Array.from(connectors.values())
    .filter((entry) => {
      if (filters.clusterId && entry.data.clusterId !== filters.clusterId) {
        return false;
      }
      if (filters.status && entry.data.status !== filters.status) {
        return false;
      }
      if (filters.siteId) {
        const site = sites.get(filters.siteId);
        if (!site) {
          return false;
        }
        return site.clusterIds.has(entry.data.clusterId ?? '');
      }
      return true;
    })
    .map(cloneConnectorRecord);
}

function getConnectorRecord(orgId: string, connectorId: string): StoredConnector | null {
  return getConnectorStore(orgId).get(connectorId) ?? null;
}

function createConnectorRecord(orgId: string, payload: ConnectorCreate): { record: StoredConnector | null; problem?: { status: number; detail: string } } {
  const clusterId = payload.clusterId;
  if (!clusterId) {
    return { record: null, problem: { status: 400, detail: 'clusterId is required to create a connector.' } };
  }

  const cluster = getClusterRecord(orgId, clusterId);
  if (!cluster) {
    return { record: null, problem: { status: 404, detail: `Cluster ${clusterId} not found.` } };
  }

  const store = getConnectorStore(orgId);
  const id = randomId('connector');
  const now = new Date();
  const labels = (payload as Partial<{ labels: Record<string, string> }>).labels;
  const extras = payload as Partial<{ version?: string; status?: string; description?: string }>;
  const version = extras.version && extras.version.trim().length > 0 ? extras.version.trim() : '1.0.0';
  const status = normalizeConnectorStatus(extras.status);
  const description = extras.description && extras.description.trim().length > 0 ? extras.description.trim() : undefined;

  const data: ConnectorRecord = {
    id,
    orgId,
    name: payload.name ?? `Connector ${id.slice(-4)}`,
    status,
    description,
    version,
    clusterId,
    registeredAt: now,
    lastSeenAt: now,
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    labels: labels ? cloneValue(labels) : undefined,
    siteHints: cluster.siteIds.size > 0 ? Array.from(cluster.siteIds) : undefined
  } as ConnectorRecord;

  const record: StoredConnector = {
    data,
    etag: generateEtag(),
    userdata: {
      status: 'available',
      redeemedAt: null
    }
  };

  store.set(id, record);
  cluster.data.updatedAt = new Date();
  cluster.data.updatedBy = 'mock-user';
  cluster.etag = generateEtag();
  syncConnectivityState(orgId);
  return { record };
}

function updateConnectorRecord(orgId: string, connectorId: string, payload: ConnectorUpdate): StoredConnector | null {
  const record = getConnectorRecord(orgId, connectorId);
  if (!record) {
    return null;
  }

  if (payload.name !== undefined) {
    record.data.name = payload.name;
  }
  const extras = payload as Partial<{ status?: string; version?: string; description?: string }>;
  if (extras.status !== undefined) {
    record.data.status = normalizeConnectorStatus(extras.status);
  }
  if (extras.version !== undefined) {
    record.data.version = extras.version;
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'description')) {
    record.data.description = extras.description ?? undefined;
  }
  const labels = (payload as Partial<{ labels?: Record<string, string> }>).labels;
  if (labels !== undefined) {
    record.data.labels = labels ? cloneValue(labels) : undefined;
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'clusterId')) {
    const currentCluster = record.data.clusterId ? getClusterRecord(orgId, record.data.clusterId) : null;
    const targetCluster = payload.clusterId ? getClusterRecord(orgId, payload.clusterId) : null;
    if (currentCluster) {
      currentCluster.data.updatedAt = new Date();
      currentCluster.data.updatedBy = 'mock-user';
      currentCluster.etag = generateEtag();
    }
    if (targetCluster) {
      targetCluster.data.updatedAt = new Date();
      targetCluster.data.updatedBy = 'mock-user';
      targetCluster.etag = generateEtag();
    }
    record.data.clusterId = payload.clusterId ?? undefined;
  }

  record.data.updatedAt = new Date();
  record.data.updatedBy = 'mock-user';
  record.etag = generateEtag();
  syncConnectivityState(orgId);
  return record;
}

function deleteConnectorRecord(orgId: string, connectorId: string): boolean {
  const store = getConnectorStore(orgId);
  const existing = store.get(connectorId);
  if (!existing) {
    return false;
  }
  store.delete(connectorId);
  syncConnectivityState(orgId);
  return true;
}

function handleConnectorUserdata(orgId: string, connectorId: string, acceptHeader: string | null): Response {
  const record = getConnectorRecord(orgId, connectorId);
  if (!record) {
    return respond({ detail: `Connector ${connectorId} not found.` }, 404);
  }

  if (record.userdata.status === 'redeemed') {
    return respond({ detail: 'Connector bootstrap already redeemed.' }, 409);
  }
  if (record.userdata.status === 'expired') {
    return respond({ detail: 'Connector bootstrap expired. Generate a new connector to obtain credentials.' }, 410);
  }

  const accept = (acceptHeader ?? '').toLowerCase();
  const opaque: Record<string, unknown> = {
    connectorId: record.data.id,
    orgId,
    clusterId: record.data.clusterId,
    issuedAt: new Date().toISOString(),
    token: `ott_${randomId('bootstrap').slice(-10)}`,
    version: record.data.version
  };

  const headers = new Headers({
    'X-Mock-Route': 'org-console',
    'Cache-Control': 'no-store',
    ETag: record.etag
  });

  let responseBody: BodyInit;

  if (accept.includes('application/json') && !accept.includes('base64')) {
    headers.set('Content-Type', 'application/json');
    responseBody = JSON.stringify(opaque);
  } else if (accept.includes('application/base64+json')) {
    headers.set('Content-Type', 'application/json');
    const json = JSON.stringify(opaque);
    const encoded =
      typeof btoa === 'function'
        ? btoa(json)
        : typeof globalThis !== 'undefined' && (globalThis as Record<string, unknown>).Buffer
        ? ((globalThis as Record<string, any>).Buffer.from(json).toString('base64'))
        : json;
    responseBody = JSON.stringify({ base64: encoded });
  } else {
    headers.set('Content-Type', 'text/cloud-config');
    const yamlPayload = JSON.stringify(opaque, null, 2);
    responseBody = `#cloud-config\nwrite_files:\n  - path: /etc/reap/connector.json\n    permissions: '0600'\n    content: |\n${yamlPayload
      .split('\n')
      .map((line) => `      ${line}`)
      .join('\n')}\nruncmd:\n  - ["/usr/local/bin/reap-bootstrap", "--config", "/etc/reap/connector.json"]\n`;
  }

  record.userdata.status = 'redeemed';
  record.userdata.redeemedAt = new Date();
  record.data.updatedAt = new Date();
  record.data.updatedBy = 'mock-user';
  record.etag = generateEtag();
  headers.set('ETag', record.etag);

  return new Response(responseBody, {
    status: 200,
    headers
  });
}

function seedConnectionMappings(orgId: string, connectionId: string, connection: IdpConnection): void {
  let orgMappings = idpMappingStore.get(orgId);
  if (!orgMappings) {
    orgMappings = new Map();
    idpMappingStore.set(orgId, orgMappings);
  }
  if (orgMappings.has(connectionId)) {
    return;
  }
  const templates = idpMappingRuleTemplates[connection.id ?? connectionId] ?? [];
  const entries = new Map<string, StoredMappingRule>();
  templates.forEach((template) => {
    const copy: Mutable<RoleMappingRule> = {
      id: template.id ?? randomId('map'),
      connectionId,
      enabled: template.enabled,
      priority: template.priority,
      match: cloneValue(template.match),
      result: cloneValue(template.result),
      createdAt: new Date(template.createdAt),
      updatedAt: new Date(template.updatedAt),
      createdBy: template.createdBy,
      updatedBy: template.updatedBy,
      orgId
    };
    entries.set(copy.id, {
      data: copy,
      etag: generateEtag()
    });
  });
  orgMappings.set(connectionId, entries);
}

function ensureMappingStore(orgId: string, connectionId: string): Map<string, StoredMappingRule> {
  let orgMappings = idpMappingStore.get(orgId);
  if (!orgMappings) {
    orgMappings = new Map();
    idpMappingStore.set(orgId, orgMappings);
  }
  let connectionMappings = orgMappings.get(connectionId);
  if (!connectionMappings) {
    connectionMappings = new Map();
    orgMappings.set(connectionId, connectionMappings);
  }
  return connectionMappings;
}

function listConnections(orgId: string, type?: 'oidc' | 'saml'): IdpConnection[] {
  ensureOrgState(orgId);
  const store = idpConnectionStore.get(orgId)!;
  return Array.from(store.values())
    .map((entry) => cloneConnectionForResponse(entry.data))
    .filter((entry) => (type ? entry.type === type : true));
}

function getConnectionRecord(orgId: string, connectionId: string): StoredConnection | null {
  ensureOrgState(orgId);
  const store = idpConnectionStore.get(orgId)!;
  return store.get(connectionId) ?? null;
}

function saveConnectionRecord(orgId: string, connection: Mutable<IdpConnection>): StoredConnection {
  ensureOrgState(orgId);
  const id = connection.id ?? randomId('idp');
  connection.id = id;
  connection.orgId = orgId;
  const store = idpConnectionStore.get(orgId)!;
  const existingCreatedAt = store.get(id)?.data?.createdAt ?? connection.createdAt;
  const createdAt =
    existingCreatedAt instanceof Date
      ? existingCreatedAt
      : existingCreatedAt
      ? new Date(existingCreatedAt as unknown as string)
      : new Date();
  connection.createdAt = createdAt;
  connection.createdBy = connection.createdBy ?? 'mock-user';
  connection.updatedAt = new Date();
  connection.updatedBy = 'mock-user';
  (connection as ConnectionWithExtras).startUrl = buildMockStartUrl(id);
  connection.config =
    connection.type === 'oidc'
      ? (cloneValue(connection.config) as IdpOidcConfigschema)
      : (cloneValue(connection.config) as IdpSamlConfigschema);
  const record: StoredConnection = {
    data: connection,
    etag: generateEtag()
  };
  store.set(id, record);
  seedConnectionMappings(orgId, id, connection);
  ensureMappingStore(orgId, id);
  return record;
}

function deleteConnectionRecord(orgId: string, connectionId: string): boolean {
  ensureOrgState(orgId);
  const store = idpConnectionStore.get(orgId)!;
  const removed = store.delete(connectionId);
  const orgMappings = idpMappingStore.get(orgId);
  orgMappings?.delete(connectionId);
  return removed;
}

function listMappingRules(orgId: string, connectionId: string): RoleMappingRule[] {
  ensureOrgState(orgId);
  const store = ensureMappingStore(orgId, connectionId);
  return Array.from(store.values()).map((entry) => {
    const copy = cloneValue(entry.data) as Mutable<RoleMappingRule>;
    copy.createdAt = copy.createdAt ? new Date(copy.createdAt as unknown as string) : new Date();
    copy.updatedAt = copy.updatedAt ? new Date(copy.updatedAt as unknown as string) : new Date(copy.createdAt);
    copy.match = cloneValue(entry.data.match) as RoleMappingRule['match'];
    copy.result = cloneValue(entry.data.result) as RoleMappingRule['result'];
    copy.createdBy = copy.createdBy ?? 'mock-user';
    copy.updatedBy = copy.updatedBy ?? 'mock-user';
    return copy as RoleMappingRule;
  });
}

function getMappingRuleRecord(orgId: string, connectionId: string, ruleId: string): StoredMappingRule | null {
  ensureOrgState(orgId);
  const store = ensureMappingStore(orgId, connectionId);
  return store.get(ruleId) ?? null;
}

function saveMappingRuleRecord(orgId: string, connectionId: string, rule: Mutable<RoleMappingRule>): StoredMappingRule {
  ensureOrgState(orgId);
  const store = ensureMappingStore(orgId, connectionId);
  const id = rule.id ?? randomId('map');
  rule.id = id;
  rule.connectionId = connectionId;
  rule.orgId = orgId;
  rule.priority = typeof rule.priority === 'number' ? rule.priority : store.size * 10 + 10;
  const now = new Date();
  const createdAtValue = rule.createdAt;
  rule.createdAt =
    createdAtValue instanceof Date ? createdAtValue : createdAtValue ? new Date(createdAtValue as unknown as string) : now;
  rule.updatedAt = now;
  rule.match = cloneValue(rule.match ?? {}) as RoleMappingRule['match'];
  rule.result = cloneValue(rule.result ?? {}) as RoleMappingRule['result'];
  rule.createdBy = rule.createdBy ?? 'mock-user';
  rule.updatedBy = 'mock-user';
  const record: StoredMappingRule = {
    data: rule,
    etag: generateEtag()
  };
  store.set(id, record);
  return record;
}

function deleteMappingRuleRecord(orgId: string, connectionId: string, ruleId: string): boolean {
  ensureOrgState(orgId);
  const store = ensureMappingStore(orgId, connectionId);
  return store.delete(ruleId);
}

function listOrgSecrets(orgId: string): Secretschema[] {
  const store = getSecretStore(orgId);
  return Array.from(store.values()).map(cloneSecretRecord);
}

function respond(body: unknown, status: number = 200, headers: Record<string, string> = {}): Response {
  const headerBag = new Headers({
    'X-Mock-Route': 'org-console',
    ...headers
  });

  if (status === 204) {
    return new Response(null, {
      status,
      headers: headerBag
    });
  }

  if (!headerBag.has('Content-Type')) {
    headerBag.set('Content-Type', 'application/json');
  }

  const payload = body === undefined ? null : body;
  return new Response(JSON.stringify(payload), {
    status,
    headers: headerBag
  });
}

async function handlePeopleRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const [orgIdRaw, resource, ...rest] = segments;
  const orgId = decodeURIComponent(orgIdRaw);
  if (!ORG_PROFILES[orgId]) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }

  ensureOrgState(orgId);
  const method = request.method.toUpperCase();

  if (resource === 'memberships') {
    if (rest.length === 0) {
      if (method === 'GET') {
        return respond(buildListResponse(listMemberships(orgId)));
      }
      return null;
    }

    const membershipId = rest[0];
    const store = getMembershipStore(orgId);
    const record = store.get(membershipId);
    if (!record) {
      return respond({ detail: `Mock membership ${membershipId} not found.` }, 404);
    }

    if (rest.length === 1) {
      if (method === 'GET') {
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }

      if (method === 'PATCH') {
        const ifMatch = request.headers.get('If-Match');
        if (ifMatch && ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        const payload = (await request.clone().json()) as MembershipUpdate;
        if (payload.roleIds) {
          const uniqueRoleIds = Array.from(new Set(payload.roleIds));
          record.data.rolesResolved = resolveRoleSummaries(orgId, uniqueRoleIds);
        }
        if (payload.labels !== undefined) {
          record.data.labels = payload.labels ? cloneValue(payload.labels) : undefined;
        }
        if (payload.status) {
          record.data.status = payload.status;
        }
        record.data.updatedAt = new Date();
        record.data.updatedBy = 'mock-user';
        record.etag = generateEtag();
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (ifMatch && ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        store.delete(membershipId);
        return respond(null, 204);
      }
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/memberships/${rest.join('/')}.` }, 501);
  }

  if (resource === 'invitations') {
    const store = getInvitationStore(orgId);

    if (rest.length === 0) {
      if (method === 'GET') {
        return respond(buildListResponse(listInvitations(orgId)));
      }
      if (method === 'POST') {
        const payload = (await request.clone().json()) as InvitationCreate;
        const now = new Date();
        const roleIds = Array.isArray(payload.roleIds) ? Array.from(new Set(payload.roleIds)) : [];
        const expiresDays = typeof payload.expiresInDays === 'number' && payload.expiresInDays > 0 ? payload.expiresInDays : 14;
        const record: InvitationRecord = {
          id: randomId('inv'),
          orgId,
          email: payload.email,
          rolesResolved: resolveRoleSummaries(orgId, roleIds),
          status: 'pending',
          expiresAt: new Date(now.getTime() + expiresDays * 86_400_000),
          invitedBy: 'user-alice',
          createdAt: now,
          updatedAt: now,
          createdBy: 'user-alice',
          updatedBy: 'user-alice'
        };
        const stored: StoredInvitation = {
          data: record,
          etag: generateEtag()
        };
        store.set(record.id!, stored);
        return respond(cloneValue(record), 201, { ETag: stored.etag });
      }
      return null;
    }

    const invitationId = rest[0];
    const record = store.get(invitationId);
    if (!record) {
      return respond({ detail: `Mock invitation ${invitationId} not found.` }, 404);
    }

    if (rest.length === 1) {
      if (method === 'GET') {
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (ifMatch && ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        record.data.status = 'revoked';
        record.data.updatedAt = new Date();
        record.data.updatedBy = 'mock-user';
        record.etag = generateEtag();
        return respond(null, 204);
      }
    }

    if (rest.length === 2 && rest[1] === 'send' && method === 'POST') {
      record.data.updatedAt = new Date();
      record.data.updatedBy = 'mock-user';
      record.etag = generateEtag();
      return respond(null, 204);
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/invitations/${rest.join('/')}.` }, 501);
  }

  if (resource === 'roles') {
    const store = getRoleStore(orgId);

    if (rest.length === 0) {
      if (method === 'GET') {
        return respond(buildListResponse(listRoles(orgId)));
      }
      if (method === 'POST') {
        const payload = (await request.clone().json()) as RoleCreate;
        const now = new Date();
        const id = randomId('role');
        const record: RoleRecord = {
          id,
          scope: 'org',
          slug: payload.slug,
          name: payload.name ?? payload.slug,
          description: payload.description ?? undefined,
          status: (payload.status ?? 'active') as Role['status'],
          permissions: cloneValue(payload.permissions) as Permissionschema[],
          createdAt: now,
          updatedAt: now,
          createdBy: 'user-alice',
          updatedBy: 'user-alice'
        };
        const stored: StoredRole = {
          data: record,
          etag: generateEtag()
        };
        store.set(id, stored);
        return respond(cloneValue(record), 201, { ETag: stored.etag });
      }
      return null;
    }

    const roleId = rest[0];
    const record = store.get(roleId);
    if (!record) {
      return respond({ detail: `Mock role ${roleId} not found.` }, 404);
    }

    if (rest.length === 1) {
      if (method === 'GET') {
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }

      if (method === 'PATCH') {
        const ifMatch = request.headers.get('If-Match');
        if (ifMatch && ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        const payload = (await request.clone().json()) as RoleUpdate;
        if (payload.name !== undefined) {
          record.data.name = payload.name ?? undefined;
        }
        if (payload.description !== undefined) {
          record.data.description = payload.description ?? undefined;
        }
        if (payload.status) {
          record.data.status = payload.status;
        }
        if (payload.permissions) {
          record.data.permissions = cloneValue(payload.permissions) as Permissionschema[];
        }
        if (payload.labels !== undefined) {
          // Labels are accepted but not stored in the mock implementation yet.
        }
        record.data.updatedAt = new Date();
        record.data.updatedBy = 'mock-user';
        record.etag = generateEtag();
        refreshResolvedRolesForRole(orgId, roleId);
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (record.data.scope === 'system') {
          return respond({ detail: 'System roles cannot be deleted in mocks.' }, 403);
        }
        if (ifMatch && ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        store.delete(roleId);
        removeRoleReferences(orgId, roleId);
        return respond(null, 204);
      }
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/roles/${rest.join('/')}.` }, 501);
  }

  if (resource === 'api-keys') {
    const store = getApiKeyStore(orgId);

    if (rest.length === 0) {
      if (method === 'GET') {
        const items = listApiKeys(orgId);
        return respond(buildListResponse(items));
      }
      if (method === 'POST') {
        try {
          const payload = (await request.clone().json()) as ApiKeyCreate;
          const name = typeof payload.name === 'string' ? payload.name.trim() : '';
          if (!name) {
            return respond({ detail: 'name is required for API key creation.' }, 400);
          }
          const rawRoleIds = Array.isArray(payload.roleIds) ? payload.roleIds.filter((value): value is string => Boolean(value)) : [];
          const roleIds = Array.from(new Set(rawRoleIds));
          if (roleIds.length === 0) {
            return respond({ detail: 'roleIds must include at least one role.' }, 400);
          }
          const now = new Date();
          const expiresAt =
            payload.expiresAt instanceof Date
              ? payload.expiresAt
              : payload.expiresAt
              ? new Date(payload.expiresAt)
              : new Date(now.getTime() + 90 * 86_400_000);
          const secret = generateApiKeySecret();
          const apiKeyId = randomId('api-key');
          const rolesResolved = resolveRoleSummaries(orgId, roleIds);
          const record: ApiKeyRecord = {
            id: apiKeyId,
            orgId,
            name,
            status: ApiKeyStatusEnum.Active,
            prefix: API_KEY_SECRET_PREFIX,
            last4: secret.slice(-4).toUpperCase(),
            roleIds,
            rolesResolved,
            createdAt: now,
            updatedAt: now,
            createdBy: 'user-alice',
            updatedBy: 'user-alice',
            createdByUserId: 'user-alice',
            expiresAt
          };
          const stored: StoredApiKey = {
            data: record,
            etag: generateEtag()
          };
          store.set(apiKeyId, stored);
          const responseBody = { ...cloneValue(record), secret };
          return respond(responseBody, 201, {
            ETag: stored.etag,
            Location: `/public/v1/orgs/${encodeURIComponent(orgId)}/api-keys/${apiKeyId}`
          });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse API key create payload', error);
          return respond({ detail: 'Invalid mock payload for API key creation.' }, 400);
        }
      }
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/api-keys.` }, 501);
    }

    const apiKeyId = rest[0];
    const record = store.get(apiKeyId);
    if (!record) {
      return respond({ detail: `Mock API key ${apiKeyId} not found.` }, 404);
    }

    if (rest.length === 1) {
      if (method === 'GET') {
        const ifNoneMatch = request.headers.get('If-None-Match');
        if (ifNoneMatch && ifNoneMatch === record.etag) {
          const headers = new Headers({
            'X-Mock-Route': 'org-console',
            ETag: record.etag
          });
          return new Response(null, { status: 304, headers });
        }
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      }

      if (method === 'PATCH') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for updates.' }, 428);
        }
        if (ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        try {
          const payload = (await request.clone().json()) as ApiKeyUpdate;
          let changed = false;
          if (typeof payload.name === 'string') {
            const trimmed = payload.name.trim();
            if (trimmed && trimmed !== record.data.name) {
              record.data.name = trimmed;
              changed = true;
            }
          }
          if (payload.roleIds) {
            const raw = payload.roleIds.filter((value): value is string => Boolean(value));
            const next = Array.from(new Set(raw));
            if (next.length === 0) {
              return respond({ detail: 'roleIds must include at least one role.' }, 400);
            }
            record.data.roleIds = next;
            record.data.rolesResolved = resolveRoleSummaries(orgId, next);
            changed = true;
          }
          if (Object.prototype.hasOwnProperty.call(payload, 'expiresAt')) {
            if (payload.expiresAt) {
              record.data.expiresAt = payload.expiresAt instanceof Date ? payload.expiresAt : new Date(payload.expiresAt);
              changed = true;
            }
          }
          if (changed) {
            record.data.updatedAt = new Date();
            record.data.updatedBy = 'mock-user';
            record.etag = generateEtag();
          }
          return respond(cloneValue(record.data), 200, { ETag: record.etag });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse API key update payload', error);
          return respond({ detail: 'Invalid mock payload for API key update.' }, 400);
        }
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for deletion.' }, 428);
        }
        if (ifMatch !== record.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        record.data.status = ApiKeyStatusEnum.Revoked;
        record.data.updatedAt = new Date();
        record.data.updatedBy = 'mock-user';
        record.etag = generateEtag();
        return respond(null, 204);
      }
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/api-keys/${rest.join('/')}.` }, 501);
  }

  return null;
}

async function handleIdpConnectionRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 3) {
    return null;
  }

  const [orgIdRaw, first, second, ...rest] = segments;
  if (first !== 'idp' || second !== 'connections') {
    return null;
  }

  const orgId = decodeURIComponent(orgIdRaw);
  const profile = ORG_PROFILES[orgId];
  if (!profile) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }

  const method = request.method.toUpperCase();

  if (rest.length === 0) {
    if (method === 'GET') {
      const url = new URL(request.url);
      const requestedType = url.searchParams.get('type');
      const type = requestedType === 'oidc' || requestedType === 'saml' ? requestedType : undefined;
      const items = listConnections(orgId, type);
      return respond(buildListResponse(items));
    }

    if (method === 'POST') {
      try {
        const payload = (await request.clone().json()) as IdpConnectionCreate;
        if (payload.type !== 'oidc' && payload.type !== 'saml') {
          return respond({ detail: 'Unsupported connection type in mock payload.' }, 400);
        }
        const now = new Date();
        const connectionId = randomId('idp');
        const draft: Mutable<IdpConnection> =
          payload.type === 'oidc'
            ? ({
                id: connectionId,
                orgId,
                type: 'oidc',
                name: payload.name,
                status: payload.status ?? 'active',
                description: payload.description ?? undefined,
                createdAt: now,
                updatedAt: now,
                createdBy: 'mock-user',
                updatedBy: 'mock-user',
                config: buildMockOidcConfigFromCreate(payload.config, connectionId)
              } as Mutable<IdpConnection>)
            : ({
                id: connectionId,
                orgId,
                type: 'saml',
                name: payload.name,
                status: payload.status ?? 'active',
                description: payload.description ?? undefined,
                createdAt: now,
                updatedAt: now,
                createdBy: 'mock-user',
                updatedBy: 'mock-user',
                config: buildMockSamlConfigFromCreate(payload.config, connectionId)
              } as Mutable<IdpConnection>);
        const record = saveConnectionRecord(orgId, draft);
        return respond(cloneConnectionForResponse(record.data), 201, {
          ETag: record.etag,
          Location: `/public/v1/orgs/${orgId}/idp/connections/${record.data.id}`
        });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse IdP connection create payload', error);
        return respond({ detail: 'Invalid mock payload for IdP connection creation.' }, 400);
      }
    }

    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/idp/connections.` }, 501);
  }

  const [connectionIdSegment, ...tailSegments] = rest;
  const connectionId = decodeURIComponent(connectionIdSegment);
  const existing = getConnectionRecord(orgId, connectionId);
  if (!existing && method !== 'POST') {
    return respond({ detail: `Mock IdP connection ${connectionId} not found.` }, 404);
  }

  if (tailSegments.length === 0) {
    if (!existing) {
      return respond({ detail: `Mock IdP connection ${connectionId} not found.` }, 404);
    }
    if (method === 'GET') {
      return respond(cloneConnectionForResponse(existing.data), 200, { ETag: existing.etag });
    }
    if (method === 'PATCH') {
      try {
        const payload = (await request.clone().json()) as IdpConnectionUpdate;
        if (payload.type && payload.type !== existing.data.type) {
          return respond({ detail: 'Mock update cannot change connection type.' }, 400);
        }
        const ifMatch = request.headers.get('If-Match');
        if (ifMatch && ifMatch !== existing.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        const next = cloneValue(existing.data) as Mutable<IdpConnection>;
        const partial = payload as Partial<Mutable<IdpConnection>>;
        if (typeof partial.name === 'string') {
          next.name = partial.name;
        }
        if ('description' in partial) {
          next.description = partial.description ?? undefined;
        }
        if (typeof partial.status === 'string') {
          next.status = partial.status as IdpConnection['status'];
        }
        if (next.type === 'oidc' && (!payload.type || payload.type === 'oidc') && payload.config) {
          const current = cloneValue(next.config) as IdpOidcConfigschema;
          const updates = cloneValue(payload.config) as IdpOidcConfigschema;
          next.config = { ...current, ...updates };
        }
        if (next.type === 'saml' && (!payload.type || payload.type === 'saml') && payload.config) {
          const current = cloneValue(next.config) as IdpSamlConfigschema;
          const updates = cloneValue(payload.config) as IdpSamlConfigschema;
          next.config = { ...current, ...updates };
        }
        next.updatedAt = new Date();
        next.updatedBy = 'mock-user';
        const record = saveConnectionRecord(orgId, next);
        return respond(cloneConnectionForResponse(record.data), 200, { ETag: record.etag });
      } catch (error) {
        console.warn('[mock:org-console] Failed to apply IdP connection patch', error);
        return respond({ detail: 'Invalid mock payload for IdP connection update.' }, 400);
      }
    }
    if (method === 'DELETE') {
      const ifMatch = request.headers.get('If-Match');
      if (ifMatch && ifMatch !== existing.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      deleteConnectionRecord(orgId, connectionId);
      return respond(undefined, 204);
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/idp/connections/${connectionId}.` }, 501);
  }

  if (tailSegments[0] === 'mappings') {
    if (!existing) {
      return respond({ detail: `Mock IdP connection ${connectionId} not found.` }, 404);
    }
    const mappingTail = tailSegments.slice(1);
    if (mappingTail.length === 0) {
      if (method === 'GET') {
        const items = listMappingRules(orgId, connectionId);
        return respond(buildListResponse(items));
      }
      if (method === 'POST') {
        try {
          const store = ensureMappingStore(orgId, connectionId);
          const payload = (await request.clone().json()) as RoleMappingRuleCreate;
          const now = new Date();
          const rule: Mutable<RoleMappingRule> = {
            id: randomId('map'),
            enabled: payload.enabled ?? true,
            priority: store.size * 10 + 10,
            match: cloneValue(payload.match ?? {}) as RoleMappingRule['match'],
            result: cloneValue(payload.result ?? {}) as RoleMappingRule['result'],
            createdAt: now,
            updatedAt: now,
            connectionId,
            orgId,
            createdBy: 'mock-user',
            updatedBy: 'mock-user'
          };
          const record = saveMappingRuleRecord(orgId, connectionId, rule);
          return respond(cloneValue(record.data), 201, { ETag: record.etag });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse mapping rule create payload', error);
          return respond({ detail: 'Invalid mock payload for mapping rule creation.' }, 400);
        }
      }
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/idp/connections/${connectionId}/mappings.` }, 501);
    }

    const [ruleIdSegment, ...ruleTail] = mappingTail;
    if (ruleTail.length > 0) {
      return respond({ detail: 'Nested mapping routes are not mocked.' }, 501);
    }
    const ruleId = decodeURIComponent(ruleIdSegment);
    const ruleRecord = getMappingRuleRecord(orgId, connectionId, ruleId);
    if (!ruleRecord) {
      return respond({ detail: `Mock mapping rule ${ruleId} not found.` }, 404);
    }
    if (method === 'GET') {
      return respond(cloneValue(ruleRecord.data), 200, { ETag: ruleRecord.etag });
    }
    if (method === 'PATCH') {
      try {
        const payload = (await request.clone().json()) as RoleMappingRuleUpdate;
        const ifMatch = request.headers.get('If-Match');
        if (ifMatch && ifMatch !== ruleRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        const next = cloneValue(ruleRecord.data) as Mutable<RoleMappingRule>;
        if ('enabled' in payload && typeof payload.enabled === 'boolean') {
          next.enabled = payload.enabled;
        }
        if (payload.match) {
          next.match = cloneValue(payload.match) as RoleMappingRule['match'];
        }
        if (payload.result) {
          next.result = cloneValue(payload.result) as RoleMappingRule['result'];
        }
        next.updatedAt = new Date();
        const record = saveMappingRuleRecord(orgId, connectionId, next);
        return respond(cloneValue(record.data), 200, { ETag: record.etag });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse mapping rule update payload', error);
        return respond({ detail: 'Invalid mock payload for mapping rule update.' }, 400);
      }
    }
    if (method === 'DELETE') {
      const ifMatch = request.headers.get('If-Match');
      if (ifMatch && ifMatch !== ruleRecord.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      deleteMappingRuleRecord(orgId, connectionId, ruleId);
      return respond(undefined, 204);
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/idp/connections/${connectionId}/mappings/${ruleId}.` }, 501);
  }

  return respond({ detail: `Mock handler missing for /orgs/${orgId}/idp/connections/${connectionId}/${tailSegments.join('/')}.` }, 501);
}

async function handleCredentialRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const [orgIdRaw, resource, ...rest] = segments;
  if (resource !== 'credentials') {
    return null;
  }

  const orgId = decodeURIComponent(orgIdRaw);
  const method = request.method.toUpperCase();

  if (rest.length === 0) {
    if (method === 'GET') {
      const items = listCredentials(orgId);
      return respond(buildListResponse(items));
    }
    if (method === 'POST') {
      try {
        const payload = (await request.clone().json()) as CredentialCreate;
        const record = createCredentialRecord(orgId, payload);
        const location = `/public/v1/orgs/${encodeURIComponent(orgId)}/credentials/${encodeURIComponent(record.data.id ?? '')}`;
        return respond(cloneCredentialRecord(record), 201, {
          Location: location,
          ETag: record.etag
        });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse credential create payload', error);
        return respond({ detail: 'Invalid payload for credential creation.' }, 400);
      }
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/credentials.` }, 501);
  }

  const [credentialId, ...tail] = rest;
  if (tail.length > 0) {
    return respond({ detail: `Mock handler missing for /orgs/${orgId}/credentials/${credentialId}/${tail.join('/')}.` }, 501);
  }

  const record = getCredentialRecord(orgId, credentialId);
  if (!record) {
    return respond({ detail: `Mock credential ${credentialId} not found.` }, 404);
  }

  if (method === 'GET') {
    const ifNoneMatch = request.headers.get('If-None-Match');
    if (ifNoneMatch && ifNoneMatch === record.etag) {
      const headers = new Headers({
        'X-Mock-Route': 'org-console',
        ETag: record.etag
      });
      return new Response(null, { status: 304, headers });
    }
    return respond(cloneCredentialRecord(record), 200, { ETag: record.etag });
  }

  if (method === 'PATCH') {
    const ifMatch = request.headers.get('If-Match');
    if (!ifMatch) {
      return respond({ detail: 'If-Match header is required for updates.' }, 428);
    }
    if (ifMatch !== record.etag) {
      return respond({ detail: 'Mock ETag mismatch.' }, 412);
    }
    try {
      const payload = (await request.clone().json()) as CredentialUpdate;
      const updated = updateCredentialRecord(orgId, credentialId, payload);
      if (!updated) {
        return respond({ detail: `Mock credential ${credentialId} not found.` }, 404);
      }
      return respond(cloneCredentialRecord(updated), 200, { ETag: updated.etag });
    } catch (error) {
      console.warn('[mock:org-console] Failed to parse credential update payload', error);
      return respond({ detail: 'Invalid payload for credential update.' }, 400);
    }
  }

  if (method === 'DELETE') {
    const ifMatch = request.headers.get('If-Match');
    if (ifMatch && ifMatch !== record.etag) {
      return respond({ detail: 'Mock ETag mismatch.' }, 412);
    }
    deleteCredentialRecord(orgId, credentialId);
    return respond(undefined, 204);
  }

  return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/credentials/${credentialId}.` }, 501);
}

async function handleAssignmentRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const [orgIdRaw, resource, ...rest] = segments;
  if (resource !== 'credential-assignments') {
    return null;
  }

  const orgId = decodeURIComponent(orgIdRaw);
  const method = request.method.toUpperCase();

  if (rest.length === 0) {
    if (method === 'GET') {
      const items = listAssignments(orgId);
      return respond(buildListResponse(items));
    }
    if (method === 'POST') {
      try {
        const payload = (await request.clone().json()) as CredentialAssignmentCreate;
        const record = createAssignmentRecord(orgId, payload);
        const location = `/public/v1/orgs/${encodeURIComponent(orgId)}/credential-assignments/${encodeURIComponent(record.data.id ?? '')}`;
        return respond(cloneAssignmentRecord(record), 201, { Location: location, ETag: record.etag });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse credential assignment create payload', error);
        return respond({ detail: 'Invalid payload for assignment creation.' }, 400);
      }
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/credential-assignments.` }, 501);
  }

  const [assignmentId, ...tail] = rest;
  if (tail.length > 0) {
    return respond({ detail: `Mock handler missing for /orgs/${orgId}/credential-assignments/${assignmentId}/${tail.join('/')}.` }, 501);
  }

  const record = getAssignmentRecord(orgId, assignmentId);
  if (!record) {
    return respond({ detail: `Mock assignment ${assignmentId} not found.` }, 404);
  }

  if (method === 'GET') {
    const ifNoneMatch = request.headers.get('If-None-Match');
    if (ifNoneMatch && ifNoneMatch === record.etag) {
      const headers = new Headers({
        'X-Mock-Route': 'org-console',
        ETag: record.etag
      });
      return new Response(null, { status: 304, headers });
    }
    return respond(cloneAssignmentRecord(record), 200, { ETag: record.etag });
  }

  if (method === 'PATCH') {
    const ifMatch = request.headers.get('If-Match');
    if (!ifMatch) {
      return respond({ detail: 'If-Match header is required for updates.' }, 428);
    }
    if (ifMatch !== record.etag) {
      return respond({ detail: 'Mock ETag mismatch.' }, 412);
    }
    try {
      const payload = (await request.clone().json()) as CredentialAssignmentUpdate;
      const updated = updateAssignmentRecord(orgId, assignmentId, payload);
      if (!updated) {
        return respond({ detail: `Mock assignment ${assignmentId} not found.` }, 404);
      }
      return respond(cloneAssignmentRecord(updated), 200, { ETag: updated.etag });
    } catch (error) {
      console.warn('[mock:org-console] Failed to parse credential assignment update payload', error);
      return respond({ detail: 'Invalid payload for assignment update.' }, 400);
    }
  }

  if (method === 'DELETE') {
    const ifMatch = request.headers.get('If-Match');
    if (ifMatch && ifMatch !== record.etag) {
      return respond({ detail: 'Mock ETag mismatch.' }, 412);
    }
    deleteAssignmentRecord(orgId, assignmentId);
    return respond(undefined, 204);
  }

  return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/credential-assignments/${assignmentId}.` }, 501);
}

async function handleSecretsRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const [orgIdRaw, first, ...rest] = segments;
  if (first !== 'secrets') {
    return null;
  }

  const orgId = decodeURIComponent(orgIdRaw);
  if (!ORG_PROFILES[orgId]) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }

  const method = request.method.toUpperCase();

  if (rest.length === 0) {
    if (method === 'GET') {
      const items = listOrgSecrets(orgId);
      return respond(buildListResponse(items));
    }
    if (method === 'POST') {
      try {
        const payload = (await request.clone().json()) as SecretCreate;
        if (!payload.type || typeof payload.type !== 'string') {
          return respond({ detail: 'Secret type is required.' }, 400);
        }
        if (typeof payload.value !== 'string' || payload.value.length === 0) {
          return respond({ detail: 'Secret value is required.' }, 400);
        }
        const record = createSecretRecord(orgId, payload);
        const data = cloneSecretRecord(record);
        return respond(data, 201, {
          Location: `/public/v1/orgs/${orgId}/secrets/${encodeURIComponent(data.id ?? '')}`,
          ETag: record.etag
        });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse secret create payload', error);
        return respond({ detail: 'Invalid mock payload for secret creation.' }, 400);
      }
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/secrets.` }, 501);
  }

  const [secretIdSegment, ...tail] = rest;
  const secretId = decodeURIComponent(secretIdSegment);
  const record = getSecretRecord(orgId, secretId);
  if (!record) {
    return respond({ detail: `Mock secret ${secretId} not found.` }, 404);
  }

  if (tail.length === 0) {
    if (method === 'GET') {
      return respond(cloneSecretRecord(record), 200, { ETag: record.etag });
    }
    if (method === 'DELETE') {
      deleteSecretRecord(orgId, secretId);
      return respond(undefined, 204);
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/secrets/${secretId}.` }, 501);
  }

  if (tail.length === 1 && tail[0] === 'value' && method === 'POST') {
    const ifMatch = request.headers.get('If-Match');
    if (ifMatch && ifMatch !== record.etag) {
      return respond({ detail: 'Mock ETag mismatch.' }, 412);
    }
    try {
      const payload = (await request.clone().json()) as SecretSetValue;
      if (!payload || typeof payload.value !== 'string' || payload.value.length === 0) {
        return respond({ detail: 'Secret value is required.' }, 400);
      }
      updateSecretValueRecord(record, payload.value);
      return respond(undefined, 204, { ETag: record.etag });
    } catch (error) {
      console.warn('[mock:org-console] Failed to parse secret value payload', error);
      return respond({ detail: 'Invalid mock payload for rotating secret value.' }, 400);
    }
  }

  return respond({ detail: `Mock handler missing for /orgs/${orgId}/secrets/${secretId}/${tail.join('/')}.` }, 501);
}

async function handleConnectivityRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const [orgIdRaw, resource, ...rest] = segments;
  const orgId = decodeURIComponent(orgIdRaw);
  const profile = ORG_PROFILES[orgId];
  if (!profile) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }

  const method = request.method.toUpperCase();

  switch (resource) {
    case 'sites':
      return handleSites(rest);
    case 'connector-clusters':
      return handleClusters(rest);
    case 'connectors':
      return handleConnectors(rest);
    default:
      return null;
  }

  async function handleSites(segments: string[]): Promise<Response | null> {
    const basePath = `/public/v1/orgs/${encodeURIComponent(orgId)}/sites`;

    if (segments.length === 0) {
      if (method === 'GET') {
        const q = url.searchParams.get('q');
        const connectorClusterId = url.searchParams.get('connectorClusterId');
        let items = listSites(orgId);
        if (connectorClusterId) {
          const sites = getSiteStore(orgId);
          items = items.filter((site) => Boolean(site.id && sites.get(site.id)?.clusterIds.has(connectorClusterId)));
        }
        if (q) {
          const search = q.toLowerCase();
          items = items.filter((site) => {
            const name = site.name?.toLowerCase() ?? '';
            const city = site.address?.city?.toLowerCase() ?? '';
            return name.includes(search) || city.includes(search);
          });
        }
        return respond(buildListResponse(items));
      }

      if (method === 'POST') {
        try {
          const payload = (await request.clone().json()) as SiteCreate;
          const record = createSiteRecord(orgId, payload);
          return respond(cloneSiteRecord(record), 201, {
            Location: `${basePath}/${record.data.id}`,
            ETag: record.etag
          });
        } catch (error) {
          if (error instanceof SiteMockValidationError) {
            return respond({ detail: error.message }, 400);
          }
          console.warn('[mock:org-console] Failed to parse site create payload', error);
          return respond({ detail: 'Invalid mock payload for site creation.' }, 400);
        }
      }

      return respond({ detail: `Mock handler missing for ${method} ${basePath}.` }, 501);
    }

    const [siteId, ...tail] = segments;
    const siteRecord = getSiteRecord(orgId, siteId);
    if (!siteRecord) {
      return respond({ detail: `Mock site ${siteId} not found.` }, 404);
    }

    if (tail.length === 0) {
      if (method === 'GET') {
        const ifNoneMatch = request.headers.get('If-None-Match');
        if (ifNoneMatch && ifNoneMatch === siteRecord.etag) {
          const headers = new Headers({
            'X-Mock-Route': 'org-console',
            ETag: siteRecord.etag
          });
          return new Response(null, { status: 304, headers });
        }
        return respond(cloneSiteRecord(siteRecord), 200, { ETag: siteRecord.etag });
      }

      if (method === 'PATCH') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for updates.' }, 428);
        }
        if (ifMatch !== siteRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        try {
          const payload = (await request.clone().json()) as SiteUpdate;
          const updated = updateSiteRecord(orgId, siteId, payload);
          return respond(cloneSiteRecord(updated!), 200, { ETag: updated!.etag });
        } catch (error) {
          if (error instanceof SiteMockValidationError) {
            return respond({ detail: error.message }, 400);
          }
          console.warn('[mock:org-console] Failed to parse site update payload', error);
          return respond({ detail: 'Invalid mock payload for site update.' }, 400);
        }
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for deletion.' }, 428);
        }
        if (ifMatch !== siteRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        deleteSiteRecord(orgId, siteId);
        return respond(undefined, 204);
      }

      return respond({ detail: `Mock handler missing for ${method} ${basePath}/${siteId}.` }, 501);
    }

    const [sub] = tail;
    if (sub === 'connector-clusters' && method === 'GET') {
      return respond(buildListResponse(listSiteClusters(orgId, siteId)));
    }

    if (sub === 'topology' && method === 'GET') {
      const expandInterfaces = shouldExpandInterfaces(url.searchParams);
      const topologyEtag = `W/"topology-${siteId}-${siteRecord.etag}"`;
      const ifNoneMatch = request.headers.get('If-None-Match');
      if (ifNoneMatch && ifNoneMatch === topologyEtag) {
        const headers = new Headers({
          'X-Mock-Route': 'org-console',
          ETag: topologyEtag
        });
        return new Response(null, { status: 304, headers });
      }
      const graph = buildSiteTopologyGraph(orgId, siteId, { expandInterfaces });
      return respond(graph, 200, { ETag: topologyEtag });
    }

    if (sub === 'intelligence' && method === 'GET') {
      const resource = tail.slice(1);
      const summary = createSiteIntelligenceMock(siteId);
      if (!summary) {
        return respond({ detail: 'No Site Intelligence snapshot exists for this mock site.' }, 404);
      }

      if (resource[0] === 'routing' && url.searchParams.has('snapshotId') && url.searchParams.get('snapshotId') !== summary.snapshot.id) {
        return respond({ detail: 'Routing snapshot not found for this site.' }, 404);
      }

      if (resource.length === 1 && resource[0] === 'summary') {
        return respond(summary);
      }

      if (resource.length === 2 && resource[0] === 'snapshots' && resource[1] === summary.snapshot.id) {
        return respond(summary);
      }

      if (resource.length === 1 && resource[0] === 'naming') {
        return respond(createSiteIntelligenceNamingMock(siteId));
      }

      if (resource.length === 2 && resource[0] === 'naming' && resource[1] === 'review-questions') {
        return respond(createSiteIntelligenceNamingReviewQuestionsMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'naming' && resource[1] === 'findings') {
        const finding = createSiteIntelligenceNamingFindingMock(siteId, decodeURIComponent(resource[2]));
        return finding ? respond(finding) : respond({ detail: 'Naming finding not found for this snapshot.' }, 404);
      }

      if (resource.length === 1 && resource[0] === 'topology') {
        return respond(createSiteIntelligenceTopologyMock(siteId));
      }

      if (resource.length === 2 && resource[0] === 'topology' && resource[1] === 'findings') {
        return respond(createSiteIntelligenceTopologyFindingsMock(siteId));
      }

      if (resource.length === 2 && resource[0] === 'topology' && resource[1] === 'review-questions') {
        return respond(createSiteIntelligenceTopologyReviewQuestionsMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'topology' && resource[1] === 'findings') {
        const finding = createSiteIntelligenceTopologyFindingMock(siteId, decodeURIComponent(resource[2]));
        return finding ? respond(finding) : respond({ detail: 'Topology finding not found for this snapshot.' }, 404);
      }

      if (resource.length === 1 && resource[0] === 'routing') {
        return respond(createSiteIntelligenceRoutingMock(siteId));
      }

      if (resource.length === 2 && resource[0] === 'routing' && resource[1] === 'igp') {
        return respond(createSiteIntelligenceRoutingIgpMock(siteId));
      }

      if (resource.length === 2 && resource[0] === 'routing' && resource[1] === 'bgp') {
        return respond(createSiteIntelligenceBgpMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'routing' && resource[1] === 'bgp' && resource[2] === 'findings') {
        return respond(createSiteIntelligenceBgpFindingsMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'routing' && resource[1] === 'bgp' && resource[2] === 'review-questions') {
        return respond(createSiteIntelligenceBgpReviewQuestionsMock(siteId));
      }

      if (resource.length === 4 && resource[0] === 'routing' && resource[1] === 'bgp' && resource[2] === 'findings') {
        const finding = createSiteIntelligenceBgpFindingMock(siteId, decodeURIComponent(resource[3]));
        return finding ? respond(finding) : respond({ detail: 'BGP finding not found for this snapshot.' }, 404);
      }

      if (resource.length === 2 && resource[0] === 'routing' && resource[1] === 'bfd') {
        return respond(createSiteIntelligenceBfdMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'routing' && resource[1] === 'bfd' && resource[2] === 'findings') {
        return respond(createSiteIntelligenceBfdFindingsMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'routing' && resource[1] === 'bfd' && resource[2] === 'review-questions') {
        return respond(createSiteIntelligenceBfdReviewQuestionsMock(siteId));
      }

      if (resource.length === 4 && resource[0] === 'routing' && resource[1] === 'bfd' && resource[2] === 'findings') {
        const finding = createSiteIntelligenceBfdFindingMock(siteId, decodeURIComponent(resource[3]));
        return finding ? respond(finding) : respond({ detail: 'BFD finding not found for this snapshot.' }, 404);
      }

      for (const protocol of ['ospf', 'isis'] as const) {
        if (resource[0] !== 'routing' || resource[1] !== protocol) continue;
        const overview = protocol === 'ospf' ? createSiteIntelligenceOspfMock : createSiteIntelligenceIsisMock;
        const findings = protocol === 'ospf' ? createSiteIntelligenceOspfFindingsMock : createSiteIntelligenceIsisFindingsMock;
        const questions = protocol === 'ospf' ? createSiteIntelligenceOspfReviewQuestionsMock : createSiteIntelligenceIsisReviewQuestionsMock;
        const detail = protocol === 'ospf' ? createSiteIntelligenceOspfFindingMock : createSiteIntelligenceIsisFindingMock;
        if (resource.length === 2) return respond(overview(siteId));
        if (resource.length === 3 && resource[2] === 'findings') return respond(findings(siteId));
        if (resource.length === 3 && resource[2] === 'review-questions') return respond(questions(siteId));
        if (resource.length === 4 && resource[2] === 'findings') {
          const finding = detail(siteId, decodeURIComponent(resource[3]));
          return finding ? respond(finding) : respond({ detail: `${protocol.toUpperCase()} finding not found for this snapshot.` }, 404);
        }
      }

      if (resource.length === 1 && resource[0] === 'vlans') {
        return respond(createSiteIntelligenceVlansMock(siteId));
      }

      if (resource.length === 3 && resource[0] === 'vlans' && resource[1] === 'findings') {
        const finding = createSiteIntelligenceVlanFindingMock(siteId, decodeURIComponent(resource[2]));
        return finding ? respond(finding) : respond({ detail: 'VLAN finding not found for this snapshot.' }, 404);
      }

      if (resource.length === 2 && resource[0] === 'vlans') {
        const vlanID = Number.parseInt(resource[1], 10);
        const vlan = Number.isInteger(vlanID) ? createSiteIntelligenceVlanMock(siteId, vlanID) : null;
        return vlan ? respond(vlan) : respond({ detail: 'Observed VLAN not found for this snapshot.' }, 404);
      }
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/sites/${siteId}/${tail.join('/')}.` }, 501);
  }

  async function handleClusters(segments: string[]): Promise<Response | null> {
    const basePath = `/public/v1/orgs/${encodeURIComponent(orgId)}/connector-clusters`;

    if (segments.length === 0) {
      if (method === 'GET') {
        const search = url.searchParams.get('q')?.trim().toLowerCase();
        const items = listClusters(orgId).filter((cluster) => {
          if (!search) return true;
          return [cluster.name, cluster.description, JSON.stringify(cluster.labels ?? {})]
            .some((value) => value?.toLowerCase().includes(search));
        });
        return respond(buildListResponse(items));
      }

      if (method === 'POST') {
        try {
          const payload = (await request.clone().json()) as ConnectorClusterCreate;
          const record = createClusterRecord(orgId, payload);
          return respond(cloneClusterRecord(record), 201, {
            Location: `${basePath}/${record.data.id}`,
            ETag: record.etag
          });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse cluster create payload', error);
          return respond({ detail: 'Invalid mock payload for connector cluster creation.' }, 400);
        }
      }

      return respond({ detail: `Mock handler missing for ${method} ${basePath}.` }, 501);
    }

    const [clusterId, ...tail] = segments;
    const clusterRecord = getClusterRecord(orgId, clusterId);
    if (!clusterRecord) {
      return respond({ detail: `Mock connector cluster ${clusterId} not found.` }, 404);
    }

    if (tail.length === 0) {
      if (method === 'GET') {
        const ifNoneMatch = request.headers.get('If-None-Match');
        if (ifNoneMatch && ifNoneMatch === clusterRecord.etag) {
          const headers = new Headers({
            'X-Mock-Route': 'org-console',
            ETag: clusterRecord.etag
          });
          return new Response(null, { status: 304, headers });
        }
        return respond(cloneClusterRecord(clusterRecord), 200, { ETag: clusterRecord.etag });
      }

      if (method === 'PATCH') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for updates.' }, 428);
        }
        if (ifMatch !== clusterRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        try {
          const payload = (await request.clone().json()) as ConnectorClusterUpdate;
          const updated = updateClusterRecord(orgId, clusterId, payload);
          return respond(cloneClusterRecord(updated!), 200, { ETag: updated!.etag });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse cluster update payload', error);
          return respond({ detail: 'Invalid mock payload for connector cluster update.' }, 400);
        }
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for deletion.' }, 428);
        }
        if (ifMatch !== clusterRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        const outcome = deleteClusterRecord(orgId, clusterId);
        if (!outcome.deleted) {
          if (outcome.reason === 'connectors_attached') {
            return respond({ detail: 'Cannot delete connector cluster while connectors remain attached.' }, 409);
          }
          return respond({ detail: 'Failed to delete connector cluster.' }, 400);
        }
        return respond(undefined, 204);
      }

      return respond({ detail: `Mock handler missing for ${method} ${basePath}/${clusterId}.` }, 501);
    }

    const [sub] = tail;
    if (sub === 'sites' && method === 'GET') {
      return respond(buildListResponse(listClusterSites(orgId, clusterId)));
    }

    if (sub === 'sites:attach' && method === 'POST') {
      const ifMatch = request.headers.get('If-Match');
      if (!ifMatch) {
        return respond({ detail: 'If-Match header is required for attach operations.' }, 428);
      }
      if (ifMatch !== clusterRecord.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      try {
        const payload = (await request.clone().json()) as ClusterSitesAttachRequest;
        const result = attachSitesToCluster(orgId, clusterId, payload);
        if (!result) {
          return respond({ detail: `Mock connector cluster ${clusterId} not found.` }, 404);
        }
        return respond(result.result, 200, { ETag: result.record.etag });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse cluster attach payload', error);
        return respond({ detail: 'Invalid mock payload for cluster attach.' }, 400);
      }
    }

    if (sub === 'sites:detach' && method === 'POST') {
      const ifMatch = request.headers.get('If-Match');
      if (!ifMatch) {
        return respond({ detail: 'If-Match header is required for detach operations.' }, 428);
      }
      if (ifMatch !== clusterRecord.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      try {
        const payload = (await request.clone().json()) as ClusterSitesDetachRequest;
        const result = detachSitesFromCluster(orgId, clusterId, payload);
        if (!result) {
          return respond({ detail: `Mock connector cluster ${clusterId} not found.` }, 404);
        }
        return respond(result.result, 200, { ETag: result.record.etag });
      } catch (error) {
        console.warn('[mock:org-console] Failed to parse cluster detach payload', error);
        return respond({ detail: 'Invalid mock payload for cluster detach.' }, 400);
      }
    }

    if (sub === 'connectors' && method === 'GET') {
      const items = listConnectors(orgId, { clusterId });
      return respond(buildListResponse(items));
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/connector-clusters/${clusterId}/${tail.join('/')}.` }, 501);
  }

  async function handleConnectors(segments: string[]): Promise<Response | null> {
    const basePath = `/public/v1/orgs/${encodeURIComponent(orgId)}/connectors`;

    if (segments.length === 0) {
      if (method === 'GET') {
        const clusterId = url.searchParams.get('clusterId') ?? undefined;
        const siteId = url.searchParams.get('siteId') ?? undefined;
        const status = url.searchParams.get('status') ?? undefined;
        const search = url.searchParams.get('q')?.trim().toLowerCase();
        const items = listConnectors(orgId, { clusterId, siteId, status }).filter((connector) => {
          if (!search) return true;
          return [connector.name, connector.description, JSON.stringify(connector.labels ?? {})]
            .some((value) => value?.toLowerCase().includes(search));
        });
        return respond(buildListResponse(items));
      }

      if (method === 'POST') {
        try {
          const payload = (await request.clone().json()) as ConnectorCreate;
          const outcome = createConnectorRecord(orgId, payload);
          if (!outcome.record) {
            const problem = outcome.problem ?? { status: 400, detail: 'Failed to create connector.' };
            return respond({ detail: problem.detail }, problem.status);
          }
          const record = outcome.record;
          return respond(cloneConnectorRecord(record), 201, {
            Location: `${basePath}/${record.data.id}`,
            ETag: record.etag
          });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse connector create payload', error);
          return respond({ detail: 'Invalid mock payload for connector creation.' }, 400);
        }
      }

      return respond({ detail: `Mock handler missing for ${method} ${basePath}.` }, 501);
    }

    const [connectorId, ...tail] = segments;
    const connectorRecord = getConnectorRecord(orgId, connectorId);
    if (!connectorRecord) {
      return respond({ detail: `Mock connector ${connectorId} not found.` }, 404);
    }

    if (tail.length === 0) {
      if (method === 'GET') {
        const ifNoneMatch = request.headers.get('If-None-Match');
        if (ifNoneMatch && ifNoneMatch === connectorRecord.etag) {
          const headers = new Headers({
            'X-Mock-Route': 'org-console',
            ETag: connectorRecord.etag
          });
          return new Response(null, { status: 304, headers });
        }
        return respond(cloneConnectorRecord(connectorRecord), 200, { ETag: connectorRecord.etag });
      }

      if (method === 'PATCH') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for updates.' }, 428);
        }
        if (ifMatch !== connectorRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        try {
          const payload = (await request.clone().json()) as ConnectorUpdate;
          const updated = updateConnectorRecord(orgId, connectorId, payload);
          return respond(cloneConnectorRecord(updated!), 200, { ETag: updated!.etag });
        } catch (error) {
          console.warn('[mock:org-console] Failed to parse connector update payload', error);
          return respond({ detail: 'Invalid mock payload for connector update.' }, 400);
        }
      }

      if (method === 'DELETE') {
        const ifMatch = request.headers.get('If-Match');
        if (!ifMatch) {
          return respond({ detail: 'If-Match header is required for deletion.' }, 428);
        }
        if (ifMatch !== connectorRecord.etag) {
          return respond({ detail: 'Mock ETag mismatch.' }, 412);
        }
        deleteConnectorRecord(orgId, connectorId);
        return respond(undefined, 204);
      }

      return respond({ detail: `Mock handler missing for ${method} ${basePath}/${connectorId}.` }, 501);
    }

    const [sub] = tail;
    if (sub === 'userdata' && method === 'GET') {
      return handleConnectorUserdata(orgId, connectorId, request.headers.get('Accept'));
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/connectors/${connectorId}/${tail.join('/')}.` }, 501);
  }
}

async function handleDeviceRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }
  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }
  const [orgIdRaw, resource, ...rest] = segments;
  if (resource !== 'devices') {
    return null;
  }
  const orgId = decodeURIComponent(orgIdRaw);
  const profile = ORG_PROFILES[orgId];
  if (!profile) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }
  const method = request.method.toUpperCase();
  if (rest.length === 0) {
    if (method !== 'GET') {
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/devices.` }, 501);
    }
    const search = url.searchParams;
    const limitParam = parseInt(search.get('limit') ?? '10', 10);
    const limit = Number.isFinite(limitParam) ? Math.min(100, Math.max(1, limitParam)) : 10;
    const after = search.get('after') ?? undefined;
    const q = search.get('q')?.trim().toLowerCase() ?? '';
    const vendorFilter = search.get('vendor')?.trim().toLowerCase();
    const stateFilter = search.get('state')?.trim().toLowerCase();
    let records = listDeviceRecords(orgId);
    if (q) {
      records = records.filter(({ data }) => {
        const haystack = [
          data.id,
          data.displayName,
          data.mgmtAddress,
          data.siteId,
          data.vendor,
          data.model
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());
        return haystack.some((value) => value.includes(q));
      });
    }
    if (vendorFilter && vendorFilter !== 'all') {
      records = records.filter((record) => (record.data.vendor ?? '').toLowerCase() === vendorFilter);
    }
    if (stateFilter && stateFilter !== 'all') {
      records = records.filter((record) => (record.data.state ?? '').toLowerCase() === stateFilter);
    }
    records.sort((a, b) => {
      const key = (value?: string | null) => (value ?? '').toLowerCase();
      const aKey = key(a.data.displayName ?? a.data.mgmtAddress ?? a.data.id);
      const bKey = key(b.data.displayName ?? b.data.mgmtAddress ?? b.data.id);
      return aKey.localeCompare(bKey);
    });
    let startIndex = 0;
    if (after) {
      const cursorIndex = records.findIndex((record) => record.data.id === after);
      if (cursorIndex >= 0) {
        startIndex = cursorIndex + 1;
      }
    }
    const pageRecords = records.slice(startIndex, startIndex + limit);
    const next =
      pageRecords.length > 0 && startIndex + limit < records.length ? pageRecords[pageRecords.length - 1].data.id ?? null : null;
    return respond({
      items: pageRecords.map((record) => cloneValue(record.data)),
      meta: {
        limit,
        next,
        prev: null
      }
    });
  }
  const [deviceIdRaw, ...tail] = rest;
  const deviceId = decodeURIComponent(deviceIdRaw);
  if (tail[0] === 'route-table') {
    if (method !== 'GET' || tail.length !== 1) {
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/devices/${deviceId}/route-table.` }, 501);
    }
    const vrf = url.searchParams.get('vrf')?.trim() ?? '';
    if (!vrf) {
      return respond({ title: 'vrf is required', status: 400 }, 400);
    }
    const page = buildMockRouteTablePage(deviceId, vrf, url.searchParams.get('asOf') ?? undefined);
    if (!page) {
      return respond({ detail: `Mock route table for device ${deviceId} vrf ${vrf} not found.` }, 404);
    }
    return respond(page, 200);
  }
  const record = getDeviceRecord(orgId, deviceId);
  if (!record) {
    return respond({ detail: `Mock device ${deviceId} not found.` }, 404);
  }
  if (tail.length === 0) {
    if (method === 'GET') {
      return respond(cloneValue(record.data), 200, { ETag: record.etag });
    }
    if (method === 'DELETE') {
      const ifMatch = request.headers.get('If-Match');
      if (ifMatch && ifMatch !== record.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      deleteDeviceRecord(orgId, deviceId);
      return respond(undefined, 204);
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/devices/${deviceId}.` }, 501);
  }
  const [sub, ...subTail] = tail;
  if (sub === 'flows') {
    if (method === 'POST') {
      if (subTail.length === 0) {
        const body = await request.json().catch(() => ({}));
        const duration = typeof body.duration === 'number' ? body.duration : 10;
        const taskId = `task-flow-${Math.random().toString(36).substring(2, 11)}`;
        const downloadUrl = `/public/v1/orgs/${encodeURIComponent(orgId)}/download/mock-netflow.json`;
        
        const taskAttrs = {
          agentCallId: '00000000-0000-0000-0000-000000000000',
          deviceId: deviceId,
          taskKey: taskId,
          protocol: 'netflow',
          status: 'pending',
          errorMessage: '',
          captureDurationSec: duration,
          resultMeta: {
            download_url: downloadUrl,
            direct_download_url: downloadUrl,
            object_path: `netflow/${orgId}/${deviceId}_netflow.jsonl.gz`,
          },
        };

        const task = {
          id: taskId,
          orgId,
          deviceId,
          name: 'sFlow/NetFlow Collection',
          status: 'pending',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          attributes: JSON.stringify(taskAttrs)
        };

        mockTaskStore.set(taskId, task);

        setTimeout(() => {
          const stored = mockTaskStore.get(taskId);
          if (stored && stored.status === 'pending') {
            stored.status = 'completed';
            const attrs = JSON.parse(stored.attributes);
            attrs.status = 'completed';
            stored.attributes = JSON.stringify(attrs);
            stored.updatedAt = new Date().toISOString();
          }
        }, 1500);

        return respond({ id: taskId }, 201);
      }
      if (subTail.length === 2 && subTail[1] === 'stop') {
        const taskId = decodeURIComponent(subTail[0]);
        const stored = mockTaskStore.get(taskId);
        if (!stored) {
          return respond({ detail: `Mock task ${taskId} not found.` }, 404);
        }
        stored.status = 'completed';
        const attrs = JSON.parse(stored.attributes);
        attrs.status = 'completed';
        stored.attributes = JSON.stringify(attrs);
        stored.updatedAt = new Date().toISOString();
        return respond(undefined, 204);
      }
    }
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/devices/${deviceId}/flows.` }, 501);
  }
  if (sub === 'running-config') {
    if (method !== 'GET') {
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/devices/${deviceId}/running-config.` }, 501);
    }
    const versions = ensureRunningConfigVersions(orgId, record.data).slice().sort((a, b) => {
      return new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime();
    });
    if (subTail.length === 0) {
      const requestedSnapshotId = url.searchParams.get('snapshotId');
      const latest = versions[0];
      if (!latest) {
        return respond({ detail: 'No running config snapshots found.' }, 404);
      }
      const resolved = requestedSnapshotId ? findRunningConfigVersion(versions, requestedSnapshotId) : latest;
      if (!resolved) {
        return respond({ detail: 'Requested snapshot not found.' }, 404);
      }
      return respond(
        {
          id: resolved.id,
          deviceId: resolved.deviceId,
          collectedAt: resolved.collectedAt,
          sizeBytes: resolved.sizeBytes,
          hash: resolved.hash,
          startupConfigInSync: false,
          text: resolved.text
        },
        200
      );
    }
    if (subTail[0] === 'versions') {
      const limitParam = parseInt(url.searchParams.get('limit') ?? '5', 10);
      const limit = Number.isFinite(limitParam) ? Math.min(5, Math.max(1, limitParam)) : 5;
      const items = versions.slice(0, limit).map((version) => ({
        id: version.id,
        collectedAt: version.collectedAt,
        sizeBytes: version.sizeBytes,
        hash: version.hash
      }));
      return respond({ items }, 200);
    }
    if (subTail[0] === 'diff') {
      const fromId = url.searchParams.get('from');
      const toId = url.searchParams.get('to');
      if (!fromId || !toId) {
        return respond({ detail: 'from and to query params are required.' }, 400);
      }
      const fromVersion = findRunningConfigVersion(versions, fromId);
      const toVersion = findRunningConfigVersion(versions, toId);
      if (!fromVersion || !toVersion) {
        return respond({ detail: 'Requested snapshot not found.' }, 404);
      }
      const diff = buildSimpleDiff(fromVersion.text, toVersion.text);
      return respond({ diff }, 200);
    }
    if (subTail[0] === 'startup-diff') {
      const latest = versions[0];
      if (!latest) {
        return respond({ detail: 'No running config snapshots found.' }, 404);
      }
      const diff = buildSimpleDiff(latest.text, `${latest.text}\n! startup config sample`);
      return respond({ diff }, 200);
    }
  }
  return respond({ detail: `Mock handler missing for /orgs/${orgId}/devices/${deviceId}/${tail.join('/')}.` }, 501);
}

async function handleTasksRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }
  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }
  const [orgIdRaw, resource, ...rest] = segments;
  if (resource !== 'tasks') {
    return null;
  }
  const orgId = decodeURIComponent(orgIdRaw);
  const method = request.method.toUpperCase();
  if (rest.length === 0) {
    return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/tasks.` }, 501);
  }
  const [taskIdRaw] = rest;
  const taskId = decodeURIComponent(taskIdRaw);

  if (method === 'GET') {
    const stored = mockTaskStore.get(taskId);
    if (!stored) {
      return respond({ detail: `Mock task ${taskId} not found.` }, 404);
    }
    const attrs = JSON.parse(stored.attributes);
    return respond({
      id: stored.id,
      orgId: stored.orgId,
      name: stored.name,
      status: stored.status,
      protocol: attrs.protocol,
      errorMessage: attrs.errorMessage,
      resultMeta: attrs.resultMeta,
      createdAt: stored.createdAt,
      updatedAt: stored.updatedAt
    }, 200);
  }
  return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/tasks/${taskId}.` }, 501);
}

async function handleDiscoveryRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }
  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2 || segments[1] !== 'discovery') {
    return null;
  }

  const orgId = decodeURIComponent(segments[0]);
  if (!ORG_PROFILES[orgId]) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }

  ensureDiscoveryState(orgId);
  const method = request.method.toUpperCase();
  const resource = segments.slice(2);

  if (resource[0] !== 'jobs') {
    return respond({ detail: `Mock handler missing for /orgs/${orgId}/discovery/${resource.join('/')}.` }, 501);
  }

  if (resource.length === 1) {
    if (method === 'GET') {
      return respond(buildListResponse(listDiscoveryJobs(orgId)));
    }
    if (method === 'POST') {
      const payload = (await request.clone().json()) as DiscoveryJobCreate;
      const record = createDiscoveryJobRecord(orgId, payload);
      return respond(cloneValue(record.data), 201, { ETag: record.etag });
    }
    return respond({ detail: `Mock handler missing for ${method} /discovery/jobs.` }, 501);
  }

  const jobId = decodeURIComponent(resource[1]);
  const jobRecord = getDiscoveryJobRecord(orgId, jobId);
  if (!jobRecord) {
    return respond({ detail: `Mock discovery job ${jobId} not found.` }, 404);
  }

  if (resource.length === 2) {
    if (method === 'GET') {
      return respond(cloneValue(jobRecord.data), 200, { ETag: jobRecord.etag });
    }
    if (method === 'PATCH') {
      const ifMatch = request.headers.get('If-Match');
      if (!ifMatch) {
        return respond({ detail: 'If-Match header is required for updates.' }, 428);
      }
      if (ifMatch !== jobRecord.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      const payload = (await request.clone().json()) as DiscoveryJobUpdate;
      const updated = updateDiscoveryJobRecord(orgId, jobId, payload);
      return respond(cloneValue(updated.data), 200, { ETag: updated.etag });
    }
    if (method === 'DELETE') {
      const ifMatch = request.headers.get('If-Match');
      if (!ifMatch) {
        return respond({ detail: 'If-Match header is required for deletion.' }, 428);
      }
      if (ifMatch !== jobRecord.etag) {
        return respond({ detail: 'Mock ETag mismatch.' }, 412);
      }
      deleteDiscoveryJobRecord(orgId, jobId);
      return respond(undefined, 204);
    }
    return respond({ detail: `Mock handler missing for ${method} /discovery/jobs/${jobId}.` }, 501);
  }

  if (resource[2] === 'runs') {
    if (resource.length === 3) {
      if (method === 'GET') {
        return respond(buildListResponse(listDiscoveryRuns(orgId, jobId)));
      }
      if (method === 'POST') {
        const runRecord = createDiscoveryRunRecord(orgId, jobRecord.data as DiscoveryJob);
        scheduleMockRunCompletion(orgId, runRecord.data.id as string);
        return respond(cloneValue(runRecord.data), 201, { ETag: runRecord.etag });
      }
      return respond({ detail: `Mock handler missing for ${method} /discovery/jobs/${jobId}/runs.` }, 501);
    }

    const runId = decodeURIComponent(resource[3]);
    const runRecord = getDiscoveryRunRecord(orgId, runId);
    if (!runRecord) {
      return respond({ detail: `Mock discovery run ${runId} not found.` }, 404);
    }

    if (resource.length === 4) {
      if (method === 'GET') {
        return respond(cloneValue(runRecord.data), 200, { ETag: runRecord.etag });
      }
      return respond({ detail: `Mock handler missing for ${method} /discovery/jobs/${jobId}/runs/${runId}.` }, 501);
    }

    if (resource.length === 5 && resource[4] === 'events' && method === 'GET') {
      return streamDiscoveryEvents(request, runRecord);
    }
  }

  return respond({ detail: `Mock handler missing for /orgs/${orgId}/discovery/${resource.slice(1).join('/')}.` }, 501);
}

async function handleVisibilityRoutes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const [orgIdRaw, resource, ...rest] = segments;
  if (resource !== 'visibility') {
    return null;
  }

  const orgId = decodeURIComponent(orgIdRaw);
  if (!ORG_PROFILES[orgId]) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }

  const method = request.method.toUpperCase();
  if (rest[0] !== 'sessions') {
    return respond({ detail: `Mock handler missing for /orgs/${orgId}/visibility/${rest.join('/')}.` }, 501);
  }

  const parseActionSuffix = <T extends string>(segment: string | undefined, actions: readonly T[]) => {
    const raw = typeof segment === 'string' ? decodeURIComponent(segment) : '';
    for (const action of actions) {
      const suffix = `:${action}`;
      if (raw.endsWith(suffix)) {
        return {
          id: raw.slice(0, -suffix.length),
          action
        } as const;
      }
    }
    return {
      id: raw,
      action: null
    } as const;
  };

  if (rest.length === 1 && method === 'POST') {
    const payload = (await request.clone().json().catch(() => ({}))) as Record<string, unknown>;
    const record = createVisibilitySessionRecord(orgId, payload);
    return respond(cloneValue(record.data), 200, {
      ETag: record.etag,
      Location: `/public/v1/orgs/${encodeURIComponent(orgId)}/visibility/sessions/${encodeURIComponent(record.data.id ?? '')}`
    });
  }

  const sessionTarget = parseActionSuffix(rest[1], ['undo'] as const);
  const sessionId = sessionTarget.id;
  if (!sessionId) {
    return respond({ detail: 'Session id is required.' }, 400);
  }
  const sessionRecord = getVisibilitySessionRecord(orgId, sessionId);
  if (!sessionRecord) {
    return respond({ detail: `Mock visibility session ${sessionId} not found.` }, 404);
  }

  if (rest.length === 2 && sessionTarget.action === null && method === 'GET') {
    return respond(cloneValue(sessionRecord.data), 200, { ETag: sessionRecord.etag });
  }

  if (rest.length === 2 && sessionTarget.action === null && method === 'DELETE') {
    deleteVisibilitySessionRecord(orgId, sessionId);
    return respond(null, 204);
  }

  if (rest.length === 3 && rest[2] === 'stream' && method === 'GET') {
    return streamVisibilitySession(request, orgId, sessionRecord);
  }

  if (rest.length === 2 && sessionTarget.action === 'undo' && method === 'POST') {
    if (sessionRecord.history.length < 1) {
      return respond({ detail: 'No mock visibility history to undo.' }, 409);
    }
    const nowIso = visibilityNowIso();
    const previous = sessionRecord.history.pop()!;
    sessionRecord.data.pendingProposalId = undefined;
    sessionRecord.data.state = {
      ...cloneValue(previous.beforeState),
      revision: sessionRecord.data.state.revision + 1,
      updatedAt: new Date(nowIso)
    } as Mutable<VisibilitySession['state']>;
    sessionRecord.data.historyDepth = sessionRecord.history.length;
    sessionRecord.data.mode = deriveVisibilitySessionMode(sessionRecord.data);
    sessionRecord.data.updatedAt = new Date(nowIso);
    sessionRecord.data.updatedBy = 'mock-user';
    sessionRecord.etag = generateEtag();
    return respond(cloneValue(sessionRecord.data), 200, { ETag: sessionRecord.etag });
  }

  if (rest.length === 3 && rest[2] === 'proposals' && method === 'POST') {
    const payload = (await request.clone().json().catch(() => ({}))) as Record<string, unknown>;
    const proposalRecord = createVisibilityProposalRecord(orgId, sessionRecord.data, payload);
    sessionRecord.data.pendingProposalId = proposalRecord.data.id;
    sessionRecord.data.mode = VisibilitySessionModeschema.Draft;
    sessionRecord.data.updatedAt = new Date(visibilityNowIso());
    sessionRecord.data.updatedBy = 'mock-user';
    sessionRecord.etag = generateEtag();
    return respond(cloneValue(proposalRecord.data), 201, {
      ETag: proposalRecord.etag,
      Location: `/public/v1/orgs/${encodeURIComponent(orgId)}/visibility/sessions/${encodeURIComponent(sessionId)}/proposals/${encodeURIComponent(proposalRecord.data.id ?? '')}`
    });
  }

  if (rest.length < 4 || rest[2] !== 'proposals') {
    return respond({ detail: `Mock handler missing for /orgs/${orgId}/visibility/sessions/${rest.slice(1).join('/')}.` }, 501);
  }

  const proposalTarget = parseActionSuffix(rest[3], ['preview', 'reject', 'apply'] as const);
  const proposalId = proposalTarget.id;
  const proposalRecord = getVisibilityProposalRecord(orgId, proposalId);
  if (!proposalRecord || proposalRecord.data.sessionId !== sessionId) {
    return respond({ detail: `Mock visibility proposal ${proposalId} not found.` }, 404);
  }

  if (rest.length === 4 && proposalTarget.action === null && method === 'GET') {
    return respond(cloneValue(proposalRecord.data), 200, { ETag: proposalRecord.etag });
  }

  if (rest.length === 4 && proposalTarget.action === 'preview' && method === 'POST') {
    const nowIso = visibilityNowIso();
    sessionRecord.data.pendingProposalId = proposalRecord.data.id;
    sessionRecord.data.mode = VisibilitySessionModeschema.Preview;
    sessionRecord.data.updatedAt = new Date(nowIso);
    sessionRecord.data.updatedBy = 'mock-user';
    sessionRecord.etag = generateEtag();
    proposalRecord.data.status = VisibilityProposalStatusschema.Previewed;
    proposalRecord.data.updatedAt = new Date(nowIso);
    proposalRecord.data.updatedBy = 'mock-user';
    proposalRecord.etag = generateEtag();
    return respond(cloneValue(sessionRecord.data), 200, { ETag: sessionRecord.etag });
  }

  if (rest.length === 4 && proposalTarget.action === 'reject' && method === 'POST') {
    const nowIso = visibilityNowIso();
    sessionRecord.data.pendingProposalId = undefined;
    sessionRecord.data.mode = deriveVisibilitySessionMode(sessionRecord.data);
    sessionRecord.data.updatedAt = new Date(nowIso);
    sessionRecord.data.updatedBy = 'mock-user';
    sessionRecord.etag = generateEtag();
    proposalRecord.data.status = VisibilityProposalStatusschema.Rejected;
    proposalRecord.data.updatedAt = new Date(nowIso);
    proposalRecord.data.updatedBy = 'mock-user';
    proposalRecord.etag = generateEtag();
    return respond(cloneValue(sessionRecord.data), 200, { ETag: sessionRecord.etag });
  }

  if (rest.length === 4 && proposalTarget.action === 'apply' && method === 'POST') {
    const nowIso = visibilityNowIso();
    const nextState =
      (proposalRecord.data.patch?.ops?.length ?? 0) > 0
        ? (() => {
            const runtime = hydrateTopologyOverlayState(sessionRecord.data.state as any, sessionRecord.data.state.base);
            const applied = applyTopologyOverlayPatch(runtime, proposalRecord.data.patch as any, {
              revision: sessionRecord.data.state.revision + 1,
              eventId: `mock-apply-${proposalRecord.data.id}`
            });
            return {
              ...cloneValue(sessionRecord.data.state),
              base: cloneValue(applied.base ?? sessionRecord.data.state.base),
              revision: applied.revision,
              updatedAt: new Date(nowIso),
              selection: serializeOverlaySelection(applied.selection) as any,
              layers: cloneValue(applied.layers),
              graphOverlays: cloneValue(applied.graphOverlays)
            } as Mutable<VisibilitySession['state']>;
          })()
        : ({
            ...cloneValue(sessionRecord.data.state),
            revision: sessionRecord.data.state.revision + 1,
            updatedAt: new Date(nowIso)
          } as Mutable<VisibilitySession['state']>);

    sessionRecord.history.push({
      proposalId: proposalRecord.data.id,
      beforeState: cloneValue(sessionRecord.data.state) as Mutable<VisibilitySession['state']>
    });
    sessionRecord.data.state = nextState;
    sessionRecord.data.pendingProposalId = undefined;
    sessionRecord.data.historyDepth = sessionRecord.history.length;
    sessionRecord.data.mode = deriveVisibilitySessionMode(
      { ...sessionRecord.data, historyDepth: sessionRecord.history.length },
      undefined
    );
    sessionRecord.data.updatedAt = new Date(nowIso);
    sessionRecord.data.updatedBy = 'mock-user';
    sessionRecord.etag = generateEtag();

    proposalRecord.data.status = VisibilityProposalStatusschema.Applied;
    proposalRecord.data.updatedAt = new Date(nowIso);
    proposalRecord.data.updatedBy = 'mock-user';
    proposalRecord.etag = generateEtag();

    return respond(cloneValue(sessionRecord.data), 200, { ETag: sessionRecord.etag });
  }

  return respond(
    {
      detail: `Mock handler missing for /orgs/${orgId}/visibility/sessions/${sessionId}/proposals/${rest
        .slice(3)
        .join('/')}.`
    },
    501
  );
}

type TopologyMockViewItem = {
  viewId: string;
  name: string;
  description?: string;
  version?: string;
  origin?: string;
  state?: string;
  ownerUserId?: string;
  sharingScope?: string;
  scopeMode?: TopologyV2ViewScopeModeschema;
  boundScope?: {
    scopeType: string;
    scopeId: string;
  };
  baseTemplateViewId?: string;
  scopeTypes: string[];
  params: Record<string, unknown>;
  hide?: boolean;
};

type MockSavedTopologyRequest = {
  scopeType: string;
  scopeId: string;
  params: Record<string, unknown>;
  limits: Record<string, unknown>;
  asOf?: string;
  timeMode: 'current' | 'fixed';
};

type StoredCustomTopologyView = {
  view: TopologyMockViewItem;
  request: MockSavedTopologyRequest;
  overlayState: Record<string, any> | null;
  overlayStatuses: Record<string, any>[];
};

type TopologyViewsOrgState = {
  customViews: Map<string, StoredCustomTopologyView>;
};

const topologyViewsStore = new Map<string, TopologyViewsOrgState>();

const TOPOLOGY_V2_VIEW_ITEMS: TopologyMockViewItem[] = [
  {
    viewId: 'physical_overview',
    name: 'Physical',
    description: 'Physical connectivity overview (devices + device-to-device links). Click a device-link to expand.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {}
  },
  {
    viewId: 'vlan_membership_summary',
    name: 'VLAN membership summary',
    description: 'Site VLANs and their observed interface memberships.',
    version: '1.0',
    scopeTypes: ['org', 'site', 'device'],
    params: {},
    hide: true
  },
  {
    viewId: 'aws_cloud_overview',
    name: 'AWS Cloud',
    description: 'AWS regional cloud connectivity with VPC hierarchy, gateways, workloads, TGW, and VPN.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {}
  },
  {
    viewId: 'org_overview',
    name: 'Intersite Topology',
    description: 'Customer-facing organization topology with inter-site VPN links.',
    version: '1.0',
    scopeTypes: ['org'],
    params: {}
  },
  {
    viewId: 'org_site_pair_links',
    name: 'VPN Tunnels',
    description: 'Overlay expansion for an inter-site VPN link.',
    version: '1.0',
    scopeTypes: ['org'],
    params: {},
    hide: true
  },
  {
    viewId: 'physical_pair_links',
    name: 'Physical (Pair Links)',
    description:
      'Overlay expansion for a device-link: shows only links between a specific device pair (physical links and symmetric LAG bundles).',
    version: '1.0',
    scopeTypes: ['site'],
    params: {},
    hide: true
  },
  {
    viewId: 'lag_link_members',
    name: 'LAG Link Members',
    description: 'Neighborhood view showing a port-channel (LAG) interface and its member physical interfaces.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {},
    hide: true
  },
  {
    viewId: 'physical_ports',
    name: 'Physical Ports',
    description: 'Neighborhood view centered on a device showing its directly contained physical and LAG interfaces.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {},
    hide: true
  },
  {
    viewId: 'lag_members',
    name: 'LAG Members',
    description: 'Neighborhood view centered on a LAG interface showing its parent device and member links.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {},
    hide: true
  },
  {
    viewId: 'routing_adjacency',
    name: 'Routing Adjacency',
    description: 'Neighborhood view centered on a border device showing routing-relevant adjacency context.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {
      protocol: {
        type: 'string',
        required: false,
        hint: 'bgp|ospf|isis|bfd',
        ui: {
          label: 'Protocol',
          order: 10
        }
      },
      instance: {
        type: 'string',
        required: false,
        dependsOn: ['protocol'],
        hint: 'bgp:default|ospf:1|isis:CORE',
        ui: {
          label: 'Instance',
          order: 20
        }
      }
    }
  },
  {
    viewId: 'l3_membership',
    name: 'L3 Membership',
    description: 'Site-wide L3 hierarchy showing devices, VRFs, L3 interfaces, and subnet memberships.',
    version: '1.0',
    scopeTypes: ['site'],
    params: {
      vrf: {
        type: 'string',
        required: false,
        ui: {
          label: 'VRF',
          order: 10,
          hidden: true
        }
      },
      subnetId: {
        type: 'string',
        required: false,
        ui: {
          label: 'Subnet',
          order: 20,
          hidden: true
        }
      }
    }
  }
] ;

const TOPOLOGY_VISIBLE_VIEW_ITEMS = TOPOLOGY_V2_VIEW_ITEMS.filter((item) => !item.hide);
const TOPOLOGY_VIEW_BY_ID = new Map<string, TopologyMockViewItem>(
  TOPOLOGY_V2_VIEW_ITEMS.map((item) => [item.viewId, item])
);

const SEEDED_CURRENT_SCOPE_VIEW_ID = 'physical_current_scope_sfo';

function buildSeededCustomTopologyViews(): Map<string, StoredCustomTopologyView> {
  return new Map([
    [
      SEEDED_CURRENT_SCOPE_VIEW_ID,
      {
        view: {
          viewId: SEEDED_CURRENT_SCOPE_VIEW_ID,
          name: 'Physical - SFO [Current scope]',
          description: 'Seeded current-scope saved view bound to SFO for topology mock validation.',
          origin: 'custom',
          state: 'active',
          ownerUserId: MOCK_CHAT_USER_ID,
          sharingScope: 'private',
          scopeMode: 'current_scope',
          boundScope: {
            scopeType: 'site',
            scopeId: 'site-sfo'
          },
          baseTemplateViewId: 'physical_overview',
          scopeTypes: ['site'],
          params: {}
        },
        request: {
          scopeType: 'site',
          scopeId: 'site-sfo',
          params: {},
          limits: {},
          timeMode: 'current'
        },
        overlayState: null,
        overlayStatuses: []
      }
    ]
  ]);
}

function ensureTopologyViewsState(orgId: string): TopologyViewsOrgState {
  if (!topologyViewsStore.has(orgId)) {
    topologyViewsStore.set(orgId, {
      customViews: buildSeededCustomTopologyViews()
    });
  }
  return topologyViewsStore.get(orgId)!;
}

function normalizeTopologyViewItem(view: TopologyMockViewItem): TopologyMockViewItem {
  return {
    ...cloneValue(view),
    origin: view.origin ?? 'system',
    state: view.state ?? 'active'
  };
}

function listCustomTopologyViews(orgId: string): TopologyMockViewItem[] {
  return Array.from(ensureTopologyViewsState(orgId).customViews.values()).map((record) => cloneValue(record.view));
}

function getCustomTopologyViewRecord(orgId: string, viewId: string): StoredCustomTopologyView | null {
  return ensureTopologyViewsState(orgId).customViews.get(viewId) ?? null;
}

function normalizeTopologyOriginFilter(value: string | null): 'all' | 'system' | 'custom' {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'system' || normalized === 'custom') {
    return normalized;
  }
  return 'all';
}

function listTopologyViewItems(orgId: string, originFilter: 'all' | 'system' | 'custom'): TopologyMockViewItem[] {
  const systemViews = TOPOLOGY_VISIBLE_VIEW_ITEMS.map(normalizeTopologyViewItem);
  const customViews = listCustomTopologyViews(orgId);
  if (originFilter === 'system') {
    return systemViews;
  }
  if (originFilter === 'custom') {
    return customViews;
  }
  return [...systemViews, ...customViews];
}

function getTopologyViewItem(orgId: string, viewId: string): TopologyMockViewItem | null {
  const custom = getCustomTopologyViewRecord(orgId, viewId);
  if (custom) {
    return cloneValue(custom.view);
  }
  const system = TOPOLOGY_VIEW_BY_ID.get(viewId);
  return system ? normalizeTopologyViewItem(system) : null;
}

function slugifyTopologyViewName(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const base = normalized || 'saved_view';
  return `${base.slice(0, 24)}_${randomId('view').slice(-6)}`;
}

const TOPO_DEVICE_IDS = {
  dist1: '740029df-3057-440b-9cba-4e3bfe96ce56',
  access1: '8e003219-e69e-4d2c-b7f7-40a8029eeaf3',
  access2: '25457002-df0b-4eea-9efb-e229b0bc6e8d',
  border1: 'b5c3eac8-6be6-4f6b-9c03-14f19a1f5081'
} as const;

const DEVICE_NODE_IDS = {
  dist1: `ref:inventory.device:${TOPO_DEVICE_IDS.dist1}`,
  access1: `ref:inventory.device:${TOPO_DEVICE_IDS.access1}`,
  access2: `ref:inventory.device:${TOPO_DEVICE_IDS.access2}`,
  border1: `ref:inventory.device:${TOPO_DEVICE_IDS.border1}`
} as const;

const IFACE_NODE_IDS = {
  dist1Te11: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/1`,
  dist1Te12: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/2`,
  dist1Te13: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/3`,
  dist1Te14: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/4`,
  dist1Te15: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/5`,
  dist1Te16: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/6`,
  dist1Te17: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/7`,
  dist1Te18: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Te1/0/8`,
  dist1Po10: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Po10`,
  dist1Po20: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Po20`,
  dist1Po30: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Po30`,
  dist1Po40: `ref:inventory.interface:${TOPO_DEVICE_IDS.dist1}#Po40`,
  access1Te11: `ref:inventory.interface:${TOPO_DEVICE_IDS.access1}#Te1/0/1`,
  access1Te12: `ref:inventory.interface:${TOPO_DEVICE_IDS.access1}#Te1/0/2`,
  access1Te13: `ref:inventory.interface:${TOPO_DEVICE_IDS.access1}#Te1/0/3`,
  access1Te14: `ref:inventory.interface:${TOPO_DEVICE_IDS.access1}#Te1/0/4`,
  access1Po11: `ref:inventory.interface:${TOPO_DEVICE_IDS.access1}#Po11`,
  access1Po21: `ref:inventory.interface:${TOPO_DEVICE_IDS.access1}#Po21`,
  access2Te11: `ref:inventory.interface:${TOPO_DEVICE_IDS.access2}#Te1/0/1`,
  access2Te12: `ref:inventory.interface:${TOPO_DEVICE_IDS.access2}#Te1/0/2`,
  access2Po12: `ref:inventory.interface:${TOPO_DEVICE_IDS.access2}#Po12`,
  border1Te11: `ref:inventory.interface:${TOPO_DEVICE_IDS.border1}#Te1/0/1`,
  border1Te12: `ref:inventory.interface:${TOPO_DEVICE_IDS.border1}#Te1/0/2`,
  border1Po13: `ref:inventory.interface:${TOPO_DEVICE_IDS.border1}#Po13`
} as const;

const TOPO_SITE_IDS = {
  sjc: '9bf2800d-1fdf-41f8-a678-ead97f0b1bf9',
  iad: 'fbeba482-c188-4d12-9a36-300e071dc78b',
  aws: 'c84d6b05-1a4f-44e6-a3db-9b854ba9a730'
} as const;

const SITE_NODE_IDS = {
  sjc: `ref:inventory.site:${TOPO_SITE_IDS.sjc}`,
  iad: `ref:inventory.site:${TOPO_SITE_IDS.iad}`,
  aws: `ref:inventory.site:${TOPO_SITE_IDS.aws}`
} as const;

const VPN_NODE_IDS = {
  sjcEdge: `ref:inventory.device:${TOPO_DEVICE_IDS.border1}`,
  iadEdge: `ref:inventory.device:30fb9c95-cc17-4b10-9c56-df12f9566ab1`,
  awsVpn: 'ref:aws.vpn_connection:vpn-0f14d2c8a1',
  sjcToAws: 'ref:vpn.endpoint:sjc:aws:1',
  awsToSjc: 'ref:vpn.endpoint:aws:sjc:1',
  sjcToIad: 'ref:vpn.endpoint:sjc:iad:1',
  iadToSjc: 'ref:vpn.endpoint:iad:sjc:1'
} as const;

type OrgVPNLinkSpec = {
  aSite: string;
  bSite: string;
  aOwner: string;
  bOwner: string;
  aEndpoint: string;
  bEndpoint: string;
  tunnelCount: number;
  providerFamily: string;
  cloudTunnelFamily?: string;
  connectionId?: string;
};

const ORG_VPN_LINK_SPECS: OrgVPNLinkSpec[] = [
  {
    aSite: SITE_NODE_IDS.sjc,
    bSite: SITE_NODE_IDS.aws,
    aOwner: VPN_NODE_IDS.sjcEdge,
    bOwner: VPN_NODE_IDS.awsVpn,
    aEndpoint: VPN_NODE_IDS.sjcToAws,
    bEndpoint: VPN_NODE_IDS.awsToSjc,
    tunnelCount: 2,
    providerFamily: 'aws',
    cloudTunnelFamily: 'aws-site-to-site-vpn',
    connectionId: 'vpn-0f14d2c8a1'
  },
  {
    aSite: SITE_NODE_IDS.sjc,
    bSite: SITE_NODE_IDS.iad,
    aOwner: VPN_NODE_IDS.sjcEdge,
    bOwner: VPN_NODE_IDS.iadEdge,
    aEndpoint: VPN_NODE_IDS.sjcToIad,
    bEndpoint: VPN_NODE_IDS.iadToSjc,
    tunnelCount: 1,
    providerFamily: 'onprem'
  }
];

function buildTopologyOverview(scopeType: string, scopeId: string) {
  const now = new Date().toISOString();
  return {
    graph: {
      graphId: `mock:physical_overview:${scopeType}:${scopeId}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: [
        { id: DEVICE_NODE_IDS.dist1, displayName: 'Dist1', labels: { family: 'physical', level: 'device' }, refs: [{ type: 'inventory.device', id: TOPO_DEVICE_IDS.dist1 }] },
        { id: DEVICE_NODE_IDS.access1, displayName: 'Access1', labels: { family: 'physical', level: 'device' }, refs: [{ type: 'inventory.device', id: TOPO_DEVICE_IDS.access1 }] },
        { id: DEVICE_NODE_IDS.access2, displayName: 'Access2', labels: { family: 'physical', level: 'device' }, refs: [{ type: 'inventory.device', id: TOPO_DEVICE_IDS.access2 }] },
        { id: DEVICE_NODE_IDS.border1, displayName: 'Border1', labels: { family: 'physical', level: 'device' }, refs: [{ type: 'inventory.device', id: TOPO_DEVICE_IDS.border1 }] }
      ],
      edges: [
        { id: `edge:device:${DEVICE_NODE_IDS.dist1}--${DEVICE_NODE_IDS.access1}`, fromId: DEVICE_NODE_IDS.dist1, toId: DEVICE_NODE_IDS.access1, directed: false, labels: { family: 'physical', relation: 'connects', level: 'device_link' }, properties: { linkCount: 4 } },
        { id: `edge:device:${DEVICE_NODE_IDS.dist1}--${DEVICE_NODE_IDS.access2}`, fromId: DEVICE_NODE_IDS.dist1, toId: DEVICE_NODE_IDS.access2, directed: false, labels: { family: 'physical', relation: 'connects', level: 'device_link' }, properties: { linkCount: 2 } },
        { id: `edge:device:${DEVICE_NODE_IDS.dist1}--${DEVICE_NODE_IDS.border1}`, fromId: DEVICE_NODE_IDS.dist1, toId: DEVICE_NODE_IDS.border1, directed: false, labels: { family: 'physical', relation: 'connects', level: 'device_link' }, properties: { linkCount: 2 } }
      ]
    },
    legend: {
      nodeRules: [
        { when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'xl' } }
      ],
      edgeRules: [
        { when: { 'labels.relation': 'connects' }, ui: { style: 'link' } }
      ]
    },
    drilldowns: [
      {
        name: 'Expand (overlay)',
        viewId: 'physical_pair_links',
        mode: 'overlay',
        appliesWhen: { 'edge.labels.level': 'device_link' },
        dynamic: {
          params: { deviceAId: 'edge.fromId', deviceBId: 'edge.toId' },
          overlayKey: 'edge.id'
        }
      }
    ],
    meta: {
      asOf: now,
      context: { family: 'physical', plane: 'underlay', purpose: 'connectivity' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

function buildTopologyVlanMembershipSummary(scopeType: string, scopeId: string) {
  const now = new Date().toISOString();
  const vlanIds = [1, 20, 30, 40, 4094];
  const devices = [
    { id: TOPO_DEVICE_IDS.dist1, name: 'Dist1', vlans: [1, 20, 30] },
    { id: TOPO_DEVICE_IDS.access1, name: 'Access1', vlans: [20, 40] },
    { id: TOPO_DEVICE_IDS.border1, name: 'Border1', vlans: [4094] }
  ];
  const vlanNodeId = (vlanId: number) => `ref:derived:l2:vlan:${vlanId}`;
  const nodes: any[] = vlanIds.map((vlanId) => ({
    id: vlanNodeId(vlanId),
    displayName: `VLAN ${vlanId}`,
    labels: { family: 'l2', domain: 'vlan', level: 'vlan' },
    properties: { vlanId }
  }));
  const edges: any[] = [];
  devices.forEach((device) => {
    const deviceNodeId = `ref:inventory.device:${device.id}`;
    nodes.push({ id: deviceNodeId, displayName: device.name, labels: { family: 'l2', level: 'device' }, refs: [{ type: 'inventory.device', id: device.id }] });
    device.vlans.forEach((vlanId) => {
      const interfaceId = `ref:inventory.interface:${device.id}#Vlan${vlanId}`;
      nodes.push({ id: interfaceId, displayName: `${device.name} · Vlan${vlanId}`, labels: { family: 'l2', level: 'interface' }, refs: [{ type: 'inventory.device', id: device.id }] });
      edges.push({ id: `edge:${interfaceId}--contains--${deviceNodeId}`, fromId: deviceNodeId, toId: interfaceId, directed: true, labels: { family: 'l2', relation: 'contains' } });
      edges.push({ id: `edge:${interfaceId}--member_of--${vlanNodeId(vlanId)}`, fromId: interfaceId, toId: vlanNodeId(vlanId), directed: true, labels: { family: 'l2', relation: 'member_of' } });
    });
  });
  return {
    graph: { graphId: `mock:vlan_membership_summary:${scopeType}:${scopeId}`, generatedAt: now, scope: { scopeType, scopeId }, nodes, edges },
    legend: {
      nodeRules: [
        { when: { 'labels.domain': 'vlan' }, ui: { icon: 'device' } },
        { when: { 'labels.level': 'interface' }, ui: { icon: 'interface', size: 'sm', renderAs: 'port_dot' } }
      ],
      edgeRules: [{ when: { 'labels.relation': 'member_of' }, ui: { style: 'membership' } }]
    },
    drilldowns: [],
    meta: { asOf: now, context: { family: 'l2', plane: 'underlay', purpose: 'vlan_membership' }, viewVersion: '1.0', topics: ['state'] }
  };
}

const orgSiteNode = (id: string) => {
  const metadata: Record<string, { name: string; siteType: string; location: string; cloud: boolean }> = {
    [SITE_NODE_IDS.sjc]: { name: 'San Jose DC', siteType: 'datacenter', location: 'San Jose, CA', cloud: false },
    [SITE_NODE_IDS.iad]: { name: 'Ashburn Branch', siteType: 'branch', location: 'Ashburn, VA', cloud: false },
    [SITE_NODE_IDS.aws]: { name: 'AWS us-east-1', siteType: 'cloud', location: 'us-east-1', cloud: true }
  };
  const peerIds = new Set<string>();
  let tunnelCount = 0;
  ORG_VPN_LINK_SPECS.forEach((link) => {
    if (link.aSite === id) {
      peerIds.add(link.bSite);
      tunnelCount += link.tunnelCount;
    } else if (link.bSite === id) {
      peerIds.add(link.aSite);
      tunnelCount += link.tunnelCount;
    }
  });
  const meta = metadata[id] ?? { name: id, siteType: 'site', location: '', cloud: false };
  const peerNames = Array.from(peerIds).map((peerId) => metadata[peerId]?.name ?? peerId);
  const vpnResourceIds = new Set<string>();
  ORG_VPN_LINK_SPECS.forEach((link) => {
    if (link.aSite === id) {
      vpnResourceIds.add(link.aOwner);
    }
    if (link.bSite === id) {
      vpnResourceIds.add(link.bOwner);
    }
  });
  const vpnResourceCount = vpnResourceIds.size;
  return {
    id,
    displayName: meta.name,
    labels: { family: 'tunnel', domain: 'ipsec', level: 'site', site_type: meta.siteType },
    refs: [{ type: 'inventory.site', id: id.replace(/^ref:inventory\.site:/, '') }],
    properties: {
      site_id: id.replace(/^ref:inventory\.site:/, ''),
      site_name: meta.name,
      site_type: meta.siteType,
      location: meta.location,
      is_cloud: meta.cloud,
      vpn_peer_count: peerIds.size,
      vpn_tunnel_count: tunnelCount,
      vpn_resource_count: vpnResourceCount,
      up_count: tunnelCount,
      down_count: 0,
      degraded_count: 0,
      peer_sites: peerNames.map((siteName) => ({ site_name: siteName })),
      presentation: {
        title: meta.name,
        selectionTitle: meta.name,
        subtitle: `${meta.siteType}${meta.location ? ` · ${meta.location}` : ''}`,
        selectionSubtitle: 'Site',
        badges: [`${peerIds.size} peer site${peerIds.size === 1 ? '' : 's'}`],
        rows: [
          { key: 'connectedPeers', label: 'Connected sites', value: peerIds.size },
          ...(peerNames.length > 0 ? [{ key: 'peerSites', label: 'Peer sites', value: peerNames.join(', ') }] : []),
          { key: 'vpnResources', label: 'VPN resources', value: vpnResourceCount },
          { key: 'tunnels', label: 'Tunnels', value: tunnelCount }
        ]
      }
    }
  };
};

const orgVPNLegend = () => ({
  nodeRules: [
    { when: { 'labels.level': 'site' }, ui: { icon: 'site', size: 'xl' } },
    { when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'sm', renderAs: 'edge_chip' } },
    { when: { 'labels.level': 'vpn_connection' }, ui: { icon: 'cloud', size: 'sm', renderAs: 'edge_chip' } },
    { when: { 'labels.level': 'vpn_endpoint' }, ui: { icon: 'endpoint', size: 'sm', renderAs: 'port_dot', showPortLabel: true, labelStagger: 16 } }
  ],
  edgeRules: [
    { when: { 'labels.level': 'site_vpn_link' }, ui: { style: 'link', stroke: '#2563eb', width: 3, labelField: 'display_label' } },
    { when: { 'labels.relation': 'contains' }, ui: { style: 'embed' } },
    { when: { 'labels.relation': 'connects' }, ui: { style: 'link', stroke: '#2563eb', width: 2, labelField: 'display_label' } }
  ]
});

const orgVPNOwnerNode = (id: string, siteId: string, connectionId?: string) => {
  const names: Record<string, string> = {
    [VPN_NODE_IDS.sjcEdge]: 'SJC edge',
    [VPN_NODE_IDS.iadEdge]: 'IAD edge',
    [VPN_NODE_IDS.awsVpn]: 'AWS VPN'
  };
  const isCloudVPN = id.startsWith('ref:aws.vpn_connection:');
  const title = names[id] ?? connectionId ?? id;
  return {
    id,
    displayName: title,
    labels: { family: 'tunnel', domain: 'ipsec', level: isCloudVPN ? 'vpn_connection' : 'device' },
    refs: [{ type: isCloudVPN ? 'aws.vpn_connection' : 'inventory.device', id: id.split(':').pop() ?? id }],
    properties: {
      kind: isCloudVPN ? 'aws_vpn_connection' : 'device',
      site_id: siteId.replace(/^ref:inventory\.site:/, ''),
      ...(connectionId ? { connection_id: connectionId } : {}),
      presentation: {
        title,
        selectionTitle: title,
        selectionSubtitle: 'VPN resource',
        renderAs: 'edge_chip'
      }
    }
  };
};

function buildTopologyOrgOverview(scopeType: string, scopeId: string) {
  const now = new Date().toISOString();
  return {
    graph: {
      graphId: `mock:org_overview:${scopeType}:${scopeId}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: [orgSiteNode(SITE_NODE_IDS.sjc), orgSiteNode(SITE_NODE_IDS.iad), orgSiteNode(SITE_NODE_IDS.aws)],
      edges: ORG_VPN_LINK_SPECS.map((link) => {
        const sitePairKey = [link.aSite, link.bSite].sort().join('--');
        const aName = String(orgSiteNode(link.aSite).displayName ?? link.aSite);
        const bName = String(orgSiteNode(link.bSite).displayName ?? link.bSite);
        const provider = link.cloudTunnelFamily === 'aws-site-to-site-vpn' ? 'AWS Site-to-Site VPN' : link.providerFamily === 'onprem' ? 'On-prem VPN' : link.providerFamily;
        return {
          id: `edge:site_vpn:${sitePairKey}`,
          fromId: link.aSite,
          toId: link.bSite,
          directed: false,
          labels: { family: 'tunnel', domain: 'ipsec', relation: 'connects', level: 'site_vpn_link' },
          properties: {
            kind: 'site_vpn_link',
            site_pair_key: sitePairKey,
            source_site_id: link.aSite,
            target_site_id: link.bSite,
            tunnel_count: link.tunnelCount,
            up_count: link.tunnelCount,
            down_count: 0,
            unknown_count: 0,
            tunnel_status_summary: `${link.tunnelCount} up`,
            provider_families: [link.providerFamily],
            cloud_tunnel_families: link.cloudTunnelFamily ? [link.cloudTunnelFamily] : [],
            aws_vpn_connection_ids: link.connectionId ? [link.connectionId] : [],
            display_label: `${link.tunnelCount} tunnel${link.tunnelCount === 1 ? '' : 's'}`,
            presentation: {
              title: 'Site-to-Site VPN',
              hoverTitle: 'Site-to-Site VPN',
              selectionTitle: 'Site-to-Site VPN',
              subtitle: `${aName} ↔ ${bName}`,
              selectionSubtitle: `${aName} ↔ ${bName}`,
              edgeLabel: `${link.tunnelCount} tunnel${link.tunnelCount === 1 ? '' : 's'}`,
              badges: ['Healthy'],
              rows: [
                { key: 'sites', label: 'Sites', value: `${aName} ↔ ${bName}` },
                { key: 'tunnels', label: 'Tunnels', value: link.tunnelCount },
                { key: 'status', label: 'Status', value: 'Healthy' },
                { key: 'tunnelStatus', label: 'Tunnel status', value: `${link.tunnelCount} up` },
                { key: 'provider', label: 'Provider', value: provider }
              ]
            }
          }
        };
      })
    },
    legend: orgVPNLegend(),
    drilldowns: [
      {
        name: 'Open AWS cloud',
        viewId: 'aws_cloud_overview',
        mode: 'replace',
        appliesWhen: { 'labels.level': 'site', 'labels.site_type': 'awsCloud' },
        dynamic: {
          scopeType: 'site',
          scopeId: 'node.properties.site_id'
        }
      },
      {
        name: 'Open physical connectivity',
        viewId: 'physical_overview',
        mode: 'replace',
        appliesWhen: { 'labels.level': 'site', 'labels.site_type': 'OnPrem' },
        dynamic: {
          scopeType: 'site',
          scopeId: 'node.properties.site_id'
        }
      },
      {
        name: 'Show VPN tunnels',
        viewId: 'org_site_pair_links',
        mode: 'overlay',
        appliesWhen: { 'edge.labels.level': 'site_vpn_link' },
        dynamic: {
          params: { aSiteId: 'edge.fromId', bSiteId: 'edge.toId' },
          overlayKey: 'edge.id'
        }
      }
    ],
    meta: {
      asOf: now,
      context: { family: 'tunnel', plane: 'underlay', purpose: 'vpn_connection' },
      composedGraphs: [{ family: 'tunnel', plane: 'underlay', purpose: 'vpn_connection', scopeType: 'org' }],
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

function buildTopologyOrgVPNDetail(scopeType: string, scopeId: string, links: OrgVPNLinkSpec[], graphSuffix: string) {
  const now = new Date().toISOString();
  const nodes = new Map<string, any>();
  const edges = new Map<string, any>();
  const addNode = (node: any) => nodes.set(node.id, node);
  const addEdge = (edge: any) => edges.set(edge.id, edge);

  links.forEach((link) => {
    const aSite = orgSiteNode(link.aSite);
    const bSite = orgSiteNode(link.bSite);
    const aName = String(aSite.displayName ?? link.aSite);
    const bName = String(bSite.displayName ?? link.bSite);
    const aOwner = orgVPNOwnerNode(link.aOwner, link.aSite, link.connectionId);
    const bOwner = orgVPNOwnerNode(link.bOwner, link.bSite, link.connectionId);
    const provider =
      link.cloudTunnelFamily === 'aws-site-to-site-vpn'
        ? 'AWS Site-to-Site VPN'
        : link.providerFamily === 'onprem'
          ? 'On-prem VPN'
          : link.providerFamily;
    addNode(aSite);
    addNode(bSite);
    addNode(aOwner);
    addNode(bOwner);
    addNode({
      id: link.aEndpoint,
      displayName: 'VPN endpoint',
      labels: { family: 'tunnel', domain: 'ipsec', level: 'vpn_endpoint' },
      properties: {
        site_id: link.aSite,
        tunnel_state: 'up',
        provider_family: link.providerFamily,
        presentation: {
          title: 'VPN endpoint',
          selectionTitle: 'VPN endpoint',
          selectionSubtitle: 'Tunnel endpoint',
          showPortLabel: true,
          portLabel: 'tunnel.1',
          rows: [{ key: 'status', label: 'Status', value: 'Up' }]
        }
      }
    });
    addNode({
      id: link.bEndpoint,
      displayName: 'VPN endpoint',
      labels: { family: 'tunnel', domain: 'ipsec', level: 'vpn_endpoint' },
      properties: {
        site_id: link.bSite,
        tunnel_state: 'up',
        provider_family: link.providerFamily,
        presentation: {
          title: 'VPN endpoint',
          selectionTitle: 'VPN endpoint',
          selectionSubtitle: 'Tunnel endpoint',
          showPortLabel: true,
          portLabel: 'tunnel.1',
          rows: [{ key: 'status', label: 'Status', value: 'Up' }]
        }
      }
    });
    addEdge({ id: `edge:${link.aSite}--${link.aOwner}#contains`, fromId: link.aSite, toId: link.aOwner, directed: true, labels: { family: 'tunnel', relation: 'contains' } });
    addEdge({ id: `edge:${link.bSite}--${link.bOwner}#contains`, fromId: link.bSite, toId: link.bOwner, directed: true, labels: { family: 'tunnel', relation: 'contains' } });
    addEdge({ id: `edge:${link.aOwner}--${link.aEndpoint}#contains`, fromId: link.aOwner, toId: link.aEndpoint, directed: true, labels: { family: 'tunnel', relation: 'contains' } });
    addEdge({ id: `edge:${link.bOwner}--${link.bEndpoint}#contains`, fromId: link.bOwner, toId: link.bEndpoint, directed: true, labels: { family: 'tunnel', relation: 'contains' } });
    addEdge({
      id: `edge:${link.aEndpoint}--${link.bEndpoint}#connects`,
      fromId: link.aEndpoint,
      toId: link.bEndpoint,
      directed: false,
      labels: { family: 'tunnel', domain: 'ipsec', relation: 'connects', level: 'vpn_endpoint' },
      properties: {
        tunnel_count: link.tunnelCount,
        tunnel_state: 'up',
        provider_family: link.providerFamily,
        cloud_tunnel_family: link.cloudTunnelFamily,
        vpn_connection_id: link.connectionId,
        display_label: `${link.tunnelCount} tunnel${link.tunnelCount === 1 ? '' : 's'}`,
        presentation: {
          title: 'VPN tunnel',
          hoverTitle: 'VPN tunnel',
          selectionTitle: 'VPN tunnel',
          selectionSubtitle: `${aName} ↔ ${bName}`,
          edgeLabel: `${link.tunnelCount} tunnel${link.tunnelCount === 1 ? '' : 's'}`,
          rows: [
            { key: 'sites', label: 'Sites', value: `${aName} ↔ ${bName}` },
            { key: 'tunnels', label: 'Tunnels', value: link.tunnelCount },
            { key: 'status', label: 'Status', value: 'Up' },
            { key: 'provider', label: 'Provider', value: provider }
          ]
        }
      }
    });
  });

  return {
    graph: {
      graphId: `mock:${graphSuffix}:${scopeType}:${scopeId}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: Array.from(nodes.values()),
      edges: Array.from(edges.values())
    },
    legend: orgVPNLegend(),
    drilldowns: [],
    meta: {
      asOf: now,
      context: { family: 'tunnel', plane: 'underlay', purpose: 'vpn_connection' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

function buildTopologyOrgSitePairLinks(scopeType: string, scopeId: string, aSiteId: string, bSiteId: string) {
  const links = ORG_VPN_LINK_SPECS.filter(
    (link) => (link.aSite === aSiteId && link.bSite === bSiteId) || (link.aSite === bSiteId && link.bSite === aSiteId)
  );
  if (links.length === 0) {
    return respond({ detail: 'Mock site pair not found.' }, 404);
  }
  return buildTopologyOrgVPNDetail(scopeType, scopeId, links, 'org_site_pair_links');
}

function parseDeviceIdFromRef(refId: string): string | null {
  const match = /^ref:inventory\.device:([0-9a-fA-F-]{36})$/.exec(refId);
  return match?.[1] ?? null;
}

function parseIfaceRef(refId: string): { deviceId: string; ifName: string } | null {
  const match = /^ref:inventory\.interface:([0-9a-fA-F-]{36})#(.+)$/.exec(refId);
  if (!match) {
    return null;
  }
  return { deviceId: match[1], ifName: match[2] };
}

type PairLinkSpec = {
  deviceA: string;
  deviceB: string;
  symmetricLags: Array<{
    lagA: string;
    lagB: string;
    members: Array<{ a: string; b: string }>;
  }>;
  directPhysical: Array<{ a: string; b: string }>;
  asymmetricLags?: Array<{
    lagA?: { id: string; members: string[] };
    lagB?: { id: string; members: string[] };
    asymmetryReason: string;
  }>;
};

const PAIR_LINK_SPECS: PairLinkSpec[] = [
  {
    deviceA: DEVICE_NODE_IDS.dist1,
    deviceB: DEVICE_NODE_IDS.access1,
    symmetricLags: [
      {
        lagA: IFACE_NODE_IDS.dist1Po10,
        lagB: IFACE_NODE_IDS.access1Po11,
        members: [
          { a: IFACE_NODE_IDS.dist1Te11, b: IFACE_NODE_IDS.access1Te11 },
          { a: IFACE_NODE_IDS.dist1Te12, b: IFACE_NODE_IDS.access1Te12 }
        ]
      },
      {
        lagA: IFACE_NODE_IDS.dist1Po20,
        lagB: IFACE_NODE_IDS.access1Po21,
        members: [
          { a: IFACE_NODE_IDS.dist1Te13, b: IFACE_NODE_IDS.access1Te13 },
          { a: IFACE_NODE_IDS.dist1Te14, b: IFACE_NODE_IDS.access1Te14 }
        ]
      }
    ],
    directPhysical: []
  },
  {
    deviceA: DEVICE_NODE_IDS.dist1,
    deviceB: DEVICE_NODE_IDS.access2,
    symmetricLags: [
      {
        lagA: IFACE_NODE_IDS.dist1Po30,
        lagB: IFACE_NODE_IDS.access2Po12,
        members: [
          { a: IFACE_NODE_IDS.dist1Te15, b: IFACE_NODE_IDS.access2Te11 },
          { a: IFACE_NODE_IDS.dist1Te16, b: IFACE_NODE_IDS.access2Te12 }
        ]
      }
    ],
    directPhysical: []
  },
  {
    deviceA: DEVICE_NODE_IDS.dist1,
    deviceB: DEVICE_NODE_IDS.border1,
    symmetricLags: [
      {
        lagA: IFACE_NODE_IDS.dist1Po40,
        lagB: IFACE_NODE_IDS.border1Po13,
        members: [
          { a: IFACE_NODE_IDS.dist1Te17, b: IFACE_NODE_IDS.border1Te11 },
          { a: IFACE_NODE_IDS.dist1Te18, b: IFACE_NODE_IDS.border1Te12 }
        ]
      }
    ],
    directPhysical: []
  }
];

function findPairLinkSpec(deviceA: string, deviceB: string): PairLinkSpec | null {
  for (const spec of PAIR_LINK_SPECS) {
    if ((spec.deviceA === deviceA && spec.deviceB === deviceB) || (spec.deviceA === deviceB && spec.deviceB === deviceA)) {
      return spec;
    }
  }
  return null;
}

function buildTopologyPhysicalPairLinks(scopeType: string, scopeId: string, deviceA: string, deviceB: string) {
  const now = new Date().toISOString();
  const spec = findPairLinkSpec(deviceA, deviceB);
  if (!spec) {
    return respond({ detail: 'Mock pair spec not found.' }, 404);
  }

  const nodeById = new Map<string, any>();
  const edgeById = new Map<string, any>();

  const pushNode = (n: any) => {
    if (!n?.id || typeof n.id !== 'string') {
      return;
    }
    if (nodeById.has(n.id)) {
      return;
    }
    nodeById.set(n.id, n);
  };
  const pushEdge = (e: any) => {
    if (!e?.id || typeof e.id !== 'string') {
      return;
    }
    if (edgeById.has(e.id)) {
      return;
    }
    edgeById.set(e.id, e);
  };

  pushNode({
    id: deviceA,
    displayName:
      deviceA === DEVICE_NODE_IDS.dist1
        ? 'Dist1'
        : deviceA === DEVICE_NODE_IDS.access1
          ? 'Access1'
          : deviceA === DEVICE_NODE_IDS.access2
            ? 'Access2'
            : 'Border1',
    labels: { family: 'physical', level: 'device' }
  });
  pushNode({
    id: deviceB,
    displayName:
      deviceB === DEVICE_NODE_IDS.dist1
        ? 'Dist1'
        : deviceB === DEVICE_NODE_IDS.access1
          ? 'Access1'
          : deviceB === DEVICE_NODE_IDS.access2
            ? 'Access2'
            : 'Border1',
    labels: { family: 'physical', level: 'device' }
  });

  const addLagNode = (lagId: string, symmetry?: 'symmetric' | 'asymmetric', asymReason?: string) => {
    const parsed = parseIfaceRef(lagId);
    const display = parsed?.ifName ?? lagId;
    const labels: Record<string, any> = { family: 'physical', level: 'interface', 'if.kind': 'lag' };
    if (symmetry) {
      labels['lag.symmetry'] = symmetry;
    }
    const properties: Record<string, any> = {};
    if (asymReason) {
      properties.lag = { asymmetryReason: asymReason };
    }
    pushNode({ id: lagId, displayName: display, labels, properties });
  };

  const addPhysicalNode = (ifaceId: string) => {
    const parsed = parseIfaceRef(ifaceId);
    const display = parsed?.ifName ?? ifaceId;
    pushNode({
      id: ifaceId,
      displayName: display,
      labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' }
    });
  };

  const addContains = (devId: string, ifaceId: string) => {
    pushEdge({
      id: `edge:${devId}--${ifaceId}#contains`,
      fromId: devId,
      toId: ifaceId,
      directed: true,
      labels: { family: 'physical', relation: 'contains' }
    });
  };

  const addConnects = (a: string, b: string, level?: string, extraLabels?: Record<string, any>) => {
    pushEdge({
      id: `edge:${a}--${b}#connects`,
      fromId: a,
      toId: b,
      directed: false,
      labels: { family: 'physical', relation: 'connects', ...(level ? { level } : {}), ...(extraLabels ?? {}) }
    });
  };

  // symmetric lag bundles: only expose lag_link, not member ports
  (spec.symmetricLags ?? []).forEach((lag) => {
    addLagNode(lag.lagA, 'symmetric');
    addLagNode(lag.lagB, 'symmetric');
    addContains(deviceA, lag.lagA);
    addContains(deviceB, lag.lagB);
    addConnects(lag.lagA, lag.lagB, 'lag_link', { 'lag.symmetry': 'symmetric' });
  });

  // direct physical links (non-member)
  (spec.directPhysical ?? []).forEach((pair) => {
    addPhysicalNode(pair.a);
    addPhysicalNode(pair.b);
    addContains(deviceA, pair.a);
    addContains(deviceB, pair.b);
    addConnects(pair.a, pair.b);
  });

  // asymmetric: include physical member ports + membership indications (both sides as available)
  (spec.asymmetricLags ?? []).forEach((asym) => {
    if (asym.lagA) {
      addLagNode(asym.lagA.id, 'asymmetric', asym.asymmetryReason);
      addContains(deviceA, asym.lagA.id);
      asym.lagA.members.forEach((m) => {
        addPhysicalNode(m);
        addContains(deviceA, m);
        pushEdge({
          id: `edge:${m}--${asym.lagA!.id}#member_of`,
          fromId: m,
          toId: asym.lagA!.id,
          directed: true,
          labels: { family: 'physical', relation: 'member_of' }
        });
      });
    }
    if (asym.lagB) {
      addLagNode(asym.lagB.id, 'asymmetric', asym.asymmetryReason);
      addContains(deviceB, asym.lagB.id);
      asym.lagB.members.forEach((m) => {
        addPhysicalNode(m);
        addContains(deviceB, m);
        pushEdge({
          id: `edge:${m}--${asym.lagB!.id}#member_of`,
          fromId: m,
          toId: asym.lagB!.id,
          directed: true,
          labels: { family: 'physical', relation: 'member_of' }
        });
      });
    }
  });

  return {
    graph: {
      graphId: `mock:physical_pair_links:${scopeType}:${scopeId}:${deviceA}:${deviceB}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: Array.from(nodeById.values()),
      edges: Array.from(edgeById.values())
    },
    legend: {
      nodeRules: [
        { when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'xl' } },
        { when: { 'labels.level': 'interface' }, ui: { icon: 'interface', size: 'sm' } }
      ],
      edgeRules: [
        { when: { 'labels.relation': 'contains' }, ui: { style: 'embed' } },
        { when: { 'labels.relation': 'member_of' }, ui: { style: 'membership' } },
        { when: { 'labels.relation': 'connects' }, ui: { style: 'link' } }
      ]
    },
    drilldowns: [
      {
        name: 'Expand LAG members (overlay)',
        viewId: 'lag_link_members',
        mode: 'overlay',
        appliesWhen: { 'edge.labels.level': 'lag_link', 'edge.labels.lag.symmetry': 'symmetric' },
        dynamic: {
          params: { lagAId: 'edge.fromId', lagBId: 'edge.toId' },
          overlayKey: 'edge.id'
        }
      }
    ],
    meta: {
      asOf: now,
      context: { family: 'physical', plane: 'underlay', purpose: 'connectivity' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

function buildTopologyLagLinkMembers(scopeType: string, scopeId: string, lagAId: string, lagBId: string) {
  const now = new Date().toISOString();
  const spec = PAIR_LINK_SPECS.find((s) => s.symmetricLags.some((l) => (l.lagA === lagAId && l.lagB === lagBId) || (l.lagA === lagBId && l.lagB === lagAId)));
  const lag = spec?.symmetricLags.find((l) => (l.lagA === lagAId && l.lagB === lagBId) || (l.lagA === lagBId && l.lagB === lagAId));
  if (!lag) {
    return respond({ detail: 'Mock lag link not found.' }, 404);
  }

  const devA = `ref:inventory.device:${parseIfaceRef(lag.lagA)?.deviceId ?? ''}`;
  const devB = `ref:inventory.device:${parseIfaceRef(lag.lagB)?.deviceId ?? ''}`;

  const nodeById = new Map<string, any>();
  const edgeById = new Map<string, any>();
  const pushNode = (n: any) => {
    if (!n?.id || typeof n.id !== 'string') {
      return;
    }
    if (nodeById.has(n.id)) {
      return;
    }
    nodeById.set(n.id, n);
  };
  const pushEdge = (e: any) => {
    if (!e?.id || typeof e.id !== 'string') {
      return;
    }
    if (edgeById.has(e.id)) {
      return;
    }
    edgeById.set(e.id, e);
  };

  pushNode({
    id: devA,
    displayName: devA === DEVICE_NODE_IDS.dist1 ? 'Dist1' : devA === DEVICE_NODE_IDS.access1 ? 'Access1' : 'Access2',
    labels: { family: 'physical', level: 'device' }
  });
  pushNode({
    id: devB,
    displayName: devB === DEVICE_NODE_IDS.dist1 ? 'Dist1' : devB === DEVICE_NODE_IDS.access1 ? 'Access1' : 'Access2',
    labels: { family: 'physical', level: 'device' }
  });
  pushNode({
    id: lag.lagA,
    displayName: parseIfaceRef(lag.lagA)?.ifName ?? 'Po',
    labels: { family: 'physical', level: 'interface', 'if.kind': 'lag', 'lag.symmetry': 'symmetric' }
  });
  pushNode({
    id: lag.lagB,
    displayName: parseIfaceRef(lag.lagB)?.ifName ?? 'Po',
    labels: { family: 'physical', level: 'interface', 'if.kind': 'lag', 'lag.symmetry': 'symmetric' }
  });

  pushEdge({
    id: `edge:${devA}--${lag.lagA}#contains`,
    fromId: devA,
    toId: lag.lagA,
    directed: true,
    labels: { family: 'physical', relation: 'contains' }
  });
  pushEdge({
    id: `edge:${devB}--${lag.lagB}#contains`,
    fromId: devB,
    toId: lag.lagB,
    directed: true,
    labels: { family: 'physical', relation: 'contains' }
  });

  lag.members.forEach((pair, idx) => {
    pushNode({
      id: pair.a,
      displayName: parseIfaceRef(pair.a)?.ifName ?? pair.a,
      labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' }
    });
    pushNode({
      id: pair.b,
      displayName: parseIfaceRef(pair.b)?.ifName ?? pair.b,
      labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' }
    });
    pushEdge({
      id: `edge:${devA}--${pair.a}#contains`,
      fromId: devA,
      toId: pair.a,
      directed: true,
      labels: { family: 'physical', relation: 'contains' }
    });
    pushEdge({
      id: `edge:${devB}--${pair.b}#contains`,
      fromId: devB,
      toId: pair.b,
      directed: true,
      labels: { family: 'physical', relation: 'contains' }
    });
    pushEdge({
      id: `edge:${pair.a}--${lag.lagA}#member_of`,
      fromId: pair.a,
      toId: lag.lagA,
      directed: true,
      labels: { family: 'physical', relation: 'member_of' }
    });
    pushEdge({
      id: `edge:${pair.b}--${lag.lagB}#member_of`,
      fromId: pair.b,
      toId: lag.lagB,
      directed: true,
      labels: { family: 'physical', relation: 'member_of' }
    });
    pushEdge({
      id: `edge:${pair.a}--${pair.b}#connects:${idx}`,
      fromId: pair.a,
      toId: pair.b,
      directed: false,
      labels: { family: 'physical', relation: 'connects' }
    });
  });

  return {
    graph: {
      graphId: `mock:lag_link_members:${scopeType}:${scopeId}:${lag.lagA}:${lag.lagB}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: Array.from(nodeById.values()),
      edges: Array.from(edgeById.values())
    },
    legend: {
      nodeRules: [
        { when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'xl' } },
        { when: { 'labels.level': 'interface' }, ui: { icon: 'interface', size: 'sm' } }
      ],
      edgeRules: [
        { when: { 'labels.relation': 'contains' }, ui: { style: 'embed' } },
        { when: { 'labels.relation': 'member_of' }, ui: { style: 'membership' } },
        { when: { 'labels.relation': 'connects' }, ui: { style: 'link' } }
      ]
    },
    drilldowns: [],
    meta: {
      asOf: now,
      context: { family: 'physical', plane: 'underlay', purpose: 'connectivity' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

function buildTopologyPhysicalPorts(scopeType: string, scopeId: string, rootNodeId?: string) {
  const now = new Date().toISOString();
  const root = rootNodeId && rootNodeId.startsWith('ref:inventory.device:') ? rootNodeId : DEVICE_NODE_IDS.dist1;
  const includeBorder = root === DEVICE_NODE_IDS.border1 || root === DEVICE_NODE_IDS.dist1;
  return {
    graph: {
      graphId: `mock:physical_ports:${scopeType}:${scopeId}:${root}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: [
        { id: DEVICE_NODE_IDS.dist1, displayName: 'Dist1', labels: { family: 'physical', level: 'device' } },
        { id: DEVICE_NODE_IDS.access1, displayName: 'Access1', labels: { family: 'physical', level: 'device' } },
        { id: DEVICE_NODE_IDS.access2, displayName: 'Access2', labels: { family: 'physical', level: 'device' } },
        ...(includeBorder ? [{ id: DEVICE_NODE_IDS.border1, displayName: 'Border1', labels: { family: 'physical', level: 'device' } }] : []),
        { id: IFACE_NODE_IDS.dist1Po10, displayName: 'Po10', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        { id: IFACE_NODE_IDS.dist1Po20, displayName: 'Po20', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        { id: IFACE_NODE_IDS.dist1Po30, displayName: 'Po30', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        ...(includeBorder ? [{ id: IFACE_NODE_IDS.dist1Po40, displayName: 'Po40', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } }] : []),
        { id: IFACE_NODE_IDS.dist1Te11, displayName: 'Te1/0/1', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.dist1Te12, displayName: 'Te1/0/2', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.dist1Te13, displayName: 'Te1/0/3', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.dist1Te14, displayName: 'Te1/0/4', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.dist1Te15, displayName: 'Te1/0/5', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.dist1Te16, displayName: 'Te1/0/6', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        ...(includeBorder ? [{ id: IFACE_NODE_IDS.dist1Te17, displayName: 'Te1/0/7', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } }] : []),
        ...(includeBorder ? [{ id: IFACE_NODE_IDS.dist1Te18, displayName: 'Te1/0/8', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } }] : []),
        { id: IFACE_NODE_IDS.access1Te11, displayName: 'Te1/0/1', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.access1Te12, displayName: 'Te1/0/2', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.access1Te13, displayName: 'Te1/0/3', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.access1Te14, displayName: 'Te1/0/4', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.access1Po11, displayName: 'Po11', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        { id: IFACE_NODE_IDS.access1Po21, displayName: 'Po21', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        { id: IFACE_NODE_IDS.access2Te11, displayName: 'Te1/0/1', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.access2Te12, displayName: 'Te1/0/2', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } },
        { id: IFACE_NODE_IDS.access2Po12, displayName: 'Po12', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        ...(includeBorder ? [{ id: IFACE_NODE_IDS.border1Te11, displayName: 'Te1/0/1', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } }] : []),
        ...(includeBorder ? [{ id: IFACE_NODE_IDS.border1Te12, displayName: 'Te1/0/2', labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' } }] : []),
        ...(includeBorder ? [{ id: IFACE_NODE_IDS.border1Po13, displayName: 'Po13', labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } }] : [])
      ],
      edges: [
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Po10}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Po10, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Po20}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Po20, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Po30}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Po30, directed: true, labels: { family: 'physical', relation: 'contains' } },
        ...(includeBorder ? [{ id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Po40}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Po40, directed: true, labels: { family: 'physical', relation: 'contains' } }] : []),
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te11}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te11, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te12}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te12, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te13}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te13, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te14}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te14, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te15}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te15, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te16}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te16, directed: true, labels: { family: 'physical', relation: 'contains' } },
        ...(includeBorder ? [{ id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te17}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te17, directed: true, labels: { family: 'physical', relation: 'contains' } }] : []),
        ...(includeBorder ? [{ id: `edge:${DEVICE_NODE_IDS.dist1}--${IFACE_NODE_IDS.dist1Te18}#contains`, fromId: DEVICE_NODE_IDS.dist1, toId: IFACE_NODE_IDS.dist1Te18, directed: true, labels: { family: 'physical', relation: 'contains' } }] : []),
        { id: `edge:${DEVICE_NODE_IDS.access1}--${IFACE_NODE_IDS.access1Te11}#contains`, fromId: DEVICE_NODE_IDS.access1, toId: IFACE_NODE_IDS.access1Te11, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access1}--${IFACE_NODE_IDS.access1Po11}#contains`, fromId: DEVICE_NODE_IDS.access1, toId: IFACE_NODE_IDS.access1Po11, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access1}--${IFACE_NODE_IDS.access1Te12}#contains`, fromId: DEVICE_NODE_IDS.access1, toId: IFACE_NODE_IDS.access1Te12, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access1}--${IFACE_NODE_IDS.access1Te13}#contains`, fromId: DEVICE_NODE_IDS.access1, toId: IFACE_NODE_IDS.access1Te13, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access1}--${IFACE_NODE_IDS.access1Te14}#contains`, fromId: DEVICE_NODE_IDS.access1, toId: IFACE_NODE_IDS.access1Te14, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access1}--${IFACE_NODE_IDS.access1Po21}#contains`, fromId: DEVICE_NODE_IDS.access1, toId: IFACE_NODE_IDS.access1Po21, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access2}--${IFACE_NODE_IDS.access2Te11}#contains`, fromId: DEVICE_NODE_IDS.access2, toId: IFACE_NODE_IDS.access2Te11, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access2}--${IFACE_NODE_IDS.access2Te12}#contains`, fromId: DEVICE_NODE_IDS.access2, toId: IFACE_NODE_IDS.access2Te12, directed: true, labels: { family: 'physical', relation: 'contains' } },
        { id: `edge:${DEVICE_NODE_IDS.access2}--${IFACE_NODE_IDS.access2Po12}#contains`, fromId: DEVICE_NODE_IDS.access2, toId: IFACE_NODE_IDS.access2Po12, directed: true, labels: { family: 'physical', relation: 'contains' } },
        ...(includeBorder ? [{ id: `edge:${DEVICE_NODE_IDS.border1}--${IFACE_NODE_IDS.border1Te11}#contains`, fromId: DEVICE_NODE_IDS.border1, toId: IFACE_NODE_IDS.border1Te11, directed: true, labels: { family: 'physical', relation: 'contains' } }] : []),
        ...(includeBorder ? [{ id: `edge:${DEVICE_NODE_IDS.border1}--${IFACE_NODE_IDS.border1Te12}#contains`, fromId: DEVICE_NODE_IDS.border1, toId: IFACE_NODE_IDS.border1Te12, directed: true, labels: { family: 'physical', relation: 'contains' } }] : []),
        ...(includeBorder ? [{ id: `edge:${DEVICE_NODE_IDS.border1}--${IFACE_NODE_IDS.border1Po13}#contains`, fromId: DEVICE_NODE_IDS.border1, toId: IFACE_NODE_IDS.border1Po13, directed: true, labels: { family: 'physical', relation: 'contains' } }] : []),
        { id: `edge:${IFACE_NODE_IDS.dist1Te11}--${IFACE_NODE_IDS.access1Te11}#connects`, fromId: IFACE_NODE_IDS.dist1Te11, toId: IFACE_NODE_IDS.access1Te11, directed: false, labels: { family: 'physical', relation: 'connects' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te12}--${IFACE_NODE_IDS.access1Te12}#connects`, fromId: IFACE_NODE_IDS.dist1Te12, toId: IFACE_NODE_IDS.access1Te12, directed: false, labels: { family: 'physical', relation: 'connects' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te13}--${IFACE_NODE_IDS.access1Te13}#connects`, fromId: IFACE_NODE_IDS.dist1Te13, toId: IFACE_NODE_IDS.access1Te13, directed: false, labels: { family: 'physical', relation: 'connects' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te14}--${IFACE_NODE_IDS.access1Te14}#connects`, fromId: IFACE_NODE_IDS.dist1Te14, toId: IFACE_NODE_IDS.access1Te14, directed: false, labels: { family: 'physical', relation: 'connects' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te15}--${IFACE_NODE_IDS.access2Te11}#connects`, fromId: IFACE_NODE_IDS.dist1Te15, toId: IFACE_NODE_IDS.access2Te11, directed: false, labels: { family: 'physical', relation: 'connects' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te16}--${IFACE_NODE_IDS.access2Te12}#connects`, fromId: IFACE_NODE_IDS.dist1Te16, toId: IFACE_NODE_IDS.access2Te12, directed: false, labels: { family: 'physical', relation: 'connects' } },
        ...(includeBorder ? [{ id: `edge:${IFACE_NODE_IDS.dist1Te17}--${IFACE_NODE_IDS.border1Te11}#connects`, fromId: IFACE_NODE_IDS.dist1Te17, toId: IFACE_NODE_IDS.border1Te11, directed: false, labels: { family: 'physical', relation: 'connects' } }] : []),
        ...(includeBorder ? [{ id: `edge:${IFACE_NODE_IDS.dist1Te18}--${IFACE_NODE_IDS.border1Te12}#connects`, fromId: IFACE_NODE_IDS.dist1Te18, toId: IFACE_NODE_IDS.border1Te12, directed: false, labels: { family: 'physical', relation: 'connects' } }] : []),
        { id: `edge:${IFACE_NODE_IDS.dist1Te11}--${IFACE_NODE_IDS.dist1Po10}#member`, fromId: IFACE_NODE_IDS.dist1Te11, toId: IFACE_NODE_IDS.dist1Po10, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te12}--${IFACE_NODE_IDS.dist1Po10}#member`, fromId: IFACE_NODE_IDS.dist1Te12, toId: IFACE_NODE_IDS.dist1Po10, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.access1Te11}--${IFACE_NODE_IDS.access1Po11}#member`, fromId: IFACE_NODE_IDS.access1Te11, toId: IFACE_NODE_IDS.access1Po11, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.access1Te12}--${IFACE_NODE_IDS.access1Po11}#member`, fromId: IFACE_NODE_IDS.access1Te12, toId: IFACE_NODE_IDS.access1Po11, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te13}--${IFACE_NODE_IDS.dist1Po20}#member`, fromId: IFACE_NODE_IDS.dist1Te13, toId: IFACE_NODE_IDS.dist1Po20, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te14}--${IFACE_NODE_IDS.dist1Po20}#member`, fromId: IFACE_NODE_IDS.dist1Te14, toId: IFACE_NODE_IDS.dist1Po20, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.access1Te13}--${IFACE_NODE_IDS.access1Po21}#member`, fromId: IFACE_NODE_IDS.access1Te13, toId: IFACE_NODE_IDS.access1Po21, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.access1Te14}--${IFACE_NODE_IDS.access1Po21}#member`, fromId: IFACE_NODE_IDS.access1Te14, toId: IFACE_NODE_IDS.access1Po21, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te15}--${IFACE_NODE_IDS.dist1Po30}#member`, fromId: IFACE_NODE_IDS.dist1Te15, toId: IFACE_NODE_IDS.dist1Po30, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.dist1Te16}--${IFACE_NODE_IDS.dist1Po30}#member`, fromId: IFACE_NODE_IDS.dist1Te16, toId: IFACE_NODE_IDS.dist1Po30, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.access2Te11}--${IFACE_NODE_IDS.access2Po12}#member`, fromId: IFACE_NODE_IDS.access2Te11, toId: IFACE_NODE_IDS.access2Po12, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        { id: `edge:${IFACE_NODE_IDS.access2Te12}--${IFACE_NODE_IDS.access2Po12}#member`, fromId: IFACE_NODE_IDS.access2Te12, toId: IFACE_NODE_IDS.access2Po12, directed: true, labels: { family: 'physical', relation: 'member_of' } },
        ...(includeBorder ? [{ id: `edge:${IFACE_NODE_IDS.dist1Te17}--${IFACE_NODE_IDS.dist1Po40}#member`, fromId: IFACE_NODE_IDS.dist1Te17, toId: IFACE_NODE_IDS.dist1Po40, directed: true, labels: { family: 'physical', relation: 'member_of' } }] : []),
        ...(includeBorder ? [{ id: `edge:${IFACE_NODE_IDS.dist1Te18}--${IFACE_NODE_IDS.dist1Po40}#member`, fromId: IFACE_NODE_IDS.dist1Te18, toId: IFACE_NODE_IDS.dist1Po40, directed: true, labels: { family: 'physical', relation: 'member_of' } }] : []),
        ...(includeBorder ? [{ id: `edge:${IFACE_NODE_IDS.border1Te11}--${IFACE_NODE_IDS.border1Po13}#member`, fromId: IFACE_NODE_IDS.border1Te11, toId: IFACE_NODE_IDS.border1Po13, directed: true, labels: { family: 'physical', relation: 'member_of' } }] : []),
        ...(includeBorder ? [{ id: `edge:${IFACE_NODE_IDS.border1Te12}--${IFACE_NODE_IDS.border1Po13}#member`, fromId: IFACE_NODE_IDS.border1Te12, toId: IFACE_NODE_IDS.border1Po13, directed: true, labels: { family: 'physical', relation: 'member_of' } }] : [])
      ]
    },
    legend: {
      nodeRules: [
        { when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'xl' } },
        { when: { 'labels.level': 'interface' }, ui: { icon: 'interface', size: 'sm', renderAs: 'port_dot' } }
      ],
      edgeRules: [
        { when: { 'labels.relation': 'contains' }, ui: { style: 'embed' } },
        { when: { 'labels.relation': 'member_of' }, ui: { style: 'membership' } },
        { when: { 'labels.relation': 'connects' }, ui: { style: 'link' } }
      ]
    },
    drilldowns: [
      {
        name: 'Show LAG members (overlay)',
        viewId: 'lag_members',
        mode: 'overlay',
        appliesWhen: { 'node.labels.level': 'interface', 'node.labels.if.kind': 'lag' },
        dynamic: { limits: { rootNodeId: 'node.id' }, overlayKey: 'node.id' }
      }
    ],
    meta: {
      asOf: now,
      context: { family: 'physical', plane: 'underlay', purpose: 'connectivity' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

function buildTopologyRoutingAdjacency(scopeType: string, scopeId: string, rootNodeId?: string) {
  const now = new Date().toISOString();
  const root = rootNodeId && rootNodeId.startsWith('ref:inventory.device:') ? rootNodeId : DEVICE_NODE_IDS.border1;
  const focusedNodeIds = new Set<string>([root, DEVICE_NODE_IDS.dist1, DEVICE_NODE_IDS.access2]);
  focusedNodeIds.add(DEVICE_NODE_IDS.border1);

  return {
    graph: {
      graphId: `mock:routing_adjacency:${scopeType}:${scopeId}:${root}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: [
        { id: DEVICE_NODE_IDS.dist1, displayName: 'Dist1', labels: { family: 'physical', level: 'device' } },
        { id: DEVICE_NODE_IDS.access2, displayName: 'Access2', labels: { family: 'physical', level: 'device' } },
        { id: DEVICE_NODE_IDS.border1, displayName: 'Border1', labels: { family: 'physical', level: 'device' } }
      ].filter((node) => focusedNodeIds.has(node.id)),
      edges: [
        {
          id: `edge:device:${DEVICE_NODE_IDS.border1}--${DEVICE_NODE_IDS.dist1}`,
          fromId: DEVICE_NODE_IDS.border1,
          toId: DEVICE_NODE_IDS.dist1,
          directed: false,
          labels: { family: 'control', relation: 'connects', domain: 'bgp', level: 'device_link', instance: 'bgp:default' },
          properties: {
            'bgp.health': 'healthy',
            'bgp.sample_sides': 2,
            'bgp.established_ratio': 1,
            counts: { process: { from: 1, to: 1 }, session: { from: 1, to: 1 } },
            process: [{
              from: { bgp_instance: 'default', vrf: 'default', local_as: '65101', router_id: '10.255.255.1' },
              to: { bgp_instance: 'default', vrf: 'default', local_as: '65221', router_id: '10.255.255.2' },
              sessions: [{
                from: { local_as: '65101', remote_as: '65221', local_ip: '10.0.0.1', peer_ip: '10.0.0.2' },
                to: { local_as: '65221', remote_as: '65101', local_ip: '10.0.0.2', peer_ip: '10.0.0.1' }
              }]
            }]
          }
        },
        {
          id: `edge:device:${DEVICE_NODE_IDS.dist1}--${DEVICE_NODE_IDS.access2}`,
          fromId: DEVICE_NODE_IDS.dist1,
          toId: DEVICE_NODE_IDS.access2,
          directed: false,
          labels: { family: 'control', relation: 'connects', domain: 'bgp', level: 'device_link', instance: 'bgp:default' },
          properties: {
            'bgp.health': 'down',
            'bgp.sample_sides': 2,
            'bgp.established_ratio': 0,
            counts: { process: { from: 1, to: 1 }, session: { from: 1, to: 1 } },
            process: [{
              from: { bgp_instance: 'default', vrf: 'default', local_as: '65221', router_id: '10.255.255.2' },
              to: { bgp_instance: 'default', vrf: 'default', local_as: '65231', router_id: '10.255.255.3' },
              sessions: [{
                from: { local_as: '65221', remote_as: '65231', local_ip: '10.0.1.1', peer_ip: '10.0.1.2' },
                to: { local_as: '65231', remote_as: '65221', local_ip: '10.0.1.2', peer_ip: '10.0.1.1' }
              }]
            }]
          }
        }
      ].filter((edge) => focusedNodeIds.has(edge.fromId) && focusedNodeIds.has(edge.toId))
    },
    legend: {
      nodeRules: [{ when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'xl' } }],
      edgeRules: [
        { when: { 'labels.level': 'device_link', 'labels.relation': 'connects', 'properties.bgp.health': 'down' }, ui: { style: 'link', stroke: '#dc2626', width: 2 } },
        { when: { 'labels.level': 'device_link', 'labels.relation': 'connects', 'properties.bgp.health': 'degraded' }, ui: { style: 'link', stroke: '#d97706', width: 2 } },
        { when: { 'labels.level': 'device_link', 'labels.relation': 'connects', 'properties.bgp.health': 'unknown' }, ui: { style: 'link', stroke: '#6b7280', width: 2 } },
        { when: { 'labels.level': 'device_link', 'labels.relation': 'connects', 'properties.bgp.health': 'healthy' }, ui: { style: 'link', stroke: '#16a34a', width: 2 } },
        { when: { 'labels.level': 'device_link', 'labels.relation': 'connects' }, ui: { style: 'link' } }
      ]
    },
    drilldowns: [],
    meta: {
      asOf: now,
      context: { family: 'physical', plane: 'underlay', purpose: 'routing_adjacency' },
      viewVersion: '1.0',
      topics: ['state', 'routing']
    }
  };
}

function buildTopologyLagMembers(scopeType: string, scopeId: string, rootNodeId?: string) {
  const now = new Date().toISOString();
  const rootLag = rootNodeId && rootNodeId.startsWith('ref:inventory.interface:') ? rootNodeId : IFACE_NODE_IDS.dist1Po10;

  const lagSpec: Record<
    string,
    { deviceId: string; lagDisplayName: string; members: Array<{ id: string; displayName: string }> }
  > = {
    [IFACE_NODE_IDS.dist1Po10]: {
      deviceId: DEVICE_NODE_IDS.dist1,
      lagDisplayName: 'Po10',
      members: [
        { id: IFACE_NODE_IDS.dist1Te11, displayName: 'Te1/0/1' },
        { id: IFACE_NODE_IDS.dist1Te12, displayName: 'Te1/0/2' }
      ]
    },
    [IFACE_NODE_IDS.access1Po11]: {
      deviceId: DEVICE_NODE_IDS.access1,
      lagDisplayName: 'Po11',
      members: [
        { id: IFACE_NODE_IDS.access1Te11, displayName: 'Te1/0/1' },
        { id: IFACE_NODE_IDS.access1Te12, displayName: 'Te1/0/2' }
      ]
    },
    [IFACE_NODE_IDS.dist1Po20]: {
      deviceId: DEVICE_NODE_IDS.dist1,
      lagDisplayName: 'Po20',
      members: [
        { id: IFACE_NODE_IDS.dist1Te13, displayName: 'Te1/0/3' },
        { id: IFACE_NODE_IDS.dist1Te14, displayName: 'Te1/0/4' }
      ]
    },
    [IFACE_NODE_IDS.access1Po21]: {
      deviceId: DEVICE_NODE_IDS.access1,
      lagDisplayName: 'Po21',
      members: [
        { id: IFACE_NODE_IDS.access1Te13, displayName: 'Te1/0/3' },
        { id: IFACE_NODE_IDS.access1Te14, displayName: 'Te1/0/4' }
      ]
    },
    [IFACE_NODE_IDS.dist1Po30]: {
      deviceId: DEVICE_NODE_IDS.dist1,
      lagDisplayName: 'Po30',
      members: [
        { id: IFACE_NODE_IDS.dist1Te15, displayName: 'Te1/0/5' },
        { id: IFACE_NODE_IDS.dist1Te16, displayName: 'Te1/0/6' }
      ]
    },
    [IFACE_NODE_IDS.access2Po12]: {
      deviceId: DEVICE_NODE_IDS.access2,
      lagDisplayName: 'Po12',
      members: [
        { id: IFACE_NODE_IDS.access2Te11, displayName: 'Te1/0/1' },
        { id: IFACE_NODE_IDS.access2Te12, displayName: 'Te1/0/2' }
      ]
    },
    [IFACE_NODE_IDS.dist1Po40]: {
      deviceId: DEVICE_NODE_IDS.dist1,
      lagDisplayName: 'Po40',
      members: [
        { id: IFACE_NODE_IDS.dist1Te17, displayName: 'Te1/0/7' },
        { id: IFACE_NODE_IDS.dist1Te18, displayName: 'Te1/0/8' }
      ]
    },
    [IFACE_NODE_IDS.border1Po13]: {
      deviceId: DEVICE_NODE_IDS.border1,
      lagDisplayName: 'Po13',
      members: [
        { id: IFACE_NODE_IDS.border1Te11, displayName: 'Te1/0/1' },
        { id: IFACE_NODE_IDS.border1Te12, displayName: 'Te1/0/2' }
      ]
    }
  };

  const spec = lagSpec[rootLag] ?? lagSpec[IFACE_NODE_IDS.dist1Po10];
  const rootDeviceId = spec.deviceId;
  return {
    graph: {
      graphId: `mock:lag_members:${scopeType}:${scopeId}:${rootLag}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      nodes: [
        { id: rootDeviceId, displayName: rootDeviceId === DEVICE_NODE_IDS.access1 ? 'Access1' : 'Dist1', labels: { family: 'physical', level: 'device' } },
        { id: rootLag, displayName: spec.lagDisplayName, labels: { family: 'physical', level: 'interface', 'if.kind': 'lag' } },
        ...spec.members.map((member) => ({
          id: member.id,
          displayName: member.displayName,
          labels: { family: 'physical', level: 'interface', 'if.kind': 'physical' }
        }))
      ],
      edges: [
        { id: `edge:${rootDeviceId}--${rootLag}#contains`, fromId: rootDeviceId, toId: rootLag, directed: true, labels: { family: 'physical', relation: 'contains' } },
        ...spec.members.map((member) => ({
          id: `edge:${member.id}--${rootLag}#member`,
          fromId: member.id,
          toId: rootLag,
          directed: true,
          labels: { family: 'physical', relation: 'member_of' }
        }))
      ]
    },
    legend: {
      nodeRules: [
        { when: { 'labels.level': 'device' }, ui: { icon: 'device' } },
        { when: { 'labels.level': 'interface' }, ui: { icon: 'interface', size: 'sm' } }
      ],
      edgeRules: [
        { when: { 'labels.relation': 'contains' }, ui: { style: 'embed' } },
        { when: { 'labels.relation': 'member_of' }, ui: { style: 'membership' } }
      ]
    },
    drilldowns: [],
    meta: {
      asOf: now,
      context: { family: 'physical', plane: 'underlay', purpose: 'connectivity' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

type MockL3MembershipRecord = {
  deviceId: string;
  deviceNodeId: string;
  deviceName: string;
  vrf: string;
  vrfNodeId: string;
  interfaceName: string;
  interfaceNodeId: string;
  interfaceKind: 'lag' | 'l3' | 'svi' | 'loopback' | 'tunnel' | 'subif';
  adminState: 'up' | 'down' | 'testing' | 'unknown';
  operState: 'up' | 'down' | 'testing' | 'unknown';
  macAddress: string;
  addresses: string[];
  subnet: string;
  subnetNodeId: string;
  runtimeProperties?: Record<string, string | number>;
};

type MockL3DeviceKey = keyof typeof TOPO_DEVICE_IDS;

const canonicalMockL3VRF = (value: unknown): string => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase().replace(/^vrf:/, '') : '';
  switch (normalized) {
    case 'management':
      return 'mgmt';
    default:
      return normalized;
  }
};

const isManagementMockL3VRF = (value: string): boolean => canonicalMockL3VRF(value) === 'mgmt';

const mockL3MembershipRecord = (
  deviceKey: MockL3DeviceKey,
  deviceName: string,
  vrf: string,
  interfaceName: string,
  interfaceKind: 'lag' | 'l3' | 'svi' | 'loopback' | 'tunnel' | 'subif',
  adminState: 'up' | 'down' | 'testing' | 'unknown',
  operState: 'up' | 'down' | 'testing' | 'unknown',
  macAddress: string,
  addresses: string[],
  subnet: string,
  runtimeProperties?: Record<string, string | number>
): MockL3MembershipRecord => ({
  deviceId: TOPO_DEVICE_IDS[deviceKey],
  deviceNodeId: DEVICE_NODE_IDS[deviceKey],
  deviceName,
  vrf,
  vrfNodeId: `ref:derived:ri:${TOPO_DEVICE_IDS[deviceKey]}#${vrf}`,
  interfaceName,
  interfaceNodeId: `ref:derived:interface:l3:${TOPO_DEVICE_IDS[deviceKey]}#${interfaceName}`,
  interfaceKind,
  adminState,
  operState,
  macAddress,
  addresses,
  subnet,
  subnetNodeId: `ref:derived:l3:subnet:${subnet}`,
  ...(runtimeProperties ? { runtimeProperties } : {})
});

const mockL3MembershipRecords = (): MockL3MembershipRecord[] => [
  mockL3MembershipRecord(
    'dist1',
    'Dist1',
    'blue',
    'Vl120',
    'svi',
    'up',
    'up',
    '78:02:b1:ea:b0:c6',
    ['198.51.120.1/24'],
    '198.51.120.0/24',
    {
      'l3.addr.health': 'preferred',
      'qos.queue_depth': 18,
      'traffic.util_pct': 62.4,
      'traffic.out_bps': 148200000,
      'traffic.in_bps': 121500000,
      'traffic.out_pps': 12400,
      'traffic.in_pps': 9700,
      'traffic.out_error_pct': 0.12,
      'traffic.in_error_pct': 0.04
    }
  ),
  mockL3MembershipRecord(
    'dist1',
    'Dist1',
    'default',
    'Po10',
    'lag',
    'up',
    'down',
    '78:02:b1:ea:b0:90',
    ['192.0.2.1/30'],
    '192.0.2.0/30',
    {
      'traffic.util_pct': 87.4,
      'traffic.out_bps': 920000000,
      'traffic.in_bps': 811000000,
      'traffic.out_pps': 84500,
      'traffic.in_pps': 73200,
      'traffic.out_error_pct': 0.42,
      'traffic.in_error_pct': 0.18,
      'traffic.out_discard_pct': 0.08
    }
  ),
  mockL3MembershipRecord('dist1', 'Dist1', 'default', 'Lo0', 'loopback', 'up', 'down', '', ['10.255.0.1/32'], '10.255.0.1/32'),
  mockL3MembershipRecord(
    'dist1',
    'Dist1',
    'default',
    'tunnel.1',
    'tunnel',
    'up',
    'testing',
    '',
    ['169.254.10.1/30'],
    '169.254.10.0/30',
    {
      'overlay.reachability': 'degraded',
      'traffic.in_bps': 8300000,
      'traffic.out_bps': 7900000,
      'traffic.in_pps': 2100,
      'traffic.out_pps': 1980,
      'traffic.util_pct': 14.2,
      'l3.pan.tcp_cps': 114,
      'l3.pan.udp_cps': 22,
      'l3.pan.other_ip_cps': 7
    }
  ),

  mockL3MembershipRecord(
    'access1',
    'Access1',
    'default',
    'Po11',
    'lag',
    'up',
    'up',
    'c4:c6:03:54:80:90',
    ['192.0.2.2/30'],
    '192.0.2.0/30',
    {
      'traffic.util_pct': 31.2,
      'traffic.in_bps': 212000000,
      'traffic.out_bps': 185000000,
      'traffic.in_pps': 18100,
      'traffic.out_pps': 16400,
      'traffic.in_error_pct': 0.03,
      'traffic.out_error_pct': 0.01,
      'traffic.out_discard_pct': 0.02
    }
  ),
  mockL3MembershipRecord('access1', 'Access1', 'default', 'Vl60', 'svi', 'down', 'down', '', ['198.51.120.11/24'], '198.51.120.0/24'),

  mockL3MembershipRecord(
    'access2',
    'Access2',
    'blue',
    'Vl120',
    'svi',
    'up',
    'up',
    '70:c9:c6:6c:c4:c6',
    ['198.51.120.10/24'],
    '198.51.120.0/24',
    {
      'l3.addr.health': 'preferred',
      'traffic.util_pct': 42.1,
      'traffic.out_bps': 185000000,
      'traffic.in_bps': 162000000,
      'traffic.out_pps': 15200,
      'traffic.in_pps': 13600,
      'traffic.out_error_pct': 0.06,
      'traffic.in_error_pct': 0.02
    }
  ),
  mockL3MembershipRecord('access2', 'Access2', 'blue', 'Et1/5.530', 'subif', 'up', 'testing', '70:c9:c6:6c:c5:30', ['172.16.53.1/30'], '172.16.53.0/30'),
  mockL3MembershipRecord('access2', 'Access2', 'guest', 'Et30', 'l3', 'unknown', 'unknown', '', ['192.0.2.3/30'], '192.0.2.0/30')
];

type MockRouteTableSeedRow = {
  prefix: string;
  nextHop?: string;
  resolvedEgressInterface?: string;
  protocol: string;
  metric?: number;
  preference?: number;
};

const MOCK_ROUTE_TABLE_SITE_ID_BY_DEVICE_ID: Record<string, string> = {
  [TOPO_DEVICE_IDS.dist1]: 'site-sfo',
  [TOPO_DEVICE_IDS.access1]: 'site-sfo',
  [TOPO_DEVICE_IDS.access2]: 'site-sfo',
  [TOPO_DEVICE_IDS.border1]: 'site-nyc'
};

const MOCK_ROUTE_TABLES_BY_DEVICE_ID: Record<
  string,
  Record<string, { vendor: string; os: string; commandId: string; rows: MockRouteTableSeedRow[] }>
> = {
  [TOPO_DEVICE_IDS.dist1]: {
    blue: {
      vendor: 'cisco',
      os: 'iosxe',
      commandId: 'show_ip_route',
      rows: [
        { prefix: '0.0.0.0/0', nextHop: '203.0.113.1', resolvedEgressInterface: 'Po10', protocol: 'static', metric: 1, preference: 1 },
        { prefix: '198.51.120.0/24', resolvedEgressInterface: 'Vl120', protocol: 'connected', metric: 0, preference: 0 },
        { prefix: '198.51.121.0/24', nextHop: '198.51.120.254', resolvedEgressInterface: 'Vl120', protocol: 'ospf', metric: 20, preference: 110 }
      ]
    },
    default: {
      vendor: 'cisco',
      os: 'iosxe',
      commandId: 'show_ip_route',
      rows: [
        { prefix: '192.0.2.0/30', resolvedEgressInterface: 'Po10', protocol: 'connected', metric: 0, preference: 0 },
        { prefix: '198.51.60.0/24', nextHop: '192.0.2.2', resolvedEgressInterface: 'Po10', protocol: 'static', metric: 10, preference: 1 },
        { prefix: '198.51.61.0/24', nextHop: '192.0.2.2', resolvedEgressInterface: 'Po10', protocol: 'bgp', metric: 100, preference: 20 }
      ]
    }
  },
  [TOPO_DEVICE_IDS.access1]: {
    default: {
      vendor: 'arista',
      os: 'eos',
      commandId: 'show_ip_route',
      rows: [
        { prefix: '0.0.0.0/0', nextHop: '192.0.2.1', resolvedEgressInterface: 'Po11', protocol: 'static', metric: 0, preference: 1 },
        { prefix: '192.0.2.0/30', resolvedEgressInterface: 'Po11', protocol: 'connected', metric: 0, preference: 0 },
        { prefix: '198.51.120.0/24', resolvedEgressInterface: 'Vl60', protocol: 'connected', metric: 0, preference: 0 }
      ]
    }
  },
  [TOPO_DEVICE_IDS.access2]: {
    blue: {
      vendor: 'arista',
      os: 'eos',
      commandId: 'show_ip_route',
      rows: [
        { prefix: '0.0.0.0/0', nextHop: '198.51.120.1', resolvedEgressInterface: 'Vl120', protocol: 'static', metric: 0, preference: 1 },
        { prefix: '198.51.120.0/24', resolvedEgressInterface: 'Vl120', protocol: 'connected', metric: 0, preference: 0 },
        { prefix: '203.0.113.0/24', nextHop: '198.51.120.254', resolvedEgressInterface: 'Vl120', protocol: 'bgp', metric: 100, preference: 20 }
      ]
    },
    guest: {
      vendor: 'arista',
      os: 'eos',
      commandId: 'show_ip_route',
      rows: [
        { prefix: '0.0.0.0/0', nextHop: '192.0.2.1', resolvedEgressInterface: 'Et30', protocol: 'static', metric: 0, preference: 1 },
        { prefix: '192.0.2.0/30', resolvedEgressInterface: 'Et30', protocol: 'connected', metric: 0, preference: 0 },
        { prefix: '198.51.200.0/24', nextHop: '192.0.2.1', resolvedEgressInterface: 'Et30', protocol: 'ospf', metric: 30, preference: 110 }
      ]
    }
  }
};

const mockRouteTableAction = (orgId: string, deviceId: string, vrf: string, asOf?: string) => {
  const query = new URLSearchParams();
  query.set('vrf', vrf);
  if (typeof asOf === 'string' && asOf.trim()) {
    query.set('asOf', asOf.trim());
  }
  return {
    label: 'Route table',
    kind: 'device_api',
    method: 'GET',
    href: `/orgs/${encodeURIComponent(orgId)}/devices/${encodeURIComponent(deviceId)}/route-table?${query.toString()}`,
    timeMode: typeof asOf === 'string' && asOf.trim() ? 'fixed' : 'live',
    surface: 'vrf_panel',
    presentation: 'table',
    deviceId,
    vrf
  };
};

const buildMockRouteTablePage = (deviceId: string, vrf: string, asOf?: string) => {
  const canonicalVrf = canonicalMockL3VRF(vrf);
  const seed = MOCK_ROUTE_TABLES_BY_DEVICE_ID[deviceId]?.[canonicalVrf];
  if (!seed) {
    return null;
  }
  const collectedAt = typeof asOf === 'string' && asOf.trim() ? asOf.trim() : new Date().toISOString();
  const items = seed.rows.map((row) => ({
    rowId: [
      canonicalVrf,
      row.prefix,
      row.nextHop ?? '-',
      row.resolvedEgressInterface ?? '-',
      row.protocol
    ].join('|'),
    vrf: canonicalVrf,
    prefix: row.prefix,
    ...(row.nextHop ? { nextHop: row.nextHop } : {}),
    ...(row.resolvedEgressInterface ? { resolvedEgressInterface: row.resolvedEgressInterface } : {}),
    protocol: row.protocol,
    ...(typeof row.metric === 'number' ? { metric: row.metric } : {}),
    ...(typeof row.preference === 'number' ? { preference: row.preference } : {}),
    sourceVendor: seed.vendor,
    attributionMethod: 'mock_snapshot'
  }));
  const summary = items.reduce(
    (acc, row) => {
      if (row.prefix === '0.0.0.0/0') {
        acc.defaultRouteCount += 1;
      }
      if (row.protocol === 'connected' || row.protocol === 'direct') {
        acc.connectedCount += 1;
      }
      if (row.protocol === 'static') {
        acc.staticCount += 1;
      } else if (!['connected', 'direct', 'local', 'unknown', ''].includes(row.protocol)) {
        acc.dynamicCount += 1;
      }
      if (row.prefix.endsWith('/32') || row.prefix.endsWith('/128')) {
        acc.hostCount += 1;
      }
      return acc;
    },
    {
      defaultRouteCount: 0,
      connectedCount: 0,
      hostCount: 0,
      staticCount: 0,
      dynamicCount: 0,
      ecmpPrefixCount: 0
    }
  );
  const prefixCounts = new Map<string, number>();
  items.forEach((row) => {
    prefixCounts.set(row.prefix, (prefixCounts.get(row.prefix) ?? 0) + 1);
  });
  summary.ecmpPrefixCount = Array.from(prefixCounts.values()).filter((count) => count > 1).length;

  return {
    deviceId,
    siteId: MOCK_ROUTE_TABLE_SITE_ID_BY_DEVICE_ID[deviceId] ?? '',
    vrf: canonicalVrf,
    snapshotId: `mock-${deviceId}-${canonicalVrf}-ip-route`,
    tableType: 'ip-route',
    commandId: seed.commandId,
    vendor: seed.vendor,
    os: seed.os,
    timeMode: typeof asOf === 'string' && asOf.trim() ? 'fixed' : 'live',
    ...(typeof asOf === 'string' && asOf.trim() ? { asOf: asOf.trim() } : {}),
    collectedAt,
    dataState: typeof asOf === 'string' && asOf.trim() ? 'as_of' : 'fresh',
    attributionMethod: 'mock_snapshot',
    rowCount: items.length,
    limit: items.length,
    offset: 0,
    hasMore: false,
    rowsTruncated: false,
    warningCount: 0,
    warnings: [],
    summary,
    items
  };
};

function buildTopologyL3Membership(
  scopeType: string,
  scopeId: string,
  orgId: string,
  asOf?: string,
  vrf?: string,
  subnetId?: string
) {
  const now = typeof asOf === 'string' && asOf.trim() ? asOf.trim() : new Date().toISOString();
  const normalizedVRF = canonicalMockL3VRF(vrf);
  const normalizedSubnetId = typeof subnetId === 'string' ? subnetId.trim() : '';
  const rows = mockL3MembershipRecords().filter((row) => {
    const rowVRF = canonicalMockL3VRF(row.vrf);
    if (isManagementMockL3VRF(rowVRF)) {
      return false;
    }
    if (normalizedVRF && rowVRF !== normalizedVRF) {
      return false;
    }
    if (normalizedSubnetId && row.subnetNodeId !== normalizedSubnetId) {
      return false;
    }
    return true;
  });

  const nodeById = new Map<string, any>();
  const edgeById = new Map<string, any>();
  const pushNode = (node: any) => {
    if (!node?.id || nodeById.has(node.id)) {
      return;
    }
    nodeById.set(node.id, node);
  };
  const pushEdge = (edge: any) => {
    if (!edge?.id || edgeById.has(edge.id)) {
      return;
    }
    edgeById.set(edge.id, edge);
  };
  const subnetDeviceEdgeState = new Map<
    string,
    {
      subnetNodeId: string;
      subnet: string;
      deviceNodeId: string;
      deviceName: string;
      interfaces: Set<string>;
      interfaceIds: Set<string>;
      vrfs: Set<string>;
      vrfIds: Set<string>;
      addresses: Set<string>;
    }
  >();

  rows.forEach((row) => {
    pushNode({
      id: row.deviceNodeId,
      displayName: row.deviceName,
      labels: { family: 'l3', level: 'device' },
      refs: [{ type: 'inventory.device', id: row.deviceId }]
    });
    pushNode({
      id: row.vrfNodeId,
      displayName: row.vrf === 'mgmt' ? 'Management' : `VRF ${row.vrf}`,
      labels: { family: 'l3', level: 'domain', domain: 'routing', instance: `vrf:${row.vrf}` },
      properties: {
        vrf: row.vrf,
        device_id: row.deviceId,
        ui: {
          actions: {
            routeTable: mockRouteTableAction(orgId, row.deviceId, row.vrf, asOf)
          }
        }
      }
    });
    pushNode({
      id: row.interfaceNodeId,
      displayName: row.interfaceName,
      labels: {
        family: 'l3',
        level: 'interface',
        'if.kind': row.interfaceKind,
        domain: 'ip'
      },
      properties: {
        vrf: row.vrf,
        addresses: row.addresses,
        subnet: row.subnet,
        mac_address: row.macAddress,
        'state.admin': row.adminState,
        'state.oper': row.operState,
        ...(row.runtimeProperties ?? {})
      },
      refs: [
        { type: 'inventory.device', id: row.deviceId },
        { type: 'inventory.interface', id: `${row.deviceId}#${row.interfaceName}` }
      ]
    });
    pushNode({
      id: row.subnetNodeId,
      displayName: row.subnet,
      labels: { family: 'l3', level: 'domain', domain: 'ip' },
      properties: { prefix: row.subnet }
    });

    pushEdge({
      id: `edge:${row.deviceNodeId}--${row.vrfNodeId}#contains:routing`,
      fromId: row.deviceNodeId,
      toId: row.vrfNodeId,
      directed: true,
      labels: { family: 'l3', relation: 'contains', domain: 'routing' }
    });
    pushEdge({
      id: `edge:${row.vrfNodeId}--${row.interfaceNodeId}#contains:routing`,
      fromId: row.vrfNodeId,
      toId: row.interfaceNodeId,
      directed: true,
      labels: { family: 'l3', relation: 'contains', domain: 'routing' }
    });
    const subnetDeviceKey = `${row.subnetNodeId}::${row.deviceNodeId}::${row.vrfNodeId}`;
    const subnetDevice = subnetDeviceEdgeState.get(subnetDeviceKey) ?? {
      subnetNodeId: row.subnetNodeId,
      subnet: row.subnet,
      deviceNodeId: row.deviceNodeId,
      deviceName: row.deviceName,
      interfaces: new Set<string>(),
      interfaceIds: new Set<string>(),
      vrfs: new Set<string>(),
      vrfIds: new Set<string>(),
      addresses: new Set<string>()
    };
    subnetDevice.interfaces.add(row.interfaceName);
    subnetDevice.interfaceIds.add(row.interfaceNodeId);
    subnetDevice.vrfs.add(row.vrf);
    subnetDevice.vrfIds.add(row.vrfNodeId);
    row.addresses.forEach((address) => subnetDevice.addresses.add(address));
    subnetDeviceEdgeState.set(subnetDeviceKey, subnetDevice);
  });

  Array.from(subnetDeviceEdgeState.values()).forEach((edgeState) => {
    const interfaces = Array.from(edgeState.interfaces).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const interfaceIds = Array.from(edgeState.interfaceIds).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const vrfs = Array.from(edgeState.vrfs).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const vrfIds = Array.from(edgeState.vrfIds).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const addresses = Array.from(edgeState.addresses).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    pushEdge({
      id: `derived:l3_membership:${edgeState.subnetNodeId}->${edgeState.deviceNodeId}->${Array.from(edgeState.vrfIds)[0] ?? ''}:connects:ip`,
      fromId: edgeState.subnetNodeId,
      toId: edgeState.deviceNodeId,
      directed: true,
      labels: { family: 'l3', relation: 'connects', domain: 'ip' },
      properties: {
        subnet: edgeState.subnet,
        device: edgeState.deviceName,
        interfaces,
        interfaceIds,
        vrfs,
        vrfIds,
        addresses,
        presentation: {
          selectionTitle: `${edgeState.subnet} attached to ${edgeState.deviceName}`,
          selectionSubtitle: 'Subnet attachment',
          rows: [
            { key: 'subnet', label: 'Subnet', value: edgeState.subnet },
            { key: 'device', label: 'Device', value: edgeState.deviceName },
            { key: 'vrfs', label: 'VRF', value: vrfs },
            { key: 'interfaces', label: 'Interface', value: interfaces },
            { key: 'addresses', label: 'Address', value: addresses }
          ]
        }
      }
    });
  });

  return {
    graph: {
      graphId:
        typeof asOf === 'string' && asOf.trim()
          ? `resolved:l3_membership:${scopeType}:${scopeId}:${now}`
          : `mock:l3_membership:${scopeType}:${scopeId}:${
              normalizedVRF ? `vrf:${normalizedVRF}:` : ''
            }${normalizedSubnetId ? `subnet:${normalizedSubnetId}` : 'all'}`,
      generatedAt: now,
      scope: { scopeType, scopeId },
      meta: {
        context: { family: 'l3', plane: 'underlay', purpose: 'l3_membership' }
      },
      nodes: Array.from(nodeById.values()),
      edges: Array.from(edgeById.values())
    },
    legend: {
      nodeRules: [
        { when: { 'labels.level': 'device' }, ui: { icon: 'device', size: 'xl' } },
        { when: { 'labels.level': 'domain', 'labels.domain': 'routing' }, ui: { icon: 'site', size: 'sm' } },
        { when: { 'labels.level': 'interface' }, ui: { icon: 'interface', size: 'sm', renderAs: 'port_dot' } },
        { when: { 'labels.level': 'domain', 'labels.domain': 'ip' }, ui: { icon: 'site', size: 'sm' } }
      ],
      edgeRules: [
        { when: { 'labels.relation': 'contains' }, ui: { style: 'embed' } },
        { when: { 'labels.relation': 'connects', 'labels.domain': 'ip' }, ui: { style: 'membership' } }
      ]
    },
    drilldowns: [
      {
        name: 'Focus subnet',
        viewId: 'l3_membership',
        mode: 'replace',
        appliesWhen: { 'labels.level': 'domain', 'labels.domain': 'ip' },
        dynamic: {
          params: {
            subnetId: 'node.id'
          }
        }
      },
      {
        name: 'Focus subnet',
        viewId: 'l3_membership',
        mode: 'replace',
        appliesWhen: { 'edge.labels.relation': 'connects', 'edge.labels.domain': 'ip' },
        dynamic: {
          params: {
            subnetId: 'edge.fromId'
          }
        }
      }
    ],
    meta: {
      asOf: now,
      context: { family: 'l3', plane: 'underlay', purpose: 'l3_membership' },
      viewVersion: '1.0',
      topics: ['state']
    }
  };
}

type MockTopologyRouteError = Error & { status: number };

const createMockTopologyRouteError = (status: number, detail: string): MockTopologyRouteError =>
  Object.assign(new Error(detail), { status });

const parseMockJsonQueryParam = (value: string | null): Record<string, unknown> => {
  if (!value) {
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const normalizeMockSavedRequest = (input: {
  scopeType?: unknown;
  scopeId?: unknown;
  params?: unknown;
  limits?: unknown;
  asOf?: unknown;
  timeMode?: unknown;
}): MockSavedTopologyRequest => ({
  scopeType: typeof input.scopeType === 'string' && input.scopeType.trim() ? input.scopeType.trim() : 'site',
  scopeId: typeof input.scopeId === 'string' && input.scopeId.trim() ? input.scopeId.trim() : 'mock-site-sjc',
  params:
    input.params && typeof input.params === 'object' && !Array.isArray(input.params)
      ? (cloneValue(input.params) as Record<string, unknown>)
      : {},
  limits:
    input.limits && typeof input.limits === 'object' && !Array.isArray(input.limits)
      ? (cloneValue(input.limits) as Record<string, unknown>)
      : {},
  asOf: typeof input.asOf === 'string' && input.asOf.trim() ? input.asOf.trim() : undefined,
  timeMode: input.timeMode === 'fixed' ? 'fixed' : 'current'
});

const buildMockSavedRequestFromBase = (base: Record<string, any> | null | undefined): MockSavedTopologyRequest =>
  normalizeMockSavedRequest({
    scopeType: base?.scope?.scopeType,
    scopeId: base?.scope?.scopeId,
    params: base?.params,
    limits: base?.limits,
    asOf: base?.asOf instanceof Date ? base.asOf.toISOString() : base?.asOf,
    timeMode: base?.timeMode
  });

const buildMockViewOverlayState = (
  base: Record<string, any> | null | undefined,
  rawState: Record<string, any> | null | undefined
): Record<string, any> => {
  const resolvedBase = rawState?.base ?? base;
  return {
    schema: 'topology.view.overlay-state.v1',
    ...(resolvedBase ? { base: cloneValue(resolvedBase) } : {}),
    layers: Array.isArray(rawState?.layers) ? cloneValue(rawState.layers) : [],
    graphOverlays: Array.isArray(rawState?.graphOverlays) ? cloneValue(rawState.graphOverlays) : []
  };
};

const mockLayerPortability = (layer: Record<string, any>): 'portable' | 'current_scope_only' => {
  const portability = typeof layer?.save?.portability === 'string' ? layer.save.portability.trim().toLowerCase() : '';
  if (portability === 'portable') {
    return 'portable';
  }
  if (portability === 'current_scope_only') {
    return 'current_scope_only';
  }
  return String(layer?.kind ?? '').trim().toLowerCase() === 'highlight' ? 'current_scope_only' : 'portable';
};

const filterMockSavedOverlayState = (
  overlayState: Record<string, any> | null,
  overlayStatuses: Record<string, any>[],
  saveMode: TopologyV2ViewScopeModeschema
): {
  overlayState: Record<string, any> | null;
  overlayStatuses: Record<string, any>[];
  warnings: TopologyV2ViewSaveWarningschema[];
  savedLayerSummary: TopologyV2ViewSaveResponse['savedLayerSummary'];
} => {
  if (!overlayState) {
    return {
      overlayState: null,
      overlayStatuses: [],
      warnings: [],
      savedLayerSummary: {
        portableLayers: 0,
        currentScopeLayers: 0,
        omittedLayers: 0
      }
    };
  }

  let portableLayers = 0;
  let currentScopeLayers = 0;
  let omittedLayers = 0;
  const keptLayerIds = new Set<string>();
  const warnings: TopologyV2ViewSaveWarningschema[] = [];
  const keptLayers = (overlayState.layers ?? []).filter((layer: Record<string, any>) => {
    const portability = mockLayerPortability(layer as Record<string, any>);
    if (portability === 'portable') {
      portableLayers += 1;
      keptLayerIds.add(String(layer.id ?? ''));
      return true;
    }
    if (saveMode === 'current_scope') {
      currentScopeLayers += 1;
      keptLayerIds.add(String(layer.id ?? ''));
      return true;
    }
    omittedLayers += 1;
    warnings.push({
      code: 'current_scope_layer_omitted',
      message: `${String(layer.label ?? layer.id ?? 'Layer')} was omitted from this reusable saved view.`,
      layerId: typeof layer.id === 'string' ? layer.id : undefined
    });
    return false;
  });

  return {
    overlayState: {
      ...cloneValue(overlayState),
      layers: keptLayers,
      graphOverlays: Array.isArray(overlayState.graphOverlays) ? cloneValue(overlayState.graphOverlays) : []
    },
    overlayStatuses: overlayStatuses
      .filter((status) => typeof status.layerId === 'string' && keptLayerIds.has(status.layerId))
      .map((status) => cloneValue(status)),
    warnings,
    savedLayerSummary: {
      portableLayers,
      currentScopeLayers,
      omittedLayers
    }
  };
};

function findMockChatArtifactRecord(
  orgId: string,
  artifactId: string
): { artifactRecord: ChatArtifactRecord; artifactRunRecord: StoredChatRun } | null {
  const state = ensureChatState(orgId);
  for (const runRecord of state.runs.values()) {
    const artifactRecord = runRecord.artifacts.find((artifact) => artifact.id === artifactId);
    if (artifactRecord) {
      return { artifactRecord, artifactRunRecord: runRecord };
    }
  }
  return null;
}

function deriveMockSavedTopologySource(
  orgId: string,
  source: TopologyV2ViewCreateRequest['source']
): {
  baseTemplateViewId: string;
  request: MockSavedTopologyRequest;
  overlayState: Record<string, any> | null;
  overlayStatuses: Record<string, any>[];
  scopeTypes: string[];
  params: Record<string, unknown>;
} {
  if (source.kind === 'visibility_session') {
    const sessionId = typeof source.sessionId === 'string' ? source.sessionId.trim() : '';
    const sessionRecord = sessionId ? getVisibilitySessionRecord(orgId, sessionId) : null;
    if (!sessionRecord) {
      throw createMockTopologyRouteError(404, `Mock visibility session ${sessionId || '(missing)'} not found.`);
    }
    if (sessionRecord.data.pendingProposalId) {
      throw createMockTopologyRouteError(409, 'Apply or reject the pending proposal before saving this view.');
    }
    const base = sessionRecord.data.state.base as Record<string, any>;
    const baseTemplateViewId = typeof base?.viewId === 'string' && base.viewId.trim() ? base.viewId.trim() : 'physical_overview';
    const baseView = TOPOLOGY_VIEW_BY_ID.get(baseTemplateViewId);
    return {
      baseTemplateViewId,
      request: buildMockSavedRequestFromBase(base),
      overlayState: buildMockViewOverlayState(base, sessionRecord.data.state as Record<string, any>),
      overlayStatuses: [],
      scopeTypes: cloneValue(baseView?.scopeTypes ?? [String(base?.scope?.scopeType ?? 'site')]),
      params: cloneValue(baseView?.params ?? {})
    };
  }

  if (source.kind !== 'topology_artifact_snapshot') {
    throw createMockTopologyRouteError(400, `Unsupported save source ${String((source as { kind?: unknown }).kind ?? '')}.`);
  }

  const artifactId = typeof source.artifactId === 'string' ? source.artifactId.trim() : '';
  const artifactRep = typeof source.artifactRep === 'string' ? source.artifactRep.trim() : '';
  const artifact = artifactId ? findMockChatArtifactRecord(orgId, artifactId) : null;
  if (!artifact) {
    throw createMockTopologyRouteError(404, `Mock topology artifact ${artifactId || '(missing)'} not found.`);
  }
  const document = buildMockChatArtifactDocument(orgId, artifact.artifactRecord, artifact.artifactRunRecord.artifacts, true);
  const presentationHints = document.presentationHints as { defaultView?: unknown } | undefined;
  const defaultRep = typeof presentationHints?.defaultView === 'string' ? presentationHints.defaultView : '';
  const selectedRep = artifactRep || defaultRep;
  const selectedView =
    selectedRep && Array.isArray(document.views)
      ? document.views.find((entry) => entry && typeof entry.rep === 'string' && entry.rep === selectedRep)
      : undefined;
  const content = (selectedView?.content ?? document.content) as Record<string, any> | undefined;
  if (!content || content.type !== 'topology' || !content.binding) {
    throw createMockTopologyRouteError(400, 'Selected artifact representation is not a topology snapshot.');
  }
  const binding = content.binding as Record<string, any>;
  const baseTemplateViewId = typeof binding.viewId === 'string' && binding.viewId.trim() ? binding.viewId.trim() : 'physical_overview';
  const baseView = TOPOLOGY_VIEW_BY_ID.get(baseTemplateViewId);
  return {
    baseTemplateViewId,
    request: normalizeMockSavedRequest({
      scopeType: binding.scope?.scopeType,
      scopeId: binding.scope?.scopeId,
      params: binding.params,
      limits: binding.limits,
      asOf: binding.asOf instanceof Date ? binding.asOf.toISOString() : binding.asOf,
      timeMode: binding.timeMode
    }),
    overlayState: buildMockViewOverlayState(binding, content.presentation?.overlayState as Record<string, any>),
    overlayStatuses: [],
    scopeTypes: cloneValue(baseView?.scopeTypes ?? [String(binding.scope?.scopeType ?? 'site')]),
    params: cloneValue(baseView?.params ?? {})
  };
}

function saveCustomTopologyViewRecord(
  orgId: string,
  payload: TopologyV2ViewCreateRequest,
  existingViewId?: string
): any {
  const name = typeof payload.name === 'string' ? payload.name.trim() : '';
  if (!name) {
    throw createMockTopologyRouteError(400, 'Saved view name is required.');
  }
  const saveMode = payload.saveMode === 'current_scope' ? 'current_scope' : 'reusable';
  const derived = deriveMockSavedTopologySource(orgId, payload.source);
  const filtered = filterMockSavedOverlayState(derived.overlayState, derived.overlayStatuses, saveMode);
  const state = ensureTopologyViewsState(orgId);
  let nextViewId = existingViewId?.trim() ?? '';
  if (!nextViewId) {
    do {
      nextViewId = slugifyTopologyViewName(name);
    } while (getTopologyViewItem(orgId, nextViewId));
  }
  const view: TopologyMockViewItem = {
    viewId: nextViewId,
    name,
    description: typeof payload.description === 'string' && payload.description.trim() ? payload.description.trim() : undefined,
    origin: 'custom',
    state: 'active',
    ownerUserId: MOCK_CHAT_USER_ID,
    sharingScope: 'private',
    scopeMode: saveMode,
    boundScope:
      saveMode === 'current_scope'
        ? {
            scopeType: derived.request.scopeType,
            scopeId: derived.request.scopeId
          }
        : undefined,
    baseTemplateViewId: derived.baseTemplateViewId,
    scopeTypes: cloneValue(derived.scopeTypes),
    params: cloneValue(derived.params)
  };
  state.customViews.set(nextViewId, {
    view,
    request: derived.request,
    overlayState: filtered.overlayState,
    overlayStatuses: filtered.overlayStatuses
  });
  return {
    view: cloneValue(view),
    warnings: filtered.warnings,
    savedLayerSummary: filtered.savedLayerSummary
  };
}

function resolveSystemTopologyView(
  orgId: string,
  viewId: string,
  request: MockSavedTopologyRequest
): any {
  if (viewId === 'org_overview') {
    return buildTopologyOrgOverview(request.scopeType, request.scopeId);
  }
  if (viewId === 'org_site_pair_links') {
    const aSiteId = typeof request.params.aSiteId === 'string' ? String(request.params.aSiteId) : '';
    const bSiteId = typeof request.params.bSiteId === 'string' ? String(request.params.bSiteId) : '';
    if (!aSiteId || !bSiteId) {
      throw createMockTopologyRouteError(400, 'Missing aSiteId/bSiteId for org_site_pair_links.');
    }
    return buildTopologyOrgSitePairLinks(request.scopeType, request.scopeId, aSiteId, bSiteId);
  }
  if (viewId === 'physical_overview') {
    return buildTopologyOverview(request.scopeType, request.scopeId);
  }
  if (viewId === 'vlan_membership_summary') {
    return buildTopologyVlanMembershipSummary(request.scopeType, request.scopeId);
  }
  if (viewId === 'aws_cloud_overview') {
    return buildMockCloudConnectivity(request.scopeType, request.scopeId);
  }
  if (viewId === 'physical_pair_links') {
    const deviceAId = typeof request.params.deviceAId === 'string' ? String(request.params.deviceAId) : '';
    const deviceBId = typeof request.params.deviceBId === 'string' ? String(request.params.deviceBId) : '';
    if (!deviceAId || !deviceBId) {
      throw createMockTopologyRouteError(400, 'Missing deviceAId/deviceBId for physical_pair_links.');
    }
    return buildTopologyPhysicalPairLinks(request.scopeType, request.scopeId, deviceAId, deviceBId);
  }
  if (viewId === 'lag_link_members') {
    const lagAId = typeof request.params.lagAId === 'string' ? String(request.params.lagAId) : '';
    const lagBId = typeof request.params.lagBId === 'string' ? String(request.params.lagBId) : '';
    if (!lagAId || !lagBId) {
      throw createMockTopologyRouteError(400, 'Missing lagAId/lagBId for lag_link_members.');
    }
    return buildTopologyLagLinkMembers(request.scopeType, request.scopeId, lagAId, lagBId);
  }
  if (viewId === 'physical_ports') {
    const rootNodeId = typeof request.limits.rootNodeId === 'string' ? request.limits.rootNodeId : undefined;
    return buildTopologyPhysicalPorts(request.scopeType, request.scopeId, rootNodeId);
  }
  if (viewId === 'routing_adjacency') {
    const rootNodeId = typeof request.limits.rootNodeId === 'string' ? request.limits.rootNodeId : undefined;
    return buildTopologyRoutingAdjacency(request.scopeType, request.scopeId, rootNodeId);
  }
  if (viewId === 'l3_membership') {
    const vrf = typeof request.params.vrf === 'string' ? String(request.params.vrf) : '';
    const subnetId = typeof request.params.subnetId === 'string' ? String(request.params.subnetId) : '';
    return buildTopologyL3Membership(request.scopeType, request.scopeId, orgId, request.asOf, vrf, subnetId);
  }
  if (viewId === 'lag_members') {
    const rootNodeId = typeof request.limits.rootNodeId === 'string' ? request.limits.rootNodeId : undefined;
    return buildTopologyLagMembers(request.scopeType, request.scopeId, rootNodeId);
  }
  throw createMockTopologyRouteError(501, `Mock resolve is not implemented for ${viewId}.`);
}

function resolveCustomTopologyView(
  orgId: string,
  record: StoredCustomTopologyView,
  request: MockSavedTopologyRequest
): any {
  const boundScope = record.view.scopeMode === 'current_scope' ? record.view.boundScope : undefined;
  const effectiveRequest: MockSavedTopologyRequest = {
    scopeType: request.scopeType || record.request.scopeType,
    scopeId: request.scopeId || record.request.scopeId,
    params: { ...cloneValue(record.request.params), ...cloneValue(request.params) },
    limits: { ...cloneValue(record.request.limits), ...cloneValue(request.limits) },
    asOf: request.asOf ?? record.request.asOf,
    timeMode: request.asOf ? 'fixed' : record.request.timeMode
  };
  if (boundScope) {
    if (
      effectiveRequest.scopeType !== String(boundScope.scopeType) ||
      effectiveRequest.scopeId !== String(boundScope.scopeId)
    ) {
      throw createMockTopologyRouteError(409, 'This saved view is bound to a different scope.');
    }
  }
  const baseTemplateViewId = String(record.view.baseTemplateViewId ?? 'physical_overview');
  const resolved = resolveSystemTopologyView(orgId, baseTemplateViewId, effectiveRequest);
  if (!record.overlayState) {
    return resolved;
  }
  return {
    ...cloneValue(resolved),
    overlayState: {
      ...cloneValue(record.overlayState),
      base: {
        ...(record.overlayState.base ? cloneValue(record.overlayState.base) : {}),
        source: TopologyOverlayBaseschemaSourceEnum.TopologyService,
        viewId: baseTemplateViewId,
        scope: {
          scopeType: effectiveRequest.scopeType,
          scopeId: effectiveRequest.scopeId
        },
        timeMode: effectiveRequest.timeMode,
        ...(Object.keys(effectiveRequest.params).length > 0 ? { params: cloneValue(effectiveRequest.params) } : {}),
        ...(Object.keys(effectiveRequest.limits).length > 0 ? { limits: cloneValue(effectiveRequest.limits) } : {}),
        ...(effectiveRequest.asOf ? { asOf: effectiveRequest.asOf } : {})
      }
    },
    overlayStatuses: cloneValue(record.overlayStatuses)
  };
}

function respondTopologyViewStream(viewId: string, resolved: any): Response {
  const payload = {
    type: 'snapshot',
    viewId,
    seq: 1,
    data: resolved
  };
  return new Response(`event: snapshot\ndata: ${JSON.stringify(payload)}\n\n`, {
    status: 200,
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      Connection: 'keep-alive',
      'X-Mock-Route': 'org-console'
    }
  });
}

async function handleTopologyV2Routes({ request, url }: MockContext): Promise<Response | null> {
  if (!url.pathname.startsWith('/public/v1/orgs/')) {
    return null;
  }

  const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 3) {
    return null;
  }
  const [orgIdRaw, resource, subresource, ...tail] = segments;
  if (resource !== 'topology') {
    return null;
  }

  const orgId = decodeURIComponent(orgIdRaw);
  if (!ORG_PROFILES[orgId]) {
    return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
  }
  const method = request.method.toUpperCase();
  const topologyAction = decodeURIComponent(subresource);

  if (topologyAction === 'path-endpoints:search') {
    if (method !== 'POST') {
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/topology/path-endpoints:search.` }, 501);
    }
    const payload = (await request.clone().json().catch(() => ({}))) as Record<string, unknown>;
    return respond(searchMockTopologyPathEndpoints(orgId, payload as any));
  }

  if (topologyAction === 'paths:trace') {
    if (method !== 'POST') {
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/topology/paths:trace.` }, 501);
    }
    const payload = (await request.clone().json().catch(() => ({}))) as Record<string, unknown>;
    return respond(traceMockTopologyPath(orgId, payload as any));
  }

  if (topologyAction !== 'views') {
    return null;
  }

  try {
    if (tail.length === 0) {
      if (method === 'GET') {
        return respond(buildListResponse(listTopologyViewItems(orgId, normalizeTopologyOriginFilter(url.searchParams.get('origin')))));
      }
      if (method === 'POST') {
        const payload = (await request.clone().json()) as TopologyV2ViewCreateRequest;
        const response = saveCustomTopologyViewRecord(orgId, payload);
        return respond(response, 201);
      }
      return respond({ detail: `Mock handler missing for ${method} /orgs/${orgId}/topology/views.` }, 501);
    }

    const [viewId, action, ...rest] = tail;
    if (!viewId) {
      return respond({ detail: 'Missing topology view id.' }, 400);
    }
    const customRecord = getCustomTopologyViewRecord(orgId, viewId);
    const view = getTopologyViewItem(orgId, viewId);
    if (!view) {
      return respond({ detail: `Topology view ${viewId} not found.` }, 404);
    }

    if (!action) {
      if (method === 'GET') {
        return respond(view);
      }
      if (method === 'PATCH') {
        if (!customRecord) {
          return respond({ detail: 'System views are read-only in mocks.' }, 405);
        }
        const payload = (await request.clone().json()) as TopologyV2ViewUpdateRequest;
        if (payload.source) {
          const response = saveCustomTopologyViewRecord(
            orgId,
            {
              name:
                typeof payload.name === 'string' && payload.name.trim()
                  ? payload.name.trim()
                  : (customRecord.view.name ?? customRecord.view.viewId ?? 'Saved topology view'),
              description:
                typeof payload.description === 'string'
                  ? payload.description.trim() || undefined
                  : customRecord.view.description ?? undefined,
              saveMode: payload.saveMode ?? customRecord.view.scopeMode ?? 'reusable',
              source: payload.source
            },
            viewId
          );
          return respond(response);
        }
        const nextView: TopologyMockViewItem = {
          ...cloneValue(customRecord.view),
          ...(typeof payload.name === 'string' && payload.name.trim() ? { name: payload.name.trim() } : {}),
          ...(Object.prototype.hasOwnProperty.call(payload, 'description')
            ? { description: typeof payload.description === 'string' && payload.description.trim() ? payload.description.trim() : undefined }
            : {})
        };
        ensureTopologyViewsState(orgId).customViews.set(viewId, {
          ...customRecord,
          view: nextView
        });
        return respond({ view: cloneValue(nextView) });
      }
      if (method === 'DELETE') {
        if (!customRecord) {
          return respond({ detail: 'System views cannot be deleted in mocks.' }, 405);
        }
        ensureTopologyViewsState(orgId).customViews.delete(viewId);
        return respond(null, 204);
      }
    }

    if (action === 'resolve' && method === 'POST') {
      const payload = (await request.clone().json()) as {
        scopeType?: string;
        scopeId?: string;
        params?: Record<string, unknown>;
        limits?: Record<string, unknown>;
        asOf?: string;
      };
      const requestPayload = normalizeMockSavedRequest({
        scopeType: payload.scopeType,
        scopeId: payload.scopeId,
        params: payload.params,
        limits: payload.limits,
        asOf: payload.asOf,
        timeMode: payload.asOf ? 'fixed' : 'current'
      });
      const resolved = customRecord ? resolveCustomTopologyView(orgId, customRecord, requestPayload) : resolveSystemTopologyView(orgId, viewId, requestPayload);
      return respond(resolved);
    }

    if (action === 'stream' && method === 'GET') {
      const requestPayload = normalizeMockSavedRequest({
        scopeType: url.searchParams.get('scopeType'),
        scopeId: url.searchParams.get('scopeId'),
        params: parseMockJsonQueryParam(url.searchParams.get('params')),
        limits: parseMockJsonQueryParam(url.searchParams.get('limits')),
        asOf: url.searchParams.get('asOf'),
        timeMode: url.searchParams.get('asOf') ? 'fixed' : 'current'
      });
      const resolved = customRecord ? resolveCustomTopologyView(orgId, customRecord, requestPayload) : resolveSystemTopologyView(orgId, viewId, requestPayload);
      return respondTopologyViewStream(viewId, resolved);
    }

    if (action === 'params' && method === 'GET' && rest.length === 2 && rest[1] === 'options') {
      return respond({ items: [], meta: {} });
    }

    return respond({ detail: `Mock handler missing for /orgs/${orgId}/topology/views/${tail.join('/')}.` }, 501);
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error && typeof (error as MockTopologyRouteError).status === 'number') {
      const routeError = error as unknown as MockTopologyRouteError;
      return respond({ detail: routeError.message }, routeError.status);
    }
    console.warn('[mock:org-console] Failed to handle topology v2 route', error);
    return respond({ detail: 'Invalid payload for topology view request.' }, 400);
  }
}

function handleCveSummaryRoute(orgId: string, url: URL): Response {
  const timestamp = url.searchParams.get('timestamp');
  let activeExposures = 13;
  let auditedSafe = 297;
  let criticalExposures = 2;
  let highExposures = 5;
  let mediumExposures = 4;
  let lowExposures = 2;
  let exposureRatio = 0.0419;

  if (timestamp) {
    const date = new Date(timestamp);
    const diffMs = Date.now() - date.getTime();
    if (diffMs > 3 * 24 * 60 * 60 * 1000) { // older than 3 days
      activeExposures = 20;
      auditedSafe = 290;
      criticalExposures = 4;
      highExposures = 8;
      mediumExposures = 6;
      lowExposures = 2;
      exposureRatio = 0.0645;
    } else if (diffMs > 12 * 60 * 60 * 1000) { // older than 12 hours
      activeExposures = 15;
      auditedSafe = 295;
      criticalExposures = 3;
      highExposures = 6;
      mediumExposures = 4;
      lowExposures = 2;
      exposureRatio = 0.0484;
    } else if (diffMs > 6 * 60 * 60 * 1000) { // older than 6 hours
      activeExposures = 14;
      auditedSafe = 296;
      criticalExposures = 2;
      highExposures = 6;
      mediumExposures = 4;
      lowExposures = 2;
      exposureRatio = 0.0451;
    }
  }

  return respond({
    totalCves: 310,
    activeExposures,
    auditedSafe,
    exposureRatio,
    criticalExposures,
    highExposures,
    mediumExposures,
    lowExposures
  });
}

function handleCveAssessmentsRoute(orgId: string, url: URL): Response {
  const timestamp = url.searchParams.get('timestamp');
  const assessedAt = timestamp || new Date().toISOString();

  const mockItems = [
    {
      id: "exp-1",
      orgId,
      deviceId: "hq-spine1",
      deviceName: "hq-spine1",
      siteId: "site-hq",
      siteName: "Headquarters",
      cveId: "CVE-2023-4911",
      severity: "CRITICAL",
      cvssScore: 9.8,
      status: "EXPOSED",
      justification: "Active glibc dynamic loader vulnerability (Looney Tunables) detected. Running OS version Cisco IOS-XE 17.9.1 is vulnerable because telnet-server configuration allows raw unauthenticated execution from public WAN.",
      causativeConfig: "line vty 0 4\n transport input telnet\n privilege level 15",
      remediationType: "UPGRADE",
      patchUpgrade: "Cisco IOS-XE 17.9.1a Patch 3 or >= 17.9.2",
      impactOfFix: "Upgrade requires a warm reload of the device. Traffic will converge and re-route through hq-spine2 during the reboot window.",
      assessedAt
    },
    {
      id: "exp-2",
      orgId,
      deviceId: "hq-fw1",
      deviceName: "hq-fw1",
      siteId: "site-hq",
      siteName: "Headquarters",
      cveId: "CVE-2023-3519",
      severity: "CRITICAL",
      cvssScore: 9.8,
      status: "EXPOSED",
      justification: "Citrix ADC/Gateway remote code execution vulnerability found. The management interface is exposed directly to the Untrusted-WAN zone without an ACL filter restriction.",
      causativeConfig: "set ns ip 10.1.1.254 -type VIP -mgmtaccess ENABLED",
      remediationType: "FIX_CONFIG",
      remediationCommands: [
        "set ns ip 10.1.1.254 -mgmtaccess DISABLED",
        "bind policy acl restrict-mgmt -srcIP 10.1.0.0/16 -destIP 10.1.1.254 -priority 10"
      ],
      impactOfFix: "Disabling management access on the VIP requires operators to use the secure out-of-band management interface (10.254.1.1).",
      assessedAt
    },
    {
      id: "exp-3",
      orgId,
      deviceId: "branch1-edge",
      deviceName: "branch1-edge",
      siteId: "site-branch1",
      siteName: "Branch 01 (Aruba Central)",
      cveId: "CVE-2024-20399",
      severity: "HIGH",
      cvssScore: 8.8,
      status: "EXPOSED",
      justification: "Cisco IOS XE Software Command Injection Vulnerability allows authenticated local attackers to execute arbitrary commands with root privileges.",
      causativeConfig: "username admin privilege 15 secret 9 $9$...",
      remediationType: "UPGRADE",
      patchUpgrade: "Cisco IOS-XE 17.12.2",
      impactOfFix: "Warm upgrade will reboot the control plane but keep data plane forwarding active with sub-second convergence.",
      assessedAt
    },
    {
      id: "exp-4",
      orgId,
      deviceId: "hq-leaf1",
      deviceName: "hq-leaf1",
      siteId: "site-hq",
      siteName: "Headquarters",
      cveId: "CVE-2023-4911",
      severity: "CRITICAL",
      cvssScore: 9.8,
      status: "NOT_EXPOSED",
      justification: "glibc Looney Tunables signature matched by version (Cisco IOS-XE 17.9.1), but audited as SAFE because the telnet-server feature is disabled in configuration, and transport input is restricted to ssh only.",
      causativeConfig: "line vty 0 4\n transport input ssh",
      assessedAt
    },
    {
      id: "exp-5",
      orgId,
      deviceId: "branch2-edge",
      deviceName: "branch2-edge",
      siteId: "site-branch2",
      siteName: "Branch 02 (Cisco Meraki)",
      cveId: "CVE-2024-20399",
      severity: "HIGH",
      cvssScore: 8.8,
      status: "NOT_EXPOSED",
      justification: "Device is safe from command injection as there are no high-privilege local user accounts configured; authentication is fully delegated to secure RADIUS/AAA servers.",
      causativeConfig: "aaa authentication login default group radius local",
      assessedAt
    },
    {
      id: "exp-6",
      orgId,
      deviceId: "branch3-edge",
      deviceName: "branch3-edge",
      siteId: "site-branch3",
      siteName: "Branch 03 (Juniper Mist)",
      cveId: "CVE-2024-21596",
      severity: "HIGH",
      cvssScore: 8.5,
      status: "EXPOSED",
      justification: "Juniper JunOS out-of-bounds write vulnerability in J-Web interface. The J-Web management service is enabled and reachable from untrusted interfaces.",
      causativeConfig: "set system services web-management http interface ge-0/0/0.0",
      remediationType: "FIX_CONFIG",
      remediationCommands: [
        "delete system services web-management http interface ge-0/0/0.0",
        "set system services web-management https interface fxp0.0"
      ],
      impactOfFix: "This will disable plain-text HTTP management on external links. Secure HTTPS management remains enabled on internal out-of-band interfaces.",
      assessedAt
    },
    {
      id: "exp-7",
      orgId,
      deviceId: "branch4-edge",
      deviceName: "branch4-edge",
      siteId: "site-branch4",
      siteName: "Branch 04 (CAPWAP Tunnel)",
      cveId: "CVE-2024-21596",
      severity: "HIGH",
      cvssScore: 8.5,
      status: "NOT_EXPOSED",
      justification: "J-Web vulnerability matched by signature, but audited as SAFE because the web-management service is completely disabled on this device.",
      causativeConfig: "deactivate system services web-management",
      assessedAt
    }
  ];

  if (timestamp) {
    const date = new Date(timestamp);
    const diffMs = Date.now() - date.getTime();
    if (diffMs > 12 * 60 * 60 * 1000) {
      mockItems.push({
        id: "exp-8",
        orgId,
        deviceId: "hq-spine2",
        deviceName: "hq-spine2",
        siteId: "site-hq",
        siteName: "Headquarters",
        cveId: "CVE-2023-4911",
        severity: "CRITICAL",
        cvssScore: 9.8,
        status: "EXPOSED",
        justification: "Historical exposure: hq-spine2 had telnet enabled during migration testing.",
        causativeConfig: "line vty 0 4\n transport input telnet",
        remediationType: "UPGRADE",
        patchUpgrade: "Cisco IOS-XE 17.9.2",
        impactOfFix: "Device requires upgrade reload.",
        assessedAt
      });
    }
  }

  return respond({ items: mockItems });
}

export const orgConsoleHandlers: MockHandler[] = [
  handlePeopleRoutes,
  handleIdpConnectionRoutes,
  handleCredentialRoutes,
  handleAssignmentRoutes,
  handleSecretsRoutes,
  handleVisibilityRoutes,
  handleTopologyV2Routes,
  handleConnectivityRoutes,
  handleDeviceRoutes,
  handleTasksRoutes,
  handleDiscoveryRoutes,
  async ({ formData: providedFormData, request, url }) => {
    if (!url.pathname.startsWith('/public/v1/orgs/')) {
      return null;
    }

    const path = url.pathname.replace(/^\/public\/v1\/orgs\//, '');
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return null;
    }

    const orgId = decodeURIComponent(segments[0]);
    const remainder = segments.slice(1).join('/');

    const profile = ORG_PROFILES[orgId];
    if (!profile) {
      return respond({ detail: `Mock organization ${orgId} not found.` }, 404);
    }

    switch (remainder) {
      case '':
        return respond(profile);
      case 'download/mock-netflow.json':
        return respond({}, 200, { 'Content-Type': 'application/json' });
      case 'cves/summary':
        return handleCveSummaryRoute(orgId, url);
      case 'cves/assessments':
        return handleCveAssessmentsRoute(orgId, url);
      case 'credentials':
        return respond(buildListResponse(listCredentials(orgId)));
      case 'credential-assignments':
        return respond(buildListResponse(listAssignments(orgId)));
      case 'api-keys':
        return respond(buildListResponse(listApiKeys(orgId)));
      case 'chat/threads': {
        const chatResponse = await handleChatRoutes(orgId, remainder, request, providedFormData);
        if (chatResponse) {
          return chatResponse;
        }
        return respond({ detail: `Mock handler missing for /orgs/${orgId}/${remainder}.` }, 501);
      }
      case 'delegations':
        return respond(
          buildListResponse(
            delegationTemplates.map((delegation) => ({
              ...delegation,
              orgId
            }))
          )
        );
      case 'audit/events':
        return respond(
          buildListResponse(
            auditEventTemplates.map((event) => ({
              ...event,
              orgId
            }))
          )
        );
      default: {
        const metricsResponse = await handleMetricsRoutes(orgId, remainder, request);
        if (metricsResponse) {
          return metricsResponse;
        }
        const chatResponse = await handleChatRoutes(orgId, remainder, request, providedFormData);
        if (chatResponse) {
          return chatResponse;
        }
        return respond({ detail: `Mock handler missing for /orgs/${orgId}/${remainder}.` }, 501);
      }
    }
  }
];
